import React from 'react'
import { DIST_PRODUCT } from '@/enums/dist-products'
import { DatevExportType } from '@/enums/datev-export-type'
import { DocumentExportType } from '@/enums/document-export-type'
import TodoTypes from '@/enums/todo-types.enum'
import TodoIntervalEnum from '@/enums/todoInterval.enum'
import TextModuleTypes from '@/enums/text-module-types.enum'
import LetterPaperLayoutTypes from '@/enums/letter-paper-layout-types.enum'
import config from '@/config'

import { PositionTypes } from '@/modules/transactionV2/models/PositionTypes'

import { ModuleTypes, RECEIPT_TYPES, ROLES, SIGNATURE_FIELD_TYPES, dateConstants, purchaseStates, PERMISSION_TYPES } from '@/modules/base/util/constants'
import { InitialTypes } from '@/modules/transactionV2/models/InitialTypes'
import { formatCurrency } from './modules/_helpers/formatCurrency'
import { wisoSteuerUrl } from '@/modules/buhl/buhl-archiv/connection/_helpers/getBuhlUrls'

const { TAX_CONSULTANT_ROLE } = ROLES

const isOrgaMax = ENV.product === DIST_PRODUCT.orgamax
const productName = isOrgaMax ? 'orgaMAX' : 'MeinBüro'
const productNameLong = isOrgaMax ? 'orgaMAX' : 'MeinBüro'
const productNameWithPrefix = isOrgaMax ? 'orgaMAX' : 'WISO MeinBüro'
// Shared by the auto dunning blocks of the invoice edit view and the invoice detail view.
const autoDunningDescription = `${productName} erinnert Deinen Kunden automatisch per E-Mail, sobald die Rechnung überfällig ist.`
const switchToBetterPlan = '</br> Bitte wechsle auf einen höherwertigen Plan!'
const switchToBetterPlanRaiseSuccess = 'Steigere Deinen Erfolg und wechsle nun auf einen höherwertigen Plan.'
const planUpgradeNeeded = 'Upgrade benötigt'
const salesTelephoneNumber = isOrgaMax ? '+49 (0)5231 7090-0' : '02735 776 65001'
const salesBusinessHours = 'Mo-Fr 07:00 bis 12:00 & 13:00 bis 17:00'
const unlockViewUpgradeText = 'Wechsle jetzt in einen höheren Plan, um'
const {
  economicAsset: { mainInfoUrl },
} = config
const productNameBookkeeping = isOrgaMax ? 'orgaMAX Buchhaltung' : 'WISO MeinBüro'
const productNameDocument = isOrgaMax ? 'orgaMAX Dokumente' : 'WISO MeinBüro Dokumente'
const customerCenterName = isOrgaMax ? 'shareHUB' : 'WISO MeinBüro Portal'
const windowsProductName = isOrgaMax ? 'orgaMAX ERP' : 'WISO MeinBüro Desktop'

const january = 'Januar'
const february = 'Februar'
const march = 'März'
const april = 'April'
const may = 'Mai'
const june = 'Juni'
const july = 'Juli'
const august = 'August'
const september = 'September'
const october = 'Oktober'
const november = 'November'
const december = 'Dezember'

const back = 'Zurück'

const mainProductNaming = `${!isOrgaMax ? 'Rechnungen' : 'Buchhaltung'}`

const typeMap = {
  document: 'Dokumente',
  main: mainProductNaming,
  staff: 'Personal',
}

const eInvoiceLink = `${isOrgaMax ? 'https://www.orgamax.de/e-rechnung' : 'https://www.buhl.de/go/30275'}`
const addNewTenantFaqLink = `${isOrgaMax ? 'https://info.orgamax.de/faq-orgamax-buchhaltung/neue-mandanten-anlegen' : 'https://handbuch.meinbuero.de/allgemein/neue-mandanten-anlegen'}`
const gmailSetupFaqLink = `${isOrgaMax ? 'https://info.orgamax.de/faq-orgamax-buchhaltung/wie-richte-ich-gmail-google-mail-als-absender-in-orgamax-ein' : 'https://www.buhl.de/go/46528'}`
const office365SetupFaqLink = `${isOrgaMax ? 'https://info.orgamax.de/faq-orgamax-buchhaltung/wie-richte-ich-ein-microsoft-365-oder-exchange-online-konto-in-orgamax-ein' : 'https://www.buhl.de/go/46529'}`
const emailProviderFaqLabel = (label, url) => `<a target="_blank" rel="noopener" href="${url}">${label}</a>`

const lang = {
  dmsError: 'Fehler bei der Kommunikation mit dem DMS',
  productName: `${productName}`,
  companyName: 'deltra Business Software GmbH & Co. KG',
  productNameLong: `${productNameLong}`,
  windowsProductName: `${windowsProductName}`,
  numerationErrorPrefix: 'Beim Speichern des',
  numerationErrorSuffix: '-Nummernkreises ist ein Fehler aufgetreten',
  numerationSpecialCharsError: (specialCharacters) => `Präfix und Suffix dürfen keines der folgenden Zeichen enthalten: ${specialCharacters}`,
  salesTelephoneNumber: `${salesTelephoneNumber}`,

  autoNumberPlaceholder: 'Autom. Vergabe',

  okButtonLabel: 'OK',
  cancelButtonLabel: 'Abbrechen',
  closeButtonLabel: 'Schließen',
  continueButtonLabel: 'Weiter',
  createAndContinueButtonLabel: 'Erstellen und Weiter',
  confirmButtonLabel: 'Bestätigen',
  finishButtonLabel: 'Fertig',
  createAndFinishButtonLabel: 'Erstellen und fertig',
  saveButtonLabel: 'Speichern',
  saveAndApplyButtonLabel: 'Speichern & übernehmen',
  sendButtonLabel: 'Absenden',
  deleteButtonLabel: 'Löschen',
  addButtonLabel: 'Hinzufügen',
  backButtonLabel: back,
  logoutButtonLabel: 'Abmelden',
  autoLogout: {
    title: 'Willkommen zurück',
    message: 'Deine Sitzung wurde nach längerer Inaktivität beendet. Bitte melde Dich erneut an.',
  },
  approveButtonLabel: 'Zustimmen',
  reSetupNowButtonLabel: 'Zugangsart bestätigen',
  reSetupNowConfirmText: 'Bitte bestätige Deine Bankdaten um weiterhin Deine Banking-Funktionen nutzen zu können.',
  reSetupNowPrepareText: 'Halte bitte dazu Deine Zugangsdaten und PIN bereit.',
  printButtonToolTip: 'Drucken',
  acceptButtonLabel: 'Akzeptieren',
  rejectButtonLabel: 'Zurückweisen',
  periodLabel: 'Zeitraum',
  of: 'von',
  editButtonLabel: 'Bearbeiten',
  createXInvoice: 'XRechnung herunterladen',
  downloadXInvoice: {
    xInvoice: 'XRechnung',
    xCreditNote: 'XGutschrift',
    xCancellation: 'XStornorechnung',
  },
  copyAndEditButtonLabel: 'Kopieren und Bearbeiten',
  tryAgainLabel: 'Erneut versuchen',
  manuallySetupLabel: 'Manuell einrichten',
  showNowTitle: 'Jetzt anzeigen',
  skipButtonLabel: 'Überspringen',
  lockButtonLabel: 'Abschließen',
  lockAndSendMail: 'Abschließen und E-Mail versenden',
  lockAndPrint: 'Abschließen und drucken',
  lockAndDownload: 'Abschließen und herunterladen',
  upgradeButton: 'Jetzt Plan wechseln',
  planUpgradeButton: 'Plan upgraden',
  resetFilter: 'Filter zurücksetzen',
  changeColumns: 'Spalten anpassen',
  autoSizeColumns: 'Spaltengröße automatisch anpassen',
  export: 'Exportieren (.xlsx)',
  webshopSync: 'Webshop-Synchronisation',
  import: 'Importieren (.xlsx)',
  addCustomFields: 'Individuelle Felder bearbeiten',
  editMasterData: 'In Stammdaten bearbeiten',
  email: 'Per E-Mail versenden',
  downloadValidationResult: 'Prüfbericht herunterladen',
  enableESignature: 'Unterschrift setzen',
  print: 'Drucken',
  fullScreen: 'Vollbild',
  endFullScreen: 'Vollbild beenden',
  printPreview: 'Vorschau drucken',
  package: 'Paketschein erstellen',
  download: 'Download PDF',
  filter: 'Filter ein/ausblenden',
  selectAll: 'Alle auswählen',
  pdfLoading: 'PDF wird geladen ...',
  pdfLoadingFailed: 'PDF konnte nicht geladen werden',
  documentLoading: 'Dokument wird geladen ...',
  documentCreating: 'Dein Dokument wird erzeugt ...',
  documentCreatingFailed: 'Dein Dokument konnte nicht erzeugt werden',
  create: 'erstellen',
  noReceipt: 'Kein Beleg vorhanden',
  faqArticle: 'FAQ-Beitrag',

  readMore: 'mehr lesen',
  readLess: 'weniger lesen',
  showMore: 'Mehr anzeigen',
  showLess: 'Weniger anzeigen',
  createNewPdf: 'PDF neu generieren',

  checkingPermission: 'Berechtigung wird geprüft.',

  imprint: 'Impressum',
  privacy: 'Datenschutz',
  termsOfConditons: 'AGB',
  customerSupport: 'Support',

  // form input
  firstNameLabel: 'Vorname',
  lastNameLabel: 'Nachname',
  companyNameLabel: 'Firmenname',
  companyNameAffixLabel: 'Namenszusatz',
  officeNameLabel: 'Kanzleiname',
  streetAndNumberLabel: 'Straße & Hausnummer',
  cityLabel: 'Ort',
  zipLabel: 'PLZ',
  locationLabel: 'Stadt',
  countryLabel: 'Land',
  countryPlaceholder: 'Land auswählen',
  phoneLabel: 'Telefon',
  emailLabel: 'E-Mail',
  passwordLabel: 'Passwort',
  emailInvalidText: 'Bitte gib eine valide E-Mail ein',
  emailNotFound: 'Die E-Mail wurde nicht gefunden',
  taxNumberLabel: 'Steuernummer',
  taxNumberInvalidError: 'Steuernummer ungültig',
  wIdNrLabel: 'Wirtschafts-Identifikationsnummer (W-IdNr.)',
  wIdNrInvalidError: 'Ungültiges Format. Erwartet: DE + 9 Ziffern, optional gefolgt von einem Unterscheidungsmerkmal (z. B. DE123456789-00001).',
  wIdNrTooltip: {
    showInfoIcon: true,
    title: 'Wirtschafts-Identifikationsnummer (W-IdNr.)',
    description:
      'Die Wirtschafts-Identifikationsnummer (W-IdNr.) wird vom Bundeszentralamt für Steuern vergeben und dient der eindeutigen Identifikation wirtschaftlich tätiger Unternehmen. Sie ist nicht verpflichtend und wird aktuell nicht auf Rechnungen verwendet.',
  },
  defaultInvoiceAddress: 'Standard Rechnungsadresse',
  defaultDeliveryAddress: 'Standard Lieferadresse',
  openTaxAccountsDrawerLabel: 'Steuerkonten-Liste öffnen',

  homeAddressLabel: 'Hausanschrift',
  poBoxLabel: 'Postfach',

  changeSuccess: 'Die Änderungen wurden erfolgreich gespeichert.',

  tags: {
    NOT_ASSIGNED: 'Keine Art zugewiesen',
    CONTRACT: 'Vertrag',
    INCOMING_INVOICE: 'Rechnungseingang',
    INVOICE_CORRECTION: 'Sonstiges',
    INVOICE: 'Rechnungsausgang',
    SIGNATURE: 'Unterschrift',
    DEPOSIT: 'Einlage',
    WITHDRAWAL: 'Entnahme',
    INTERIM_REPORT: 'X-Bericht',
    COUNT_REPORT: 'Z-Bericht',
    CANCELLATION: 'Stornorechnung',
  },

  selectInput: {
    noOptionsMessage: () => 'Keine Treffer',
    createLabel: value => `'${value}' hinzufügen`,
    placeholder: 'Auswählen',
    loadingMessage: () => 'Lädt...',
    clearValueText: 'Auswahl entfernen',
    createPlaceholder: 'Wählen oder eingeben',
    interval: 'Intervall auswählen',

    customer: {
      placeholder: (allowCreate) => `Kunden ${allowCreate ? 'eingeben oder ' : ''}wählen`,
      loadingMessage: () => 'Kunden werden geladen ...',
    },
    supplier: {
      placeholder: (allowCreate) => `Lieferanten ${allowCreate ? 'eingeben oder ' : ''}wählen`,
      loadingMessage: () => 'Lieferanten werden geladen ...',
    },
    user: {
      placeholder: (allowCreate) => `Nutzer ${allowCreate ? 'eingeben oder ' : ''}wählen`,
      loadingMessage: () => 'Nutzer werden geladen ...',
      taxOffice: 'Eigene Kanzlei',
      ownCompany: 'Eigener Mandant',
      allUsers: 'Für alle Benutzer freigeben',
    },
    tenant: {
      placeholder: (allowCreate) => `Mandanten ${allowCreate ? 'eingeben oder ' : ''}wählen`,
      loadingMessage: () => 'Mandanten werden geladen ...',
    },
  },

  // currency symbols
  currencyEuro: '€',

  genericModules: {
    supplierCustomerModal: {
      supplier: 'Lieferant',
      company: 'Firmenkunde',
      private: 'Privatkunde',
      createSupplier: 'Lieferant anlegen',
      createCustomer: 'Kunden anlegen',
    },
  },
  generics: {
    additionalFunctions: 'Weitere Funktionen',
    totalAmount: 'Gesamtbetrag',
    dueDate: 'Fälligkeitsdatum',
    invoiceDate: 'Rechnungsdatum',
    general: 'Allgemein',
    details: 'Details',
    close: 'Schließen',
    inbox: 'Posteingang',
    withdrawCancellation: 'Kündigung zurückziehen',
    reactivate: 'Reaktivieren',
    selection: 'Auswahl',
    available: 'Verfügbar',
    total: 'Gesamt',
    pay: 'Bezahlen',
    emptyProp: '–',
    assign: 'Zuweisen',
    action: 'Aktionen',
    goToCustomer: 'Gehe zu Kunde',
    goToSupplier: 'Gehe zu Lieferant',
    abort: 'Abbrechen',
    all: 'alle',
    allProperCase: 'Alle',
    apply: 'Übernehmen',
    cancellations: 'Storni',
    category: 'Kategorie',
    choose: 'Wählen',
    company: 'Firma',
    containsAbbreviation: 'Enth.',
    contents: 'Inhalte',
    copy: 'Kopieren',
    copied: 'Kopiert',
    createDate: 'Erstellt am',
    addedAt: 'Hinzugefügt am',
    creditNotes: 'Gutschriften',
    letters: 'Geschäftsbriefe',
    createNew: 'Neu erstellen',
    date: 'Datum',
    entryDate: 'Eingegangen am',
    default: 'Standard',
    setAsDefault: 'als Standard setzen',
    defaultCategory: 'Standardkategorie',
    deliveryNotes: 'Lieferscheine',
    deliveryDate: 'Lieferdatum',
    deliveryDateRange: 'Lieferzeitraum',
    description: 'Beschreibung',
    discount: 'Rabatt',
    download: 'Herunterladen',
    downloadSelection: 'Auswahl herunterladen',
    downloadComplete: 'Download wird vorbereitet.',
    error: 'Fehler',
    revokeSelection: 'Auswahl aufheben',
    selectAll: 'Alle auswählen',
    edit: 'Bearbeiten',
    failed: 'Fehlgeschlagen',
    success: 'Erfolgreich',
    partially_success: 'teilw. Erfolgreich',
    finished: 'Fertig',
    for: 'Für',
    gross: 'Brutto',
    hideText: 'Eingabe verbergen',
    hour: 'Uhr',
    info: 'Info',
    inspect: 'Prüfen',
    introduction: 'Einleitung',
    invoices: 'Rechnungen',
    isLoading: 'Es wird geladen...',
    isLoadingWithTitle: (title) => `${title} wird geladen`,
    label: 'Bezeichnung',
    list: 'Liste',
    listHeaderPrintButton: (nodes) => nodes.length && nodes.length > 1 ? `${nodes.length} Dokumente drucken` : 'Dokument drucken',
    loaderText: 'Lade ...',
    loading: 'Lädt...',
    make: 'Erstellen',
    manual: 'Anleitung',
    minus: 'Minus',
    name: 'Name',
    new: 'Neu',
    net: 'Netto',
    no: 'Nein',
    offers: 'Angebote',
    orders: 'Aufträge',
    purchases: 'Bestellungen',
    plus: 'Plus',
    preview: 'Vorschau',
    printingWait: 'Drucke: Einen Augenblick bitte!',
    printingError: 'Das Dokument konnte nicht gedruckt werden.',
    private: 'Privat',
    protocol: 'Protokoll',
    refineSearch: 'Bitte passe Deine Suchanfrage an!',
    refineFilter: 'Bitte passe Deine aktiven Filter an!',
    refresh: 'Aktualisieren',
    delete: 'Löschen',
    deleteSelection: 'Auswahl löschen',
    deleteAnyway: 'Trotzdem löschen',
    reset: 'Zurücksetzen',
    discard: 'Verwerfen',
    save: 'Speichern',
    saving: 'Wird gespeichert',
    saveAndExecute: 'Speichern & Ausführen',
    saveAndPreview: 'Speichern & Vorschau',
    saveAndApply: 'Speichern & übernehmen',
    saveAndNew: 'Speichern und neu',
    saveAndNext: 'Speichern und nächste',
    serviceDate: 'Leistungsdatum',
    serviceDateRange: 'Leistungszeitraum',
    settings: 'Einstellungen',
    system: 'System',
    show: 'Ansehen',
    rename: 'Umbenennen',
    subject: 'Vorgangstitel',
    assigned: 'Verknüpft',
    taxAbbreviation: 'USt.',
    thumbnails: 'Kacheln',
    withoutData: 'Ohne Datum',
    yes: 'Ja',
    zoom: 'Zoomen',
    startDate: 'Startdatum',
    endDate: 'Enddatum',
    back: 'Zurück',
    inProgress: 'In Bearbeitung',
    markAsSeen: 'Als gelesen markieren',
    downloadFolder: 'Ordner herunterladen',
    number: {
      short: 'Nr.',
    },
    client: 'Mandant',
    taxConsultant: 'Kanzlei',
    multiple: 'Mehrere',
    reAnalyze: 'KI-Analyse wiederholen',
    unlimited: 'Unbegrenzt',
    more: 'weitere',
    showSettings: 'Einstellungen einblenden',
    hideSettings: 'Einstellungen ausblenden',
    expandAll: 'Alle aufklappen',
    collapseAll: 'Alle zuklappen',
    invalidInput: 'Die Eingabe ist fehlerhaft',
    or: 'oder',
    understood: 'Verstanden',
    upgradeNow: 'Jetzt upgraden',
    buyNow: 'Jetzt kaufen',
  },

  // default messages
  defaultTitle: 'Vorgang',
  defaultErrorMessage: 'Entschuldigung, es ist ein Fehler aufgetreten!',
  defaultForbiddenMessage: 'Du hast keine Berechtigung!',

  // error boundary messages
  errorBoundary: {
    title: 'Ein Fehler ist aufgetreten',
    description: 'Die Seite konnte nicht geladen werden. Bitte versuche es erneut.',
    reloadButton: 'Seite neu laden',
  },

  // DO-13470: in-app notice asking for the PostHog consent (Usercentrics service "PostHog")
  posthogConsentNotice: {
    text: 'Hilf uns orgaMAX zu verbessern. Dürfen wir technische Nutzungsdaten auswerten?',
    accept: 'Ja gerne',
    details: 'Details',
    close: 'Schließen',
  },
  defaultForbiddenSubscriptionModuleMessage: 'Du hast dieses Modul nicht erworben, bitte upgrade Dein Paket!',
  defaultSendEmailErrorMessage: 'Beim Versenden der E-Mail ist ein Fehler aufgetreten.',
  defaultErrorPleaseValidate: 'Bitte überprüfe Deine Eingaben und alle Pflichtfelder!',
  criticalErrorMessage: 'Es ist ein kritischer Fehler aufgetreten. Bitte melde Dich bei unserem Support!',
  smallBusinessText: 'Als Kleinunternehmer im Sinne von § 19 Abs. 1 UStG wird keine Umsatzsteuer berechnet.',
  tokenExpiredMessage: 'Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.',
  loginBootstrapErrorMessage: 'Deine Daten konnten nicht vollständig geladen werden. Einige Funktionen stehen möglicherweise noch nicht zur Verfügung.',
  loginBootstrapAuthErrorMessage: 'Deine Anmeldung konnte nicht abgeschlossen werden. Bitte melde Dich erneut an.',
  tenantSettingsDegradedMessage: 'Deine Einstellungen konnten nicht geladen werden. Bitte lade die Seite später neu.',
  finalizeLoadingMessage: 'Dein Konto wird vorbereitet. Das kann einen Moment dauern.',
  finalizeErrorMessage: 'Deine Anmeldung konnte nicht abgeschlossen werden. Bitte versuche es erneut oder melde Dich neu an.',
  emptyNotesMessage: 'Keine Anmerkungen vorhanden',
  invalidEmailErrorMessage: 'Bitte gib eine valide E-Mail-Adresse ein',
  invalidPhoneErrorMessage: 'Bitte gib eine valide Telefonnummer ein',
  invalidNumberErrorMessage: 'Bitte gib einen validen Wert ein',
  invalidDateErrorMessage: 'Bitte gib ein gültiges Datum im Format TT.MM.JJJJ ein',
  dateOutOfRangeErrorMessage: 'Das Datum liegt außerhalb des zulässigen Zeitraums',
  openDatePickerLabel: 'Kalender öffnen',
  invalidPasswordMessage: 'Dein Passwort muss mindestens 8 Zeichen, einen Kleinbuchstaben und eine Zahl oder einen Großbuchstaben enthalten!',
  emptyRequiredFieldsErrorMessage: 'Bitte fülle alle Pflichtfelder aus',
  emptyRequiredFieldErrorMessage: 'Dies ist ein Pflichtfeld',
  loadingDataMessage: 'Daten werden geladen',
  loadingDataErrorMessage: 'Daten konnten nicht geladen werden',
  invalidWebsiteMessage: 'Webseite muss eine gültige URL sein',
  emptyDocumentList: 'Du hast noch keine Dokumente angelegt',
  emptyUserList: 'Du hast noch keine Kunden oder Lieferanten angelegt.',
  lastUsedDocuments: 'Zuletzt angesehene Vorgänge',
  lastUsedCustomers: 'Zuletzt angesehene Kunden',
  fileSizeLimitErrorMessage: (limit) => `Die Datei darf maximal ${limit} MB groß sein`,
  invalidUrl: 'Die URL ist ungültig und muss mit https:// beginnen',

  account: {
    documentTitle: {
      login: 'Login',
      forgotPassword: 'Neues Passwort anfordern',
      resetPassword: 'Passwort zurücksetzen',
      acceptInvitation: 'Passwort vergeben',
      undoTenantDeletion: 'Mandant wiederherstellen',
      approve: 'E-Mail bestätigen',
      approveResend: 'Bestätigungslink senden',
    },
    cancellation: {
      title: 'Vertragskündigung',
      description: 'Bestätige bitte die Vertragskündigung.',
      reasonDescription: 'Dein Feedback',
      mainReasonLabel: 'Was ist der Hauptgrund für Deine Vertragskündigung?',
    },
    deletion: {
      description2: (endDate) => `Dein Vertrag läuft damit am <strong>${endDate}</strong> aus.`,
      mainReasonLabel: 'Was ist der Hauptgrund für Deine Kontolöschung?',
      missingFeatures: 'Fehlende Funktionen',
      missingFeaturesDescription: 'Welche Funktionen fehlen Dir?',
      mainReasonPlaceholder: 'Bitte wähle einen Hauptgrund aus',
      additionalCommentsPlaceholder: 'Teile uns mit, was wir künftig besser machen können.',
      unsuitablePricing: 'Unpassende Preispakete',
      difficultUsage: 'Schwierige Bedienung',
      insufficientSupport: 'Unzureichender Support / Service',
      duplicateAccount: 'Doppelter Account',
      other: 'Sonstiges',
      feedback: 'Dein Feedback',
      sure: 'Ich bin mir sicher',
      pleaseInsert: 'Bitte gib "<strong>Ich bin mir sicher</strong>" ein',
      accountDeletion: 'Kontolöschung',
      accountDeletionSuccess: 'Konto erfolgreich gelöscht',
      accountDeletionDemo: 'Löschung Demo-Konto',
      forceDelete: 'Konto sofort löschen',
      pleaseConfirm: 'Bestätige bitte die Kontolöschung.',
      pleaseConfirmDemo: 'Bestätige bitte die Löschung des Demo-Kontos.',
      deletionInformation: 'Sofern Du Dein Konto nicht sofort löschst, werden Deine Daten nach <strong>90 Tagen</strong> unwiderruflich gelöscht. Dieser Prozess kann nicht rückgängig gemacht werden und Du kannst Dich nach Löschung des Kontos nicht mehr anmelden.',
      deletionInformationForce: '<span style="color: red;">Du hast Konto sofort löschen gewählt. Deine Daten werden <strong>unwiderruflich gelöscht</strong></span>. Dieser Prozess kann nicht rückgängig gemacht werden und Du kannst Dich nach Löschung des Kontos nicht mehr anmelden.',
      deletionInformationWindows: `Achtung! Mit Löschung Deines Kontos werden alle bereits vorhandenen Dokumente und Belege gelöscht, es ist nicht mehr möglich diese über die Freigabe im ${customerCenterName} aufzurufen. Des Weiteren sind die aus Deinem Windows Produkt verbundenen und hochgeladenen Dokumente ebenfalls gelöscht und nicht mehr abrufbar.`,
      deletionInformationDemo: 'Du wirst zu Deiner Sicherheit ausgeloggt. Beim nächsten Login startest Du mit Deinem Haupt-Mandanten.',
      safetyPhraseErrorMessage: 'Bitte gib den richtigen Text ein',
      safetyPhrase: 'Ich bin mir sicher',
      reasonErrorMessage: 'Bitte gib ein Feedback ein',
      confirmSuccessMessage: 'Mit Klick auf "Verlassen" wirst Du automatisch ausgeloggt.',
      confirm: 'Verlassen',
      deletionError: 'Leider konnte der Prozess nicht abgeschlossen werden, bitte versuche es später noch einmal oder wende Dich an den Support.',
      deletionErrorActiveSubscriptionExists: 'Dein Konto kann erst gelöscht werden, wenn alle aktiven Pläne gekündigt sind.',
      deletionErrorOwner: 'Das Konto kann nur vom Kontoinhaber gelöscht werden.',
      strategicTitle: 'Strategische Gründe',
      strategicBusinessClosure: 'Geschäftsaufgabe/Insolvenz',
      strategicSystemChange: 'Systemwechsel',
      strategicTooLittleUsage: 'Wird zu wenig genutzt',
      contractualTitle: 'Vertragliche Gründe',
      contractualPrice: 'Preis',
      contractualContractTerms: 'Vertragsbedingungen',
      productTitle: 'Produkt- & Leistungsgründe',
      productMissingFeatures: 'Fehlende Funktionen',
      productDifficultToUse: 'Schwierige Bedienung',
      productErrorMessages: 'Fehlermeldungen',
      productSupportService: 'Support/Service',
    },
    readonlyMode: 'Lesemodus',
  },

  // article
  articleDeleteConfirmCaption: 'Artikel löschen',
  articleDeleteConfirmText: multiple => `Möchtest Du ${multiple ? 'die' : 'den'} Artikel wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  articleDeleteSuccessMessage: multiple => `${multiple ? 'Die' : 'Der'} Artikel wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  articleSaveSuccessMessage: 'Der Artikel wurde erfolgreich gespeichert',
  articleSaveDefaultErrorMessage: 'Beim Speichern des Artikels ist ein Fehler aufgetreten',
  articleSaveNumberExistsMessage: 'Die Artikelnummer existiert bereits.',
  articleSaveEanErrorMessage: 'Die EAN Nummer ist bereits vergeben',
  articleEanFieldPlaceholder: 'EAN hinzufügen',
  articleNotesSaveSuccessMessage: 'Die Anmerkung wurde gespeichert',
  articleSearchCategory: 'Artikel',
  articleVatSmallBusinessHint: 'Als Kleinunternehmer wird der Umsatzsteuersatz in Angeboten und Rechnungen nicht berücksichtigt. Um aussagekräftige Statistiken zu erhalten, sollte der Umsatzsteuersatz jedoch korrekt eingetragen werden.',
  // article - image
  articleImageUploadSuccessMessage: 'Das Artikelbild wurde erfolgreich hochgeladen',
  articleImageUploadDefaultError: 'Beim Hochladen des Artikelbildes ist ein Fehler aufgetreten',
  articleImageDeleteSuccessMessage: 'Das Artikelbild wurde erfolgreich gelöscht',
  articleImageDeleteDefaultError: 'Beim Löschen des Artikelbildes ist ein Fehler aufgetreten',
  articleImageCropError: 'Beim Zuschneiden des Artikelbildes ist ein Fehler aufgetreten',
  articleImageSelectCrop: 'Bildbereich auswählen',
  articleImageCropSelection: (height, width) => `Ausgewählte Bildgröße: ${height} x  ${width} px`,
  // article - shop
  articleShopDeleteConfirmTitle: 'Verknüpfung löschen',
  articleShopDeleteConfirmQuestion: 'Möchtest Du die Verknüpfung wirklich löschen?',
  articleShopDeleteConfirmDescription: 'Hinweis: Der Artikel bleibt in Deinem Webshop bestehen. Allerdings wird der Artikel nicht mehr automatisch mit Deinen Bestellungen verknüpft.',
  positionSavedAsArticle: 'Die Position wurde erfolgreich als Artikel gespeichert.',
  article: {
    kinds: {
      salesAndPurchase: 'Verkaufs- und Einkaufsartikel',
      purchase: 'Einkaufsartikel',
      sales: 'Verkaufsartikel',
    },
    editForm: {
      newArticle: 'Neuen Artikel erstellen',
      notes: 'Hier kannst Du Anmerkungen zu diesem Artikel hinterlegen',
      imageTypes: 'Bildgröße maximal 1300x1300px, erlaubt sind jpg/jpeg-, png- und bmp-Dateien.',
      maxHeightImageError: (fileName) => `Die Höhe von ${fileName} überschreitet die zulässige maximale Höhe von %spx.`,
      maxWidthImageError: (fileName) => `Die Breite von ${fileName} überschreitet die zulässige maximale Breite von %spx.`,
      minHeightImageError: (fileName) => `Die Höhe von ${fileName} unterschreitet die zulässige minimale Höhe von %spx.`,
      minWidthImageError: (fileName) => `Die Breite von ${fileName} unterschreitet die zulässige minimale Breite von %spx.`,
      titles: {
        fiBu: 'Individuelles Erlöskonto',
        articleData: 'Stammdaten',
        articleDataSub: 'Artikeldaten',
        price: 'Preise',
        priceAndInformation: 'Preise & Informationen',
        informationTitle: 'Informationen',
        stockManagement: 'Lagerbestand',
        miscellaneous: 'Sonstiges',
        analysis: 'Auswertungen',
        history: 'Historie',
        articleImage: 'Artikelbild',
        differentialTax: 'Differenzbesteuerung',
        differentialTaxCalculation: 'Berechnung',
        differentialTaxInfoText: 'Die abzuführende Umsatzsteuer beträgt:',
      },
      historyColumns: {
        customerOrSupplier: 'Kunde/Lieferant',
        supplier: 'Lieferant',
      },
      labels: {
        kind: 'Artikelart',
        number: 'Artikel-Nr.*',
        description: 'Beschreibung',
        title: 'Bezeichnung*',
        vat: 'Steuersatz',
        purchasePriceSmall: 'Einkaufspreis',
        purchasePrice: 'Einkaufspreis (netto)',
        purchasePriceGross: 'Einkaufspreis (brutto)',
        purchasePriceTax: 'Einkaufspreis Gebrauchtgegenstand (brutto)',
        laborShareSmall: 'Lohnanteil',
        laborShare: 'Lohnanteil netto',
        laborShareGross: 'Lohnanteil brutto',
        netUnitPrice: 'Einzelpreis (netto)',
        grossUnitPrice: 'Einzelpreis (brutto)',
        priceSmall: 'Verkaufspreis',
        price: 'Verkaufspreis (netto)',
        priceGross: 'Verkaufspreis (brutto)',
        weight: 'Gewicht in kg',
        cashArticle: 'Kassenartikel',
        cashArticleLabel: 'Handelt es sich bei diesem Artikel um einen Kassenartikel?',
        cashArticleType: 'Ist ein Gutscheinartikel',
        fiBu: 'Erlöskonto auswählen',
        fiBuLabel: 'Erlöskonto',
        unit: 'Artikeleinheit',
        category: 'Artikelkategorie',
        reservedAmount: 'Reserviert',
        availableAmount: 'Verfügbar',
        stockKeeping: 'Lagerbestandsgeführt',
        stockAmount: 'Lagerbestand',
        minimumStock: 'Mindestbestand',
        notes: 'Anmerkungen',
        notesAlert: 'Anmerkung bei Erstellung neuer Vorgänge anzeigen?',
        articleImageUploadHeader: 'Artikelbild hochladen',
        articleImageUpload: 'Bild herziehen oder',
        articleImageUploadChoose: ' auswählen',
        turnoverTotal: 'Umsatz Gesamt',
        invoiceCount: 'Aufträge Gesamt',
        averageAmount: 'Ø Menge je Auftrag',
        averagePrice: 'Ø Verkaufspreis',
        averagePriceNet: 'Ø Verkaufspreis netto',
        analysisDescription: 'Umsatz der letzten 12 Monate',
        isDifferentialTax: 'Artikel unterliegt der Differenzbesteuerung',
        factsInput: 'Sachverhalt',
        isDifferentialTaxToolTipHeader: 'Was ist die Differenzbesteuerung ?',
        isDifferentialTaxToolTipContent: 'Bei der Differenzbesteuerung wird die Umsatzsteuer nicht vom Verkaufspreis aus berechnet, sondern nur die Differenz zwischen Einkaufs- und Verkaufspreis dient als Berechnungsgrundlage. Voraussetzungen siehe §25a UStG.',
        isDifferentialTaxToolTipContent2: 'Mit der Aktivierung werden unter dem Bereich Preise & Informationen die Funktionen für die Differenzbesteuerung angezeigt. ',
        informationTooltipHeader: 'Die Differenzberechnung setzt sich wie folgt zusammen:',
        informationTooltipContent: 'Verkaufspreis (brutto) - Einkaufspreis (Gebrauchtgegenstand) = Differenzbetrag',
        informationTooltipContent2: 'Daraus ergibt sich die abzuführende Differenzsteuer. Es wird hier nur auf die Einzeldifferenz Besteuerung eingegangen. Bei Kleinunternehmern wird keine Umsatzsteuer berechnet.',
        graduatedPriceToggleLable: 'Staffelpreise aktivieren',
        addNewGraduatedPriceLable: 'Neuen Staffelpreis hinzufügen',
        graduatedPrice: 'Staffelpreise',
        graduatedQuantity: 'ab Menge',
        grossUnitPriceSmall: 'Preis',
        supplierListSection: 'Lieferanten',
        addSupplier: 'Lieferant hinzufügen',
        defaultSupplier: 'Standardlieferant',
        supplierArticleNumber: 'Artikel-Nr. Lieferant',
        lotSize: 'Losgröße',
        supplierDesignation: 'Bezeichnung',
        supplierDescription: 'Beschreibung',
        purchaseGraduatedPrices: 'Einkaufs-Staffelpreise (netto)',
        tierQuantity: 'Menge ab',
        tierPurchasePrice: 'EK netto',
        addPurchaseTier: 'Staffel hinzufügen',
        removeSupplierRow: 'Lieferant entfernen',
        addSupplierDialogTitle: 'Lieferant zuordnen',
        confirmAddSupplier: 'Hinzufügen',
        assignSuppliersDrawer: {
          title: 'Lieferanten zuordnen',
          search: {
            placeholder: 'Lieferanten durchsuchen',
          },
          assignSelection: 'Auswahl zuordnen',
          columns: {
            name: 'Name',
            street: 'Straße',
            zip: 'PLZ',
            city: 'Ort',
            phone: 'Telefon',
            tags: 'Tags',
          },
        },
        unlinkSupplierModal: {
          title: 'Soll die Zuordnung wirklich aufgehoben werden?',
          descriptionWhenRemovingDefault:
            'Dabei wird der Lieferant selbst nicht gelöscht, sondern lediglich die bestehende Zuordnung entfernt. Im Anschluss sollte ein neuer Standard-Lieferant ausgewählt werden, um eine reibungslose Weiterarbeit sicherzustellen.',
          descriptionSimple:
            'Dabei wird der Lieferant selbst nicht gelöscht, sondern lediglich die bestehende Zuordnung entfernt.',
          confirm: 'Zuordnung aufheben',
          selectPlaceholder: 'Lieferant auswählen',
          tooltip: 'Zuordnung aufheben',
        },
        assignSuppliersEmpty: 'Keine Lieferanten gefunden.',
        assignedSuppliers: 'Zugeordnete Lieferanten',
        markSupplierAsDefault: (name) => `${name} als Standard-Lieferant markieren`,
        supplierArticleNumberAtSupplier: 'Artikel-Nr. des Lieferanten',
        purchasePrices: 'Einkaufspreise',
        tierQuantityShort: 'Menge',
        tierPurchasePriceShort: 'Einkaufspreis',
        addPurchasePrice: 'Preis hinzufügen',
        standardSupplierShort: 'Standard',
        netPrice: 'Einzelpreis',
        graduatedPriceWarningLabel: 'Warnung!',
        graduatedPriceWarningInfo: ' Du hast Staffelpreise hinterlegt und den Steuersatz geändert. Willst Du Deine Einstellungen erneut prüfen oder sollen die Staffelpreise aktualisiert werden?',
        graduatedPriceErrorMessage: 'Die eingegebene Menge ist bereits vorhanden, bitte überprüfen.',
        purchasePriceSupplierHint: (warehouseTabName) => `Lieferantenspezifische Einkaufspreise lassen sich im Reiter '${warehouseTabName}' hinterlegen.`,
      },
      jumpMarkLinkTexts: {
        articleData: {
          headline: 'Stammdaten',
          text: 'Bestimme Artikel-Nr., Bezeichnung und hinterlege eine Beschreibung. Es ist auch möglich ein Bild hochzuladen.',
        },
        priceInformation: {
          headline: 'Preise & Informationen',
          text: 'Der Artikel Einkaufs-, sowie Verkaufspreis inkl. Steuersatz sind hier zu finden, ebenso weitere Daten wie Einheiten.',
        },
        customFields: {
          headline: 'Individuelle Felder',
          text: 'Deine selbstdefinierten Feldtypen sind hier abruf- und editierbar.',
        },
        stockManagement: {
          headline: 'Lager',
          text: 'Lagerbestand, Mindestbestand und Lieferanten mit lieferantenspezifischen Einkaufsdaten.',
        },
        eCommerce: {
          headline: 'E-Commerce',
          text: 'Hast Du einen Webshop angebunden, kannst Du die Verknüpfung zu Deinem Artikel hier vornehmen.',
        },
        miscellaneous: {
          headline: 'Sonstiges',
          text: 'Erweiterte Einstellungen wie EAN, Aktivierung als Kassenartikel oder auch Anmerkungen sind definierbar.',
        },
        analysis: {
          headline: 'Auswertungen',
          text: 'Artikelspezifische Auswertungen können aufgerufen und analysiert werden.',
        },
        history: {
          headline: 'Historie',
          text: 'Hier sind sämtliche Vorgänge gelistet, in denen Dein Artikel vorkommt.',
        },
      },
      laborShareModal: {
        title: 'Lohnanteil auf Vorgängen ausweisen?',
        description: 'Möchtest Du, dass Lohnanteile auf Vorgängen ausgewiesen werden? Du kannst diese Einstellung jederzeit im Bereich “Weitere Einstellungen” ändern.',
        error: 'Beim Speichern der Lohnanteile-Einstellung ist ein Fehler aufgetreten.',
        success: 'Die Lohnanteile-Einstellung wurde erfolgreich gespeichert.',
      },
      laborShareEqualsPriceModal: {
        title: 'Lohnanteil entspricht dem Verkaufspreis',
        description: 'Der Lohnanteil beträgt 100&nbsp;% des Verkaufspreises. Bitte bestätige, dass Du diese Eingabe bewusst so vornehmen möchtest.',
        confirm: 'Speichern',
        cancel: 'Abbrechen',
      },
      articleList: {
        buttons: {
          addPriceRow: 'Preis hinzufügen',
        },
        hints: {
          differentialTaxDisabled: 'Bei Artikeln die der Differenzbesteuerung unterliegen, können keine Staffelpreise hinterlegt werden.',
          deletePriceRowDisabled: 'Mindestens ein Preis muss vorhanden sein.',
          missingGraduatedPricesAccess: 'Das Feature "Staffelpreise" ist in Deinem aktuellen Plan nicht enthalten.',
        },
      },
      errors: {
        duplicateQuantity: {
          title: 'Menge bereits vorhanden',
          body: 'Mengen dürfen nicht doppelt vorhanden sein.',
        },
        defaultQuantityMissing: {
          title: 'Standardmenge fehlt',
          body: 'Mindestens ein Preis mit einer Menge von 1 muss vorhanden sein.',
        },
        laborShareHigherThanPrice: {
          title: 'Der Lohnanteil ist zu hoch',
          body: 'Der Lohnanteil darf nicht größer als der Verkaufspreis sein.',
        },
      },
    },
    list: {
      listEmptyStateTitle: 'Jetzt Artikel per Excel-Tabelle hochladen',
      buttonLabel: 'Los geht´s',
    },
    codes: {
      noOptionsMessage: 'EAN eingeben',
      createLabel: (label) => `EAN '${label}' erstellen`,
      labelKey: 'EAN',
    },
    categoryInput: {
      optionCreated: (option) => `Die Kategorie ${option} wurde erfolgreich erstellt.`,
    },
    unitInput: {
      optionCreated: (option) => `Die Einheit ${option} wurde erfolgreich erstellt.`,
    },
    customerInput: {
      optionCreated: (option) => `Die Kategorie ${option} wurde erfolgreich erstellt.`,
    },
    selectInput: {
      no: 'Art-Nr.',
      available: 'Verfügbar',
      selectInputLabel: 'hinzufügen',
      placeholder: 'Artikel / Leistung eingeben oder suchen',
    },

    detail: {
      title: {
        priceAndInformation: 'Preise & Informationen',
        stock: 'Lagerbestand',
        description: 'Artikelbeschreibung',
        image: 'Artikelbild',
        statistic: 'Umsatzübersicht',
        note: 'Anmerkungen',
      },
      subTitle: {
        statistic: 'Umsatz der letzten 12 Monate',
      },
      label: {
        stock: {
          amount: 'Lagerbestand',
          minimum: 'Mindestbestand',
          reserved: 'Reserviert',
          available: 'Verfügbar',
        },
        prices: {
          graduated: 'Staffelpreise',
          price: (smallBusiness) => smallBusiness ? 'Verkaufspreis' : 'Verkaufspreis netto',
          gross: 'Verkaufspreis brutto',
          purchasePrice: (smallBusiness) => smallBusiness ? 'Einkaufspreis' : 'Einkaufspreis netto',
          purchasePriceGross: 'Einkaufspreis brutto',
          laborShare: (smallBusiness) => smallBusiness ? 'Lohnanteil' : 'Lohnanteil netto',
          laborShareGross: 'Lohnanteil brutto',
        },
        kind: 'Artikelart',
        unit: 'Einheit',
        mwst: 'Steuersatz',
        number: 'Artikel-Nr.',
        category: 'Kategorie',
        weight: 'Gewicht',
        statistic: {
          turnover: 'Umsatz Gesamt',
          invoiceAmount: 'Aufträge Gesamt',
          amount: 'Ø Menge je Auftrag',
          price: (smallBusiness) => smallBusiness ? 'Ø Verkaufspreis' : 'Ø Verkaufspreis netto',
        },
      },
      placeholder: {
        minStockUndefined: 'Nicht vergeben',
        priceGraduated: 'Hinterlegt',
        description: 'Keine Artikelbeschreibung vorhanden',
      },
    },
  },
  //
  differentialTax: {
    differentialTaxLabel: 'Sammlungsstücke und Antiquitäten/Sonderregelung',
    differentialTaxName: 'differentialTax01',
  },
  // account
  loginTitle: 'Willkommen zurück<br />Jetzt anmelden',
  loginLabel: 'Jetzt anmelden',
  loginButtonLabel: 'Anmelden',
  loginButtonLabelAlternative: 'Einloggen',
  registerError: 'Die Domäne der E-Mail ist nicht zulässig.',
  loginRegisterLink: 'Jetzt kostenlos registrieren',
  loginFooterText: 'Du hast noch kein Konto?',
  loginRegisterBoxText: `Neu bei ${productName}?`,
  confirmToRegisterLink: 'Zurück zur Registrierung',
  loginForgotPasswordLink: 'Passwort vergessen?',
  // orgaMAX login in the new brand design
  loginBrand: {
    title: 'Willkommen zurück!<br />Jetzt anmelden',
    emailLabel: 'E-Mail',
    passwordLabel: 'Passwort',
    registerLink: 'Jetzt kostenlos testen',
  },
  loginWrongPasswordOrEmailError: 'E-Mail-Adresse oder Passwort ist nicht korrekt.',
  loginUserLockedError: 'Dein Benutzer ist gesperrt',
  loginEmptyPasswordError: 'Bitte gib Dein Passwort ein',
  loginEmptyEmailError: 'Bitte gib Deine E-Mail ein',
  loginCompletionTitle: 'Fast startklar!',
  loginCompletionSubtitle: 'Nach diesen kleineren Einstellungen kann es auch schon losgehen.',
  approveEmailInfoMessage: 'Bitte bestätige noch Deine E-Mail-Adresse.',
  registrationSubheading: 'Kostenlos registrieren & testen',
  registrationSuccess: `Herzlich willkommen bei ${productName}. Schön, dass Du da bist!`,
  registerLoginLink: 'Zum Login',
  registerFooterText: 'Du hast schon ein Konto?',
  registerButton: 'Jetzt durchstarten',
  registerAgbText: 'AGB',
  registerAvvText: 'AVV',
  // non-breaking spaces: the sentence wraps only at the links
  registerAgbAndAvvAgree: (agbLink, avvLink) => <>Ich{' '}stimme{' '}den{' '}{agbLink}{' '}und{' '}dem{' '}{avvLink}{' '}zu.</>,
  registrationBenefits: {
    bullet1: 'Test endet automatisch',
    bullet2: 'Keine Zahlungsdaten erforderlich',
    bullet3: 'Persönlicher Support',
  },
  // orgaMAX registration in the new brand design
  registrationBrand: {
    emailLabel: 'E-Mail',
    emailRequired: 'Bitte gib Deine E-Mail-Adresse ein.',
    emailInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein, zum Beispiel max@firma.de.',
    emailExists: `Zu dieser E-Mail-Adresse gibt es schon ein ${productName}-Konto.`,
  },
  userSupportLabel: 'Hilfe nötig? Hier geht\'s zur Fernwartung',
  emailAddressFormLabel: 'E-Mail-Adresse eingeben',
  passwordFormLabel: 'Passwort eingeben',
  demoUserTokenExpiredMessage: `Dein Testzeitraum ist abgelaufen. <br/>Abonniere ${productNameLong}, um alle Funktionen nutzen zu können.`,
  demoUserNoAccessMessage: `Erstelle jetzt Deinen ${productNameLong} Zugang, um alle Funktionen nutzen zu können.`,
  demoDescription: 'Möchtest Du für den 14-tägigen Testzeitraum Demodaten laden, um Abläufe und Vorgänge besser kennenzulernen?',
  demoSubdescription1: 'Diese Daten könntest Du im Anschluss schnell und unkompliziert entfernen.',
  demoSubdescription2: 'Von Dir hinzugefügte Daten werden dabei ebenfalls entfernt, sodass Du frisch anfangen kannst.',
  forgotPasswordButtonLabel: 'Link senden',
  forgotPasswordTitle: 'Passwort vergessen',
  forgotPasswordDescription: 'Wir senden Dir dazu einen Link per E-Mail.',
  forgotPasswordSuccesMessage: 'Wir haben Dir einen Link zum Zurücksetzen Deines Passwortes gesendet!',
  forgotPasswordLoginDescription: 'Du hast bereits einen Account?',
  // orgaMAX "Passwort vergessen" in the new brand design
  forgotPasswordBrand: {
    text: 'Gib Deine E-Mail-Adresse ein. Wir senden Dir dazu einen Link per E-Mail.',
    emailLabel: 'E-Mail',
    emailNotFound: `Zu dieser E-Mail-Adresse gibt es kein ${productName}-Konto.`,
  },
  resetPasswordTitle: 'Passwort eingeben',
  resetPasswordButtonLabel: 'Passwort bestätigen',
  resetPasswordDescription: 'Bitte gib ein Passwort ein.',
  resetPasswordSuccessMessage: 'Dein Passwort wurde erfolgreich zurückgesetzt!',
  // orgaMAX "Passwort eingeben" (after a password reset) in the new brand design
  resetPasswordBrand: {
    passwordLabel: 'Passwort',
    passwordRequired: 'Bitte gib ein Passwort ein.',
    rulesNotMet: 'Dein Passwort erfüllt noch nicht alle Anforderungen.',
  },
  // the chips below every password field of the new design (onboarding, "Passwort eingeben", "Passwort vergeben")
  passwordRules: {
    minLength: 'Mind. 8 Zeichen',
    upperAndLower: 'Groß- und Kleinbuchstaben',
    digitOrSpecial: 'Zahl oder Sonderzeichen',
  },
  acceptInvitationPasswordDescription: 'Bitte gib ein Passwort ein.',
  acceptInvitationTitle: 'Passwort vergeben',
  acceptInvitationSuccesMessage: 'Dein Account wurde erfolgreich eingerichtet!',
  acceptInvitationInvalidCodeMessage: 'Dein Einladungscode ist ungültig, bitte lass Dir einen neuen zusenden!',
  approveResendTitle: 'E-Mail-Adresse bestätigen',
  approveResendDescription1: 'Du hast Deine E-Mail-Adresse leider noch nicht bestätigt.',
  approveResendDescription2: 'Um Dich einloggen zu können, klicke bitte auf den Link, den wir Dir per E-Mail gesendet haben.',
  approveResendButtonLabel: 'Bestätigungslink anfordern',
  approveResendSuccessMessage: 'Wir haben Dir einen neuen Bestätigungslink gesendet.',
  approveResendEmailInvalidMessage: 'Es gibt Probleme mit der E-Mail-Adresse. Bitte melde Dich beim Support.',
  approveTitle: 'E-Mail bestätigen',
  approveDescription1: 'Deine E-Mail-Adresse wird bestätigt.',
  approveDescription2: 'Bitte warte einen Augenblick!',
  approvedTitle: 'E-Mail bestätigt',
  approvedDescription: 'Deine E-Mail-Adresse wurde bestätigt.',
  approvedButtonLabel: 'Zum Login',
  invoiceCounterTitle: 'Rechnungszähler',
  trialExpiredFooterText: 'Testphase ist abgelaufen',
  emailAddressTitle: 'E-Mail-Adresse',
  passwordTitle: 'Passwort',
  connectTitle: 'Verbinden',
  extendedSettingsTitle: 'Erweiterte Einstellungen',

  footer: {
    subscriptionExpired: 'Abo abgelaufen',
    trialExpiredText: 'Testphase ist abgelaufen',
    trialText: (remainingDays) => `Noch ${remainingDays} Tag${remainingDays === 1 ? '' : 'e'} testen`,
    trialWithDiscountText: (remainingDays) => `Nur noch ${remainingDays} Tag${remainingDays === 1 ? '' : 'e'}: Testen & Rabatt sichern`,
    subscriptionText: (remainingDays) => `Abo endet in  ${remainingDays} Tag${remainingDays === 1 ? '' : 'en'}`,
    cookies: 'Cookie Einstellungen',
    isDemo: 'Demomodus',
    impressum: 'Impressum',
    agb: 'AGB',
    legal: 'Datenschutz',
  },

  revenueLimitWarning: {
    notchText: (amount) => `Nur noch ${amount} Umsatzvolumen übrig. Jetzt upgraden`,
    dashboardTitle: 'Du näherst Dich Deinem Umsatzlimit',
    untilLimit: (amount, percent, percentClass) => `Noch ${amount} bzw. <span class="${percentClass}">${percent} %</span> bis zum Limit`,
    currentRevenue: 'aktueller Umsatz',
    revenueLimit: 'Umsatzlimit',
    upgradeButton: 'Jetzt erweitern',
    infoTooltip: 'Dein Umsatzlimit richtet sich nach Deinem gebuchten Tarif.',
    closeNotch: 'Hinweis schließen',
  },

  // IncomeExpense
  incomeExpense: {
    title: 'Einnahmen-Ausgaben-Auswertung',
    list: {
      tableCellTitle: 'Zeitraum',
      tableCellTitle1: 'Vorjahr',
      tableCellTitle2: 'Jahr',
      tableCellTitle3: 'Differenz Vj.',
      tableCellTitle5: 'Gewinn / Verlust',
      tableSubCellTitle: 'in €',
      tableSubCellTitle1: 'in %',
      tableTotalCellTitle: 'Gesamt',
    },
  },
  // bwa
  bwa: {
    title: 'Allgemeine Geschäftsentwicklung',
    solidTitles: ['Betrieblicher Rohertrag', 'Betriebliche Kosten'],
    list: {
      tableCellTitle: 'Differenz VM.',
      tableCellTitle1: 'Differenz VJ.',
      tableCellTitle2: 'Differenz VQ.',
      tableSubCellTitle: 'in €',
      tableSubCellTitle1: 'in %',
      tableCellYearTitle: 'Jahr',
    },
  },

  // VatReturn
  vatReturn: {
    correction: 'Korrektur',
    form: {
      placeHolderYear: 'Jahr',
      placeHolderInterval: 'Intervall',
      placeHolderPeriod: 'Zeitraum',
      confirmButtonLabel: 'UStVA erstellen',
      intervalOptions: {
        month: 'Monat',
        quarter: 'Quartal',
        year: 'Jahr',
      },
    },
    periods: {
      quarter1: '1. Quartal',
      quarter2: '2. Quartal',
      quarter3: '3. Quartal',
      quarter4: '4. Quartal',
      year: 'Jahr',
      january,
      february,
      march,
      april,
      may,
      june,
      july,
      august,
      september,
      october,
      november,
      december,
    },
    manual: {
      title: 'UMSATZSTEUERVORANMELDUNG',
      subTitle1: 'Download erfolgreich!',
      subTitle2: 'So geht es jetzt weiter',
      elster1: 'Login ELSTER',
      elster2: 'und logge Dich ein.',
      period1: 'Zeitraum wählen',
      period2: 'Wähle den Zeitraum für Deine Meldung aus.',
      xml1: 'XML hochladen',
      xml2: 'Lade die herunterge-ladene Datei hoch.',
    },
    infoText1: 'Erstelle Deine Umsatzsteuervoranmeldung (USt-VA) für den ausgewählten Zeitraum und übermittle diese direkt an Dein zuständiges Finanzamt über die Funktion "Prüfen & Übermitteln". Zusätzlich hast Du die Möglichkeit, Deine USt-VA als XML-Datei zu speichern, indem Du "Elster-XML speichern" wählst und sie anschließend ins ELSTER-Portal hochladen.',
    infoText2: 'Stelle sicher, dass Du den korrekten Voranmeldungszeitraum ausgewählt hast. Mit dem Klick auf "USt-VA erstellen" wird das Formular generiert, das die anzumeldenden Werte enthält.',
  },
  taxReports: {
    title: 'Steuerauswertungen',
  },
  buhlArchiv: {
    connection: {
      title: 'Schnittstelle zu WISO Steuer',
      submenuItem: {
        title: 'Schnittstelle zu WISO Steuer',
        text: 'Hier kannst Du die Schnittstelle zu WISO Steuer zum Übermitteln von EÜR Exporten verwalten.',
      },
      login: {
        description: 'Übertrage Deine Einnahmenüberschussrechnung (EÜR) direkt in Deine Steuer-Box - ganz ohne erneutes Abtippen. So sparst Du Zeit und vermeidest Übertragungsfehler. Die Daten kannst Du anschließend in <a target="_blank" href="https://www.buhl.de/go/45968">WISO Steuer</a> übernehmen und für Deine Einkommensteuererklärung nutzen. Für die Nutzung benötigst Du ein kostenfreies buhl:Konto.',
        submit: 'Verbindung herstellen',
        info: 'Mehr Informationen',
      },
      info: {
        description: 'Die Verbindung wurde erfolgreich hergestellt. Du kannst Deine EÜR nun an WISO Steuer übermitteln. Erstelle dazu eine <a href="/taxReports/euer">neue EÜR</a> oder übertrage bereits erstellte EÜRs aus <a href="/taxReports#euer">Deiner Historie.</a>',
        connected: 'Erfolgreich verbunden',
        error: 'Fehler beim Verbinden',
        email: 'Verbundenes buhl:Konto:',
        disconnect: 'Verbindung trennen',
        retry: 'Erneut abrufen',
      },
      confirmDisconnect: {
        title: 'Verbindung zu WISO Steuer trennen',
        description: 'Die Verbindung wird getrennt, so dass keine EÜR mehr an WISO Steuer übermittelt werden kann. Dies hat keine Auswirkung auf bereits übertragene Daten. Du kannst die Verbindung jederzeit wiederherstellen.',
      },
      toast: {
        connected: 'Verbindung zu WISO Steuer erfolgreich hergestellt.',
      },
      success: {
        login: 'Zum Login',
      },
      errors: {
        redirectLogin: 'Beim Weiterleiten zum buhl:Konto ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        connect: 'Es konnte keine Verbindung hergestellt werden. Bitte überprüfe die eingegebenen Daten.',
        disconnect: 'Die Verbindung zu WISO Steuer konnte nicht getrennt werden. Bitte versuche es erneut.',
      },
    },
  },
  taxForms: {
    loaderText: 'Lade Steuerauswertungen',
    noCurrentTaxFormKeyErrorMessage: 'Berechnung des Steuerformulars nicht möglich. Steuerformulartyp ist nicht gesetzt. Bitte öffne das Formular erneut.',
    invalidTaxFormKeyStateErrorMessage: 'Berechnung des Steuerformulars nicht möglich. Steuerformulartyp passt nicht zur Anfrage.',
    unknownTaxFormKey: 'Ungültige Konfiguration. TaxFormKey ist unbekannt.',
    calculationFinishedMessage: 'Berechnung erfolgreich abgeschlossen',
    hintExistsMessage: 'Es gibt Hinweise. Bitte überprüfe Deine Eingaben.',
    noCalculationResultSummary: 'Kein Berechnungsergebnis vorhanden.',
    resetInput: 'Alle Eingaben zurücksetzen',
    taxNumberCaption: 'Steuernummer',
    businessIdentificationNumberCaption: 'Wirtschafts-IdNr.',
    preview: {
      title: 'Vorschau',
      titleElster: 'Vorschau Elsterübertragung',
      downloadSuffix: 'Vorschau.pdf',
      autoRefresh: 'aut. aktualisieren',
      refresh: 'Vorschau aktualisieren',
      zoom: 'Vergrößern',
      previewIsCreating: 'Vorschau wird erstellt...',
      createPreviewErrorMessage: 'Beim Erstellen der Vorschau ist ein Fehler aufgetreten!',
      noPreviewText: 'Die Vorschau ist erst nach {0}-Erstellung verfügbar.',
      noPreviewDefined: 'Für dieses Steuerformular ist keine Vorschau-Darstellung definiert.',
    },
    summaryMain: {
      liveWidgetTitle: 'Umsatzsteuer',
      liveWidgetLinkTaxFormMainPage: 'Steuerformulare ansehen',
      subTitleCurrentMonth: (m, y) => `akt. Monat ${m} ${y}`,
      subTitlePriorMonth: (m, y) => `Vormonat ${m} ${y}`,
      subTitleMonth: (m, y) => `Monat ${m} ${y}`,
    },
    forms: {
      base: {
        exportModal: {
          title: (year) => `EÜR ${year} an WISO Steuer übertragen`,
          description: 'Mit der Verknüpfung zu WISO Steuer überträgst Du Deine EÜR direkt in Deine Steuer-Box und kannst diese bequem in WISO Steuer abrufen. <br /> Alternativ kannst Du die EÜR als XML-Datei herunterladen. Diese lässt sich manuell importieren oder nachträglich aus Deiner Historie an WISO Steuer übermitteln.',
          hints: {
            title: 'Info:',
            preview: 'Amtlicher EÜR-Abschluss nur jährlich möglich: Der Export umfasst immer das komplette Kalenderjahr, unabhängig von der aktuellen Filterung.',
          },
          buttons: {
            download: 'Download (XML)',
            export: 'Übermitteln an WISO Steuer',
            connect: 'Mit WISO Steuer verbinden',
          },
        },
        primaryActionButtonText: 'Prüfen & Übermitteln',
        createXmlButtonText: 'Elster-XML speichern',
        sendToTaxOfficeButtonText: 'Übermitteln',
        sendToTaxOfficeButtonTextToolTip: 'Du musst noch bestätigen.',
        sendToTaxOfficeCheckboxText: 'Ich bestätige, dass der Anmeldezeitraum und die Steuernummer korrekt sind.',
        subscriptionNeedForSend: 'In der Testphase können keine Formulare an Elster übertragen werden.',
        primaryActionSuccessMessage: {
          xml: 'Das Steuerformular ist erfolgreich heruntergeladen worden. Die Datei findest Du im Downloadverzeichnis.',
          xmlToBuhlArchiv: `Deine EÜR wurde an WISO Steuer übermittelt. <a href="${wisoSteuerUrl}" target="_blank">Jetzt WISO Steuer öffnen</a>.`,
          protocol: 'Das Protokoll ist erfolgreich heruntergeladen worden. Die Datei findest Du im Downloadverzeichnis.',
          online: 'Die Steuerformulardaten sind erfolgreich an das Finanzamt übermittelt worden.',
        },
        createpostfix: ' erstellen', // Postfix is concated with specified headline at runtime (e.g. 'Umsatzsteuervoranmeldung')
        tocEntryPeriodSelectionCaption: 'Voranmeldungszeitraum bestimmen',
        tocLabel: 'Gehe zu',
        primaryActionNotDefined: 'Ausführung der Aktion nicht möglich. Prozess ist nicht definiert.',
        noPrimaryActionSelected: 'Ausführung der Aktion nicht möglich. Es ist keine Aktion ausgewählt.',
        invalidTaxSettingsMessage: 'Ausführung der Aktion nicht möglich. Deine Steuereinstellungen sind unvollständig.',
        notVerifiedErrorTitle: 'Übermittlung aktuell nicht möglich',
        notVerifiedErrorMessage: 'Die Umsatzsteuer-Voranmeldung kann in diesem Fall nicht direkt aus orgaMAX übermittelt werden.\n\nBitte exportiere die Daten als ELSTER-XML und reiche diese über Dein ELSTER-Konto beim Finanzamt ein.',
        notVerifiedPrimaryButtonLabel: 'ELSTER-XML exportieren',
        ignoreHints: 'Alle Hinweise ignorieren',
        inputFieldIsCalculatedTooltip: 'Dieser Wert wird automatisch berechnet.',
        taxAssessmentPeriod: 'Veranlagungszeitraum',
        closeReport: 'Auswertung schließen',
        wisoExport: 'WISO Steuer Export',
        buhlArchivExport: 'Übermitteln an WISO Steuer',
        tenDaysRule: '10-Tage-Regelung',
      },
      taxFormSettings: {
        headline: 'Steuereinstellungen',
        navtitle: 'Steuereinstellungen',
        navtext: 'Hier können die notwendigen Angaben zur Erstellung des Steuerformulars festgelegt werden, wie Steuernummer, Anschrift und andere.',
        description: 'In diesem Bereich gelangst Du zu den steuerrelevanten Einstellungen, diese Angaben sind für die Erstellung der Steuerformulare einzutragen.',
        validationHintText: 'Bitte trage folgende fehlende Informationen in den {0} nach, um eine der Auswertungen erstellen zu können:',
        primaryActionButtonText: 'Steuereinstellungen öffnen',
      },
      ustva: {
        shortCutName: 'UStVA',
        headline: 'Umsatzsteuervoranmeldung',
        navtitle: 'Umsatzsteuervoranmeldung',
        navtext: 'USt-VA monatlich oder quartalsweise erzeugen und an Dein Finanzamt weiterleiten',
        description: '<p>Hier steht Dir ein Formular für die Erstellung der Umsatzsteuervoranmeldung zur Verfügung. Du kannst im Formular die Einstellungen zum gewünschten Voranmeldungszeitraum vornehmen.</p>'
          + '<p>Erstelle Deine Umsatzsteuervoranmeldung (USt-VA) und übermittle diese direkt an Dein zuständiges Finanzamt. Zusätzlich hast Du die Möglichkeit, Deine USt-VA als XML-Datei zu speichern und sie anschließend ins ELSTER-Portal hochladen.</p>',
        primaryActionButtonText: 'Formular öffnen',
        summaryCaptions: {
          taxNumber: 'Steuernummer',
          businessIdentificationNumber: 'Wirtschafts-IdNr.',
          company: 'Unternehmensdaten',
          salestaxsum: 'Erhaltene USt.',
          inputtaxsum: 'Bezahlte USt.',
          taxresultpostpay: 'USt-Nachzahlung',
          taxresultcredit: 'USt-Erstattung',
          taxresult: 'USt-Ergebnis',
        },
        sendToTaxOfficeCheckboxTooltipBody: (taxNumber, period) => `
        Deine Umsatzsteuervoranmeldung ist zur Übermittlung an das zuständige Finanzamt vorbereitet.
        <p>
        Deine Steuernummer <b>${taxNumber}</b>
        <br />
        Meldezeitraum <b>${period}</b>
        <p/>
        Indem Du bestätigst, versicherst Du, dass die angegebene Steuernummer korrekt ist und Du berechtigt bist, unter dieser Nummer Deine Umsatzsteuervoranmeldung einzureichen.
        `,
      },
      euer: {
        shortCutName: 'EÜR',
        headline: 'Einnahmenüberschussrechnung',
        navtitle: 'Einnahmenüberschussrechnung',
        navtext: 'EÜR-Auswertung: Übersicht Deiner Einnahmen und Ausgaben. Die Anlage AVEÜR ist Bestandteil der Einnahmenüberschussrechnung.',
        description: '<p>Hier kannst Du Deine Einnahmenüberschussrechnung (EÜR) als übersichtliche Auswertung einsehen. Die EÜR zeigt Dir eine Zusammenfassung Deiner betrieblichen Einnahmen und Ausgaben für das ausgewählte Wirtschaftsjahr.</p>'
          + '<p>Mit der EÜR-Auswertung behältst Du Deine Finanzen stets im Blick. Nutze die Übersicht, um Werte direkt anhand der Zeilen und Kennzahlen bei Elster einzutragen oder exportiere die Ergebnisse im WISO Steuer Format für eine weiterführende Bearbeitung.</p>',
        primaryActionButtonText: 'Formular öffnen',
        summaryCaptions: {
          taxNumber: 'Steuernummer',
          company: 'Unternehmensdaten',
          salestaxsum: 'Erhaltene USt.',
          inputtaxsum: 'Bezahlte USt.',
          taxresultpostpay: 'USt-Nachzahlung',
          taxresultcredit: 'USt-Erstattung',
          taxresult: 'USt-Ergebnis',
        },
        noItemsForSelectedCategoryTitle: 'Keine Einträge in dieser Kategorie',
        noItemsForSelectedCategoryDescription: 'Die Einnahmenüberschussrechnung zeigt nur bezahlte Belege an.\nDiese Kategorie enthält möglicherweise keine bezahlten Belege.',
        noSelectedCategoryTitle: 'Keine Kategorie ausgewählt.',
        noSelectedCategoryDescription: 'Klicke links auf eine Kategorie.',
        exportHistory: {
          tooltip: {
            download: 'Herunterladen',
            transfer: 'Übermitteln an WISO Steuer',
            transferDisabled: 'Aktuell nicht mit WISO Steuer verbunden. Bitte Schnittstelle einrichten.',
            transferred: 'Bereits an WISO Steuer übermittelt',
          },
          title: 'Historie',
          noRowsToShow: 'Keine Exporte vorhanden',
          loadingOoo: 'Exporte werden geladen',
          status: {
            notConnected: 'Nicht verbunden',
            notTransferred: 'Nicht übermittelt',
            error: 'Fehler',
          },
          columns: {
            createdAt: 'Erstellt am',
            year: 'Jahr',
            transferredAt: 'Übermittlung am',
            transferredAtColumnHeader: 'an WISO Steuer',
            quickAction: 'Aktionen',
          },
          buttons: {
            openWisoSteuer: 'WISO Steuer öffnen',
          },
          hints: {
            reload: 'Bitte lade die Seite neu.',
          },
          errors: {
            fetch: 'Fehler beim Laden der Historie',
            transferArchive: 'Fehler beim Übermitteln an WISO Steuer',
            download: 'Fehler beim Herunterladen des Exports',
          },
        },
      },
      taxFormHistoryList: {
        headline: 'Erstellte Steuerformulare und Exportdateien',
        navtitle: 'Exporte',
        navtext: 'Die von Dir erzeugten XML Dateien, Protokolle und Berichte können jederzeit hier heruntergeladen und weiterverarbeitet werden.',
        description: 'Die Liste beinhaltet alle von Dir erzeugten Exportdateien für den Versand mit Elster und Protokolle, die abhängig vom gewählten Steuerformular Aufschluss über den Inhalt des erzeugten Steuerformulars bieten.',
        noExportsFound: 'Keine Exporte vorhanden',
        innerLoaderText: 'Exporte werden geladen',
        actionDownloadXml: 'XML (herunterladen)',
        actionDownloadProtocol: 'Herunterladen',
        actionShowProtocol: 'Protokoll',
        actionShowTransferTicket: 'Übertragungsprotokoll',
        columns: {
          createdAt: 'Datum',
          year: 'Jahr',
          period: 'Zeitraum',
          art: 'Art',
          action: 'Aktionen',
        },
        shortCutIsCorrection: 'ber.',
        legazyPostfix: '(ALT)',
      },
    },
    requiredVatFields: {
      companyAddress: { text: 'Firmenanschrift', fields: ['companyName', 'city', 'street', 'zipCode', 'countryIso'] },
      email: { text: 'E-Mail', fields: ['email'] },
      phone1: { text: 'Telefon 1', fields: ['phone1'] },
      taxNumber: { text: 'Steuernummer', fields: ['taxNumber'] },
      taxOfficeId: { text: 'Finanzamt', fields: ['taxOfficeId'] },
    },
  },
  // moduleSubscription
  moduleSubscription: {
    list: {
      allProducts: 'Alle Produkte & Erweiterungen',
      success: 'Dein Kauf wurde erfolgreich abgeschlossen, Deine Module sind direkt verfügbar.',
      moduleItem: {
        modules: {
          default: 'Steigere Deine Effizienz und erweitere Deine Nutzungsmöglichkeiten mit nur einem Klick, um Deine Arbeitsabläufe zu optimieren und Deine Softwareerfahrung zu verbessern.',
          [ModuleTypes.SIGNATURES]: 'Mit der Erweiterung “Elektronische Unterschrift” kannst Du Dokumente schnell und unkompliziert zur Unterschrift bereitstellen. Es ist möglich, Unterschriften direkt in das Dokument zu platzieren und mehreren Empfängern gleichzeitig Zugriff zur digitalen Unterzeichnung zu gewähren. So wird der Prozess der Dokumentenfreigabe und -unterschrift effizienter und vollständig papierlos.',
          [ModuleTypes.MULTI_USER]: 'Erweitere Dein Team mit zusätzlichen Benutzern, fördere die Zusammenarbeit und komme gemeinsam schneller ans Ziel.',
          [ModuleTypes.TIME_TRACKING]: 'Erfasse Deine Arbeitszeiten, behalte den Überblick über Deine geleisteten Stunden und gib diese anschließend zur Abrechnung frei.',
          [ModuleTypes.RECURRING_INVOICES]: 'Erstelle wiederkehrende Rechnungen und versende diese automatisch in unterschiedlichen Intervallen an Deine Kunden. Verfügbare Intervalle: monatlich, jährlich, wöchentlich, quartalsweise, halbjährlich, 2-monatlich, 14-tägig.',
          [ModuleTypes.MULTI_LETTERPAPER]: 'Mit der Erweiterung “Mehrere Briefpapiere” hinterlegst Du beliebig viele zusätzliche Briefpapiere und Layouts und wählst je Geschäftsdokument aus, welches verwendet werden soll.',
          [ModuleTypes.PUBLIC_API]: `Mit dem Add-on „${productName} API“ erstellst Du eigene Erweiterungen und erhältst Zugriff auf die ${productName}-API, um Deine Systeme mit ${productName} zu verbinden.`,
          [ModuleTypes.STORAGE_PACKAGE]: 'Erweitere den Speicherplatz für Deine Dokumente um jeweils 10 GB. Du kannst die Erweiterung beliebig oft buchen und Deinen Speicherplatz so unbegrenzt erweitern.',
        },
        descriptions: {
          [ModuleTypes.SIGNATURES]: [
            'Dokumente schnell und unkompliziert zur Unterschrift bereitstellen.',
            'Unterschriften direkt im Dokument platzieren',
            'Mehreren Empfängern gleichzeitig Zugriff zur digitalen Unterzeichnung zu gewähren',
          ],

          [ModuleTypes.MULTI_USER]: [
            'Im Team besser zusammenarbeiten',
          ],
          [ModuleTypes.TIME_TRACKING]: [
            'Projektzeiten erfassen & abrechnen',
            'Kosten & Budgets überblicken',
          ],
          [ModuleTypes.RECURRING_INVOICES]: [
            'Erstellung von wiederkehrende Rechnungen',
            'Automatisches Versenden in vordefinierten Intervallen',
          ],
          [ModuleTypes.DIRECT_DEBIT]: [
            'Erstellung von Lastschriften',
            'SEPA Lastschriftmandate erstellen und verwalten',
          ],
          [ModuleTypes.MULTI_LETTERPAPER]: [
            'Verschiedene Briefpapiere für unterschiedliche Zwecke nutzen',
            'Eigene PDF-Vorlagen hochladen und zuordnen',
          ],
          [ModuleTypes.PUBLIC_API]: [
            'Eigene Erweiterungen im Entwicklerbereich erstellen',
            'API-Key und Secret für Deine Erweiterungen erhalten',
            `Fremdsysteme per API mit ${productName} verbinden`,
          ],
          [ModuleTypes.STORAGE_PACKAGE]: ({ price, packageSize }) => [
            `Je ${packageSize} GB zusätzlicher Speicherplatz für ${price} € pro Monat`,
            'Unbegrenzt oft für Deine Dokumente buchbar',
            'Sofort nach dem Kauf verfügbar',
          ],
        },
        storageAmount: (size) => `${size} GB`,
        disabled: {
          title: 'Information zu Erweiterungen',
          description: `Du hast aktuell keinen aktiven Plan für dieses Produkt.
            Erweiterungen können nur bei einem aktiven Plan direkt in den Warenkorb gelegt werden.

            <p>Hast Du Dich für einen Plan entschieden, dann:
            <ul><li>1. Lege Deinen gewünschten Plan in den Warenkorb und gehe auf “Zur Kasse”</li>
            <li>2. Dir wird nun eine Auswahl der verfügbaren Erweiterungen angezeigt</li>
            <li>3. Lege nun die Erweiterung in den Warenkorb</li>
            <li>4. Schließe den Bestellvorgang ab</li>
            <li>5. Glückwunsch! Du hast Plan und Erweiterung erworben</li></ul></p>`,
        },
        pricePrefix: 'ab',
        price: (price) => `${price} €`,
        priceHint: 'pro Monat bei jährl. Abrechnung*',
      },
      shoppingCart: {
        title: 'Warenkorb',
        openCart: 'Weiter zum Warenkorb',
        cleanUpModuleMessage: 'Eine oder mehrere Erweiterungen wurden aus dem Warenkorb entfernt, da bereits in einem anderen Produkt enthalten.',
        checkout: 'Zur Kasse',
        legalMessage: '*Preis gilt monatlich bei jährlicher Abrechnung zzgl. USt. Add-ons sind an die Tariflaufzeit gebunden',
        legalMessageMonth: '*Preis gilt monatlich zzgl. USt. Add-ons sind an die Tariflaufzeit gebunden',
        empty: 'Dein Warenkorb ist aktuell leer',
        item: {
          priceNotExists: 'Kein Preis Verfügbar',
          oneMonth: '1 Monat',
          twelveMonths: '12 Monate',
        },
        checkoutFooterLine: 'Über 60.000 Kunden vertrauen auf orgaMAX',
        periodTitleLabel: 'Tariflaufzeit',
        billingPeriodLabel: 'Abrechnungszeitraum',
        periodInMonths: (months) => `${months} ${months > 1 ? 'Monate' : 'Monat'}`,
        periods: {
          year: 'Jährlich',
          month: 'Monatlich',
        },
      },
      expansions: 'Ausbaustufen',
      buyingGuide: 'Kaufberatung',
      ads: {
        ad2: {
          headline: 'Du möchtest eine telefonische Kaufberatung?',
          description: '<a href="tel:+4927356474103">02735 6474-103</a><br />Mo - Fr.: 08:00 - 17:00 Uhr<br /><a href="mailto:beratung@meinbuero.de">beratung@meinbuero.de</a>',
        },
        ad3: {
          headline: 'Abonniere unseren YouTube-Kanal',
          description: 'Aktuelle Themen, How-To-Videos, lustige Shorts: Auf unserem YouTube-Kanal halten wir Dich auf dem Laufenden.',
          buttonLabel: 'Jetzt abonnieren',
        },
      },
      productItem: {
        trialVersion: 'Testversion',
        details: {
          plan: 'Ausbaustufe',
          sales: 'Umsatz / 365 Tage',
          documents: 'Verwaltbare Dokumente',
          storage: 'Speichervolumen',
          storageValue: (used, limit) => `${used} GB<span> von ${limit} GB</span>`,
          users: 'Benutzer',
          usersValue: (used, limit) => limit === 0 ? 'Unbegrenzt' : `${used}<span> von ${limit}</span>`,
          staffs: 'Mitarbeiter',
          staffsValue: (used, limit) => limit === 0 ? 'Unbegrenzt' : `${used}<span> von ${limit}</span>`,
          testStart: 'Beginn Deines Tests',
          subscriptionStart: 'Beginn Deines Abos',
          testEnd: 'Ende Deines Tests',
          subscriptionRenewal: 'Verlängerung Deines Abos',
          cancelledOn: 'Gekündigt zum',
        },
      },
      productInformation: {
        headline: 'Produkthinweise',
      },
      moreDetails: 'Zur detaillierten Leistungsübersicht',
    },
  },
  moduleItemList: {
    title: 'Verfügbare Add-ons',
  },
  // Banking
  bankingTitle: 'Banking',
  bankingListTitle: 'Bank/Kasse',
  bankingListEmptyMessage: 'Keine Umsätze gefunden',
  bankingAccountListHeader: 'Alle Konten',
  bankingDetails: 'Bankkontakt anzeigen',
  bankingPINUpdate: 'Pin aktualisieren',
  bankingPINChange: 'TAN Verfahren wechseln',
  bankingDelete: 'Konto entfernen',
  bankingDeleteToolTip: 'Entfernt die Kontoverbindung endgültig aus der Anwendung. Die Kontodaten werden vollständig aus der Anwendung gelöscht.',
  bankingStatusChangeDeactivate: 'Kontoanbindung deaktivieren',
  bankingStatusChangeDeactivateToolTip: 'Deaktiviere das Konto, wenn Du es nicht mehr benötigst. Alle bisherigen Zuordnungen zu Ausgaben, Rechnungen, sonstige Einnahmen, etc. bleiben bestehen. Du kannst das Bankkonto jederzeit reaktivieren.',
  bankMissingPinErrorMessage: 'Bitte gib Deinen Pin ein',
  bankMissingTanErrorMessage: 'Bitte gib die TAN ein',
  bankingGetStartedTitle: 'Deine Finanzen im Blick',
  bankingGetStartedDescription: `Richte jetzt Deine Konten bei ${productNameLong} ein.`,
  bankingFetchBankAccountErrorMessage: 'Beim Lesen der Bankdaten ist es zu einem Fehler gekommen. Bitte versuche es erneut oder kontaktiere bitte unseren Support.',
  bankingDeleteBankAccountSuccessMessage: `Das Konto wurde erfolgreich aus ${productNameLong} entfernt`,
  bankingDeleteBankContactSuccessMessage: `Der Bankkontakt und alle dazugehörigen Konten wurde erfolgreich aus ${productNameLong} entfernt.`,
  bankingDeleteBankAccountErrorMessage: 'Beim Entfernen des Kontos ist ein Fehler aufgetreten',
  bankingDeleteBankContactErrorMessage: 'Beim Entfernen des Bankkontaktes ist ein Fehler aufgetreten.',
  bankingAccountCheckSuccessMessage: (bankAccountName) => `Die Umsätze für das Konto ${bankAccountName} sind erfolgreich abgeglichen worden.`,
  bankingContactSyncSuccessful: (bankName) => `Dein Bankkontakt bei der Bank "${bankName}" wurde erfolgreich synchronisiert.`,
  bankingAccountCheckErrorMessage: 'Bei Abgleich des Kontos ist ein Fehler aufgetreten.',
  bankingDeleteBankAccountTitle: 'Kontoanbindung entfernen',
  bankingDeactivateBankAccountTitle: 'Kontoanbindung deaktivieren',
  bankingSetupBankAccount: 'Konto einrichten',
  bankingDeleteBankContactTitle: 'Bankkontakt entfernen',
  bankingDeleteBankAccountNotAllowedMessage: 'Das Konto kann nicht entfernt werden, da es noch mit anderen Buchungen verknüpft ist. Bitte deaktiviere das Konto oder entferne die Verknüpfungen zu den Buchungen, bevor Du es entfernst.',
  bankingUpdateBankAccountSuccessMessage: 'Das Konto wurde erfolgreich aktualisiert.',
  bankingUpdateBankAccountErrorMessage: 'Beim Aktualisieren ist ein Fehler aufgetreten.',
  bankingDeleteAccountDescription: (bankAccountName) => `Bist Du sicher, dass Konto ${bankAccountName} entfernt werden soll?`,
  bankingDeleteBankContactDescription: (bankName) => `Bist Du sicher, dass der Bankkontakt ${bankName} vollständig entfernt werden soll?`,
  bankingDeactivateAccountDescription: (bankAccountName) => `Bist Du sicher, dass Konto ${bankAccountName} deaktiviert werden soll? Alle bisherigen Zuordnungen zu Ausgaben, Rechnungen, sonstigen Einnahmen etc. bleiben bestehen. Du kannst das Bankkonto jederzeit reaktivieren.`,
  bankingEmptyFieldErrorMessage: (fieldLabel) => `${fieldLabel} ist ein Pflichtfeld`,
  bankingBankAccountNotFoundErrorMessage: 'Das ausgewählte Konto wurde nicht gefunden',
  bankingSetContactPinSuccessMessage: 'Deine Einstellungen wurden erfolgreich gespeichert',
  noBankAccountsSetUpTitle: 'Kein Bankkonto',
  noTurnoversMessage: 'Keine Umsätze vorhanden',
  autobankingDisabledMessage: 'Autobanking deaktiviert. Jetzt aktualisieren!',
  changedAccessTypeMessage: 'Neue Zugangsart freigeschaltet. Jetzt aktualisieren!',
  bankingSaldo: 'Saldo',
  noBankingPsd2Transaction: 'Es konnte keine Bank-Transaktion eröffnet werden. Bitte versuche es später erneut.',
  bankContactDetailsForm: {
    header: 'Bankkontakt Details',
    applyButtonText: 'Speichern und synchronisieren',
    groupDetails: 'Details',
    groupLinkedBankAccounts: 'Zugehörige Konten',
    noData: 'Keine Bankkontaktinformationen gesetzt. Fehler beim Aufruf.',
    noLinkedBankAccounts: '- Keine verknüpften Bankkonten vorhanden. -',
    initBankCardBankName: 'Wird geladen...',
    saveAndSyncSuccessfull: 'Deine Änderungen sind erfolgreich übernommen worden.',
    errorOnSaveAndSync: 'Operation nicht durchgeführt. Es ist ein Fehler beim Speichern und Synchronisieren aufgetreten.',
    noDataToSync: 'Es konnte keine geänderten Einstellungen am Kontakt ermittelt werden. Die Operation ist nicht durchgeführt worden.',
    bankingDeleteContactNotAllowedMessage: 'Der Bankkontakt kann nicht entfernt werden, da er noch mit anderen Buchungen verknüpft ist. Bitte entferne die Verknüpfungen zu den Buchungen, bevor Du den Kontakt entfernst.',
    inputs: {
      defaultRequiredMessage: 'Bitte fülle das Pflichtfeld aus.',
      bic: 'BIC',
      bicRequiredMessage: 'Bitte gib eine BIC an.',
      userIdRequiredMessage: 'Bitte gib eine {0} an.',
      iban: 'IBAN',
      pin: 'PIN',
      tan: 'TAN-Verfahren',
      tanRequiredMessage: 'Bitte wähle ein TAN-Verfahren aus.',
      sca: 'ScA-Verfahren',
      scaRequiredMessage: 'Bitte wähle ein ScA-Verfahren aus.',
      hbciVersion: 'HBCI-Version',
      url: 'Url',
      unknown: 'Keine Angabe',
    },
    tooltips: {
      delete: {
        title: 'Bankkontakt vollständig entfernen',
        message: 'Mit Entfernen des Bankkontakts werden alle zugehörigen Konten und auch der Bankkontakt in der Anwendung unwiderruflich vollständig entfernt.'
          + 'Ein Bankkontakt mit Konten in verknüpften Buchungen kann nicht entfernt werden. ',
      },
      contactCheck: {
        title: 'Bankkonto neu synchronisieren',
        message: 'Es wird eine vollständige Synchronisation des ausgewählten Bankkontos durchgeführt, ggf. werden Transaktionslücken in bereits abgerufenen Umsätzen geschlossen '
          + 'und es können neue Bankkontoparameter wie Sicherheitsverfahren abgerufen werden.',
      },
      contactSync: {
        title: 'Bankkontakt Änderungen synchronisieren',
        message: 'Falls es zu Änderungen in Deinem Bankkontakt gekommen ist oder die Bank das Authentifizierungsverfahren geändert hat, hast Du hier die Möglichkeit, einen vollständigen Abgleich Deines Bankkontakts durchzuführen. Dies muss normalerweise durch die Eingabe der PIN oder Passwort bestätigt werden.',
      },
    },
  },
  payKind: {
    open: 'Offen',
    cash: 'Kasse',
    bank: 'Bank',
    clearingAccount: 'Verrechnungskonto',
    offline: 'Offline Konto',
  },
  dateTimeFormatSuffix: 'U\\hr',

  bankingAccountList: {
    loaderText: 'Konten werden geladen',
    addNewBankAccount: 'Neues Konto hinzufügen',
    activeBankAccounts: (bankAccounts) => bankAccounts === 1 ? '1 Konto' : `${bankAccounts} Konten`,
    deleteOpenTransactions: 'Offene Transaktionen löschen (dev)',
    lastUpdated: 'Zul. aktualisiert:',
    openTransactionsDeleteSuccess: 'Alle offenen Transaktionen wurden erfolgreich gelöscht.',
    accountsHidden: (counter) => counter === 1 ? '1 Konto ausgeblendet' : `${counter} Konten ausgeblendet`,
  },

  // Banking - assignments
  bankingAssignmentsSuccessMessage: 'Die Buchung wurde erfolgreich zugeordnet.',
  bankingAssignmentsErrorMessage: 'Beim Zuweisen der Buchung ist ein Fehler aufgetreten.',
  bankingDeleteAssignmentsErrorMessage: 'Beim Aufheben der Zuordnung ist ein Fehler aufgetreten.',
  bankingDeleteAssignmentsNotAllowedErrorMessage: 'Das Aufheben der Zuordnung ist nicht erlaubt.',
  bankingDeleteAssignmentsClosingInvoiceErrorMessage: 'Die Zuordnungen dieser Buchung können nicht gelöst werden, da eine Schlussrechnung existiert',
  // Banking - cash journal
  bankingCashJournalSetupLabel: 'Anfangsbestand festlegen',
  bankingCashJournalGetStartedDescription: 'Vergib jetzt den Anfangsbestand Deiner Kasse',
  bankingCashJournalSetupModalSubTitle: 'Lege hier den Anfangsbestand Deiner Kasse fest',
  bankingCashJournalSetupSuccess: 'Die Festlegung Deines Anfangsbestandes war erfolgreich.',
  bankingCashJournalSetupError: 'Beim Festlegen Deines Anfangbestandes ist ein Fehler aufgetreten',
  bankingCashJournalBalanceErrorMessage: 'Der Anfangsbestand darf nicht kleiner oder gleich 0,00 € sein',
  bankingCashJournalBookingForInvoiceSuccess: 'Die Buchung/en wurden erfolgreich erstellt und zugewiesen',
  bankingCashJournalBookingForInvoiceError: 'Beim Erstellen und Zuordnen der Buchung/en ist ein Fehler aufgetreten.',
  bankingCashJournalOpeningBalanceLabel: 'Anfangsbestand',
  bankingBuhlBankInputTooltip: 'Deine Online-Banking Zugangsdaten, PIN und TAN werden ausschließlich über die PSD2 Buhl Banking - API (PBB-API) an das Rechenzentrum Deiner Bank verschickt.',
  // Banking - agreement modal
  bankingAgreementModalTitle: 'PSD2-Einführung',
  bankingAgreementModalDescriptionLine1: 'Die PSD 2 (Payment Services Directive 2) ist eine Richtlinie der Europäischen Union (EU) zur Regulierung von Zahlungsdiensten, die am 14.09.2019 in Kraft trat. ',
  bankingAgreementModalDescriptionLine2: 'Seit diesem Zeitpunkt erfolgt Dein Zugriff auf Dein Zahlungskonto über die PSD2-Schnittstelle. Die starke Kundenauthentifizierung bewirkt, dass Du nun häufiger als bisher eine PIN/TAN eingeben musst.',
  bankingAgreementModalDescriptionLine3: 'Zusätzlich benötigen wir Deine Zustimmung zu buhl:Banking. Buhl hat die BaFin-Erlaubnis für Kontoinformationsdienste und Zahlungsauslösedienste.',
  bankingAgreementModalDescriptionLine3Mbo: 'Buhl hat die BaFin-Erlaubnis für Kontoinformationsdienste und Zahlungsauslösedienste. Mit Deiner Zustimmung zum buhl:Banking erkennst Du alle hier vorgestellen Vorgaben an.',
  bankingAgreementModalDescriptionLine4Omo: (link1, link2, company) => <>Es gelten die {link1} und die {link2} der {company}. </>,
  bankingAgreementModalDescriptionLine4Mbo: (link1, link2, company) => <>Es gelten unsere {link1} und die {link2} der {company}. </>,
  bankingAgreementModalNotOwnerHint: 'Der Hauptaccount muss den Nutzungsbedingungen zustimmen.',
  termsOfUse: 'Nutzungsbedingungen',
  privacyPolicy: 'Datenschutzerklärung',
  useBuhlBankung: 'buhl:Banking nutzen',
  // Banking - pin
  bankingDefaultPinErrorMessage: 'Beim Überprüfen der Daten ist ein Fehler aufgetreten.',
  bankingCancelPinRequestErrorMessage: 'Die PIN-Eingabe wurde abgebrochen.',
  bankingCancelTanRequestErrorMessage: 'Die TAN-Eingabe wurde abgebrochen.',
  bankingCancelTransactionErrorMessage: 'Vorgang wurde abgebrochen.',
  bankingChangeTanProcedureSuccessMessage: 'Das TAN-Verfahren wurde erfolgreich aktualisiert.',
  bankingChangeActiveStateSuccessMessage: 'Der Aktivierungsstatus wurde erfolgreich aktualisiert.',
  bankingAutobankingInfo: `Hinweis: Das Speichern der PIN aktiviert das Autobanking. Mit dieser komfortablen Funktion siehst Du immer Deine aktuellsten Bankumsätze, ohne einen manuellen Schritt durchzuführen. Erwartet die Bank aufgrund von PSD2 eine TAN, so deaktiviert ${productName} das Autobanking und weist Dich darauf hin.`,
  // Banking - internal transfer
  bankInternalTransferAssign: 'Gegenbuchung zuordnen',
  bankInternalTransferCreate: 'Gegenbuchung erzeugen',
  bankInternalTransferSuccessMessage: 'Die Gegenbuchung wurde erfolgreich erstellt.',
  bankInternalTransferErrorMessage: 'Beim Erstellen der Gegenbuchung ist ein Fehler aufgetreten.',
  bankInternalTransferCashBalanceNegativeError: 'Der Betrag ist höher als der aktuelle Kassenbuchbestand. Das Kassenbuch darf nicht negativ sein.',
  bankInternalTransferPaymentDatePastInitialBalanceDateError: 'Das Zahlungsdatum darf nicht vor dem initialen Bestandsdatum liegen.',
  bankInternalTransferFormInvalid: 'Das Erstellen der gewünschten Gegenbuchung ist nicht möglich. Bitte überprüfe Deine Eingaben.',
  bankInternalTransferMissingBooking: 'Die Zuordnung ist nicht möglich. Bitte wähle eine Gegenbuchung aus.',

  // Bank Account Setup
  bankAccountSetupCheckboxStorePinLabel: 'Pin speichern',
  bankAccountSetupSelectBankAccountsTitle: 'Konten auswählen',
  banKAccountSetupSelectBankAccountsSubtitle: 'Wähle ein oder mehrere Konten zum Einrichten aus',
  bankAccountSetup: {
    modalSubtitle: 'Wähle aus, welchen Konto-Typ Du einrichten möchtest.',
    shopRequired: {
      shopifyTitle: 'Es besteht aktuell keine Verbindung<br />zu einem Shopify-Shop',
      buttonConnect: 'Verbindung herstellen',
    },
    accountSetup: 'Kontoeinrichtung',
    bankSelection: {
      master: {
        title: 'Online Banking. Sicher. Live.',
        subtitle: 'Automatisch. Leicht gemacht!',
      },
      whichBank: 'Welche Bank möchtest Du nutzen?',
      banknameOrBic: 'IBAN, Bankname oder BIC',
      paymentProvider: 'Weitere Zahlungsdienste',
      other: 'Sonstige',
      types: {
        sparkasse: 'Sparkasse',
        commerzbank: 'Commerzbank',
        deutsche_bank: 'Deutsche Bank',
        volksbank: 'Volksbank',
        postbank: 'Postbank',
        paypal: 'PayPal',
        n26: 'N26',
        ing_diba: 'ING-DIBA',
        dkb: 'DKB',
        sparda: 'Sparda-Bank',
        raiffeisen: 'Raiffeisen',
        shopifyPay: 'Shopify Payments',
        newCashBox: 'Neues Kassenbuch',
        offlineAccount: 'Offline Konto',
        klarna: 'Klarna',
      },
      searchStrings: {
        klarna: 'Klarna Zahlungsdienst',
        dkb: 'Deutsche Kreditbank Berlin',
      },
      bic: 'BIC',
      empty: {
        title: 'Leider keine passende Bank gefunden',
        hint: 'Korrekt geschrieben? Du kannst auch nach dem BIC suchen',
      },
      loadingData: 'Bankdaten werden geladen.',
    },
    login: {
      enterLoginData: 'Bitte gib hier die Zugangsdaten Deiner Bank ein.',
    },
    pin: {
      title: 'PIN eingeben',
      enterPin: 'Gib hier den PIN Deiner Bank ein.',
    },
    tan: {
      title: 'Wähle Dein TAN-Verfahren',
      decoupledTitle: 'Authentifizierung bestätigen',
      decoupledManualTitle: 'Authentifizierung benötigt',
      decoupledAutomaticHint: 'Bitte bestätige die Anfrage auf dem dafür eingerichteten Gerät.',
      decoupledManualHint: 'Bitte authentifiziere die Anfrage auf dem dafür eingerichteten Gerät.',
      decoupledModalTitle: 'Freigabe erforderlich',
      decoupledModalSubtitle: 'Bitte TAN eingeben',
      tanWillBeChecked: 'TAN wird geprüft ...',
    },
    tanLogin: {
      title: 'Bitte TAN eingeben',
    },
    type: {
      title: 'Bitte wähle die Zugangsart aus',
    },
    bankAccount: {
      title: 'Welche Konten möchtest Du aktivieren?',
      active: 'Aktiv',
      loading: 'Konten werden geladen.',
    },
    agb: {
      title: 'Unseren AGB zustimmen & loslegen',
    },
    cashbox: {
      addOpeningStock: 'Lege den Anfangsbestand Deiner Kasse an.',
    },
    error: {
      unknown: 'Ein unbekannter Fehler ist aufgetreten.',
    },
    faq: {
      title: 'FAQ',
    },
    next: 'Weiter',
    finished: 'Fertig',
    optional: '(optional)',
  },

  // Bank XS2A Redirect
  bankAccountSetupRedirectInitTitle: 'Zugang freischalten',
  bankAccountSetupRedirectInitDescription: `Du wirst im nächsten Schritt auf die Seite Deiner Bank weitergeleitet, um den Zugang zum Online-Banking in ${productNameLong} freizuschalten`,
  bankAccountSetupRedirectPendingDescription: 'Zugang erfolgreich freigeschaltet? Dann geht es im nächsten Schritt weiter zur Kontoauswahl.',

  // Bank XS2A Redirect Back Landing
  bankAccountSetupRedirectBackTitle: 'Der Vorgang wurde von der Bank erfolgreich abgeschlossen',
  bankAccountSetupRedirectBackDescription: 'Du kannst dieses Fenster nun schließen',

  // Bank Assignment
  bankAssignmentClearAssignmentLabel: 'Zuordnung aufheben',
  bankAssignmentMultiSelectionError: 'Bei Zuordnung von mehreren Belegen, muss die Summe ausgewählter Belege, dem offenen Buchungsbetrag entsprechen.',
  bankAssignmentMultiSelectionTransactionUniqueError: 'Mehrere Belege können bei dieser Weiterverarbeitungsoption nur zugeordnet werden, wenn die Art der Belege eindeutig ist.',
  bankAssignmentFilterAllMultiSelectionError: 'Bei Zuordnung von mehreren Belegen, muss die Summe ausgewählter Belege, das selbe Vorzeichen wie der Buchungsbetrag besitzen.',

  // Booking Assignment
  bookingAssignmentMultiSelectionError: 'Bei Zuordnung von mehreren Buchungen, muss die Summe ausgewählter Buchungen, dem offenen Betrag entsprechen.',
  // Bank Booking
  bankBookingDelete: 'Löschen',
  bankBookingDeleteModalTitle: 'Zahlung löschen',
  bankBookingDeleteModalText: 'Möchtest Du die Zahlung wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
  bankBookingDeleteSuccessMessage: 'Die Buchung wurde erfolgreich gelöscht.',
  bankBookingCancel: 'Stornieren',
  bankBookingCancelModalTitle: 'Umsatz stornieren',
  bankBookingCancelModalDescription: 'Möchtest Du diesen Kontenumsatz wirklich stornieren?',
  bankBookingCancelModalNegativeCashJournalMessage: 'Durch die Stornierung des Umsatzes würde der Kassenbuchsaldo ins Negative gehen.',
  bankBookingCancelSuccessMessage: 'Die Buchung wurde erfolgreich storniert',
  bookingAssignmentDeleteRevenueModalTitle: 'Zuordnung aufheben',
  bookingAssignmentDeleteRevenueModalText: 'Die Zahlung ist mit einer sonstigen Einnahme verknüpft. Wenn Du die Zuordnung löst, wird die Einnahme ebenfalls gelöscht.',
  bookingPrivateAssignmentSuccessMessage: 'Die Buchung wurde erfolgreich zugeordnet',

  // Bank Booking Assignment for Invoices in Cash Journal
  bookingAssignmentsSelection: {
    title: 'Welchen Beleg willst Du zuordnen?',
    bookingDate: 'Zahlungsdatum',
    create: 'Erstellen',
    loaderText: 'Die Buchung wird erstellt',
    open: 'Offen',
    subtitle: 'Filtere nach der Rechnung zu der eine Kassenbuchung erstellt werden soll.',
    totalOfInvoices: 'Summe ausgewählter Belege:',
  },

  bookingAssignment: {
    discount: 'Skonto',
    surcharge: 'Zuschlag',
    surchargeOf: 'Zuschlag von ',
    discountOf: 'Skonto von ',
    headers: {
      proceeding: {
        subTitle: 'Wähle aus, wie damit verfahren werden soll.',
        finish: 'Abschließen',
        aHead: 'Weiter',
      },
    },
    selectionFilter: {
      groupLabel: 'Belegart filtern',
      proposals: 'Vorschläge',
      all: 'Alle',
      invoice: 'Rechnungen',
      creditNote: 'Gutschriften',
      expense: 'Ausgaben',
      revenue: 'sonst. Einnahmen',
    },
    columns: {
      type: 'Art',
      invoice: 'Rechnung',
      partialInvoice: 'Teilrechnung',
      shopifyCashInvoice: 'Kassenrechnung Shopify',
      shopifyCashCancellation: 'Kassenstorno Shopify',
      expense: 'Ausgabe',
      revenue: 'sonst. Einnahmen',
      cancellation: 'Stornorechnung',
      creditNote: 'Gutschrift',
      manualCreditNote: 'Rechnungskorrektur/Gutschrift',
      name: 'Name',
      number: 'Nummer',
      date: 'Belegdatum',
      updatedAt: 'Geändert am',
      outstandingAmount: 'Offen',
      bookingDate: 'Datum',
      bookingContactName: 'Auftraggeber',
      purposeDescription: 'Verwendungszweck',
      bookingAmount: 'Betrag',
    },
    expense: {
      open: 'Offener Ausgabenbetrag:',
      sum: 'Summe ausgewählter Buchungen',
    },
    revenue: {
      open: 'Offener Einnahmenbetrag:',
      sum: 'Summe ausgewählter Buchungen',
    },
    noRowsToShow: 'Keine Buchungen vorhanden',
  },

  // booking
  booking: {
    bookings: 'Buchungen',
    openBookings: 'offene Buchungen',
    bookingAssignmentsLoader: 'Lädt Details ...',
    bookingAmount: 'Betrag',
    bookingContactName: 'Zahlungsempfänger',
    senderName: 'Auftraggeber',
    iban: 'IBAN',
    bic: 'BIC',
    date: 'Datum',
    valueDate: 'Wertstellung',
    usage: 'Verwendungszweck',
    errorUpdateReference: 'Der Verwendungszweck konnte leider nicht aktualisiert werden.',
    successUpdateReference: 'Der Verwendungszweck wurde erfolgreich angepasst.',
    recipient: 'Empfänger',
    client: 'Auftraggeber',
    expense: 'Ausgabe',
    expenseTitle: 'Details der Ausgabe',
    revenueTitle: 'Details der Einnahme',
    otherIncome: 'Sonst. Einnahme',
    joinDocument: 'Beleg auswählen',
    showAssignmentDetails: 'Details anzeigen',
    assignments: 'Zuordnungen',
    rebookingAccount: 'Gegenkonto*',
    createRebooking: 'Gegenbuchung erzeugt',
    description: 'Beschreibung',
    amount: 'Betrag*',
    fee: 'Gebühr*',
    sumUpHint: 'Trage bitte hier die Gebühren aus Deiner SumUp Abrechnung mit der entsprechenden PID ein.',
    sumUpErrorMessage: 'Bitte gib eine Gebühr an.',
    clearingAccountPosHint: 'Trage bitte hier die eventuell anfallenden EC-Anbieter Gebühren ein.',
    book: 'Buchen',
    assign: 'Zuordnen',
    bookingsLoading: 'Buchungen werden geladen',
    assignmentStates: {
      notAssigned: 'Nicht zugeordnet',
      fullyAssigned: 'Zugeordnet',
      partiallyAssigned: 'Teilw. zugeordnet',
    },
    assignmentType: {
      booking: 'Buchung',
      cancellation: 'Stornorechnung',
      creditNote: 'Gutschrift',
      discount: 'Skonto',
      expense: 'Ausgabe',
      fee: 'Bankgebühr',
      income: 'Einnahme',
      invoice: 'Rechnung',
      revenue: 'Sonstige Einnahme',
      surcharge: 'Zuschlag',
    },
    assignmentDetails: {
      customer: 'Kunde',
      description: 'Beschreibung',
      invoiceDate: 'Rechnungsdatum',
      invoiceNumber: 'Rechnungsnr.',
      supplierOrCustomer: '(L)ieferant / (K)unde',
      documentDate: 'Belegdatum',
      receiptNumber: 'Beleg-Rechnungsnr.',
      positionsLabel: 'Kategorie - Steuersatz / Betrag',
      none: 'Keine',
      total: 'Rechnungsbetrag',
      paymentAmount: 'Zahlbetrag',
      openAmountLeft: 'Offener Restbetrag',
    },
    createExpenseOrRevenue: {
      expenseTitle: 'Ausgabe erstellen',
      revenueTitle: 'Sonstige Einnahme erstellen',
      revenueTitleShort: 'Einnahme erstellen',
      positionsLabel: 'Kategorie / Steuersatz',
      splitSumMismatch: (positionsTotal, bookingAmount) => `Die Summe der Positionen (${positionsTotal}) weicht vom Umsatzbetrag (${bookingAmount}) ab.`,
    },
    transferForm: {
      title: 'Gegenbuchung erzeugen',
      subtitle: 'Wähle das Konto aus, auf dem direkt eine Gegenbuchung in Höhe des Buchungsbetrages erfolgen soll.',
      subtitle2: 'Eine Gegenbuchung kann nur auf unseren internen Offline-Konten durchgeführt werden. Die Gegenbuchung findet keine Anwendung bei Online-Konten.',
    },
    transferSelection: {
      title: 'Gegenbuchung zuordnen',
      subtitle: 'Wähle aus der untenstehenden Liste die passende Gegenbuchung aus, um einen buchhalterischen Geldtransfer durchzuführen. Es werden Dir nur exakt gleichhohe Beträge angezeigt.',
    },
    assignDocument: {
      title: 'Bestehenden Beleg auswählen',
      openAmount: 'Offener Zahlbetrag:',
      totalAmount: 'Summe ausgewählter Belege:',
      differenceOf: 'Differenz von',
      treatAs: 'behandeln als',
      searchPlaceholder: 'Name, Betrag, Belegdatum, etc. durchsuchen',
      proceedingOptionsNotPossible: {
        assignmentsShouldPositive: 'Diese Auswahl ist nicht möglich, der Betrag unter “Summe ausgewählter Belege” muss positiv sein',
        assignmentsShouldNegative: 'Diese Auswahl ist nicht möglich, der Betrag unter “Summe ausgewählter Belege” muss negativ sein',
      },
      proceedingOptions: {
        surcharge: 'Zuschlag',
        partial: 'Teilzahlung',
        discount: 'Skonto',
        bankCharge: 'Gebühr',
        bookLater: 'Restbetrag',
      },
      proceedingOptionsDescription: {
        surcharge: '(Nicht weiter verbuchen)',
        partial: '(Rechnungsstatus: teilweise bezahlt)',
        discount: '(Rechnungsstatus: vollständig bezahlt)',
        bankCharge: '(Rechnungsstatus: vollständig bezahlt)',
        bookLater: '(Kann später verbucht/zugeordnet werden)',
      },
      proceedingOptionsDescriptionExpense: {
        surcharge: '(Status: vollständig bezahlt)',
        partial: '(Status: teilweise bezahlt)',
        discount: '(Status: vollständig bezahlt)',
        bankCharge: '(Status: vollständig bezahlt)',
        bookLater: '(Status: Kann später verbucht/zugeordnet werden)',
      },
    },
    total: 'Gesamt:',
    nr: 'Nr.:',
    loading: 'Lädt...',
    dataIsBeingProcessed: 'Daten werden verarbeitet...',
    list: {
      listEmptyStateTitle: 'Keine Buchungen vorhanden',
      noEntryFound: 'Keine Buchungen gefunden',
    },
    buttonLabels: {
      joinAndContinue: 'Zuweisen und weiter',
      joinAndFinish: 'Zuweisen und fertig',
      bookAndContinue: 'Buchen und weiter',
      bookAndFinish: 'Buchen und fertig',
      assignAndContinue: 'Zuordnen und weiter',
      assignAndFinish: 'Zuordnen und fertig',
    },
  },

  banking: {
    bankingInfo: {
      additional: '+ weitere Banken',
      buttons: {
        submit: 'Jetzt minutenschnell einrichten',
        later: 'Später einrichten',
      },
      slide1:
      {
        header: `Multibanking mit ${productName}.<br />Alle Konten stets im Blick.`,
        subHeader: 'Top-aktuell verbunden sein und Deine<br />Kontobewegungen live abrufen.',
      },
      slide2: {
        header: 'Spare kostbare Zeit, durch<br />automatisierten Abgleich.',
        subHeader: 'Alles voll automatisch. Abgleich von offenen<br />oder bezahlten Rechnungen und Belegen.',
      },
      slide3: {
        header: 'Finanzen im Blick',
        subHeader: 'Mit unserer grafisch dargestellten Analyse<br />ist es kinderleicht, den Überblick zu behalten.',
      },

    },
    contextMenu: {
      clearingAccountErrorMesage: 'Diese Buchung ist Teil einer Umbuchung und darf nicht getrennt werden.',
    },
    list: {
      areLoading: 'Umsätze werden geladen',
      areLoadingDescription: 'Die Abfrage läuft bereits. Bei vielen Datensätzen kann dies etwas länger dauern. Die Daten werden automatisch aktualisiert',
      viewSales: 'Umsätze abrufen',
      linkInvoices: 'Rechnung verlinken',
      createOutput: 'Ausgabe',
      createRevenue: 'Sonst. Einnahme',
      bookDeposit: 'Privateinlage buchen',
      bookWithdrawal: 'Privatentnahme buchen',
      privateDeposit: 'Privateinlage',
      privateWithdrawal: 'Privatentnahme',
      reassign: 'Umsätze neu bewerten',
      reassignmentInProgress: 'Neubewertung wurde gestartet',
      reassignmentDone: 'Neubewertung beendet',
      reassignmentAlreadyStarted: 'Neubewertung bereits gestartet',
      reassignmentError: 'Neubewertung leider fehlgeschlagen',
      reassignmentLonger: 'Neubewertung dauert etwas länger als erwartet',
      payment: 'Zahlen',
      editRevenue: 'Sonstige Einnahme bearbeiten',
      editExpense: 'Ausgabe bearbeiten',
    },
    modal: {
      amount: 'Betrag',
      purposeDescription: 'Verwendungszweck',
      dateErrorMessage: 'Das Datum der Privateinlage oder Privatentnahme darf nicht vor dem Datum des initialen Kassenbestands liegen.',
      cashBookErrorMessage: 'Der Betrag der Privatentnahme übersteigt den Wert des Kassenbestands.',
      errorMessage: 'Beim Speichern ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      private: 'Privat',
    },
    tan: 'TAN',
    pin: 'PIN',
    initialSyncDone: 'Das Konto konnte nicht synchronisiert werden.',
    search: 'Konto durchsuchen',
    searchAll: 'Konten durchsuchen',
    errorMessageCashJournalEmpty: 'Bitte lege zuerst einen Anfangsbestand für das Kassenbuch fest.',
    warningMessageMovementNotSupported: 'Dieses Konto unterstützt keinen Umsatzabruf',
  },

  // logo upload
  logoUploadMinWidthError: 'Dein Logo muss mindestens 100 Pixel breit sein!',
  logoUploadMaxWidthError: 'Dein Logo darf maximal 800 Pixel breit sein!',
  logoUploadMinHeightError: 'Dein Logo muss mindestens 100 Pixel hoch sein!',
  logoUploadMaxHeightError: 'Dein Logo darf maximal 600 Pixel hoch sein!',
  logoUploadMinSizeError: 'Dein Logo muss mindestens 2,5 kB groß sein!',
  logoUploadMaxSizeError: 'Die ausgewählte Datei überschreitet die maximal zulässige Größe von 10 MB.',
  logoUploadFileTypeError: 'Das ausgewählte Dateiformat ist nicht zulässig. Bitte wähle eines der zulässigen Formate aus (JPG, PNG).',

  // customer

  customerDeleteConfirmCaption: multiple => `Kunde${multiple ? 'n' : ''} löschen`,
  customerDeleteConfirmText: multiple => `Möchtest Du ${multiple ? 'die ' : 'den'} Kunden wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  customerDeleteSuccessMessage: multiple => `${multiple ? 'Die Kunden' : 'Der Kunde'} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  customerDeleteNotAllowedMessage: multiple => `${multiple ? 'Die Kunden können' : 'Der Kunde kann'} nicht gelöscht werden, da Vorgänge für ${multiple ? 'sie' : 'ihn'} erfasst wurden.`,
  customerDeleteNotAllowedMessageFinanceError: 'Der Kunde kann nicht gelöscht werden, da Vorgänge mit diesem verknüpft sind!',
  customerSaveSuccessMessage: 'Der Kunde wurde erfolgreich gespeichert!',
  customerSaveErrorMessage: 'Beim Speichern des Kunden ist ein Fehler aufgetreten.',
  customerNotesSaveSuccessMessage: 'Das Kundenprofil wurde erfolgreich aktualisiert!',
  customerNotesInfoText: 'Hier kannst Du Anmerkungen zu diesem Kunden hinterlegen',
  customerEditAndSaveInMasterDataSuccessMessage: 'Die Änderungen wurden in den Stammdaten gespeichert.',
  customerEditAndSaveInMasterDataErrorMessage: 'Der Kontakt wurde nicht aktualisiert! Bitte versuche es später noch einmal.',
  customerSearchCategory: 'Kunden',
  customerNotFoundErrorMessage: 'Der Kunde konnte nicht gefunden werden.',
  getNextCustomerNumberErrorMessage: 'Beim Ermitteln der nächsten Kundennummer ist ein Fehler aufgetreten',
  validateCustomerNumberErrorMessage: 'Beim Validieren der Kundennummer ist ein Fehler aufgetreten',
  customerEditEmptyDeliveryConditionMessage: 'Bitte gib eine gültige Lieferbedingung an.',
  customerEditEmptyPayConditionMessage: 'Bitte gib eine gültige Zahlungsbedingung an.',
  customerDefaultAddresses: 'Standardadressen',
  customerAddAddressButtonLabel: 'Adresse',
  customerAdditionalAddresses: 'Weitere Adressen',
  customerAddAdditionalAddress: 'Weitere Adresse hinzufügen',
  customerBillingAddressTitle: 'Standardadresse',
  customerDeliveryAddressTitle: 'Lieferadresse',
  customerAddressFormModalEdit: 'Adresse bearbeiten',
  customerAddressFormModalCreate: 'Adresse hinzufügen',
  customerDeliveryEqualsBillingAddress: 'Lieferadresse entspricht Rechnungsadresse',
  customerDefaultAddressEmptyErrorMessage: 'Bitte gib mindestens eine Adresse an',
  customerBillingAddressEmptyErrorMessage: 'Bitte gib eine Rechnungsadresse an',
  customerValidBillingAddressErrorMessage: 'Bitte gib eine gültige Rechnungsadresse an',
  customerPersonNetPriceAsDefaultNotAllowedErrorMessage: 'Privatkunden dürfen keine steuerfreie Rechnungen erhalten',
  customerNoDeliveryAddressErrorMessage: 'Bitte gib eine Lieferadresse an',
  customerInfoTitle: 'Kundeninformationen',
  customerVatNumberLabel: 'USt.-IDNr.',
  customerVatNumberValidSuccessMessage: 'Die angegebene USt.-IDNr. ist in Ordnung',
  customerVatNumberInvalidErrorMessage: 'Die angegebene USt.-IDNr. ist nicht korrekt',
  customerVatNumberValidationOwnVatNumberMissingError: 'Zur Validierung dieser USt.-IDNr. wird die Angabe Deiner eigenen USt.-IDNr. benötigt',
  customerDiscount: 'Rabatt auf Listenpreise',
  customerDebitorNoDuplicate: debitorNo => `Die Debitorennummer ${debitorNo} wurde bereits hinterlegt.`,
  customerDebitorOutOfRange: debitorNo => `Die Debitorennummer ${debitorNo} liegt außerhalb des gültigen Bereiches von 10001 - 69999`,
  supplierCreditorNoDuplicate: creditorNo => `Die Kreditorennummer ${creditorNo} wurde bereits hinterlegt.`,
  supplierCreditorOutOfRange: creditorNo => `Die Kreditorennummer ${creditorNo} liegt außerhalb des gültigen Bereiches von 70001 - 99999`,
  supplierNumberDuplicate: number => `Die Lieferantennummer ${number} wurde bereits hinterlegt.`,
  debtorNo: 'Debitorennummer',
  debtorNoAbbreviation: 'Debitoren-Nr.',
  companyOrCustomerNumber: category => category === 'customers' ? 'Firma | Kd-Nr.' : 'Lieferanten-Nr.',
  creditorNoAbbreviation: 'Kreditoren-Nr.',
  creditorNo: 'Kreditorennummer',
  textInputErrorMessageMax: 'höchste Debitorennummer lautet 69999',
  textInputErrorMessageMin: 'niedrigste Debitorennummer lautet 10001',
  textInputErrorMessageCreditorMax: 'höchste Kreditorennummer lautet 99999',
  textInputErrorMessageCreditorMin: 'niedrigste Kreditorennummer lautet 70001',

  customer: {
    list: {
      customerListEmptyStateTitle: 'Jetzt Kunden per Excel-Tabelle hochladen',
      listNoSearchResultTitle: 'Keine Kunden gefunden',
      buttonLabel: 'Los geht´s',
    },
    model: {
      person: 'Person',
      company: 'Firma',
      customerNumberAbbreviation: 'Kd-Nr.',
    },
    detail: {
      errorMessage: 'Die Kundennummer ist bereits vergeben',
      customerNumberInvalid: 'Die eingegebene Kundennummer ist ungültig',
      buyerReferenceError: (name, number) => `Die Leitweg-ID wird bereits in Kunde <b>${name}</b> mit der Kunden-Nr. <b>${number}</b> verwendet`,
      title: 'Kundennummer',
      netPriceAsDefaultLabel: 'steuerfreie Rechnung',
    },
    editForm: {
      addressDescription: 'Lege hier die Standardadressen für Deinen Kunden fest.',
      jumpMarkLinkTexts: {
        addressForm: {
          headline: 'Adressen',
          text: 'Gib die Standardrechnungsadresse, sowie Lieferadresse des Kunden an und ergänze dies um weitere Adressen.',
        },
        contactPersonForm: {
          headline: 'Ansprechpartner',
          text: 'Du kannst Ansprechpartner des Kunden definieren und diese in den Vorgängen auswählen. Das Hinzufügen, sowie bearbeiten von Ansprechpartnern bleibt wie bisher bestehen.',
        },
        communicationForm: {
          headline: 'Kommunikation',
          text: 'E-Mail-Adresse, Telefon, Mobiltelefon. In diesem Bereich können die Kommunikationsmittel eingetragen werden.',
        },
        informationForm: {
          headline: 'Kundeninformationen',
          text: 'Weitere Informationen und Einteilung in Kategorien können hier vorgenommen werden, sollen weitere Kategorien zur Auswahl stehen, gehe in die Einstellungen.',
        },
        customFields: {
          headline: 'Individuelle Felder',
          text: 'Deine selbstdefinierten Feldtypen sind hier abruf- und editierbar.',
        },
        eInvoice: {
          headline: 'E-Rechnung',
          text: 'Trage die Leitweg-ID für die Korrespondenz für öffentliche Einrichtungen ein, oder andere zusätzliche Angaben.',
        },
        conditionsForm: {
          headline: 'Konditionen',
          text: 'Verschiedene Konditionen für die Rechnungsstellung, wie zur Abrechnung von Stundensätzen über die Zeiterfassung können hier definiert werden.',
        },
        ecommerceForm: {
          headline: 'E-Commerce',
          text: '',
        },
        accountingForm: {
          headline: 'Fibukonten',
          text: 'Lege eine eigene Debitorennummer für Deinen Kunden fest.',
        },
        notesForm: {
          headline: 'Anmerkungen',
          text: 'Du möchtest gerne bei Angebots- oder Rechnungserstellung Hinweise zu Deinem Kunden angezeigt bekommen oder Dir eigene Notizen machen. Dies kann hier vorgenommen werden.',
        },
        bankAccountForm: {
          headline: 'Bankverbindung',
          text: 'Die Bankdaten des Kunden hinterlegen, um ggf. direkt Gutschriften oder Korrekturen überweisen zu können.',
        },
      },
      eInvoice: {
        title: 'E-Rechnung',
        company: {
          subTitle1: 'In diesem Bereich können die kundenspezifischen Grundeinstellungen für die E-Rechnung vorgenommen werden. Die E-Rechnung wird als ',
          subTitle2: 'XRechnung',
          subTitle3: ' gemäß DIN EN 16931 sowie als ZUGFeRD-Rechnung erzeugt.',
        },
        person: {
          subTitle: `Die E-Rechnung ist nur für den B2B Kunden ab dem 01.01.2025 verpflichtend. Weitere Informationen zum Thema E-Rechnung findest Du unter <a target="_blank" href="${eInvoiceLink}">${eInvoiceLink}</a>.`,
        },
        buyerReference: 'Leitweg-ID',
        buyerReferencePlaceHolder: 'Pflicht für behördliche Rechnungen, bei B2B Rechnungen optional',
        customerOwnSupplierNumber: 'Kennung des Verkäufers',
        customerReferenceNumber: 'Referenznummer des Kunden',
        tooltips: {
          xInvoice: {
            title: 'Was ist eine XRechnung?',
            description: 'XRechnung ist ein Standard für elektronische Rechnungen in Deutschland, der die Anforderungen der EU-Richtlinie zur elektronischen Rechnungsstellung bei öffentlichen Aufträgen erfüllt. Es handelt sich um ein XML-basiertes Format, das eine effiziente und standardisierte Rechnungsübermittlung zwischen Unternehmen und öffentlichen Einrichtungen ermöglicht.',
          },
          buyerReference: {
            title: 'Was ist eine Leitweg-ID?',
            description: 'Die Leitweg-ID ist eine eindeutige Kennung, die in der elektronischen Rechnungsstellung verwendet wird, um Rechnungen präzise an die entsprechende Behörde oder Abteilung innerhalb der öffentlichen Verwaltung Deutschlands zu adressieren. Sie sichert eine effiziente und korrekte Rechnungszuordnung.',
            showInfoIcon: true,
          },
          customerReferenceNumber: {
            title: 'Wozu dient diese Referenznummer?',
            description: 'Da E-Rechnungen im XRechnungsformat aktuell eine Leitweg-ID (das Feld BT-10) erfordern, nutzen wir diese Referenznummer für die Erstellung von XRechnungen im B2B-Bereich. Dadurch kannst Du auch an andere Unternehmen korrekte XRechnungen ausstellen. Die Referenznummer kann entweder von Dir vergeben werden oder wird Dir gegebenenfalls von Deinem Kunden mitgeteilt. <strong>Initial vergeben wir automatisch eine eindeutige Nummer für Dich.</strong>',
            showInfoIcon: true,
          },
        },
        hint: {
          existingBuyerReference: {
            title: 'Info:',
            description: 'Die Leitweg-ID wird bereits in folgenden Kunden verwendet:',
          },
        },
      },
    },
  },
  customerListEmptyStateTitle: 'Jetzt Kunden per Excel-Tabelle hochladen',
  customerDetailContactHeadline: 'Kontaktinformationen',
  customerDetailContactMail: 'Mail',
  customerDetailContactWebsite: 'Webseite',
  customerDetailContactTelefon: 'Telefon',
  customerDetailContactTelefon2: 'Telefon 2',
  customerDetailContactFax: 'Fax',
  customerDetailContactMobil: 'Mobil',
  customerDetailContactAddress: 'Addresse',
  customerDetailContactNoInfo: 'keine Angabe',
  customerStatisticsAmount: 'Betrag',
  customerStatisticsCount: 'Anzahl',
  customerStatisticsOrderCount: 'Anzahl Aufträge',
  createCustomerLabel: 'Neuen Kunden erstellen',
  updateCustomerLabel: 'Kunde bearbeiten',
  customerSaveLabel: 'Kunde speichern',
  customerLabel: 'Kunde',
  supplierLabel: 'Lieferant',
  suppliersLabel: 'Lieferanten',

  businessPartnerStatisticsAmount: 'Betrag',
  businessPartnerStatisticsCount: 'Anzahl',
  businessPartnerStatisticsOrderCount: 'Anzahl Aufträge',

  // customer contact form
  customerContactForm: {
    salutation: 'Anrede',
    email: 'E-Mail-Adresse',
    website: 'Webseite',
    phone1: 'Telefon 1',
    phone2: 'Telefon 2',
    mobile: 'Mobiltelefon',
    fax: 'Fax',
    isMainContact: 'Hauptansprechpartner',
    noInfo: 'Keine Angabe',
    headline: 'Kontaktinformationen',
    address: 'Adresse',
  },

  // contact person form
  contactPersonForm: {
    firstName: 'Vorname',
    lastName: 'Nachname*',
    email: 'E-Mail',
    phone1: 'Telefon 1',
    phone2: 'Telefon 2',
    mobile: 'Mobil',
    fax: 'Fax',
    birthday: 'Geburtstag',
    website: 'Webseite',
    address: 'Adresse',
    jobTitle: {
      optionCreated: (option) => `Die Position ${option} wurde erfolgreich erstellt.`,
    },
    salutation: {
      optionCreated: (option) => `Die Anrede ${option} wurde erfolgreich erstellt.`,
    },
    title: {
      optionCreated: (option) => `Der Titel ${option} wurde erfolgreich erstellt.`,
    },
  },

  // contextMenue
  contextMenu: {
    show: 'Anzeigen',
    copyAndEdit: 'Kopieren und Bearbeiten',
    conclude: 'Abschließen',
    concludeMail: 'Abschließen und E-Mail versenden',
    concludePrint: 'Abschließen und drucken',
    concludePDF: 'Abschließen und herunterladen',
  },

  // dunning
  dunning: {
    getAttachmentName: (dunningLabel, invoiceNumber) => `${dunningLabel} zu Rechnung ${invoiceNumber}.pdf`,
    downloadError: 'Die Mahnung konnte nicht heruntergeladen werden.',
    pageTitle: 'Mahnwesen',
    settingsLoading: 'Einstellungen werden geladen',
    automatedEmailToggle: 'Automatischen E-Mail Versand von Mahnungen aktivieren',
    upgradeText: `${unlockViewUpgradeText} das Mahnwesen freizuschalten.`,
    transaction: {
      recipientChange: {
        title: 'Mahnungsempfänger ändern',
        description: 'Möchtest Du die E-Mail des Kunden als E-Mail für den Empfang von Mahnungen für diese Rechnung setzen?',
      },
      invalidEmail: 'Bitte gib eine gültige E-Mail-Adresse ein',
      // Below the fields after a save with an empty list - the wording of the design.
      errors: {
        emptyRecipients: 'Bitte Empfänger eingeben',
        emptyConfirmationRecipients: 'Bitte mindestens einen Benutzer auswählen',
      },
    },
    detail: {
      saveError: 'Die Einstellungen für automatische Mahnungen konnten nicht gespeichert werden',
      hints: {
        emptyRecipients: 'Ohne Empfänger wird das automatische Mahnen für diese Rechnung deaktiviert.',
        emptyConfirmationRecipients: 'Ohne Empfänger wird die Versandbestätigung für diese Rechnung deaktiviert.',
        legacyConfirmation: 'Aktuell erhalten alle Benutzer eine Versandbestätigung. Wähle Empfänger aus, um das einzuschränken.',
      },
    },
    // Shared by the auto dunning blocks of the invoice edit view and the invoice detail view.
    settings: {
      title: 'Automatische Mahnungen',
      toggle: 'Für diese Rechnung aktivieren',
      description: autoDunningDescription,
      recipientsLabel: 'Empfänger der Mahnung',
      // Below the dunning recipients while the tenant may only mail its own addresses.
      trialHint: 'Im Testzeitraum gehen Mahnungen nur an Deine eigenen hinterlegten E-Mail-Adressen, nicht an die eingetragenen Empfänger.',
      confirmation: {
        toggle: 'Versandbestätigung senden',
        description: 'Die ausgewählten Benutzer erhalten eine E-Mail, sobald eine Mahnung verschickt wurde.',
        recipientsLabel: 'Empfänger der Versandbestätigung',
        recipientsPlaceholder: 'Benutzer auswählen',
        emptyRecipientsError: 'Bitte wähle mindestens einen Benutzer für die Versandbestätigung der Mahnung aus',
      },
    },
  },
  dunningModal: {
    levels: {
      paymentReminder: 'eine Zahlungserinnerung',
      firstReminder: 'die erste Mahnung',
      secondReminder: 'die zweite Mahnung',
      lastReminder: 'die letzte Mahnung',
      default: 'eine Mahnung',
    },
    textTemplate: 'Möchtest Du zu der ausgewählten Rechnung %s erstellen?',
    successInfo: 'Mahnung erfolgreich erstellt: %s',
    success: 'Mahnung erfolgreich erstellt.',
    error: 'Fehler beim Erstellen der Mahnung.',
    executing: 'Mahnung wird erstellt',
    sendButtonText: 'Per E-Mail senden',
    showButtonText: 'PDF anzeigen',
    titlePart: 'erstellen',
  },

  // customer delivery address
  deleteDeliveryAddressSuccessMessage: 'Die Lieferbedingung wurde erfolgreich gelöscht',
  deleteDeliveryAddressErrorMessage: 'Beim Löschen der Lieferbedingung ist ein Fehler aufgetreten',

  // contact person
  contactPersonInsideCustomerSaveSuccessMessage: 'Der Ansprechpartner wurde aktualisiert. Bitte speichere den Kunden, um die Details auf dem Server zu aktualisieren!',

  // transactionEmail
  transactionEmail: {
    getOfferSubject: (number) => `Angebot ${number}`,
    getInvoiceSubject: (number) => `Rechnung ${number}`,
    getDunningSubject: (number) => `Rechnung ${number}`,
    getDeliveryNoteSubject: (number) => `Lieferschein ${number}`,
    getOrderSubject: (number) => `Auftragsbestätigung ${number}`,
    getPurchaseSubject: (number) => `Bestellung ${number}`,
    getCancellationSubject: (number) => `Stornorechnung zu Rechnung ${number}`,
    getCreditNoteSubject: (number) => `Gutschrift zu Rechnung ${number}`,
    getManualCreditNoteSubject: (number) => `Rechnungskorrektur/Gutschrift ${number}`,
  },
  // transactionEdit
  transactionEdit: {
    deliveryAddress: {
      title: 'Kundenadresse für Direktlieferung hinzufügen:',
      inputs: {
        deliveryAddress: 'Lieferadresse',
      },
      tooltip: {
        noAddressFound: {
          title: 'Keine Adresse vorhanden',
          description: 'Es existiert keine valide Adresse zu dem ausgewählten Kunden. Bitte füge in den Stammdaten eine Lieferadresse hinzu.',
        },
      },
    },
    deliveryCondition: {
      selectDeliveryCondition: 'Lieferbedingung wählen',
      noDeliveryConditionFound: 'Keine Lieferbedingung gefunden',
      noText: (deliveryConditionName) => `Du hast die Lieferbedingung "${deliveryConditionName}" gewählt und keinen Text angegeben. Daher wird dieser Abschnitt nicht in der Ausgabe erscheinen.`,
    },
    payCondition: {
      selectPayCondition: 'Zahlungsbedingung wählen',
      noPayConditionFound: 'Keine Zahlungsbedingung gefunden',
      dueDate: (date) => `Zahlungsziel: ${date} Tage`,
    },
    transactionPreview: {
      downloadSuffix: 'Vorschau.pdf',
      autoRefresh: 'aut. aktualisieren',
      zoom: 'Vergrößern',
      refresh: 'Aktualisieren',
      createPreviewErrorMessage: 'Beim Erstellen der Vorschau ist ein Fehler aufgetreten!',
      layoutSettings: 'Layout & Briefpapier anpassen',
    },
    info: {
      drawer: {
        title: 'Informationsblock bearbeiten',
        subTitle: 'Welche Informationsfelder sollen angezeigt werden?',
        listItem1: 'Es lassen sich max. 7 Informationsfelder einstellen.',
        listItem2: `Die vordefinierten Felder bzgl. Vorgangsnummer (z. B. Angebots-, Auftrags-, Rechnungsnummer),<br/>
        sowie Kundennummer, Datum, werden für jeden Vorgang neu generiert.`,
        listItem3: `Entfernen oder Hinzufügen von Informationsfeldern, sowie Änderungen werden für zukünftige<br/>
        Vorgänge gespeichert.`,
      },
      title: 'Informationsblock',
      addInfoField: 'Informationsfeld hinzufügen',
      infoNumeration: {
        invoice: {
          title: 'Rechnungs-Nr. festlegen',
          description: 'Die finale Rechnungs-Nr. wird automatisch vergeben, sobald Du die Rechnung abschließt.',
        },
        offer: {
          title: 'Angebots-Nr. festlegen',
          description: 'Die finale Angebots-Nr. wird automatisch vergeben, sobald Du das Angebot speicherst.',
        },
        deliveryNote: {
          title: 'Lieferschein-Nr. festlegen',
          description: 'Die finale Lieferschein-Nr. wird automatisch vergeben, sobald Du den Lieferschein speicherst.',
        },
        order: {
          title: 'Auftrags-Nr. festlegen',
          description: 'Die finale Auftrags-Nr. wird automatisch vergeben, sobald Du den Auftrag speicherst.',
        },
        purchase: {
          title: 'Bestell-Nr. festlegen',
          description: 'Die finale Bestell-Nr. wird automatisch vergeben, sobald Du die Bestellung speicherst.',
        },
      },
      defaultLabels: {
        invoiceNumber: 'Rechnungs-Nr.',
        customerNumber: 'Kunden-Nr.',
        supplierNumber: 'Lieferanten-Nr.',
        customerVatNumber: 'Kunden-USt.ID',
        invoiceDate: 'Rechnungsdatum',
        deliveryDate: 'Lieferdatum',
        validityDate: 'Gültig bis',
        shippingType: 'Versandart',
        customField: 'Eigenes Feld',
        customerReference: 'Unsere Kd-Nr.',
      },
      selectInfoFieldPlaceholder: 'Informationsfeld wählen',
      customFieldDefaultValueModal: {
        title: 'Informationsfeld vorbelegen',
        description: 'Soll dieses Informationsfeld bei neuen Vorgängen dieses Vorgangstyps automatisch vorbelegt werden?',
        prefillField: 'Informationsfeld vorbelegen',
        doNotPrefillField: 'Informationsfeld nicht vorbelegen',
      },
    },
    legalMessages: {
      label: 'Text für steuerlichen Sachverhalt',
    },
    laborShareInfo: {
      label: 'Text für Lohnkosten',
      text: {
        smallBusiness: (laborShare) => `Der Rechnungsbetrag enthält Lohnkosten in Höhe von insgesamt ${laborShare}.`,
        nonGerman: (laborShare) => `Im Bruttobetrag sind ${laborShare} Lohnkosten enthalten.`,
        oneVat: (laborShare, vatPercent, vatAmount) => `Im Bruttobetrag sind ${laborShare} Lohnkosten enthalten. Die darin enthaltene Umsatzsteuer ${vatPercent} % beträgt ${vatAmount}.`,
        multipleVats: (laborShares, isTransactionWithNegativeValues) => {
          const parts = laborShares.map(({ vatPercent, totalNet, vatAmount }) => {
            return `${formatCurrency(isTransactionWithNegativeValues ? -totalNet : totalNet)} netto zum Umsatzsteuersatz von ${vatPercent} % (Umsatzsteuer ${formatCurrency(isTransactionWithNegativeValues ? -vatAmount : vatAmount)})`
          })
          return `Der Bruttobetrag enthält Lohnkosten in Höhe von ${parts.join(' sowie ')}.`
        },
      },
    },
    recipient: {
      notesOnCustomerOrSupplier: 'Anmerkungen zum Kunden/Lieferanten',
      recipientSetting: {
        customerNewHeading: 'Hier kannst Du Standard-Adressen hinterlegen, die für eine Rechnungserstellung notwendig sind.<br/><br/> Optional lassen sich über die weiteren Tabs die gesamten Kundeneinstellungen vervollständigen.',
        supplierNewHeading: 'Hier kannst Du Standard-Adressen hinterlegen, die für eine Bestellung notwendig sind.<br/><br/> Optional lassen sich über die weiteren Tabs die gesamten Lieferanteneinstellungen vervollständigen.',
        addresses: {
          headline: 'Adressen',
        },
        contactPersons: {
          headline: 'Ansprechpartner',
        },
        information: {
          headline: 'Info und Kommunikation',
        },
        conditions: {
          headline: 'Konditionen & Bankverbindung',
        },
        eInvoice: {
          headline: 'E-Rechnung',
        },
        communication: {
          headline: 'Kommunikation',
        },
        notes: {
          headline: 'Anmerkungen',
        },
      },
      recipientDrawer: {
        modal: {
          title: 'Achtung!',
          description: 'Es sind nicht gespeicherte Eingaben vorhanden, Deine Eingaben gehen verloren, wenn Du diese Seite verlässt oder verwirfst.<br><br>Willst Du die gemachten Eingaben <b>Speichern</b> oder <b>Verwerfen</b>?',
        },
        newCustomerTitle: 'Neuen Kunden anlegen',
        editCustomerTitle: 'Kunden Details - Stammdaten',
        newSupplierTitle: 'Neuen Lieferanten anlegen',
        editSupplierTitle: 'Lieferanten Details - Stammdaten',
        billingAddressHintTitle: 'Rechnungsadresse fehlt',
      },
      recipientSelectInput: {
        newCustomerLabel: 'Neuen Kunden anlegen',
        placeholder: 'Kunden anlegen oder suchen',
        createNewCustomerLabel: 'als Kunden anlegen',
        newSupplierLabel: 'Neuen Lieferanten anlegen',
        placeholderSupplier: 'Lieferanten anlegen oder suchen',
        createNewSupplierLabel: 'als Lieferanten anlegen',
      },
    },
    positions: {
      notesAlertModalTitle: 'Anmerkungen zum Artikel',
      noPositions: 'Keine Positionen',
      addPositionLabel: 'Position hinzufügen',
      tooltip: {
        addPositionDisabled: {
          deliveryNoteFromOrder: 'Bei aus Aufträgen generierten Lieferscheinen können keine Positionen hinzugefügt werden.',
        },
      },
      importFromTransaction: {
        menuItem: 'Positionen aus Vorgang importieren',
        drawerTitle: 'Position aus bestehendem Vorgang übernehmen',
        description:
          'Du kannst in der Liste nach verschiedenen Vorgangsarten suchen und filtern.<br />Mit der Auswahl des Vorgangs wird dieser mit allen ausgewählten Positionen in den aktuellen Vorgang übernommen.',
        dataSourceDocument: 'Positionswerte aus Vorlage übernehmen',
        dataSourceMaster: 'Artikelstammdaten verwenden',
        positionTableTitlePrefix: 'Positionen auswählen aus ',
        searchTransactions: 'Vorgänge durchsuchen',
        searchPositions: 'Positionen durchsuchen …',
        emptyTransactions: 'Keine Vorgänge',
        emptyPositions: 'Keine Positionen — zuerst einen Vorgang auswählen',
        confirm: 'Auswahl übernehmen',
        transactionColumns: {
          kind: 'Art',
          transactionNumber: 'Vorgangs-Nr.',
          customerName: 'Kunde / Lieferant',
          date: 'Datum',
        },
        positionColumns: {
          positionNo: 'Pos.',
          articleNumber: 'Artikel-Nr.',
          quantity: 'Anzahl',
          unit: 'Einheit',
          description: 'Beschreibung',
          title: 'Bezeichnung',
          unitPriceNet: 'Einzelpreis (netto)',
          totalNet: 'Gesamt (Netto)',
          totalGross: 'Gesamt (Brutto)',
        },
      },
      totalDiscountOrSurcharge: {
        add: 'Gesamtrabatt/-zuschlag',
        disabled: {
          paymentProviderActive: 'Zahlungsdienste aktiviert, es können keine Gesamtrabatt/- zuschläge hinzugefügt werden.',
          noValidStateRows: 'Füge erst eine Position hinzu',
        },
      },
      subtotal: 'Zwischensumme',
      subtotalNet: 'Zwischensumme netto',
      sum: 'Summe',
      saveToMasterData: 'Artikel speichern',
      openArticleDrawer: 'Artikelstammdaten öffnen',
      addAlternativePositionLabel: 'Alternativposition hinzufügen',
      alternativPositionTitle: 'Alternativ',
      stockRatio80: '≥ 80 %',
      stockRatio60: '≥ 60 %',
      stockRatio40: '≥ 40 %',
      stockRatio20: '≥ 20 %',
      stockRatioLow: '< 20 %',

      supplierChangeWithArticle: {
        title: 'Lieferant geändert',
        message: 'Es sind bereits Artikelpositionen vorhanden. Menge und Preis bleiben vorerst unverändert. Losgrößen- und lieferantenspezifische Daten werden erst durch „Artikeldaten aktualisieren“ für den neuen Lieferanten übernommen.',
        refreshArticleDataTooltip: 'Artikeldaten aktualisieren',
        lotSizeMismatchWarning: 'Menge entspricht nicht der Losgröße',
        lotSizeMismatchWarningDescription: 'Der Lieferant liefert diesen Artikel in einer bestimmten Losgröße. Wenn Du “Artikeldaten aktualisieren” klickst werden die Artikelinformationen aktualisiert und die Menge auf die nächste Losgröße angepasst.',
        updateLotSize: 'Artikeldaten aktualisieren',
      },
      columnLabels: {
        index: 'Pos.',
        title: 'Bezeichnung',
        description: 'Bezeichnung',
        number: 'Nr.',
        amount: 'Menge',
        unit: 'Einheit',
        price: 'Preis',
        vat: 'USt.',
        discount: 'Rabatt',
        total: 'Gesamt',
      },
      addMultipleArticles: {
        menuItem: 'Mehrere Positionen hinzufügen',
        drawerTitleBase: 'Artikel hinzufügen',
        searchPlaceholder: 'Artikel durchsuchen',
        categoryPlaceholder: 'Artikelkategorie auswählen',
        selectedFilterLabel: 'Ausgewählt',
        applySelection: 'Auswahl übernehmen',
        emptyState: 'Keine Artikel gefunden',
        stockInsufficientTitle: 'Lagerbestand nicht ausreichend',
        stockQuantityExceededTooltip: 'Die eingegebene Menge überschreitet den aktuellen Lagerbestand.',
        columns: {
          amount: 'Menge',
          number: 'Artikel-Nr.',
          title: 'Bezeichnung',
          description: 'Beschreibung',
          stock: 'Lagerbestand',
          gross: 'Brutto',
          net: 'Netto',
        },
        unsavedModal: {
          title: 'Achtung, Auswahl nicht gespeichert',
          description: 'Deine Änderungen sind noch nicht gespeichert. Wenn Du diesen Bereich verlässt, geht Deine Auswahl verloren. Möchtest Du Deine Auswahl übernehmen oder verwerfen?',
        },
      },
      articleDrawer: {
        suppliersReadOnlyHint: 'Nur außerhalb des Vorgangs im Stammdatensatz änderbar.',
        labels: {
          unit: 'Einheit',
          vat: 'Steuersatz',
          category: 'Artikelkategorie',
          purchasePrice: 'Einkaufspreis (netto)',
          purchasePriceSmall: 'Einkaufspreis',
          price: 'Verkaufspreis (netto)',
          priceGross: 'Verkaufspreis (brutto)',
          priceSmall: 'Verkaufspreis',
        },
        masterDataChanged: 'Übernahme in aktuellen Vorgang',
        masterDataChangedDescription: 'Du hast Änderungen an den Stammdaten vorgenommen, willst Du diese Änderungen direkt übernehmen?',
        masterDataReadOnlyDescription: 'Du hast Änderungen an den Stammdaten vorgenommen. Diese Änderungen können nicht übernommen werden - die Positionen sind schreibgeschützt.',
      },
      actions: {
        duplicate: 'Duplizieren',
        newPosition: 'Neue Position',
        deletePosition: 'Position löschen',
        deleteGroupPositions: 'Gruppe löschen',
      },
      types: {
        [PositionTypes.TEXT]: { initial: 'FR', label: 'Freitext' },
        [PositionTypes.HEADLINE]: { initial: 'Ü', label: 'Überschrift' },
        [PositionTypes.HORIZONTAL_RULE]: { initial: 'T', label: 'Trennlinie' },
        [PositionTypes.PAGE_BREAK]: { initial: 'SU', label: 'Seitenumbruch' },
        [PositionTypes.SUBTOTALS]: { initial: '∑', label: 'Zwischensumme' },
        [PositionTypes.TOTAL_DISCOUNT]: { initial: 'R', label: 'Gesamtrabatt' },
        [PositionTypes.TOTAL_SURCHARGE]: { initial: 'Z', label: 'Gesamtzuschlag' },
      },
      initials: {
        [InitialTypes.MANUALLY_CREATED]: {
          initial: 'M',
          title: 'Manuell angelegter Artikel',
          description: 'Der Artikel/die Leistung lässt sich durch einen Klick auf folgende Option in den Stammdaten speichern:',
        },
        [InitialTypes.STOCK_MANAGED]: {
          initial: 'L',
          title: 'Aktueller Lagerbestand',
          description: 'Für diesen Artikel ist die Lagerbestandsführung aktiviert.',
        },
        [InitialTypes.GRADUATED_PRICE_MANAGED]: {
          initial: 'S',
          title: 'Staffelpreis-Übersicht',
          description: 'Dieser Artikel hat folgende Staffelpreise hinterlegt:',
          additionalDescription: 'Weitere Staffelpreise des Artikels befinden sich in den Stammdaten.',
        },
        [InitialTypes.DIFFERENTIAL_TAX]: {
          initial: 'D',
          title: 'Differenzbesteuerung',
          description: 'Dieser Artikel wird nach §25 UStG bewertet, es wird keine Umsatzsteuer dargestellt.',
        },
        [InitialTypes.DEPOSIT_INVOICE]: {
          initial: 'A',
          title: 'Abschlags- oder Anzahlungsposition',
          description: 'Die Abschlags-/Anzahlungsposition gibt an, welcher Betrag abgerechnet werden soll.</br>Bei manueller Abschlagsrechnung ist der Betrag auf 0,00€ gesetzt, aus Aufträgen heraus wird die Auftragssumme vorausgefüllt. Es können keine weiteren Positionen hinzugefügt werden. Die Positionsbezeichnung, sowie die Beschreibung können editiert werden.',
        },
        [InitialTypes.POSITIONS_GROUPED]: {
          initial: 'G',
          title: 'Positionsgruppe',
          description: 'Diese Gruppe besteht aus zusammenhängenden Positionen. Du kannst die ganze Gruppe entweder verschieben oder löschen. Einzelne Positionen innerhalb der Gruppe können jedoch nur bearbeitet, aber nicht einzeln verschoben oder gelöscht werden.',
        },
        [InitialTypes.ALTERNATIVE_POSITION]: {
          initial: 'A',
          title: 'Alternativposition',
          description: 'Die Alternativposition bietet Dir und Deinem Kunden die Möglichkeit, zwischen verschiedenen Alternativen hinsichtlich Preis, Qualität oder Verfügbarkeit eines Produkts oder einer Dienstleistung zu wählen.',
        },
        [InitialTypes.OPTIONAL_POSITION]: {
          initial: 'O',
          title: 'Optionale Positionen',
          // terminology-ignore:T001 – "Sie" refers to the optional positions (3rd person)
          description: 'Diese Positionen bieten Dir zusätzliche Optionen, die Du je nach Bedarf in Dein Angebot aufnehmen kannst. Sie sind nicht fest im Angebot enthalten und beeinflussen den Gesamtpreis nur, wenn sich Dein Kunde dafür entscheidet, sie hinzuzufügen. Nutze optionale Positionen, um Dein Angebot flexibel an Deine Anforderungen anzupassen.',
        },
      },
    },
    positionsDrawer: {
      title: 'Positionen auswählen',
      alternativePositions: {
        title: 'Alternative Position auswählen',
        description: 'Es sind noch Alternativpositionen im Angebot enthalten. Bevor Du fortfährst, wähle bitte die vom Kunden gewünschten Positionen:',
      },
      optionalPositions: {
        title: 'Optionale Position auswählen',
        description: 'Im Angebot stehen folgende Positionen optional zur Auswahl.',
        descriptionHint: '(Es ist nicht notwendig eine Option anzuwählen)',
      },
      transferPositions: (type) => `In ${type} überführen`,
      totalSum: 'Angebotssumme:',
      totalSumHint: 'inkl. gewählter Positionen',
      totalDiscount: 'RABATT',
      totalSurcharge: 'ZUSCHLAG',
      totalNet: 'SUMME (NETTO)',
      totalGross: 'SUMME (BRUTTO)',
      scrollToBottom: 'Scrollen für mehr',
      columnHeaders: {
        pos: 'Pos.',
        title: 'Bezeichnung',
        description: 'Beschreibung',
        amount: 'Menge',
        unit: 'Einheit',
        vatPercent: 'USt.',
        priceNetAfterDiscount: 'Preis',
        totalNetAfterDiscount: 'Gesamt',
        totalGrossAfterDiscount: 'Gesamt',
        status: 'Status',
      },
    },
    conclusionTextPlaceholder: 'Verfasse hier Deinen Schlusstext.',
    relatedTransactions: {
      createCollectiveInvoice: 'Sammelrechnung erstellen',
      description: 'Du kannst in der Liste nach Positionen, Beschreibung, Auftragsnummern suchen, mit Selektieren des Auftrag wird dieser mit allen Positionen in die Rechnung übernommen.',
      additionalDescription: (number, date) => `aus Auftrag - ${number} vom ${date}`,
      saveSelectedTransactions: 'Auswahl in Rechnung übernehmen',
      countSelectedTransactions: (transactionsCount, positionsCount) => `${transactionsCount} ${transactionsCount === 1 ? 'Auftrag' : 'Aufträge'}, ${positionsCount} ${positionsCount === 1 ? 'Position' : 'Positionen'}`,
      confirmModalChangeAmount: {
        title: 'Bitte beachten',
        description: 'Die Menge im Auftrag stimmt nicht mit der hier angegebenen überein. Wenn Du die Rechnung speicherst und abschließt, gilt der Auftrag als komplett abgerechnet. Beachte, dass dies eine Sammelrechnung ist und keine Teilabrechnungen für einzelne Posten möglich sind.',
        confirm: 'Jetzt speichern',
        abort: 'Erneut prüfen',
      },
      tooltipTitle: 'Erstellen einer Sammelrechnung aus verschiedenen Vorgängen',
      tooltipDescription: `
      1. Wähle einen Kunden aus</br>
      2. Klicke auf den Button</br>
      3. Wähle die gewünschten Aufträge aus der Liste</br>
      4. Überführe die ausgewählten Aufträge in die Rechnung</br>
      5. Bearbeite und passe die Positionen an</br>
      6. <b>Tipp:</b> Ändere den Vorgangstitel in "Sammelrechnung"</br>
      7. Speichere die Rechnung</br>
      </br>
      und erstelle somit eine Sammelrechnung aus mehreren Aufträgen.`,
      expandAll: 'Alle ausklappen',
      closeAll: 'Alle einklappen',
      columnHeaderNames: {
        number: 'Auftrags-Nr.',
        title: 'Bezeichnung',
        description: 'Beschreibung',
        amount: 'Menge',
        priceNet: 'Einzelpreis (netto)',
        totalNet: 'Gesamt (netto)',
        totalGross: 'Gesamt (brutto)',
      },
      amountExceededWarningMessage: 'Du überschreitest die im Auftrag angegebene Menge, nimm die Änderung direkt im Auftrag vor.',
      selectionDetail: {
        title: 'Details zu Deiner Auswahl:',
        amount: 'AUSGEWÄHLTE MENGE',
        totalNetSum: 'SUMME (NETTO)',
        totalGrossSum: 'SUMME (BRUTTO)',
      },
    },
    infoFormField: {
      tooltipTitle: 'Vorgangsnummern / Nummernkreise editieren',
      tooltipDescription: 'Die Vorgangsnummern, wie Angebots-, Auftrags- und Rechnungsnummern, werden gemäß des festgelegten Nummernkreises automatisch vergeben. Du kannst dies jedoch mit einem Klick auf das Nummernfeld direkt im Vorgang selbst anpassen. Im darauffolgenden Fenster kannst Du die gewünschte Nummer vergeben. Der nächste Vorgang wird dann in diesem Nummernkreis weiterzählen.',
      nonEditableTooltipTitle: 'Feld nicht bearbeitbar',
      nonEditableTooltipDescription: (type) => `Die ${type === 'relatedOrderNumber' ? 'Auftragsnummer ' : 'Angebotsnummer '}ist ein Systemwert, der automatisch vergeben wird. Für eigene Nummern kannst Du ein Freitextfeld nutzen.`,
      relatedReferenceResetTooltip: 'Wert zurücksetzen',
    },

    settingsTabLabels: {
      invoiceCreation: 'Rechnungserstellung',
      eInvoiceSettings: 'E-Rechnungseinstellungen',
    },
    eInvoiceSettings: {
      title: 'E-Rechnungseinstellungen',
      description: [
        'Der Bereich „E-Rechnungseinstellungen“ ist relevant, wenn Du eine Rechnung an einen Firmenkunden (',
        ') oder eine öffentliche Verwaltung (',
        ') erstellst. Hier kannst Du zusätzliche Angaben machen, die in der ',
        '',
        ' aufgeführt werden. Alternativ kannst Du die Erstellung einer E-Rechnung auch deaktivieren.',
      ],
      tooltipTexts: {
        label: {
          b2b: 'B2B',
          b2g: 'B2G',
          eInvoice: 'E-Rechnung',
        },
        content: {
          b2b: 'Business-to-Business – Geschäftsbeziehungen zwischen Unternehmen, z. B. Lieferanten und Geschäftskunden.',
          b2g: 'Business-to-Government – Geschäftsbeziehungen zwischen Unternehmen und öffentlichen Institutionen oder Behörden.',
          eInvoice: 'Elektronische Rechnung im standardisierten Format, die den Versand und die Verarbeitung digital erleichtert.',
        },
      },
      tooltipTitle: 'Deaktivieren der Erzeugung von E-Rechnungen',
      tooltipContent: `Wenn diese Einstellung deaktiviert ist, erstellt ${productNameBookkeeping} diese Rechnung lediglich als ein Rechnungs-PDF ohne eingebettete XML-Datei. Dieses Dokument entspricht nicht den Anforderungen elektronischer Rechnungen. Nach Abschluss des Rechnungsvorgangs steht weder eine ZUGFeRD-, noch eine XRechnungs-Datei zur Verfügung und kann für dieses Dokument auch nicht nachträglich erstellt werden.`,
      toggleTooltipContent: 'Privatkunden sind von der automatischen Validierung ausgenommen, da für sie keine E-Rechnung erstellt wird.',
      toggleLabel: 'Diese Rechnungen als E-Rechnung erstellen',
      customerNameLabel: 'Kundenname',
      customerNumberLabel: 'Kundennummer',
      buyerReferenceLabel: 'Leitweg-ID',
      customerReferenceNumberLabel: 'Referenznummer des Kunden',
      customerMailLabel: 'E-Mail',
      customerOwnSupplierNumberLabel: 'Kennung des Verkäufers',
      customerSection: {
        description: 'Hier findest Du die relevanten Stammdaten für die E-Rechnung des Kunden.<br/>Falls noch kein Kunde ausgewählt wurde, wähle bitte zunächst einen Kunden in der Ansicht „Rechnungserstellung“ aus.',
      },
      customer: {
        tooltip: {
          customerOwnSupplierNumber: {
            showInfoIcon: true,
            title: 'Kennung des Verkäufers',
            description: 'Wenn keine USt.-ID vorliegt, müssen <b>Kennung des Verkäufers</b> und <b>Steuernummer</b> angegeben werden.',
          },
        },
      },
      noCustomerSelected: 'Kein Kunde ausgewählt',
      notRequired: 'nicht erforderlich',
      validation: {
        tooltipContentDisabled: {
          disabledInTransaction: 'Die Validierung ist nicht möglich, da Du die Erstellung von E-Rechnungen für diesen Vorgang deaktiviert hast. Du kannst diese Rechnung als E-Rechnung erstellen und validieren, indem Du die Option für diesen Vorgang aktivierst.',
          disabledGlobally: (globalLink) => (
            <>
              Die Validierung ist nicht möglich, da Du die Erstellung von E-Rechnungen deaktiviert hast.
              Du kannst diese Rechnung als E-Rechnung erstellen und validieren, indem Du die Option für diesen Vorgang aktivierst
              oder die Option global für alle Rechnungen unter {globalLink} aktivierst.
            </>
          ),
          globalSettingsLinkLabel: 'Meine Firma > E-Rechnung',
        },
        buttonLabel: 'E-Rechnung validieren',
        valid: 'E-Rechnung gültig',
        invalid: 'E-Rechnung ungültig',
        notValidated: 'E-Rechnung nicht validiert',
        faqLabel: 'FAQs zum Thema Prüfbericht',
        validationSuccessMessage: 'Das erneute Prüfen wurde erfolgreich abgeschlossen.',
        recheck: 'Erneut prüfen',
        checkNow: 'Jetzt E-Rechnung prüfen',
        validating: 'Wird geprüft...',
        errors: {
          download: 'Das Prüfbericht-Dokument konnte nicht heruntergeladen werden. Bitte versuche es erneut.',
          validate: 'Das Prüfen der E-Rechnung ist fehlgeschlagen. Bitte versuche es erneut.',
        },
      },
      missingDataHint: {
        small: {
          title: 'E-Rechnungseinstellungen überprüfen',
          description: {
            prefix: (count) => `Für eine gültige E-Rechnung ${count === 1 ? 'fehlt noch' : 'fehlen noch'}`,
            link: (count) => `${count === 1 ? 'eine Angabe' : `${count} Angaben`}`,
            suffix: 'Bitte überprüfe Deine Eingaben im Reiter E-Rechnungseinstellungen.',
          },
        },
        big: {
          title: 'Hinweis',
          description: 'Für eine gültige E-Rechnung fehlen noch Angaben. Bitte ergänze die folgenden Felder, um eine valide E-Rechnung zu erzeugen:',
          labels: {
            email: 'E-Mail des Kunden',
            contactPerson: 'Absenderinformationen',
            bankAccount: 'Eigene Bankverbindung',
          },
        },
      },
      recommendedSettingsHint: {
        title: 'Hinweis',
        description: 'Für E-Rechnungen müssen Preisart und Umsatzsteuerberechnung zu den Anforderungen von XRechnung/ZUGFeRD passen. Bitte verwende <b>Netto</b> und die <b>vertikale Umsatzsteuerberechnung</b>, damit die Steuerbeträge auf Netto-/Steuersatzebene berechnet werden und keine vermeidbaren Cent-Abweichungen entstehen. <br/><br/>Mit dem Button werden diese Einstellungen global unter „Meine Firma“ angepasst und die Steuern dieses Dokuments neu berechnet.',
        button: 'Einstellungen für E-Rechnung übernehmen',
        toast: {
          success: 'Die empfohlenen Einstellungen wurden erfolgreich übernommen.',
          error: 'Beim Übernehmen der empfohlenen Einstellungen ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        },
      },
      tooltip: {
        recipientPerson: 'Privatkunden sind von der E-Rechnungspflicht nicht betroffen. Da keine E-Rechnung erstellt wird, sind keine weiteren Einstellungen notwendig.',
      },
      errors: {
        projectReferenceAndProjectName: 'Projektreferenz und Projektname in den zusätzlichen Rechnungsfeldern müssen entweder beide gefüllt oder beide leer sein.',
      },
    },
  },
  transactionHistory: {
    noRowsToShow: 'Keine Vorgänge vorhanden',
    loadingOoo: 'Vorgänge werden geladen',
  },
  payments: {
    cell: {
      paymentCellLabel: 'Offline-Zahlung erfassen',
      paymentCellLabelCreditNote: 'Offline-Zahlung erstatten',
      assignmentCellLabel: 'Bankbuchung zuordnen',
      bankTransferCellLabel: 'Überweisen',
    },
    bankTransferDrawer: {
      title: 'Überweisung',
      submitButton: 'Ausführen',
      confirmButton: 'Bestätigen',
      successMessage: 'Deine Überweisung wurde erfolgreich ausgeführt',
      bankTransferForm: {
        customer: 'Kunde',
        supplierCustomer: 'Lieferant/Kunde',
        iban: {
          label: 'IBAN *',
          placeholder: 'Auswählen oder eingeben',
          optionSuffix: {
            customer: '(aus Kundenstamm)',
            supplier: '(aus Lieferantenstamm)',
          },
        },
        fromAccount: 'Von welchem Konto soll überwiesen werden? *',
        recipient: 'Zahlungsempfänger *',
        usage: 'Verwendungszweck *',
        execution: 'Ausführung am *',
        executionDirect: 'Nächstmöglich',
        amount: 'Betrag *',
        realTimeTransfer: 'Echtzeitüberweisung',
      },
      disabledSubmitTooltip: 'Es sind noch nicht alle Pflichtfelder gefüllt.',
    },
    paymentDrawer: {
      submitButton: 'Zahlung erfassen',
      addPayment: 'Zahlung hinzufügen',
      buttonAssignBooking: 'Buchung zuordnen',
      title: 'Offline-Zahlung erfassen',
      total: 'Gesamtbetrag',
      openAmount: 'offener Betrag',
      invoice: {
        number: 'rechnungsnummer',
        date: 'rechnungsdatum',
        dueTo: 'fällig am',
      },
      creditNote: {
        title: 'Jetzt Gutschrift bezahlen',
        number: 'Gutschriftnummer',
        date: 'Datum der Gutschrift',
        dueTo: 'fällig am',
      },
      expense: {
        number: 'Beleg-Nr.',
        date: 'Belegdatum',
        dueTo: 'fällig am',
      },
      revenue: {
        number: 'Beleg-Nr.',
        date: 'Belegdatum',
      },
    },
    assignmentDrawer: {
      secondViewHeader: 'Details zur Rechnung',
      secondViewHeaderCreditNote: 'Details zur Gutschrift',
      secondViewHeaderManualCreditNote: 'Details zur Rechnungskorrektur/Gutschrift',
      secondViewHeaderExpense: 'Details zur Ausgabe',
      secondViewHeaderRevenue: 'Details zur Einnahme',
      errorMessageMultipleBookings: 'Die Zuordnung von mehreren Buchungen ist nicht erlaubt.',
    },
  },

  // transaction
  transactionEmptyCustomerErrorMessage: 'Bitte füge einen Kunden zu %s hinzu.',
  transactionEmptyPositionsErrorMessage: 'Bitte füge mindestens eine Position zu %s hinzu.',
  transactionEmptyDeliveryConditionErrorMessage: 'Bitte füge eine Lieferbedingung zu %s hinzu',
  transactionEmptyPayConditionErrorMessage: 'Bitte füge eine Zahlungsbedingung zu %s hinzu.',
  transactionDiscountPercentErrorMessage: 'Der Rabatt einer Position muss größer als 0% und kleiner als 1000% sein!',
  transactionArticleStockAmountExceededWarningMessage: 'Achtung, der verfügbare Bestand beträgt XXX.',
  transactionArticleStockAmountMinimumStockWarningMessage: 'Achtung, der Mindestbestand beträgt XXX.',
  transactionEmptyCustomerCompanyName: 'Bitte überprüfe den Firmennamen des Kunden.',
  transactionEmptyCustomerLastName: 'Bitte überprüfe den Nachnamen des Kunden.',
  transactionEInvoiceSettingsErrorMessage: 'Bitte überprüfe die E-Rechnungseinstellungen.',
  transactionLetterPaperIdMissingErrorMessage: 'Es konnte keine gültige Briefpapier-Vorlage zugeordnet werden. Bitte wende Dich an unseren Support.',
  transactionEmptyCustomerEmail: 'Bitte trage beim Kunden eine Kontakt E-Mail-Adresse ein.',
  transactionEmptyDirectDebitMandateId: 'Bitte wähle ein SEPA-Lastschriftmandat aus oder beantrage ein neues.',

  // demoData
  demoHeaderText: 'Demo-Modus',
  demoHeaderButtonText: 'Beenden',
  endDemoModusConfirmTitle: 'Demo-Modus beenden',
  endDemoModusConfirmContent: 'Hierbei werden sämtliche Demodaten und von Dir zum Test erstellte Daten unwiderruflich gelöscht.',
  confirmDemoDataTitle1: 'Herzlich willkommen',
  confirmDemoDataTitle2: `bei Deinem ${productNameLong}!`,
  confirmDemoDataDescriptionLine1: 'Du befindest Dich derzeit im Demo-Modus.',
  confirmDemoDataDescriptionLine2: `Wir haben wie gewünscht für Dich einen Satz an Demodaten vorgeladen, damit Du alle Funktionen von ${productNameLong} bequem ausprobieren kannst.`,
  confirmDemoDataDescriptionLine3: 'Diese Daten haben keine Auswirkungen auf Deine Rechnungen oder anderen Vorgänge. Du kannst den Demo-Modus jederzeit beenden, um mit einem neuen System für Deine Rechnungsstellung zu beginnen.',
  confirmDemoDataDescriptionLine4: 'Alle Demodaten und nachträglich erzeugten Daten werden dann gelöscht.',
  confirmDemoDataDescriptionLine5: `Wir wünschen Dir viel Freude und Erfolg bei Deinem ${productNameLong}.`,
  confirmDemoDataDescriptionLine6: 'Bei Rückfragen kannst Du Dich jederzeit an unseren Support wenden.',

  // booking
  bookingSearchCategory: 'Buchungssätze',

  // invoice
  cashInvoiceTitle: 'Kassenrechnung',
  invoiceTitle: 'Rechnung',
  recuringInvoiceTitle: 'Abo-Rechnung',
  invoiceDeleteConfirmCaption: multiple => `Rechnung${multiple ? 'en' : ''} löschen`,
  invoiceDeleteConfirmText: multiple => `Möchtest Du die Rechnung${multiple ? 'en' : ''} wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  invoiceDeleteSuccessMessage: multiple => `Die Rechnung${multiple ? 'en' : ''} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  manualCreditNoteConfirmCaption: multiple => `Rechnungskorrektur${multiple ? 'en' : ''} löschen`,
  manualCreditNoteConfirmText: multiple => `Möchtest Du die Rechnungskorrektur${multiple ? 'en' : ''} wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  manualCreditNoteSuccessMessage: multiple => `Die Rechnungskorrektur${multiple ? 'en' : ''} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  invoiceEditLockedMessage: 'Die Rechnung wurde bereits abgeschlossen und kann nicht mehr bearbeitet werden.',
  invoiceEditCanceledMessage: 'Die Rechnung wurde bereits storniert und kann nicht mehr bearbeitet werden.',
  invoiceLockSuccessMessage: 'Die Rechnung wurde erfolgreich abgeschlossen.',
  manualCreditNoteLockSuccessMessage: 'Die Rechnungskorrektur/Gutschrift wurde erfolgreich abgeschlossen.',
  invoiceZeroAmountSuccessMessage: 'Die 0,00€ Rechnung wurde direkt auf bezahlt gesetzt.',
  invoiceSaveErrorMessage: 'Beim Speichern der Rechnung ist ein unerwarteter Fehler aufgetreten.',
  invoiceSaveSuccessMessage: 'Die Rechnung wurde erfolgreich gespeichert.',
  invoiceSearchCategory: 'Rechnungen',
  invoiceCancelSuccessMessage: 'Die Rechnung wurde erfolgreich storniert',
  invoiceHasPaymentsErrorMessage: 'Zu der Rechnung existiert bereits mindestens eine Zahlung, daher kann diese nicht mehr storniert oder gelöscht werden.',
  cancellationDeleteErrorMessage: 'Eine Stornorechnung kann nicht gelöscht werden.',
  cancellationEditErrorMessage: 'Eine Stornorechnung kann nicht bearbeitet werden.',
  invoiceColumnEditSaveInSettingsLabel: 'als Standard für Rechnungen speichern',
  invoiceCopySuccessMessage: 'Die Rechnung wurde erfolgreich kopiert.',
  invoiceCopyErrorMessage: 'Beim Kopieren der Rechnung ist ein Fehler aufgetreten.',
  invoiceInfoDelDateHint: 'Es erscheint ein Hinweissatz im Schlusstext.',
  invoiceInfoDisabledDelDateHint: 'Da das Rechnungsdatum nicht dem Liefer-/Leistungsdatum entspricht, kannst Du das Liefer-/Leistungsdatum nicht ausblenden.',
  noInvoicesLeftErrorMessage: 'Die Anzahl der maximalen Rechnungen für diesen Monat ist erreicht.',
  invoiceCreditModalCaption: 'Gutschrift erstellen',
  enterPosition: 'Position eingeben',
  investPosition: 'Position anlegen',
  invoiceCreditModalDescription: (number) => (`Möchtest Du eine Gutschrift für die Rechnung ${number} erstellen?`),
  manualCreditNote: {
    lockModal: {
      heading: 'Rechnungskorrektur/Gutschrift verbindlich abschließen?',
      contentText: 'Wenn Du eine Rechnungskorrektur/Gutschrift abschließt, kann sie anschließend nicht mehr bearbeitet oder gelöscht werden.',
      info: {
        title: 'Info:',
        description: 'Aus rechtlichen Gründen können abgeschlossene Rechnungskorrekturen/Gutschriften nicht gelöscht oder bearbeitet werden. Bei Bedarf können sie jedoch storniert und ggf. neu erstellt werden.',
      },
    },
  },
  invoice: {
    createInvoice: 'Rechnung erstellen',
    editInvoice: 'Rechnung bearbeiten',
    editManualCreditNote: 'Rechnungskorrektur/Gutschrift bearbeiten',
    sendCredit: 'Gutschrift versenden',
    sendInvoice: 'Rechnung versenden',
    sendManualCreditNote: 'Rechnungskorrektur/Gutschrift versenden',
    manualCreditNoteEmailSubheadLine: 'Versende Deine Rechnungskorrektur/Gutschrift bequem per E-Mail',
    list: {
      listEmptyStateButtonLabel: 'Los geht\'s',
      listEmptyStateSubtitle: 'Jetzt die erste Rechnung erstellen',
      listEmptyStateTitle: 'Keine Rechnungen vorhanden',
      listNoSearchResultTitle: 'Keine Rechnungen gefunden',
    },
    edit: {
      notesHint: 'Anmerkungen werden nicht auf dem Dokument angezeigt',
    },
    lockModal: {
      heading: 'Rechnung verbindlich abschließen?',
      contentText: 'Wenn Du eine Rechnung abschließt, kann sie anschließend nicht mehr bearbeitet oder gelöscht werden.',
      submitButtonText: 'Jetzt abschließen',
      info: {
        title: 'Info:',
        description: 'Aus rechtlichen Gründen können abgeschlossene Rechnungen nicht gelöscht oder bearbeitet werden. Bei Bedarf können sie jedoch storniert und ggf. neu erstellt werden.',
      },
    },
    draftInvoice: 'Rechnung Entwurf',
    invoiceWithNumber: (number) => `Rechnung ${number}`,
    manualCreditNote: 'Rechnungskorrektur/Gutschrift Entwurf',
    manualCreditNoteNumber: (number) => `Rechnungskorrektur/Gutschrift ${number}`,
    invoiceDate: 'Rechnungsdatum',
    detail: {
      dunningToggle: 'Autom. mahnen',
    },
    deleteAssignment: 'Die Zuordnung wurde erfolgreich aufgehoben.',
    deleteAssignmentError: 'Die Zuordnung kann über diesen Weg nicht aufgehoben werden, bitte direkt unter Bank/Kasse aufheben.',
  },
  columnsSetting: {
    selectAll: 'Alle Spalten auswählen',
    deselectAll: 'Alle Spalten abwählen',
    setDefault: 'Standard wiederherstellen',
    default: 'Standard',
    additional: 'Weitere Informationen',
    custom: 'Individuelle Felder',
    adjust: 'Spalten anpassen',
    saving: 'Spalteneinstellungen werden gespeichert',
    saveSuccess: 'Die Spalteneinstellung wurde erfolgreich gespeichert',
    saveError: 'Beim Speichern der Spalteneinstellung ist ein Fehler aufgetreten',
    autoSizeColumns: 'Spaltengröße automatisch anpassen',
  },

  // recurringInvoice
  recurringInvoiceTitle: 'Abo-Rechnung',
  recurringInvoiceCopySuccessMessage: 'Die Abo-Rechnung wurde erfolgreich kopiert.',
  recurringInvoiceCopyErrorMessage: 'Beim Kopieren der Abo-Rechnung ist ein Fehler aufgetreten.',
  recurringInvoiceDeleteSuccessMessage: 'Die Abo-Rechnung wurde erfolgreich gelöscht',
  recurringInvoiceDeleteConfirmCaption: 'Abo-Rechnung löschen',
  recurringInvoiceDeleteConfirmText: 'Möchtest Du die Abo-Rechnung wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  recurringInvoiceSaveSuccessMessage: 'Die Abo-Rechnung wurde erfolgreich gespeichert',
  recurringInvoiceSaveErrorMessage: 'Beim Speichern der Abo-Rechnung ist ein unerwarteter Fehler aufgetreten.',
  recurringInvoiceStartSuccessMessage: 'Die Abo-Rechnung wurde erfolgreich gestartet',
  recurringInvoiceStartErrorMessage: 'Die Abo-Rechnung konnte nicht gestartet werden',
  recurringInvoiceFinishSuccessMessage: 'Die Abo-Rechnung wurde erfolgreich beendet',
  recurringInvoiceFinishErrorMessage: 'Beim Beenden der Abo-Rechnung ist ein Fehler aufgetreten',
  recurringInvoiceReactivateSuccessMessage: 'Die Abo-Rechnung wurde erfolgreich reaktiviert',
  recurringInvoiceReactivateErrorMessage: 'Beim Reaktivieren der Abo-Rechnung ist ein Fehler aufgetreten',
  recurringFinishConfirmCaption: 'Abo-Rechnung beenden',
  recurringFinishConfirmText: 'Möchtest Du die Abo-Rechnung wirklich beenden? Dies kann nicht rückgängig gemacht werden.',
  recurringInvoiceInvalidStartDateMessage: 'Das Datum der erstmaligen Ausführung darf nicht in der Vergangenheit liegen',
  recurringInvoiceInvalidEndDateMessage: 'Das Datum der letzten Ausführung darf nicht vor der ersten Ausführung liegen',
  recurringInvoiceMissingRecurrenceErrorMessage: 'Bitte gib eine Wiederholung an',
  recurringInvoiceMissingEmailErrorMessage: 'Bitte gib die E-Mail des Rechnungs-Empfängers an',
  recurringInvoiceMissingSubjectErrorMessage: 'Bitte gib einen Betreff ein!',
  recurringInvoiceEditTitle: 'Abo-Einstellungen',
  recurringInvoiceEditSubtitle: 'Abo-Rechnungen werden zum Fälligkeitstermin automatisch per E-Mail verschickt',
  invoiceEmailHeadline: 'Zeit, sich bezahlen zu lassen',
  invoiceEmailSubheadline: 'Versende Deine Rechnung bequem per E-Mail',
  recurringInvoiceEditHeadline: 'Abo-Rechnung bearbeiten',
  recurringInvoiceButtons: {
    start: 'Abo starten',
    end: 'Abo beenden',
    reactivate: 'Abo reaktivieren',
  },
  recurringInvoiceReactivate: {
    title: 'Abgelaufenes Abo reaktivieren',
    description: 'Mit dieser Option kannst Du ein bereits abgelaufenes Abo wieder aktivieren. Du kannst einen neuen Ausführungsbeginn definieren, beachte jedoch, dass bei einem in der Vergangenheit liegenden Ausführungsbeginn, alle bis dahin fälligen Abos erstellt und versandt werden.',
    nextDate: 'Neuer - Ausführungsbeginn',
    endDate: 'Neues - Datum der letzten Ausführung',
  },
  recurringInvoiceInputLabels: {
    startDate: 'Erste Ausführung',
    endDate: 'Letzte Ausführung',
    recurrence: 'Wiederholung',
    recipient: 'E-Mail des Rechnungsempfängers',
    subject: 'Betreff-Zeile der E-Mail',
    mailContent: 'E-Mail-Text',
    confirmationRecipientUser: 'Empfänger für Versandbestätigungen',
  },
  recurringInvoiceDetails: 'Abo-Rechnung-Details',
  recurringInvoiceLoadTitle: 'Lade Abo-Rechnung',
  recurringInvoicesLoadTitle: 'Lade Abo-Rechnungen',
  recurringInvoiceNewTitle: 'Neue Abo-Rechnung erstellen',
  recurringInvoice: {
    createRecurringInvoice: 'Abo-Rechnung erstellen',
    showAdvancedSettings: 'Weitere Einstellungen einblenden',
    hideAdvancedSettings: 'Weitere Einstellungen ausblenden',
    sendLabel: {
      default: 'wird verschickt an:',
      confirmation: 'Empfänger der Versandbestätigungen:',
    },
    lockModal: {
      heading: 'Abo-Rechnung starten?',
      contentText: 'Nach Start der Abo-Rechnung kannst Du diese nicht mehr bearbeiten.',
      submitButtonText: 'Jetzt starten',
    },
  },
  recurringInvoiceRecipientConfirmation: {
    title: 'E-Mail des Rechnungsempfängers ändern',
    description: 'Möchtest Du die E-Mail des Kunden als E-Mail des Rechnungsempfängers übernehmen?',
  },
  recurringInvoiceRecurrence: {
    weeklyTitle: 'wöchentlich',
    biweeklyTitle: '14-tägig',
    monthlyTitle: 'monatlich',
    bimonthlyTitle: '2-monatlich',
    quarterTitle: '3-monatlich',
    biyearlyTitle: 'halbjährlich',
    yearlyTitle: 'jährlich',
  },

  recurringInvoicePeriods: {
    weeklyTitle: 'Wöchentliche',
    biweeklyTitle: '14-tägige',
    monthlyTitle: 'Monatliche',
    bimonthlyTitle: '2-monatliche',
    quarterTitle: '3-monatliche',
    biyearlyTitle: 'Halbjährliche',
    yearlyTitle: 'Jährliche',
  },
  // closingInvoice
  closingInvoice: {
    createClosingInvoice: 'Neue Schlussrechnung',
    editClosingInvoice: 'Schlussrechnung bearbeiten',
  },

  // depositInvoice
  depositInvoiceTitle: 'Abschlagsrechnung',
  depositInvoiceSaveSuccessMessage: 'Die Abschlagsrechnung wurde erfolgreich gespeichert',
  depositInvoiceEditTitle: 'Abschlagsrechnung bearbeiten',
  depositInvoiceLoading: 'Lade Abschlag',
  depositInvoiceNewTitle: 'Neue Abschlagsrechnung erstellen',
  depositInvoiceNotesContainerViewPlaceholder: 'Gib hier Anmerkungen zu dieser Abschlagsrechnung ein',
  depositInvoiceRelatesTo: 'Gehört zu Projekt: ',
  depositInvoice: {
    createDepositInvoice: 'Abschlagsrechnung erstellen',
    amountModal: {
      title: 'Abschlagsrechnung erstellen',
      openNetLabel: 'Offener Netto-Betrag',
      openNetCaptionFull: 'Gesamter Auftrag, noch nicht abgerechnet',
      openNetCaptionOriginal: (amount) => `Ursprünglicher Auftrag: ${amount}`,
      openNetCaptionBilled: (amount) => `Bereits abgerechnet: ${amount}`,
      compositionTitle: 'Zusammensetzung des offenen Auftragsbetrags',
      compositionTitleSingle: 'Enthaltener Umsatzsteuersatz',
      billedTitle: 'Bisher abgerechnet',
      billedEmpty: 'Keine Abschlagsrechnungen vorhanden…',
      billedColType: 'Typ',
      billedColName: 'Name',
      billedColDate: 'Datum',
      billedColAmount: 'Betrag',
      modeLabel: 'Abschlagshöhe berechnen',
      modePercent: 'Prozentual',
      modeAbsolute: 'Absoluter Betrag',
      percentSuffix: '%',
      absoluteSuffix: '€',
      absoluteCaption: (percent) => `Entspricht ${percent} % des offenen Netto-Betrags`,
      percentCaption: (amount) => `Abschlagsbetrag: ${amount}`,
      splitTitle: 'Aufteilung nach Umsatzsteuer',
      splitSinglePosition: 'Rechnungsposition',
      depositAmountLabel: 'Abschlagsbetrag (Netto)',
      depositAmountCaption: (percent) => `${percent} % des offenen Betrags`,
      infoMulti: 'Die Steuerverteilung wird automatisch aus dem noch offenen Auftragsbetrag übernommen. Je Umsatzsteuersatz wird automatisch eine eigene Rechnungsposition erstellt. Die Umsatzsteuersätze können nicht geändert werden.',
      infoSingle: 'Der Umsatzsteuersatz wird automatisch aus dem noch offenen Auftragsbetrag übernommen und kann nicht geändert werden.',
      previewRate: (rate) => `${rate} % USt`,
      confirm: 'Abschlagsrechnung erstellen',
      cancel: 'Abbrechen',
      loading: 'Offener Betrag wird geladen …',
      loadError: 'Der offene Betrag konnte nicht geladen werden.',
      nothingOpen: 'Für diesen Auftrag ist kein offener Netto-Betrag mehr vorhanden.',
    },
    positions: {
      placeHolder: 'Abschlagsbezeichnung eingeben',
    },
    editDepositInvoice: 'Abschlagsrechnung bearbeiten',
    nextDepositInvoice: (depositInvoiceCount = 1) => `${depositInvoiceCount + 1}. Abschlagsrechnung erstellen`,
    createClosingInvoice: 'Schlussrechnung erstellen',
    hints: {
      notAllDepositsLocked: 'Es müssen erst alle Abschlagsrechnungen abgeschlossen sein',
      notAllDepositsPaid: 'Es müssen erst alle Abschlagsrechnungen bezahlt sein',
      closingInvoiceExists: 'Es existiert bereits eine Schlussrechnung',
    },
    errors: {
      notAllDepositsLocked: 'Es wurden nicht alle Abschlagsrechnungen abgeschlossen',
      closingInvoiceExists: 'Es existiert bereits eine Schlussrechnung',
    },
  },

  // partialInvoice
  partialInvoiceTitle: 'Teilrechnung',
  partialInvoice: {
    editPartialInvoice: 'Teilrechnung bearbeiten',
    drawer: {
      title: 'Auftragspositionen auswählen',
      description: 'Wähle die Auftragspositionen aus, die Du in die Teilrechnung übernehmen möchtest. Im nächsten Schritt kannst Du Mengen, Bezeichnungen und Beschreibungen anpassen – und bei Bedarf Nachtragspositionen ergänzen.',
      buttons: {
        apply: 'Übernehmen',
      },
      groupLabels: {
        orderPositions: 'Auftragspositionen',
        discountSurcharge: 'Rabatt/Zuschlag',
      },
      discountTypes: {
        percentage: 'prozentual',
        absolute: 'absolut',
      },
      tooltips: {
        percentageDiscount: 'Dieser Rabatt wird automatisch auf alle Teilrechnungen angewendet, um die mathematische Korrektheit zu wahren.',
        absoluteDiscount: 'Dieser Rabatt ist bearbeitbar und kann flexibel auf mehrere Teilrechnungen verteilt werden.',
      },
      badges: {
        fullyInvoiced: 'abgerechnet',
        partiallyInvoiced: 'teilw. abgerechnet',
        notInvoiced: 'offen',
      },
      summary: {
        title: 'Summe der ausgewählten Auftragspositionen:',
      },
    },
  },

  // cancellation
  cashCancellationTitle: 'Kassenstorno',
  cancellationTitle: 'Stornorechnung',
  cancellationsTitle: 'Stornorechnungen',
  cancellationEmailHeadline: 'Stornorechnung versenden',
  cancellationEmailSubheadLine: 'Versende Deine Stornorechnung bequem per E-Mail',
  cancellationSubtitle: 'zur Rechnungs-Nr.',
  cancellationNumber: 'Stornorechnungs-Nr',
  cancellationTextModuleDescription: 'Lege hier Deine Standardtexte für Stornorechnungen fest',
  cancellationTextModulesToggleLabel: 'Stornorechnungen standardmäßig auch an mich versenden',

  // credit note
  cashCreditNoteTitle: 'Kassengutschrift',
  creditNoteTitle: 'Gutschrift',
  creditNoteEmailHeadline: 'Gutschrift versenden',
  creditNoteEmailSubheadLine: 'Versende Deine Gutschrift bequem per E-Mail',
  creditNoteNumber: 'Gutschrift-Nr',

  // closingInvoice
  closingInvoiceTitle: 'Schlussrechnung',

  // shopify cash
  shopifyCashInvoiceTitle: 'Kassenrechnung Shopify',
  shopifyCashCancellationTitle: 'Stornorechnung Shopify',
  shopifyCashCreditNoteTitle: 'Gutschrift Shopify',
  shopifyCashOrderTitle: 'Auftrag',

  // manualcreditNote

  manualCreditNoteTitle: 'Rechnungskorrektur/Gutschrift',
  transactionTooltip: {
    title: 'Rechnungskorrektur/Gutschrift kann nicht gespeichert werden',
    description: `Ein negativer Gesamtbetrag bei einer manuellen Rechnungskorrektur/Gutschrift ist nicht möglich.
    </br>
    <b>Bitte überprüfe Deine Positionen und den Gesamtbetrag.</b>`,
    description2: 'Die Rechnungskorrektur/Gutschrift wird von unserem System als diese identifiziert und entsprechend verbucht.',
  },

  // transactionTypes
  transactionTypes: {
    offer: 'Angebot',
    cashInvoice: 'Kassenrechnung',
    invoice: 'Rechnung',
    dunning: 'Mahnung',
    cashCancellation: 'Kassenstornorechnung',
    cancellation: 'Stornorechnung',
    cashCreditNote: 'Kassengutschrift',
    creditNote: 'Gutschrift',
    depositInvoice: 'Abschlagsrechnung',
    closingInvoice: 'Schlussrechnung',
    recurringInvoice: 'Abo-Rechnung',
    deliveryNote: 'Lieferschein',
    order: 'Auftrag',
    expense: 'Ausgabe',
    revenue: 'Einnahme',
    shopifyCashInvoice: 'Shopify-Kassenrechnung',
    shopifyCashCancellation: 'Shopify-Kassenstornorechnung',
    shopifyCashCreditNote: 'Shopify-Kassengutschrift',
    shopifyCashOrder: 'Shopify-Kassenauftrag',
    refundInvoice: 'Erstattungsrechnung',
    refundOrder: 'Erstattungsauftrag',
    article: 'Artikel',
    plural: {
      offer: 'Angebote',
      cashInvoice: 'Kassenrechnungen',
      invoice: 'Rechnungen',
      dunning: 'Mahnungen',
      cashCancellation: 'Kassenstornorechnungen',
      cancellation: 'Stornorechnungen',
      cashCreditNote: 'Kassengutschriften',
      creditNote: 'Gutschriften',
      depositInvoice: 'Abschlagsrechnungen',
      closingInvoice: 'Schlussrechnungen',
      recurringInvoice: 'Abo-Rechnungen',
      deliveryNote: 'Lieferscheine',
      order: 'Aufträge',
      expense: 'Ausgaben',
      revenue: 'Einnahmen',
      shopifyCashInvoice: 'Shopify-Kassenrechnungen',
      shopifyCashCancellation: 'Shopify-Stornorechnungen',
      shopifyCashCreditNote: 'Shopify-Gutschriften',
      shopifyCashOrder: 'Shopify-Aufträge',
      manualCreditNote: 'Rechnungskorrekturen/Gutschriften',
      letter: 'Geschäftsbriefe',
      partialInvoice: 'Teilrechnungen',
    },
  },

  // shop-sync-modal
  shopSyncModal: {
    headerButtonLabel: 'Webshop synchronisieren',
    finalizeToast: 'Dein Bestand wird im Hintergrund aktualisiert. Du kannst währenddessen weiterarbeiten.',
    errorShopNotFoundToast: 'Der angegebene Webshop wurde nicht gefunden',
    errorExternalReferencesNotFoundToast: 'Zu dem gewählten Webshop wurden keine SKU-Verknüpfungen gefunden',
    requiredForm: {
      buttonPrimary: 'Fertig',
      buttonCancel: 'Abbrechen',
    },
    syncForm: {
      title: 'Jetzt Lagerbestand synchronisieren',
      description: `Beim ersten Abgleich kannst Du wählen, ob die Lagerbestände aus ${productNameLong} zu Deinem Webshop oder umgekehrt synchronisiert werden.`,
      selectionTitle: 'Webshop auswählen:',
      descriptionSyncTo: 'In welche Richtung soll die Synchronisation stattfinden:',
      buttonPrimary: 'Weiter',
      buttonCancel: 'Abbrechen',
      errorHint: 'Es wurden keine SKU-Verknüpfungen zum Webshop gefunden',
    },
    syncSummaryForm: {
      description: {
        toShop: `Der Lagerbestandssync zwischen ${productNameLong} und Deinem gewähltem Webshop wird direkt nach Bestätigung gestartet.<br/><br/><b>Wichtige Informationen:</b><br/>Wenn der 1. Abgleich erfolgt ist, wird ${productNameLong} als führendes System gegenüber Deinem Webshop agieren.
        <br/><br/>Automatische Bestandsveränderungen in ${productNameLong} werden direkt zu Deinem Webshop synchronisiert.
        <br/><br/>Manuelle Anpassungen des Bestandes können nachträglich erfolgen.
        <br/><br/>Nur lagerbestandsgeführte Artikel werden übertragen.`,
        toApp: `Der Lagerbestandssync zwischen Deinem gewähltem Webshop und ${productNameLong} wird direkt nach Bestätigung gestartet.<br/><br/><b>Wichtige Informationen:</b><br/>Wenn der 1. Abgleich erfolgt ist, wird ${productNameLong} als führendes System gegenüber Deinem Webshop agieren.
        <br/><br/>Automatische Bestandsveränderungen in ${productNameLong} werden direkt zu Deinem Webshop synchronisiert.
        <br/><br/>Manuelle Anpassungen des Bestandes können nachträglich erfolgen.
        `,
        // toApp: `Nach Bestätigung wird ein Bestandssync von Deinem gewählten Shop zu ${productNameLong} gestartet.
        // Ab diesem Zeitpunkt ist ${productNameLong} das führende System, im Klartext bedeutet dies, das bei Bestandsänderungen innerhalb ${productNameLong} dieses zu Deinem Shop synchronisiert wird.`,
      },
      buttonPrimary: 'Starten',
      buttonCancel: back,
    },
  },

  userHeader: {
    confirmEmail: 'Bitte bestätige Deine E-Mail-Adresse',
    confirmTaxConsultant: 'Offene Anfrage Deiner Steuerkanzlei',
  },

  confirmEmail: {
    title: 'Bitte E-Mail-Adresse bestätigen!',
    subTitle: 'Wirf einen Blick in Dein Postfach.',
    description: (email) => `Ein Bestätigungslink ist an Deine E-Mail-Adresse: <b>${email}</b> gesendet worden. Klicke auf den Link oder gib direkt den 4-stelligen Code ein.`,
    resendEmail: 'Code erneut senden',
    deliveryHint: 'Die Zustellung kann bis zu einer Minute dauern. Bitte prüfe auch Deinen Spam-Ordner.',
    // DO-13065: soft reminders for users who are let in with an unverified address
    verifyNow: 'Jetzt verifizieren',
    pendingBar: 'Deine E-Mail-Adresse ist noch nicht verifiziert. Bestätige sie, um alle Funktionen ohne Einschränkung zu nutzen.',
    sendEmailBlocked: 'E-Mails können erst versendet werden, wenn die E-Mail-Adresse verifiziert wurde.',
    sendEmailBlockedToast: 'Die E-Mail konnte nicht versendet werden: Bitte zuerst die E-Mail-Adresse verifizieren.',
  },

  // confirm email
  confirmEmailTitle: 'Gib den Code ein, den Du per<br />E-Mail erhalten hast',
  confirmEmailCodeError: 'Dein Code ist falsch oder unvollständig',
  confirmEmailResendLabel: 'Neuen Code anfordern',
  confirmEmailDescription: `Gib bitte den Code aus der E-Mail "Dein ${productNameLong} Aktivierungscode" ein, um die Registrierung abzuschließen`,
  confirmEmailButtonLabel: 'Code bestätigen',
  confirmResendCode: 'Code wurde neu verschickt.',

  // project
  deleteProjectSuccessMessage: 'Das Projekt wurde erfolgreich gelöscht',
  deleteProjectErrorMessage: 'Beim Löschen des Projektes ist ein Fehler aufgetreten',
  deleteProjectErrorMessageStateNotAllowed: 'Projekte mit einer zugehörigen Rechnung können nicht gelöscht werden.',
  projectDeliveredEditErrorMessage: 'Das Projekt wurde bereits verschickt und kann nicht mehr bearbeitet werden.',
  projectCopyErrorMessage: 'Beim Kopieren des Projektes ist ein Fehler aufgetreten.',
  projectDeleteConfirmCaption: 'Projekt löschen',
  projectDeleteConfirmText: 'Möchtest Du den Projekt wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  projectMissingTitleMessage: 'Bitte gib einen Projektnamen ein',
  projectMissingBudgetMessage: 'Bitte gib ein Projektbudget ein',
  projectEditSuccessMessage: 'Die Änderungen wurden erfolgreich gespeichert',
  projectEditErrorMessage: 'Beim Speichern der Änderungen ist ein Fehler aufgetreten',
  projectDeleteSuccessMessage: 'Das Projekt wurde erfolgreich gelöscht',
  projectCustomerMissingMessage: 'Bitte füge einen Kunden zu der Abschlagsrechnung hinzu.',
  projectTitle: 'Projekt',
  projectHeadline: 'Projekte und Abschlagsrechnungen',
  project: {
    documentTitle: {
      edit: 'Projekt bearbeiten',
      new: 'Neue Abschlagsrechnung',
    },
    upgradeText: `${unlockViewUpgradeText} Projekte und Abschlagsrechnungen freizuschalten.`,
    form: {
      edit: {
        labels: {
          title: 'Projektname',
          startDate: 'Projektstart',
          budget: 'Gesamtbudget (Netto)',
          description: 'Projektbeschreibung',
        },
      },
      messages: {
        nameNotSet: 'Bitte gib einen Projektnamen ein',
        budgetNotSet: 'Bitte gib ein Projektbudget ein',
      },
    },
  },

  // delivery note
  deliveryNoteTitle: 'Lieferschein',
  deleteDeliveryNoteSuccessMessage: 'Der Lieferschein wurde erfolgreich gelöscht',
  deleteDeliveryNoteErrorMessage: 'Beim Löschen des Lieferscheins ist ein Fehler aufgetreten',
  deleteDeliveryNoteErrorMessageStateNotAllowed: 'Lieferscheine mit einer zugehörigen Rechnung können nicht gelöscht werden.',
  deliveryNoteDeliveredEditErrorMessage: 'Der Lieferschein wurde bereits verschickt und kann nicht mehr bearbeitet werden.',
  deliveryNoteCopyErrorMessage: 'Beim Kopieren des Lieferscheins ist ein Fehler aufgetreten.',
  deliveryNoteDeleteConfirmCaption: 'Lieferschein löschen',
  deliveryNoteDeleteConfirmText: 'Möchtest Du den Lieferschein wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  deliveryNoteWithTransactionsDeleteConfirmText: 'Mit dem Lieferschein sind mehrere Vorgänge verknüpft. Möchtest Du den Lieferschein wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  deliveryNoteToInvoiceConnectedSuccessMessage: 'Die Rechnung wurde erfolgreich mit dem Lieferschein verknüpft.',
  deliveryNoteToInvoiceConnectedErrorMessage: 'Beim Verknüpfen der Rechnung ist ein Fehler aufgetreten.',
  deliveryNoteRevokeConnectionSuccessMessage: 'Die Verknüpfung wurde erfolgreich aufgehoben.',
  deliveryNoteRevokeConnectionErrorMessage: 'Beim Aufheben der Verknüpfen ist ein Fehler aufgetreten.',
  deliveryNoteEmailHeadline: 'Lieferschein versenden',
  deliveryNoteEmailSubheadline: 'Versende Deinen Lieferschein bequem per E-Mail',
  deliveryNote: {
    deliveryNote: 'Lieferscheine',
    deliveryNoteDetails: 'Lieferschein-Details',
    createDeliveryNote: 'Lieferschein erstellen',
    editDeliveryNote: 'Lieferschein bearbeiten',
    sendDeliveryNote: 'Lieferschein versenden',
    list: {
      listEmptyStateTitle: 'Keine Lieferscheine vorhanden',
      listNoSearchResultTitle: 'Keine Lieferscheine gefunden',
      listEmptyStateSubtitle: 'Jetzt den ersten Lieferschein erstellen',
      listEmptyStateButtonLabel: 'Los geht\'s',
    },
    detail: {
      connectedTransactions: {
        title: 'Zugehöriger Auftrag',
      },
    },
    collective: {
      title: 'Teil-/Sammellieferschein erstellen',
      customerLabel: 'zu Kunde',
      description: 'Die Tabelle zeigt alle Aufträge zum Kunden, die noch nicht vollständig geliefert worden sind. Durch Anhaken der Checkbox werden Positionen für den zu erstellenden Lieferschein ausgewählt. Das Feld Menge kann dabei auf die korrekte Liefermenge angepasst werden.',
      searchPlaceholder: 'Nach Auftrags-Nr., Bezeichnung, Beschreibung suchen',
      expandAll: 'Alle ausklappen',
      closeAll: 'Alle einklappen',
      emptyStateText: 'Keine offenen Aufträge zum Kunden vorhanden',
      columnHeaderNames: {
        number: 'Auftrags-Nr.',
        title: 'Bezeichnung',
        description: 'Beschreibung',
        status: 'Status',
        openAmount: 'Offene Liefermenge',
        delivered: 'Geliefert',
        toDeliver: 'Zu Liefern',
      },
      status: {
        open: 'Offen',
        partiallyDelivered: 'Teilw. geliefert',
        delivered: 'Geliefert',
      },
      selectionDetail: {
        title: 'Details zur Auswahl:',
        positions: 'AUSGEWÄHLTE POSITIONEN',
        amounts: 'AUSGEWÄHLTE MENGEN',
      },
      buttons: {
        preview: 'Lieferschein Vorschau',
        create: 'Lieferschein erstellen',
      },
      createError: 'Der Lieferschein konnte nicht erstellt werden.',
      previewError: 'Die Vorschau konnte nicht geladen werden.',
    },
  },

  // letter
  letterTitle: 'Geschäftsbrief',
  deleteLetterSuccessMessage: 'Der Geschäftsbrief wurde erfolgreich gelöscht',
  deleteLetterErrorMessage: 'Beim Löschen des Geschäftsbriefes ist ein Fehler aufgetreten',
  letterCopyErrorMessage: 'Beim Kopieren des Geschäftsbriefes ist ein Fehler aufgetreten.',
  letterDownloadErrorMessage: 'Beim Herunterladen des Geschäftsbriefes ist ein Fehler aufgetreten.',
  letterNoTitleErrorMessage: 'Bitte gib den Betreff an.',
  changeLetterNotesErrorMessage: 'Beim Aktualisieren der Anmerkungen ist ein Fehler aufgetreten.',
  letterDeleteConfirmCaption: 'Geschäftsbrief löschen',
  letterDeleteConfirmText: 'Möchtest Du den Geschäftsbrief wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  letterEmailHeadline: 'Geschäftsbrief versenden',
  letterEmailSubheadline: 'Versende Deinen Geschäftsbrief bequem per E-Mail',
  createLetter: 'Geschäftsbrief erstellen',
  letterTransaction: {
    letters: 'Geschäftsbriefe',
    letterDetails: 'Geschäftsbrief-Details',
    createLetter: 'Geschäftsbrief erstellen',
    editLetter: 'Geschäftsbrief bearbeiten',
    sendLetter: 'Geschäftsbrief versenden',
    subject: 'Betreff',
    introduction: 'Brieftext',
    conclusion: 'Grußformel',
    conclusionTextPlaceholder: 'Verfasse hier Deine Grußformel.',
    list: {
      listEmptyStateTitle: 'Keine Geschäftsbriefe vorhanden',
      listNoSearchResultTitle: 'Keine Geschäftsbriefe gefunden',
      listEmptyStateSubtitle: 'Jetzt den ersten Geschäftsbrief erstellen',
      listEmptyStateButtonLabel: 'Los geht\'s',
    },
    detail: {
      date: 'Datum',
    },
  },

  // order confirmation
  orderTitle: 'Auftrag',
  deleteOrderSuccessMessage: 'Der Auftrag wurde erfolgreich gelöscht',
  deleteOrderErrorMessage: 'Beim Löschen des Auftrags ist ein Fehler aufgetreten',
  deleteOrderErrorMessageStateNotAllowed: 'Aufträge mit zugehörigen Lieferscheinen/ Rechnungen können nicht gelöscht werden.',
  deleteOrderErrorMessageSourceNotAllowed: 'Aufträge aus Webshopbestellungen können nicht gelöscht werden',
  orderLockedEditErrorMessage: 'Der Auftrag wurde bereits weiterverarbeitet und kann nicht mehr bearbeitet werden.',
  orderCopyErrorMessage: 'Beim Kopieren des Auftrags ist ein Fehler aufgetreten.',
  orderDeleteConfirmCaption: 'Auftrag löschen',
  orderDeleteConfirmText: 'Möchtest Du den Auftrag wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  orderEmailHeadline: 'Auftrag versenden',
  orderEmailSubheadline: 'Versende Deinen Auftrag bequem per E-Mail',
  order: {
    sum: 'Auftragssumme',
    outstandingSum: 'Restbetrag',
    orders: 'Aufträge',
    orderDetails: 'Auftrag-Details',
    createOrder: 'Auftrag erstellen',
    editOrder: 'Auftrag bearbeiten',
    sendOrder: 'Auftrag versenden',
    createInvoice: 'Rechnung erstellen',
    createPartialInvoice: 'Teilrechnung erstellen',
    createDepositInvoice: 'Abschlagsrechnung erstellen',
    directPaymentMethodsActiveHint: 'Um eine Abschlags- oder Teilrechnung zu erstellen, müssen die direkten Zahlungsmethoden zuvor deaktiviert werden.',
    createDeliveryNote: 'Lieferschein erstellen',
    createInvoiceAndDeliveryNote: 'Rechnung & Lieferschein erstellen',
    createCollectiveDeliveryNote: 'Teil-/Sammellieferschein erstellen',
    createInvoices: 'Rechnung erstellen',
    createInvoicesAndDeliveryNotes: 'Rechnung & Lieferschein erstellen',
    upgradeText: `${unlockViewUpgradeText} Aufträge freizuschalten.`,
    list: {
      listEmptyStateTitle: 'Keine Aufträge vorhanden',
      listNoSearchResultTitle: 'Keine Aufträge gefunden',
      listEmptyStateSubtitle: 'Jetzt den ersten Auftrag erfassen',
      listEmptyStateButtonLabel: 'Los geht\'s',
      listGenerateInvoiceToast: 'Rechnungen werden erstellt',
      listGenerateDeliveryNoteToast: 'Lieferscheine werden erstellt',
      listGenerateInvoiceDeliveryNoteToast: 'Lieferscheine und Rechnungen werden erstellt',
      listGenerateOrderToast: 'Deine Bestellungen werden abgerufen',
    },
    detail: {
      date: 'Auftragssdatum',
      amount: 'Betrag',
    },
    reportLoadingMessages: {
      renderDeliveryNote: 'Erstelle Lieferschein',
      renderDeliveryNoteAndInvoice: 'Erstelle Rechnung & Lieferschein',
      renderInvoice: 'Erstelle Rechnung',
      renderPartialInvoice: 'Erstelle Teilrechnung',
    },
    states: {
      open: 'Offen',
      partiallyCharged: 'Teilw. verrechnet',
      partiallyDelivered: 'Teilw. geliefert',
      invoiced: 'Verrechnet',
      delivered: 'Geliefert',
    },
    success: {
      toDeliveryNoteAndInvoice: 'Lieferschein und Rechnung erfolgreich erstellt.',
    },
    errors: {
      toDeliveryNoteAndInvoice: (deliveryNoteSuccess, invoiceSuccess) => {
        if (deliveryNoteSuccess && !invoiceSuccess) {
          return 'Der Lieferschein wurde erfolgreich erstellt. Beim Erstellen der Rechnung ist ein Fehler aufgetreten.'
        } if (!deliveryNoteSuccess && invoiceSuccess) {
          return 'Die Rechnung wurde erfolgreich erstellt. Beim Erstellen des Lieferscheins ist ein Fehler aufgetreten.'
        }

        return 'Beim Erstellen des Lieferscheins und der Rechnung ist ein Fehler aufgetreten. Bitte versuche es erneut.'
      },
      toDeliveryNote: {
        notAllowed: 'Zu diesem Auftrag existiert bereits ein Lieferschein.',
        default: 'Beim Erstellen des Lieferscheins ist ein Fehler aufgetreten.',
      },
      toInvoice: {
        notAllowed: 'Zu diesem Auftrag existiert bereits eine Rechnung.',
        default: 'Beim Erstellen der Rechnung ist ein Fehler aufgetreten.',
      },
    },
  },

  // purchase
  purchaseTitle: 'Bestellung',
  deletePurchaseSuccessMessage: 'Die Bestellung wurde erfolgreich gelöscht.',
  deletePurchaseErrorMessage: 'Beim Löschen der Bestellung ist ein Fehler aufgetreten.',
  purchaseCopyErrorMessage: 'Beim Kopieren der Bestellung ist ein Fehler aufgetreten.',
  purchaseDeleteConfirmCaption: 'Bestellung löschen',
  purchaseDeleteConfirmText: 'Möchtest Du die Bestellung wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  purchaseEmailHeadline: 'Bestellung versenden',
  purchaseEmailSubheadline: 'Versende Deine Bestellung bequem per E-Mail',
  purchaseMarkAsOrderedSuccessMessage: 'Die Bestellung wurde erfolgreich als bestellt / nicht geliefert markiert.',
  purchaseMarkAsOrderedErrorMessage: 'Beim Markieren der Bestellung als bestellt / nicht geliefert ist ein Fehler aufgetreten.',
  purchaseMarkAsDeliveredSuccessMessage: 'Die Bestellung wurde erfolgreich als geliefert markiert.',
  purchaseMarkAsDeliveredErrorMessage: 'Die Bestellung konnte nicht als geliefert markiert werden.',
  purchaseOpenSuccessMessage: 'Die Bestellung wurde erfolgreich als offen markiert.',
  purchaseOpenErrorMessage: 'Die Bestellung konnte nicht als offen markiert werden.',
  purchase: {
    purchases: 'Bestellungen',
    createPurchase: 'Bestellung erstellen',
    editPurchase: 'Bestellung bearbeiten',
    sendPurchase: 'Bestellung versenden',
    markAsDelivered: 'Als geliefert markieren',
    markAsPartiallyDelivered: 'Als teilweise geliefert markieren',
    markAsOpen: 'Als offen markieren',
    markAsOrdered: 'Als bestellt / nicht geliefert markieren',
    deleteTooltip: 'Eine gelieferte Bestellung kann nicht gelöscht werden. Markiere die Bestellung als bestellt / nicht geliefert, um sie löschen zu können.',
    editTooltip: 'Eine gelieferte Bestellung kann nicht bearbeitet werden. Markiere die Bestellung als bestellt / nicht geliefert, um sie bearbeiten zu können.',
    activity: {
      open: 'Als offen markiert',
      ordered: 'Als bestellt / nicht geliefert markiert',
      delivered: 'Als geliefert markiert',
      partiallyDelivered: 'Als teilweise geliefert markiert',
    },
    editState: {
      [purchaseStates.OPEN]: {
        successMessage: 'Die Bestellung wurde erfolgreich als offen markiert.',
        errorMessage: 'Die Bestellung konnte nicht als offen markiert werden.',
      },
      [purchaseStates.ORDERED]: {
        successMessage: 'Die Bestellung wurde erfolgreich als bestellt / nicht geliefert markiert.',
        errorMessage: 'Die Bestellung konnte nicht als bestellt / nicht geliefert markiert werden.',
      },
      [purchaseStates.DELIVERED]: {
        successMessage: 'Die Bestellung wurde erfolgreich als geliefert markiert.',
        errorMessage: 'Die Bestellung konnte nicht als geliefert markiert werden.',
      },
      [purchaseStates.PARTIALLY_DELIVERED]: {
        successMessage: 'Die Bestellung wurde als teilweise geliefert markiert.',
        errorMessage: 'Die Bestellung konnte nicht als teilweise geliefert markiert werden.',
      },
    },
    editStateWithInventory: {
      [purchaseStates.DELIVERED]: {
        successMessage: 'Deine Bestellung wurde als geliefert markiert. Die enthaltenen lagerbestandsgeführten Artikel wurden dem Bestand hinzugefügt.',
      },
      [purchaseStates.ORDERED]: {
        successMessage: 'Deine Bestellung wurde als bestellt / nicht geliefert markiert. Die enthaltenen lagerbestandsgeführten Artikel wurden aus dem Bestand entfernt.',
      },
    },
    partialDelivery: {
      title: 'Teilweise geliefert markieren',
      deliveryDate: 'Lieferdatum',
      articleLabel: 'Artikelbez.',
      receivedNow: 'eingegangen',
      delivered: 'geliefert',
      orderedAmount: 'Menge',
      plannedDeliveryDate: 'geplanter Liefertermin',
      note: 'Anmerkung',
      defaultNote: 'Wareneingang',
      saveSuccess: 'Teillieferung wurde gespeichert. Lagerbestände wurden aktualisiert.',
      saveError: 'Teillieferung konnte nicht gespeichert werden.',
      noPositions: 'Keine artikelbezogenen Positionen vorhanden.',
      searchPlaceholder: 'Bestellung durchsuchen',
      missingArticleWarning: 'Dieser Artikel ist nicht im Artikelstamm enthalten. Die Position kann als geliefert markiert werden, ein Lagereingang wird jedoch nicht dokumentiert.',
    },
    upgradeText: `${unlockViewUpgradeText} Bestellungen freizuschalten.`,
    list: {
      listEmptyStateTitle: 'Keine Bestellungen vorhanden',
      listNoSearchResultTitle: 'Keine Bestellungen gefunden',
      listEmptyStateSubtitle: 'Jetzt die erste Bestellung erfassen',
      listEmptyStateButtonLabel: 'Los geht\'s',
    },
    detail: {
      date: 'Bestelldatum',
      amount: 'Betrag',
      title: 'Bestellung-Details',
    },
    states: {
      open: {
        label: 'Offen',
      },
      ordered: {
        label: 'Bestellt / nicht geliefert',
      },
      delivered: {
        label: 'Geliefert',
      },
      partiallyDelivered: {
        label: 'Teilweise geliefert',
      },
    },
    deliveryDateSelectioNModal: {
      title: 'Gib das Datum der Lieferung ein',
    },
  },

  // offer
  offerTitle: 'Angebot',
  offerToInvoiceSuccessMessage: 'Das Angebot wurde erfolgreich in eine Rechnung umgewandelt',
  offerDeleteConfirmCaption: 'Angebot %s löschen',
  offerDeleteConfirmText: 'Möchtest Du das Angebot wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  offerDeleteSuccessMessage: 'Das Angebot %s wurde erfolgreich gelöscht',
  offerDeleteSuccessMessagePlain: 'Das Angebot wurde erfolgreich gelöscht',
  offerSaveErrorMessage: 'Beim Speichern des Angebots ist ein unerwarteter Fehler aufgetreten.',
  offerSaveSuccessMessage: 'Das Angebot wurde erfolgreich gespeichert.',
  offerEditLockedMessage: 'Das Angebot wurde bereits abgerechnet und kann nicht mehr bearbeitet werden.',
  offerDeleteLockedMessage: 'Das Angebot wurde bereits abgerechnet und kann nicht mehr gelöscht werden.',
  offerSearchCategory: 'Angebote',
  offerColumnEditSaveInSettingsLabel: 'als Standard für Angebote speichern',
  offerEmailHeadline: 'Zeit für einen neuen Auftrag',
  offerEmailSubheadline: 'Versende Dein Angebot bequem per E-Mail',
  offerCopySuccessMessage: 'Das Angebot wurde erfolgreich kopiert.',
  offerCopyErrorMessage: 'Beim Kopieren des Angebots ist ein Fehler aufgetreten.',
  noOffersLeftErrorMessage: 'Die Anzahl der maximalen Angebote für diesen Monat ist erreicht.',
  offer: {
    offerDetails: 'Angebots-Details',
    createOffer: 'Angebot erstellen',
    renderOffer: 'Erstelle Angebot',
    editOffer: 'Angebot bearbeiten',
    sendOffer: 'Angebot versenden',
    list: {
      listEmptyStateTitle: 'Keine Angebote vorhanden',
      listNoSearchResultTitle: 'Keine Angebote gefunden',
      listEmptyStateSubtitle: 'Jetzt das erste Angebot erstellen',
      listEmptyStateButtonLabel: 'Los geht\'s',
    },
    detail: {
      date: 'Angebotsdatum',
      amount: 'Betrag',
    },
    states: {
      open: {
        label: 'Offen',
        color: 'blue',
      },
      accepted: {
        label: 'Angenommen',
        color: 'green',
      },
      rejected: {
        label: 'Abgelehnt',
        color: 'red',
      },
      invoiced: {
        label: 'Rechnung erstellt',
        color: 'lightBlue',
      },
      projectCreated: {
        label: 'Abschlagsrechnung angelegt',
        color: 'lightBlue',
      },
      orderCreated: {
        label: 'Auftrag erzeugt',
        color: 'lightBlue',
      },
      selected: {
        label: 'Positionen ausgewählt',
        color: 'blue',
      },
    },
    actions: {
      convertToInvoice: 'Rechnung erstellen',
      createOrder: 'Auftrag erstellen',
      accepted: 'Angenommen',
      acceptAndCreateOrder: 'Annehmen & Auftrag erstellen',
      copyAndEdit: 'Kopieren und Bearbeiten',
      delete: 'Löschen',
      reject: 'Abgelehnt',
      setToOpen: 'Auf offen setzen',
    },
    tooltipEditHint: 'Änderungen sind nur im Status "Offen" möglich.',
  },

  // expense
  expenses: 'Ausgaben',
  expenseTitle: 'Ausgabe',
  expenseDeleteSuccessMessage: multiple => `Die Ausgabe${multiple ? 'n' : ''} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  expensePartialDeleteErrorMessage: (deleted, failed) => `${deleted} Ausgabe${deleted > 1 ? 'n' : ''} wurde${deleted > 1 ? 'n' : ''} gelöscht. ${failed} Ausgabe${failed > 1 ? 'n' : ''} konnte${failed > 1 ? 'n' : ''} nicht gelöscht werden.`,
  expenseAndEconomicAssetDeleteConfirmCaption: multiple => (multiple
    ? 'Ausgaben können über diesen Weg nicht gelöscht werden.'
    : 'Ausgabe kann über diesen Weg nicht gelöscht werden.'),
  expenseDeleteConfirmCaption: multiple => `Ausgabe${multiple ? 'n' : ''} löschen`,
  expenseEconomicAssetButton: 'Zur Anlagenverwaltung',
  expenseDeleteConfirmText: multiple => `Möchtest Du die ${multiple ? 'ausgewählten Ausgaben' : 'Ausgabe'} wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  // FLAG:useDocuAi — was beim Löschen SONST noch mitgeht. Der Dialog nannte bisher nur die
  // Ausgabe, obwohl er immer schon die finance:API-Verbuchung mit entfernt hat.
  expenseDeleteConfirmReceiptDeleted: 'Der zugehörige Beleg wird mit gelöscht.',
  expenseDeleteConfirmReceiptKept: 'Der zugehörige Beleg bleibt unter „Dokumente“ erhalten.',
  receiptArchive: {
    // FLAG:useDocuAi — the two halves of the checkbox-dependent consequence, rendered in a fixed
    // slot UNDER the checkbox (see the two delete-confirmation-modals). They are deliberately one
    // short sentence each: the paragraph above the checkbox must stay identical while toggling, so
    // everything that CHANGES with the checkbox lives here and nowhere else.
    someOriginalsRetained: 'Einige Dokumente zu den bereits gelöschten Einträgen konnten nicht mitgelöscht werden und bleiben erhalten.',
    deleteOriginalsLabel: 'Hochgeladene Belege ebenfalls löschen und nicht unter „Dokumente“ behalten',
    documentsRetainedAfterDelete: 'Die Ausgabe/Einnahme und ihre Buchung wurden gelöscht. Mindestens ein Dokument konnte nicht mitgelöscht werden oder wird noch verwendet. Erhaltene Dokumente bleiben unter „Dokumente“ in ihrem Ordner erreichbar.',
    title: 'Entfernte Belege',
    notBooked: 'Die Prüfdaten wurden gespeichert, aber es wurde keine Buchung erstellt. Bitte prüfe den Belegstatus und die Buchungsvoraussetzungen.',
    deleteExpense: 'Die Ausgabe und ihre vorhandene Buchung werden gelöscht. Gemeinsam verwendete Belege bleiben bei ihren anderen Verknüpfungen.',
    deleteExpenseWithoutReceipt: 'Die Ausgabe und ihre vorhandene Buchung werden gelöscht. Es ist kein Beleg verknüpft.',
    deleteExpenseWithoutDocuments: 'Die Ausgabe, ihre vorhandene Buchung und ihre nicht anderweitig verwendeten Belege werden gelöscht.',
    deleteExpenses: 'Die ausgewählten Ausgaben und ihre vorhandenen Buchungen werden gelöscht. Gemeinsam verwendete Belege bleiben bei ihren anderen Verknüpfungen.',
    deleteExpensesWithoutReceipts: 'Die ausgewählten Ausgaben und ihre vorhandenen Buchungen werden gelöscht. Es sind keine Belege verknüpft.',
    deleteExpensesWithoutDocuments: 'Die ausgewählten Ausgaben, ihre vorhandenen Buchungen und ihre nicht anderweitig verwendeten Belege werden gelöscht.',
    deleteRevenue: 'Die sonstige Einnahme und ihre vorhandene Buchung werden gelöscht. Gemeinsam verwendete Belege bleiben bei ihren anderen Verknüpfungen.',
    deleteRevenueWithoutReceipt: 'Die sonstige Einnahme und ihre vorhandene Buchung werden gelöscht. Es ist kein Beleg verknüpft.',
    deleteRevenueWithoutDocuments: 'Die sonstige Einnahme, ihre vorhandene Buchung und ihre nicht anderweitig verwendeten Belege werden gelöscht.',
    deleteRevenues: 'Die ausgewählten sonstigen Einnahmen und ihre vorhandenen Buchungen werden gelöscht. Gemeinsam verwendete Belege bleiben bei ihren anderen Verknüpfungen.',
    deleteRevenuesWithoutReceipts: 'Die ausgewählten sonstigen Einnahmen und ihre vorhandenen Buchungen werden gelöscht. Es sind keine Belege verknüpft.',
    deleteRevenuesWithoutDocuments: 'Die ausgewählten sonstigen Einnahmen, ihre vorhandenen Buchungen und ihre nicht anderweitig verwendeten Belege werden gelöscht.',
  },
  expenseDeleteConfirmBookingRemoved: 'Die Verbuchung zu dieser Ausgabe wird ebenfalls entfernt.',
  expenseAndEconomicAssetDeleteConfirmText: multiple => `${multiple ? 'Mindestens eine der ausgewählten Ausgaben ist' : 'Diese Ausgabe ist'} mit einem Anlagegut verbunden, daher muss die Löschung über das zugehörige Anlagegut veranlasst werden. Bitte gehe hierfür in den Bereich <b>${isOrgaMax ? 'Finanzen' : 'Buchhaltung'} → Anlagenverwaltung</b> oder wähle <b>„Zur Anlagenverwaltung“</b> aus. Markiere dann das betreffende Anlagegut in der Liste und klicke auf <b>„Löschen“</b>. <br/><br/> Weitere Informationen findest Du in der <a target="_blank" href=${mainInfoUrl}>FAQ.</a> `,
  expenseSaveSuccessMessage: 'Die Ausgabe wurde erfolgreich gespeichert.',
  expenseSaveErrorMessage: 'Beim Speichern der Ausgabe ist ein Fehler aufgetreten.',
  expenseEmptySupplierOrCustomer: 'Bitte gib einen Lieferanten oder Kunden an.',
  expenseEmptyReceiptNumber: 'Bitte gib eine Beleg-Rechnungsnummer an.',
  expenseEmptyDate: 'Bitte gib ein Belegdatum an.',
  expenseDisabledPriceTotalHint: 'Bitte entferne die Zahlung, um den Betrag zu ändern.',
  expensePaymentEmptyPriceTotalErrorMessage: 'Bitte gib einen Betrag an, der größer als 0,00 € ist.',
  expensePaymentEmptyReceiptNumber: 'Bitte gib eine Beleg-Rechnungsnummer an.',
  expensePaymentEmptyPayee: 'Bitte wähle einen Lieferanten/Kunden aus.',
  expensePaymentDrawerTitle: 'Jetzt Ausgabe bezahlen',
  expenseInvalidPositionsError: 'Deine Gesamtsumme stimmt nicht mit dem hinterlegten Betrag überein. Bitte überprüfe Deine Eingabe.',
  expenseReceiptNumberInvalidCharacterErrorMessage: 'Die eingegebene Beleg-Rechnungsnr. enthält ungültige Zeichen.',
  expenseReceiptUsageInvalidCharacterErrorMessage: 'Der eingegebene Verwendungszweck enthält ungültige Zeichen.',
  expenseOcrWrongPriceFormat: 'Der eingegebene Wert ist keine valider Preis.',
  expenseOcrWrongDateFormat: 'Der eingegebene Wert hat kein valides Datumformat.',
  expenseEmptyOcrValue: 'Bitte gib einen Wert ein.',
  expenseCopyErrorMessage: 'Beim Kopieren der Ausgabe ist ein Fehler aufgetreten.',
  // expense file upload messages
  expenseFileTypeError: 'Die Datei \'%s\' hat keinen zulässigen Dateityp.',
  expenseFileMinSizeError: 'Die Datei \'%s\' muss mindestens 2,5 kB groß sein!',
  expenseFileMaxSizeError: 'Die Datei \'%s\' darf maximal 25 MB groß sein!',
  expenseFileSuccessMessage: 'Die Datei wurde erfolgreich hochgeladen',
  expenseSaveSupplierNotFoundError: 'Der ausgewählte Zahlungsempfänger wurde nicht gefunden',
  expenseSaveCashBalanceNegativeError: 'Der Betrag ist höher als der aktuelle Kassenbuchbestand. Das Kassenbuch darf nicht negativ sein.',
  expenseSavePaymentDatePastInitialBalanceDateError: 'Das Zahlungsdatum darf nicht vor dem initialen Bestandsdatum liegen.',
  expenseSavePaymentExpenseAmountExceeded: 'Der zugeordnete Betrag überschreitet den Ausgabenbetrag',
  expenseSaveReceiptNumberMaxLengthExceeded: 'Beleg-Rechnungsnr. darf maximal 36 Zeichen lang sein.',
  expenseEditTitle: 'Ausgabe bearbeiten',
  expenseConfirmTitle: 'Ausgabe prüfen',
  expenseNewTitle: 'Neue Ausgabe erstellen',
  expense: {
    uploader: {
      textTitle: 'Es sind aktuell keine Dokumente vorhanden',
    },
    previewToolbar: {
      items: {
        zoomIn: 'Schrittweise vergrößern',
        zoomOut: 'Schrittweise verkleinern',
        rotate90: 'Um 90 Grad drehen',
        reset: 'Zurücksetzen',
        ocrStart: 'Manuelle Texterkennung starten',
        analyzeStart: 'Dokument analysieren',
        delete: 'Dokument entfernen',
        unlink: 'Dokument entfernen',
        download: 'Dokument herunterladen',
      },
    },
    receipt: {
      remove: 'Beleg löschen',
      upload: 'Beleg hochladen',
      uploading: 'Beleg wird hochgeladen...',
      openDocumentContainer: 'Dokumentencontainer öffnen',
      deleteTempFileError: 'Beim Löschen des Belegs ist ein Fehler aufgetreten.',
    },
    edit: {
      transferForm: {
        fromAccount: 'Vom Konto*',
        recipient: 'Zahlungsempfänger*',
        iban: 'IBAN*',
        bic: 'BIC*',
        usage: 'Verwendungszweck*',
        executionDate: 'Ausführung',
        executionDirect: 'Nächstmöglich',
        amount: 'Betrag*',
      },
    },
    navigationConfirmation: {
      title: 'Dokument von Ausgabe entfernen',
      description: 'Willst Du das Dokument von der Ausgabe entfernen?',
      confirmButtonLabel: 'Ja',
      abortButtonLabel: 'Nein',
    },
  },

  // expense-revenue

  expenseAndRevenue: {
    drawer: {
      payment: 'Zahlung',
      receiptNumber: 'BELEG-RECHN.-NR.',
      receiptDate: 'BELEGDATUM',
      deliveryDate: 'LIEFERDATUM',
      revenueDetails: 'Sonstige Einnahmen',
      expenseDetails: 'Ausgaben',
      positions: 'Kategorisierung',
      withoutCategoryLabel: 'Ohne Kategorie',
      description: 'Beschreibung',
      // Die IBAN aus den STAMMDATEN des Kontakts, nicht die auf dem Beleg erkannte — in der
      // Prüfmaske heißt das andere Feld „IBAN des Verkäufers" und liest documentInvoiceDetails.iban.
      // Ohne den Zusatz wurden die beiden verwechselt und der Unterschied als Fehler gemeldet.
      iban: 'IBAN (Lieferant/Kunde):',
      tags: 'Tags',
      noIban: 'nicht hinterlegt',
      descriptionDefaultText: 'keine Beschreibung vorhanden',
    },
  },

  // dunning
  dunningModalContextText: 'Möchtest Du zu der ausgewählten Rechnung eine Mahnung erstellen?',
  dunningCreateSuccessMessage: 'Die Zahlungserinnerung/Mahnung wurde erfolgreich erstellt.',
  dunningCreateErrorMessage: 'Beim Erstellen der Mahnung ist ein Fehler aufgetreten',
  dunnningEmailHeadline: 'Zeit, Dein Geld einzufordern',
  dunnningEmailSubheadline: 'Versende Deine Mahnung bequem per E-Mail',
  dunningLastActiveDunningLevelReachedMessage: 'Die finale Mahnstufe wurde erreicht. Über die Rechnungsdetails kann die Mahnung erneut versandt werden.',
  dunningRecipientEmptyRecipientsErrorMessage: 'Bitte gib mindestens einen Empfänger für den automatischen Mahnungsversand an',

  // time tracking
  timeTracking: {
    fromDateTime_toDateTime: 'Von - Bis',
    'h:mm': 'Std. / Min.',
    fromToOptionLabel: 'Von - Bis',
    hourMinuteOptionLabel: 'Std. / Min.',
    VALIDATION_DATETIME_FORMAT: 'Kein valides Zeitformat. Beispiel: "13:15" -> 13:15 Uhr',
    VALIDATION_DATETIME_COMPARISON: 'Der Zeitpunkt "Bis" darf nicht vor dem Zeitpunkt "Von" sein.',
    VALIDATION_DATETIME_COMPARISON_IS_SAME: 'Der Zeitpunkt "Bis" und "Von" darf nicht gleich sein.',
    VALIDATION_TIMESTRING_FORMAT: 'Kein valides Zeitformat. Beispiel: "13:15" -> 13 Stunden und 15 Minuten',
    VALIDATION_CUSTOMER_EMPTY: 'Bitte wähle einen Kunden aus.',
    VALIDATION_DURATIONINMINUTES_ZERO: 'Bitte gib eine Zeit an.',
    VALIDATION_TIMETYPE_EMPTY: 'Bitte wähle ein Zeitformat. "Von - Bis" oder "Std. / Min."',
    title: 'Erfasste Zeiten abrechnen',
    subtitle: 'Offene Aufwände',
    startDateLabel: 'Von',
    endDateLabel: 'Bis',
    timeTypeLabel: 'Art',
    dateLabel: 'Datum wählen',
    hourlyRateLabel: 'Stundensatz (netto)',
    hourlyRatePlaceholder: 'Stundensatz eingeben oder wählen',
    hourlyRateLoadingMessage: 'Stundensätze werden geladen ...',
    taskLabel: 'Tätigkeit',
    taskDescriptionLabel: 'Tätigkeitsbeschreibung',
    totalAmount: 'Gesamtbetrag',
    newTimeTrackingTitle: 'Neue Zeit erfassen',
  },
  timeTrackingTitle: 'Zeiterfassung',
  timeTrackingDeleteConfirmCaption: 'Erfasste Zeit löschen',
  timeTrackingDeleteConfirmText: 'Möchtest Du die erfasste Zeit wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  timeTrackingDeleteSuccessMessage: 'Die erfasste Zeit wurde erfolgreich gelöscht',
  timeTrackingSaveSuccessMessage: 'Die Zeit wurde erfolgreich erfasst!',
  timeTrackingSaveErrorMessage: 'Beim Speichern der Zeit ist ein Fehler aufgetreten.',
  noTimeToBillSelectedMessage: 'Bitte wähle eine Zeit zum Abrechnen aus.',
  timeTrackingLoadingMessage: 'Lade Zeiterfassung',
  timeTrackingLoadTrackedTime: 'Lade erfasste Zeit',
  timeTrackingSaving: 'Wird gespeichert',

  // transaction (offer & invoice, etc.)
  transactionNoTitleErrorMessage: 'Bitte gib einen Titel an.',
  transactionColumnEditAmountNotCheckedHint: 'Wenn Du diese Spalte ausblendest, muss eine Mengenangabe zumindest im Beschreibungstext stehen.',
  transactionColumnEditUsedDiscountHint: 'Du kannst diese Spalte nicht ausblenden, da in dieser Rechnung mindestens ein Rabatt vergeben wurde.',
  transactionColumnEditDifferentVatHint: 'Du kannst diese Spalte nicht ausblenden, da in dieser Rechnung unterschiedliche Umsatzsteuersätze verwendet werden.',
  transactionSendEmailSuccessMessage: 'Die E-Mail wurde erfolgreich versendet.',
  transactionCustomFieldInfo: 'Es muss immer eine Bezeichnung zu einem Wert bestehen, damit das Feld angezeigt wird.',
  transactionErrors: {
    transactionTypeInvoice: 'der Rechnung',
    transactionTypeDepositInvoice: 'der Abschlagsrechnung',
    transactionTypeOffer: 'dem Angebot',
    transactionTypeDeliveryNote: 'dem Lieferschein',
    transactionTypeOrder: 'dem Auftrag',
    transactionTypeRecurringInvoice: 'der Abo-Rechnung',
    transactionTypeManualCreditNote: 'der Gutschrift/Rechnungskorrektur',
    transactionTypeLetter: 'dem Geschäftsbrief',
    transactionTypePurchase: 'der Bestellung',
    transactionTypePartialInvoice: 'der Teilrechnung',
  },

  // letter
  letterHeaderSaveSuccessMessage: 'Der Briefpapier-Kopf wurde erfolgreich gespeichert',
  letterHeaderSaveErrorMessage: 'Beim Speichern des Briefpapier-Kopfes ist ein Fehler aufgetreten',
  letterSenderSaveSuccessMessage: 'Die Absenderzeile wurde erfolgreich gespeichert.',
  letterSenderSaveErrorMessage: 'Beim Speichern der Absenderzeile ist ein Fehler aufgetreten.',
  letterFooterSaveSuccessMessage: 'Der Briefpapier-Fuß wurde erfolgreich gespeichert.',
  letterFooterSaveErrorMessage: 'Beim Speichern des Briefpapier-Fußes ist ein Fehler aufgetreten.',

  // template selection
  templateSelectionSaveSuccessMessage: 'Das Template wurde erfolgreich gespeichert.',
  templateSelectionSaveErrorMessage: 'Beim Speichern des Templates ist ein Fehler aufgetreten.',
  templateSelectionApplyErrorMessage: 'Beim Anzeigen des Templates ist ein Fehler aufgetreten.',
  templateSelectionLoadErrorMessage: 'Beim Laden der Designs ist ein Fehler aufgetreten.<br/>Bitte versuche es erneut',
  templateSelectionConfirmSaveMessage: 'Beim Speichern des neuen Templates gehen Deine vorhandenen Einstellungen verloren.',

  // empty list views
  emptyInvoiceListHeading: 'Noch keine Rechnungen vorhanden',
  emptyOfferListHeading: 'Noch keine Angebote vorhanden',
  emptyExpenseListHeading: 'Noch keine Ausgaben vorhanden',
  emptyArticleListHeading: 'Noch keine Artikel vorhanden',
  emptyCustomerListHeading: 'Noch keine Kunden vorhanden',
  emptyTimeTrackingListHeading: 'Noch keine Zeiten erfasst',
  emptyInvoiceListMessage: 'Jetzt die erste Rechnung erstellen',
  emptyOfferListMessage: 'Jetzt das erste Angebot erstellen',
  emptyExpenseListMessage: 'Jetzt die erste Ausgabe erfassen',
  emptyArticleListMessage: 'Jetzt den ersten Artikel anlegen',
  emptyCustomerListMessage: 'Jetzt den ersten Kunden anlegen',
  emptyTimeTrackingListMessage: 'Jetzt die ersten Zeiten erfassen',
  emptyListButtonText: 'Los geht\'s',

  // payment
  paymentSaveErrorMessage: 'Beim Speichern der Zahlung ist ein Fehler aufgetreten.',
  paymentSaveSuccessMessage: 'Die Zahlung wurde erfolgreich gespeichert.',
  paymentDeviationError: 'Bitte wähle wie der Zuschlag verbucht werden soll.',
  paymentTimeoutMessage: 'Deine Sitzung ist abgelaufen.\nBitte logge Dich ein, um die Zahlung zu erfassen.',
  paymentNoCashJournalBalanceDefinedMessage: 'Bitte lege zuerst den Anfangsbestand in Deinem Kassenbuch fest.',
  paymentModal: {
    titleInvoice: 'Zahlung erfassen',
    titleCreditNote: 'Zahlung erstatten',
    addAssignment: '"Bankbuchung zuordnen"',
    addAccountNoteBegin: 'Hast Du Dein Online-Konto angebunden, kannst Du über die Funktion ',
    addAccountNote: (type) => ` oder im Bereich <a href="/banking">Bank/Kasse</a> die Umsätze Deinen ${['expense', 'revenue'].includes(type) ? 'Belegen' : 'Rechungen'} zuordnen.`,
    addAccountNoteToolTip: 'Du kannst in der Liste der Rechnungen die Option "Bankbuchung zuordnen" nutzen.',
    label: {
      customer: 'Kunde',
      date: 'Datum des Zahlungseingangs',
      creditNoteDate: 'Datum des Zahlungsausgangs',
      amount: 'Höhe der Zahlung',
      notes: 'Anmerkung',
      targetBankAccount: 'Zahlung erfassen auf (Konto)',
    },
    labelExpense: {
      customer: 'Kunde',
      date: 'Zahldatum',
      amount: 'Zahlbetrag ',
      notes: 'Anmerkung',
      targetBankAccount: 'Zahlbetrag erfassen (Konto)',
    },
    dunning: {
      hint: 'Diese Rechnung befindet sich in der Mahnstufe ',
      label: 'Mahngebühr',
    },
    toInvoiceTitle: 'zur Rechnungs-Nr.',
  },
  paymentDrawer: {
    labels: {
      bank: 'Überweisung',
      cashBook: 'Kassenbuch',
      clearing_account: 'Verrechnungskonto',
    },
  },
  paymentState: {
    paidThrough: (paidThrough, name) => `Bezahlen über ${paidThrough === 'cash' ? 'Kassenbuch' : `${name}`}:`,
    payDate: 'Zahlungsdatum*',
    unlink: {
      title: 'Zuordnung aufheben',
      confirm: {
        title: 'Achtung!',
        description: 'Bist Du sicher, dass Du die Zuweisung zur Buchung auflösen möchtest? Dies kann nicht rückgängig gemacht werden.',
      },
      error: 'Beim Aufheben der Zuordnung ist ein Fehler aufgetreten.',
    },
    delete: {
      tooltip: 'Zahlung entfernen',
    },
  },
  paymentLabelTransactionNumberInputInvoice: 'Rechnungsnummer',
  paymentLabelTransactionNumberInputCreditNote: 'Gutschriftsnummer',
  paymentDeviationView: {
    less: {
      coinsTitle: 'Als Teilzahlung behandeln',
      percentTitle: 'Als Skonto behandeln',
      bankTitle: 'Als Bankgebühr behandeln',
    },
    additional: {
      coinsTitle: 'Als Zuschlag behandeln',
      calendarTitle: 'Restbetrag später verbuchen',
    },
  },
  paymentNotesTextCreditNote: (number) => `Zahlung zur Gutschrift-Nr. ${number}`,
  paymentNotesTextManualCreditNote: (number) => `Zahlung zur Rechnungskorrektur/Gutschrift-Nr. ${number}`,
  paymentNotesTextInvoice: (number) => `Zahlung zur Rechnung-Nr. ${number}`,
  paymentNotesTextExpense: (number) => `Zahlung zu Beleg ${number || ''}`,
  paymentNotesTextRevenue: (number) => `Zahlung zu Beleg ${number || ''}`,

  onlineBankingModal: {
    linkNow: 'Jetzt Online-Bankkonto anbinden',
    description: 'Leider konnten wir kein eingerichtetes Bankkonto finden.',
    description2: 'Damit Du bequem Überweisungen ausführen kannst, richte bitte Dein<br>Online-Bankkonto ein.',
  },

  // search
  searchFoundIn: 'Gefunden in',

  // settings - account
  avvAcknlowedgeRequired: 'Bitte akzeptiere den Vertrag zur Auftragsverarbeitung (AVV) und unsere AGBs.',
  accountEmailAlreadyExistsMessage: 'Zu der eingegebenen E-Mail-Adresse existiert bereits ein Konto',
  accountEmailApproveSuccess: 'Dein Konto wurde erfolgreich aktiviert.',
  accountEmailApproveError: 'Bei der Aktivierung Deines Accounts ist ein Fehler aufgetreten. Bitte versuche es erneut.',
  accountDetailsSuccessMessage: 'Deine Accountdaten wurden erfolgreich aktualisiert.',
  accountDetailsErrorMessage: 'Deine Accountdaten konnten nicht aktualisiert werden!',
  accountTaxSuccessMessage: {
    default: 'Deine Steuereinstellungen wurden erfolgreich aktualisiert.',
    smallBusiness: {
      title: 'E-Rechnungen deaktiviert.',
      message: 'Als Kleinunternehmer bist Du nicht verpflichtet, E-Rechnungen zu erstellen. Bei Bedarf kann die Einstellung jederzeit wieder aktiviert werden.',
    },
  },
  accountCalculationSuccessMessage: 'Deine Umsatzsteuerberechnung wurde erfolgreich aktualisiert.',
  accountTaxErrorMessage: 'Deine Steuereinstellungen konnten nicht aktualisiert werden!',
  userDetailsSuccessMessage: 'Deine Nutzerdaten wurden erfolgreich aktualisiert.',
  userDetailsErrorMessage: 'Deine Nutzerdaten konnten nicht aktualisiert werden!',
  passwordChangeSuccessMessage: 'Dein Passwort wurde erfolgreich aktualisiert.',
  accountPasswordWrongPasswordMessage: 'Das alte Passwort ist nicht korrekt!',
  accountWrongPasswordMessage: 'Das von Dir eingegebene Passwort ist nicht korrekt!',
  accountPasswordErrorMessage: 'Dein Passwort konnte nicht aktualisiert werden!',
  accountSendEmailSuccessMessage: 'E-Mail - Absender erfolgreich aktualisiert.',
  accountSendEmailErrorMessage: 'E-Mail - Absender konnte nicht aktualisiert werden!',
  accountSignatureSuccessMessage: 'E-Mail Signatur erfolgreich gespeichert.',
  accountSignatureErrorMessage: 'Bitte überprüfe Deine Eingaben.',
  accountDeleteSuccessMessage: 'Dein Account wurde erfolgreich gelöscht.',
  accountDeleteErrorMessage: 'Fehler beim Löschen Deines Accounts, bitte kontaktiere uns!',
  accountDeleteRequestExistsMessage: 'Für Deinen Account wurde bereits eine Löschung beantragt.',
  accountEditEmailErrorMessage: 'E-Mail-Adresse wurde nicht geändert, da die neue E-Mail-Adresse der bisherigen entspricht',
  accountSettingsHeading: 'Account',
  accountEmailSuccessMessage: 'Prüfe bitte Deinen Posteingang zur Bestätigung der neuen E-Mail-Adresse!',
  accountEmailIsConfirmed: 'Deine E-Mail ist bereits bestätigt.',
  accountEmailConnectedToDesktop: `E-Mail-Adresse konnte nicht geändert werden. Einer Deiner Mandanten hat eine bestehende Verbindung zu ${productName} Desktop.`,
  accountEmailErrorMessage: 'Bei der Änderung Deiner E-Mail-Adresse ist ein Fehler aufgetreten.',
  accountEmailChangeSuccessMessage: 'Deine E-Mail-Adresse wurde erfolgreich geändert.',
  accountEmailApproveWrong: 'Die eingegebene E-Mail-Adresse stimmt nicht überein.',
  changeEmailSameAsCurrentError: 'Entspricht Deiner aktuellen E-Mail-Adresse',
  changeEmailHint: (email) => {
    if (email) return `Du bist mit der E-Mail-Adresse <b>${email}</b> angemeldet. Bevor Du Dich mit Deiner neuen E-Mail-Adresse anmelden kannst, musst Du diese bestätigen. Hierfür schickt ${productNameLong} Dir eine E-Mail mit einem Bestätigungs-Link.`
    return `Hinweis: Bevor Du Dich mit Deiner neuen E-Mail-Adresse anmelden kannst, musst Du diese bestätigen. Hierfür schickt ${productNameLong} Dir eine E-Mail mit einem Bestätigungs-Link.`
  },
  changeEmailNotConfirmedMessage: 'Bitte bestätige Deine Registrierung. Prüfe dazu Dein Postfach der aktuellen E-Mail-Adresse.',

  approvalHintModal: {
    title: 'Keine E-Mail erhalten?',
    listStarter: 'Überprüfe...',
    list: {
      emailCorrectness: '... die Richtigkeit Deiner E-Mail-Adresse:',
      emailModify: 'E-Mail-Adresse korrigieren',
      spam: '... Deinen SPAM-Ordner',
      iconNumbers: { first: '1', second: '2' },
    },
    point1: 'Teilweise kann es einige Minuten dauern, bis die E-Mail bei Dir ankommt.',
    point2: 'Solltest Du weiterhin Probleme haben, melde Dich bitte bei uns.',
  },

  // subscription
  subscriptionCancelSuccessMessage: 'Du hast Deinen Vertrag erfolgreich gekündigt.',
  subscriptionCancelWarningMessageIsMobile: 'Du hast Dein Abo auf einem mobilen Gerät abgeschlossen. Bitte verwalte Dein Abo auf dem entsprechenden mobilen Gerät und kündige zusätzlich das Abo im App Store oder Play Store.',
  subscriptionCancelErrorMessage: 'Beim Kündigen Deines Vertrages ist ein Fehler aufgetreten.',
  subscriptionCancelErrorMessageOwner: 'Du hast nicht die Berechtigung, diesen Vertrag zu kündigen. Nur der Kontoinhaber hat diese Berechtigung.',
  subscriptionCancelReasonDescription: `Hilf uns ${productNameLong} noch besser zu machen und teile uns mit, warum Du Deinen Vertrag gekündigt hast.`,

  // subscription - selection
  subscriptionSelectionTitle: 'Plan wählen & durchstarten',
  subscriptionSelectionTitleByProduct: {
    document: `${!isOrgaMax ? 'WISO ' : ''}${productName} Dokumente`,
    main: `${!isOrgaMax ? 'WISO ' : ''}${productName} ${mainProductNaming}`,
    staff: `${!isOrgaMax ? 'WISO ' : ''}${productName} Personal`,
  },
  subscriptionSelectionsubTitle: '',
  subscriptionSelectionHintMonth: '*Preis gilt monatlich. Alle oben genannten Preise verstehen sich zzgl. der gesetzlichen Umsatzsteuer. Die Mindestvertragslaufzeit beträgt 12 Monate.',
  subscriptionSelectionHintYear: '*Preis gilt monatlich bei jährlicher Abrechnung. Alle oben genannten Preise verstehen sich zzgl. der gesetzlichen Umsatzsteuer. Die Mindestvertragslaufzeit beträgt 12 Monate.',

  subscriptionTrial: {
    title: 'Testversion',
    description: `Jetzt upgraden um ${productNameLong} unbegrenzt zu benutzen.`,
    expiredAt: (end) => `Dein Testzeitraum endet am <b>${end}</b>.`,
  },

  // subscription - details
  subscriptionDetails: {
    cancelSubscription: 'Kündigen',
    hideCancelSubscription: 'Abo kündigen',
    changeOrRenewSubscription: 'Plan verlängern/wechseln',
    changeSubscription: 'Plan wechseln',
    nextRenewal: 'Nächste Erneuerung am',
    cancelledTo: 'Gekündigt zum',
    gigabyte: 'GB',
    module: 'Modul',
    name: 'Name',
    subscriptionEndsHint: 'Dein Tarif läuft am <b>{{endDate}}</b> aus.',
    subscriptionRenewHint: 'Dein Tarif verlängert sich am <b>{{endDate}}</b> automatisch.',
    usage: 'Nutzung',
    yourPlan: (billingData) => 'Dein Plan',
    monthWithAnnualBilling: 'pro Monat bei jährlicher Abrechnung*',
    monthWithMonthlyBilling: 'pro Monat*',
    monthPerUser: 'mtl. pro Benutzer',
    monthTotal: (price) => `Gesamt ${price.toFixed(2)} € pro Monat`,
    locked: 'Du hast keine Berechtigung den Plan einzusehen',
    discountPercent: (discountPercent) => `${discountPercent} % Ersparnis`,
    cancel: {
      confirmRedirect: {
        title: 'Kündigung',
        description: `Du wirst nun aus ${productNameWithPrefix} ausgeloggt und zum buhl:Kundencenter weitergeleitet. Dort kannst Du Dich erneut anmelden und Dein Produkt bequem online kündigen.`,
        confirmLabel: 'Fortsetzen',
        cancelLabel: 'Abbrechen',
      },
    },
  },

  oldPlanModal: {
    title: 'Wechsle jetzt Deinen Plan',
    text: `Gerne ändern wir Deinen Plan.<br/>Rufe uns dazu einfach unter ${salesTelephoneNumber} an!`,
  },

  subscriptionModal: {
    title: 'Welche Produkte möchtest Du gerne testen?',
    text: 'Unsere großzügige 14-tägige Testphase bietet Dir die Gelegenheit, diese Produkte in Kombination zu erleben und ihre Leistungsfähigkeit ausführlich zu bewerten.',
    upgradeTitle: 'Wähle Deine bevorzugte Lösung aus',
    upgradeText: 'Wenn Du eine unserer Lösungen langfristig nutzen möchtest, triff Deine Auswahl und erwirb sie.',
    learnMore: 'Mehr erfahren',
    subscriptionType: {
      button: {
        label: {
          active: 'Entfernen',
          inactive: 'Hinzufügen',
        },
      },
      types: {
        desktop: {
          title: `${productNameWithPrefix} shareHub`,
          bulletPoints: [
            `Mit ${productName} ${isOrgaMax ? 'shareHub' : 'Portal'} ist es ein Leichtes, Informationen und Geschäftsdokumente elektronisch zu teilen, anstatt sie auszudrucken.`,
          ],
          hint: {
            title: `Was ist ${productName} ${isOrgaMax ? 'shareHub' : 'Portal'}?`,
            text: `Das ${productName} Kundencenter wurde in ${isOrgaMax ? 'shareHub' : 'Portal'} umbenannt. ${isOrgaMax ? 'shareHub' : 'Portal'} ist nun Deine digitale Zentrale um Dokumente wie Angebote, Aufträge und Rechnungen mit Deinen Kunden zu teilen. Dafür schickst Du Deinem Kunden einfach eine Mail mit einem Link zum jeweiligen Dokument. Dein Kunde kann diese Dokumente dann direkt im Browser ansehen und z. B. ein Angebot direkt annehmen und einen Auftrag oder eine Rechnung direkt bezahlen. Natürlich kann jedes Dokument auch als PDF heruntergeladen werden.`,
          },
        },
        main: {
          title: `${productNameWithPrefix} ${mainProductNaming}`,
          bulletPoints: [
            '<b>Einfach:</b> Mit einem Klick aus dem Angebot die Rechnung erstellen',
            '<b>Flexibel:</b> Buchhaltung im Browser & per App mobil erledigen',
            '<b>Automatisch:</b> Das Mahnwesen läuft von selbst',
            '<b>Skalierbar:</b> Deine Firma wächst? Einfach passende Tools dazu buchen',
          ],
          trialSelectBulletPoints: [
            'Mühelose Rechnungserstellung',
            'Effiziente Kundenverwaltung',
            'Schnelles Angebots- und Auftragsversenden',
            'All-in-One Lösung für Fakturierung',
            'Unverzichtbare Funktionen',
          ],
          hint: {
            title: `Was ist ${productName} ${mainProductNaming}?`,
            text: 'Leichtes Erstellen von Rechnungen, effiziente Verwaltung von Kundenstammdaten und reibungsloses Versenden von Angeboten und Aufträgen bilden die zentralen Funktionen dieses Produkts. Es vereint sämtliche essenziellen Features, die im Bereich der Fakturierung unverzichtbar sind.',
          },
          trialSelectTabSummary: 'Mühelose Rechnungserstellung, Effiziente Kundenverwaltung, schnelles Angebotsversenden und mehr.',
        },
        taxconsultant: {
          title: `${productNameWithPrefix} Steuerberater`,
          bulletPoints: [
            'Mandanten verwalten',
            'Aufgaben teilen',
            'Remote Zugang',
          ],
          hint: {
            title: `Was ist ${productName} Kanzlei?`,
            text: 'Verwalte Deine Mandanten, erstelle gemeinsame Aufgaben oder teile Dokumente.',
          },
        },
        document: {
          title: `${productNameWithPrefix} Dokumente`,
          bulletPoints: [
            '<b>Zeitsparend:</b> Dokumente per Scan-App aufnehmen & übertragen',
            '<b>Intelligent:</b> Die Texterkennung erfasst Beleginfos automatisch',
            '<b>Mühelos:</b> Digitale Übergabe an Steuerbüro inkl. DATEV-Cloud',
            '<b>Organisiert:</b> Alle Belege an einen Ort – per App, Mail oder Drag & Drop',
          ],
          trialSelectBulletPoints: [
            'Dokumente und Belge erfassen',
            'Belege organisieren, wiederfinden',
            'Inklusive intelligenter Texterkennung',
            'Scan App mit intelligenter Texterkennung',
            'Schnelle Übergabe an Deinen Steuerberater',
          ],
          hint: {
            title: `Was ist ${productName} Dokumente?`,
            text: 'Dokumentenmanagement-System, das über leistungsstarke OCR-Texterkennung verfügt. Neben der intelligenten Tagging-Funktion ermöglicht es eine nahtlose Verwaltung Deiner Dokumente.</br></br>Noch besser: Du kannst offene Belege direkt über die Plattform begleichen. Vereinfache Deine Abläufe und behalte die Kontrolle über Deine Dokumente und Finanzen.',
          },
          trialSelectTabSummary: 'Dokumente und Belge erfassen, organisieren und wiederfinden, inkl. Texterkennung, Scan App und mehr.',
        },
        // taxconsultant: 'Steuerberater',
        staff: {
          title: `${productNameWithPrefix} Personal`,
          bulletPoints: [
            'Urlaub und Abwesenheiten planen und erfassen',
            'Von überall erreichbar via Browser',
            'Vorbereitende Lohnbuchhaltung',
            'Datenexport an Lohnbuchhaltungs-Software und Steuerberater',
          ],
          trialSelectBulletPoints: [
            'Arbeitszeiterfassung',
            'Urlaub und Abwesenheiten planen und erfassen',
            'Von überall erreichbar via Browser',
            'Vorbereitende Lohnbuchhaltung',
            'Datenexport an Lohnbuchhaltungssoftware',
          ],
          hint: {
            title: `Was ist ${productName} Personal?`,
            text: 'Unser umfassendes Personalprodukt integriert Mitarbeiterfragebögen, präzise Zeiterfassung, unkomplizierte Auslagenerstattung und effiziente Urlaubsplanung. Eine All-in-One-Lösung für Deine Personalverwaltung.',
          },
          trialSelectTabSummary: 'Von überall aus Urlaub und Abwesenheiten planen, Arbeitszeiten erfassen, Lohnbuchhaltung und mehr.',
        },
      },
    },
    owned: 'Produkt gebucht',
    trialVersionAdded: 'Testversion hinzugefügt',
    tooltipText: 'Details anzeigen',
  },

  modals: {
    passwordPromptModal: {
      title: 'Passwort eingeben',
      description: 'Bitte gib hier Dein Passwort ein, welches Du als z. B. Einmalpasswort festgelegt hast. Du kannst auch über die Benutzerverwaltung ein neues Passwort festlegen, falls Dir dies noch nicht bekannt ist.',
    },
  },

  // upgrade modal
  upgradeModal: {
    subscriptionLoadError: 'Leider können wir Dir aufgrund einer technischen Störung derzeit keine Pläne anzeigen.',
    upgradeNotAvailable: 'Planupgrades stehen in Kürze zur Verfügung.',
    tooltipPlanNotActivatable: 'Dieser Plan ist aufgrund Deiner Konfiguration bzw. Deines aktuellen Plans nicht aktivierbar.',
    activateNow: 'Jetzt Plan auswählen',
    bestseller: 'Bestseller',
    topSeller: 'Top Seller',
    coupon: 'Gutschein',
    couponCode: 'Gutscheincode',
    couponCodePlaceholder: 'Gutscheincode eingeben',
    couponCodeDisabledHint: 'Es kann nur ein Gutscheincode aktiviert werden',
    couponCodeValid: 'Dein Gutscheincode wurde erfolgreich aktiviert.',
    couponCodeInvalid: 'Dein Gutscheincode ist ungültig oder abgelaufen.',
    couponCodeApply: 'Anwenden',
    loaderText: 'Pläne werden geladen',
    minusDiscount: 'abzgl. Rabatt',
    insteadOf: 'statt',
    onRequest: 'Ab 99,00 €',
    fromPrice: (formattedPrice) => `ab ${formattedPrice}`,
    buhlMarketPlaceRedirect: 'Du wirst zum buhl:Marketplace weitergeleitet',
    confirmEmailTitle: 'Damit wir sichergehen, dass Deine E-Mail "{{email}}" korrekt ist, gib bitte den erhaltenen Aktivierungscode ein oder fordere einen neuen an.',
    reducedPriceText: (duration, period, price) => `in ${duration > 1 ? 'den' : 'dem'} ersten ${duration > 1 ? duration + ' ' : ''}${period === 'year' ? (duration > 1 ? 'Jahren' : 'Jahr') : (duration > 1 ? 'Monaten' : 'Monat')}, danach ${price}`,
    discountPercentText: (discountPercent) => `Jetzt ${discountPercent}% sparen`,
    footer: {
      detailedOverview: 'Detaillierte Leistungsübersicht',
    },
    features: {
      shop: 'Shop-Anbindung',
      cashRegister: 'Digitale Kasse',
      shipping: 'Versand-Anbindung',
      scans: 'Belegerkennungen',
      cashTSS: 'Kasse (TSE)',
      multiTenant: 'Multimandanten',
      ordersAndDeliveryNotes: 'Aufträge & Lieferscheine',
      stockManagement: 'Lagerverwaltung',
      timeTracking: 'Zeiterfassung',
      suppliers: 'Lieferanten',
      recurringInvoices: 'Abo-Rechnungen',
      revenues: 'Sonstige Einnahmen',
      vatReturn: 'Umsatzsteuervoranmeldung',
      bookingSummary: 'Buchungsübersicht',
      expenses: 'Ausgaben',
      documents: 'Dokumente',
      todos: 'To\'dos',
      projects: 'Projekte',
      dunning: 'Mahnungen',
      banking: 'Banking',
      shippingUnlimited: 'Versand-Anbindung (unbegrenzt)',
      reporting: 'Auswertungen',
      shopify_connector: '',
      smtpImapAccount: 'E-Mail (SMTP/ IMAP)',
      customers: 'Kunden',
      articles: 'Artikel',
      offers: 'Angebote',
      invoices: 'Rechnungen',
      marketplace: 'Erweiterungen Marktplatz',
      documentExports: 'Steuerberaterexport',
      bookings: 'Buchungen',
      signatures: 'Unterschriften',
      clients: 'Mandate',
      'taxform-dashboard': 'Steuer Dashboard',
      'taxform-ustva': 'Umsatzsteuervoranmeldung',
      'taxform-taxFormSettings': 'Steuereinstellungen',
      'taxform-taxFormHistoryList': 'Historie von UstVa\'s',
      graduatedPrices: 'Staffelpreise',
      'financeform-export': 'Steuerberaterexport',
      'financeform-exportDatev': 'DATEV-Export',
      'financeform-exportList': 'Steuerberaterexport Liste',
      'financeform-exportDatevView': 'DATEV-Export Liste',
      sales: 'Umsatz pro Jahr',
      documentCount: 'Dokumentenanzahl',
      storageSpace: 'Speicherplatz für Dokumente',
      invoiceCount: 'Rechnungsanzahl pro Monat',
      customerCount: 'Kunden mit erstellter Rechnung',
      user: 'Nutzer',

      shippingConnection: 'Versand-Anbindung',
      gigabyte: 'GB',
    },
    plans: {
      salesBorderLabel: (val) => `bis ${val} Jahresumsatz`,
      includedUserLabel: (count) => count && `${count} Benutzer`,
      cashRegister: 'Digitale Kasse',
      documentCount: 'Anzahl maximaler hochgeladener Dokumente',
      documentScans: 'Belegerkennungen',
      invoices: 'Rechnungen pro Monat',
      unlimited: 'Unbegrenzte',
      gigabyte: 'GB',
      perMonth: 'pro Monat*',
      planXlHint: `Wir finden die auf Dich zugeschnittene Lösung.<br/>Rufe uns dazu einfach unter ${salesTelephoneNumber} an!`,
      salesBorder: 'Umsatz pro Jahr',
      shippingConnection: 'Versand-Anbindung',
      shopConnection: 'Shop-Anbindung',
      storageSpace: 'Speicherplatz für Dokumente',
      user: 'Benutzer enthalten',
      s: [
        { label: 'Beliebte Funktionen:', icon: 'none', html: true },
        { label: '(E-)Rechnungen senden & empfangen', icon: 'checked', tooltip: 'Lies, schreibe und versende unbegrenzt viele E-Rechnungen.' },
        { label: 'Angebote, Aufträge & Lieferscheine', icon: 'checked' },
        { label: 'KI-gestützte Belegerfassung', icon: 'checked', tooltip: 'Erfasse alle Deine Belege, Ausgaben und E-Rechnungen mit der orgaMAX App oder lade diese hoch. Die automatische Texterkennung erkennt die Inhalte des Belegs und überträgt diese direkt in Deine Buchhaltung.' },
        { label: 'Online-Banking-Verknüpfung', icon: 'checked', tooltip: 'Verwalte alle Deine Konten und Zahlungen an einem Ort und verknüpfe Deine Bankkonten und Zahlungsanbieter wie Paypal mit unserer BaFin-lizenzierten Schnittstelle.' },
        { label: 'Elektronisches Kassenbuch', icon: 'checked' },
        { label: 'Zahlungserinnerungen & Mahnungen', icon: 'checked', tooltip: 'Erstelle und versende Zahlungserinnerungen und Mahnungen zu überfälligen Rechnungen.' },
        { label: 'UstVA, EÜR & GuV Auswertungen', icon: 'checked', tooltip: 'Erstelle Deine Umsatzsteuervoranmeldung und Einnahmen-Überschuss-Rechnung in 2 Minuten und behalte stets den Überblick über Deine Ein- und Ausgaben.' },
        { label: 'Abschreibungen & Anlagen', icon: 'checked', tooltip: 'Verwalte Deine Anlagegüter und schreibe diese linear, degressiv oder sofort als Geringwertiges Wirtschaftsgut (GwG) ab. Du erhältst hierbei direkt die passenden Vorschläge zur Wahl der richtigen Abschreibungsmethode und -dauer.' },
        { label: 'Steuerberaterzugang', icon: 'checked', tooltip: 'Lade kostenlos Deinen Steuerberater zur Einsicht in Deine Buchhaltung ein.' },
        { label: 'DATEV & ELSTER Schnittstelle', icon: 'checked' },
      ],
      m: [
        { label: 'Alle Funktionen aus <b>S</b> & zszl.:', icon: 'none', html: true },
        { label: 'Automatisches Mahnwesen', icon: '', tooltip: 'Versende Zahlungserinnerungen und Mahnungen vollautomatisiert per E-Mail, sobald diese einen kritischen Zahlungsstatus erhalten.' },
        { label: 'Automatischer Belegimport', icon: '', tooltip: 'Das orgaMAX Postfach ermöglicht den automatischen Import und die Verarbeitung von E-Rechnungen (ZUGFeRD, XRechnung) sowie PDF-Dokumenten. Du kannst E-Mails mit entsprechenden Anhängen an dieses Postfach weiterleiten, damit die Anhänge automatisch in die Anwendung eingelesen und verarbeitet werden.' },
        { label: 'Angebotsannahme mit E-Unterschrift', icon: '', tooltip: 'Schneller zum Auftrag: Lass Deine Angebote und Auftragsbestätigungen direkt online und verbindlich von Deinem Kunden unterschreiben.' },
        { label: 'Abschlags- & Schlussrechnungen', icon: '' },
        { label: 'Sammelrechnungen', icon: '', tooltip: 'Fasse mehrere Aufträge zu einer Rechnung zusammen.' },
        { label: 'Mehrere Briefpapiere', icon: '', tooltip: 'Lege mehrere verschiedene Briefpapiere zu Deinen Geschäftsdokumenten an.' },
        { label: 'Optionale Angebotspositionen', icon: '', tooltip: 'Mehr Umsatz garantiert: Füge optionale Positionen zu Deinen Angeboten hinzu und freue Dich über höhere Bestellungen Deiner Kunden.' },
        { label: 'To dos & Erinnerungen', icon: '', tooltip: 'Erstelle Aufgaben und To-Dos für Dich und Deine Mitarbeitenden.' },
        { label: 'Teil-/Sammellieferscheine', icon: '', tooltip: 'Erstelle Teil- oder Sammellieferscheine für Deine Kunden.' },
      ],
      l: [
        { label: 'Alle Funktionen aus <b>M</b> & zszl.:', icon: 'none', html: true },
        { label: 'Lagerverwaltung', icon: '', tooltip: 'Behalte stets den Überblick über den Lagerbestand Deiner Artikel.' },
        { label: 'Lieferantenbestellungen', icon: '', tooltip: 'Erstelle und verwalte Bestellungen an Deine Lieferanten.' },
        { label: 'Artikelreservierungen', icon: '', tooltip: 'Sieh jederzeit, welche Artikel reserviert und verfügbar sind.' },
        { label: 'Staffelpreise', icon: '', tooltip: 'Lege unterschiedliche Artikelpreise zu unterschiedlichen Abnahmemengen fest.' },
      ],
      xl: [
        { label: 'unbegrenzter Umsatz', icon: '' },
        { label: 'bevorzugter Support', icon: '' },
      ],
      buchhaltung: [
        { label: 'Alles für den täglichen Ablauf:', icon: 'none', html: true },
        { label: 'E-Rechnungen empfangen & versenden', icon: 'checked', tooltip: 'Lies, schreibe und versende unbegrenzt viele E-Rechnungen.' },
        { label: 'Angebote erstellen', icon: 'checked' },
        { label: 'Belegmanagement', icon: 'checked', tooltip: 'Erfasse alle Deine Belege, Ausgaben und E-Rechnungen mit der orgaMAX App oder lade sie hoch. Die automatische Texterkennung erkennt die Inhalte des Belegs und überträgt diese direkt in Deine Buchhaltung.' },
        { label: 'Banking & Zahlungsabgleich', icon: 'checked', tooltip: 'Verwalte alle Deine Konten und Zahlungen an einem Ort und verknüpfe Deine Bankkonten und Zahlungsanbieter wie Paypal mit unserer BaFin-lizenzierten Schnittstelle.' },
        { label: 'Buchhaltung & Steuern', icon: 'checked', tooltip: 'Erstelle Deine Umsatzsteuervoranmeldung und Einnahmen-Überschuss-Rechnung in 2 Minuten und behalte stets den Überblick über Deine Ein- und Ausgaben.' },
        { label: 'KI-Funktionen & Automatisierungen', icon: 'checked', tooltip: 'KI-Agent MAX erfasst Belege im Stapel, importiert sie automatisch über Dein orgaMAX Postfach und prüft, optimiert und übersetzt Deine Vorgangs- und Leistungstexte.' },
      ],
      // DO-13587: texts of the plan cards in the shop checkout (design "Plan wählen"), without a full stop at the end
      auftrag: [
        { label: 'Deine Basis für das Auftragswesen:', icon: 'none', html: true },
        { label: 'Angebote, Aufträge & Rechnungen erstellen', icon: 'checked', tooltip: 'Vom Angebot über die Auftragsbestätigung bis zum Lieferschein - alle Vorgänge bauen aufeinander auf.' },
        { label: 'E-Rechnungen empfangen & versenden', icon: 'checked', tooltip: 'Lies, schreibe und versende unbegrenzt viele E-Rechnungen.' },
        { label: 'Belege erfassen & verwalten', icon: 'checked', tooltip: 'Erfasse alle Deine Belege, Ausgaben und E-Rechnungen mit der orgaMAX App oder lade sie hoch. Die automatische Texterkennung erkennt die Inhalte des Belegs und überträgt diese direkt in Deine Buchhaltung.' },
        { label: 'Zahlungen per Banking abgleichen', icon: 'checked', tooltip: 'Verwalte alle Deine Konten und Zahlungen an einem Ort und verknüpfe Deine Bankkonten und Zahlungsanbieter wie Paypal mit unserer BaFin-lizenzierten Schnittstelle.' },
        { label: 'Vorgänge direkt verbuchen', icon: 'checked' },
        { label: 'Buchhaltung & Steuern erledigen', icon: 'checked', tooltip: 'Erstelle Deine Umsatzsteuervoranmeldung und Einnahmen-Überschuss-Rechnung in 2 Minuten und behalte stets den Überblick über Deine Ein- und Ausgaben.' },
        { label: 'Dokumente zentral verwalten', icon: 'checked', tooltip: 'Importiere, verknüpfe und durchsuche externe Dokumente - inklusive 25 GB Speichervolumen.' },
        { label: 'KI-Funktionen & Automatisierungen nutzen', icon: 'checked', tooltip: 'KI-Agent MAX erfasst Belege im Stapel, importiert sie automatisch über Dein orgaMAX Postfach und prüft, optimiert und übersetzt Deine Vorgangs- und Leistungstexte.' },
      ],
      auftragPro: [
        { label: 'Alles aus AUFTRAG, plus:', icon: 'none', html: true },
        { label: 'Aufträge flexibel abrechnen', icon: 'add', tooltip: 'Teil-, Sammel-, Abschlags- & Schlussrechnungen' },
        { label: 'Aufträge flexibel ausliefern', icon: 'add', tooltip: 'Teil- & Sammellieferscheine' },
        { label: 'Projekte organisieren', icon: 'add', tooltip: 'Zeiten erfassen & abrechnen, Aufgaben & To-dos' },
        { label: 'Dokumente unterschreiben lassen', icon: 'add', tooltip: 'Angebote, Aufträge & externe Dokumente' },
      ],
      warenwirtschaft: [
        { label: 'Alles aus AUFTRAG PRO, plus:', icon: 'none', html: true },
        { label: 'Lager verwalten', icon: 'add', tooltip: 'Bestände führen & Verfügbarkeiten prüfen' },
        { label: 'Bestellungen abwickeln', icon: 'add', tooltip: 'Lieferantenbestellungen erstellen & verfolgen' },
        { label: 'Einkaufskonditionen nutzen', icon: 'add', tooltip: 'Lieferantenpreise & Losgrößen hinterlegen' },
        { label: 'Preise flexibel gestalten', icon: 'add', tooltip: 'Mehrere Preislisten & Staffelpreise nutzen' },
      ],
      document_s: [
        { label: 'Zentrale Ablage von Dokumenten & Belegen', icon: 'none' },
        { label: 'Belege per App abfotografieren', icon: 'checked' },
        { label: 'Automatische Texterkennung', icon: 'checked' },
        { label: 'Belegexport zu DATEV Unternehmen Online', icon: 'checked' },
      ],
      document_m: [
        { label: 'Alles aus <b>S</b>, plus:', icon: 'none', html: true },
        { label: 'Freigaben von Dokumenten anfordern', icon: 'add' },
        { label: 'Dokumente direkt online bezahlen', icon: 'add' },
        { label: 'Weitere Nutzer hinzubuchen', icon: 'add' },
      ],
      document_l: [
        { label: 'Alles aus <b>M</b>, plus:', icon: 'none', html: true },
        { label: 'Dokumente digital unterschreiben', icon: 'add' },
        { label: 'Kostenloses Onboarding', icon: 'add' },
      ],
      document_l_erp: [
        { label: 'Zentrale Ablage von Dokumenten & Belegen', icon: 'checked' },
        { label: 'Belege per App abfotografieren', icon: 'checked' },
        { label: 'Automatische Texterkennung', icon: 'checked' },
        { label: 'Belegexport zu DATEV Unternehmen Online', icon: 'checked' },
        { label: 'Freigaben von Dokumenten anfordern', icon: 'checked' },
        { label: 'Dokumente direkt online bezahlen', icon: 'checked' },
        { label: 'Weitere Nutzer hinzubuchen', icon: 'checked' },
        { label: 'Dokumente digital unterschreiben', icon: 'checked' },
        { label: 'Kostenloses Onboarding', icon: 'checked' },
        { label: `inkl. Anbindung an ${productName} ERP`, icon: 'checked' },
      ],
      staff_s: [
        { label: 'Alle Funktionen', icon: 'checked' },
      ],
      staff_m: [
        { label: 'Alle Funktionen', icon: 'checked' },
      ],
      staff_l: [
        { label: 'Alle Funktionen', icon: 'checked' },
      ],
      staff_xl: [
        { label: 'Alles aus <b>L</b>, plus:', icon: 'none', html: true },
        { label: 'Weitere Mitarbeitende hinzubuchbar', icon: 'add' },
      ],
    },
    modules: {
      title: 'Erweiterungen hinzufügen',
      titleAlternative: 'Erweiterungen',
      subTitle: 'Du kannst die gewünschte Erweiterung direkt in den Warenkorb legen, sollte nichts passendes für Dich dabei sein, klicke direkt auf "Weiter".',
      allModulesSubTitle: 'Du kannst die gewünschten Erweiterungen direkt in den Warenkorb legen, sollte nichts passendes für Dich dabei sein, klicke direkt auf "Weiter".',
      emptySelection: 'Es sind keine Erweiterungen für Dein aktives/gewähltes Abo verfügbar.',
      disabledHint: {
        owned: 'Du hast diese Erweiterung bereits gekauft.',
        inCart: 'Diese Erweiterung ist bereits im Warenkorb.',
        default: 'Diese Erweiterung ist aufgrund Deiner Konfiguration, bzw. Deines aktuellen Plans nicht aktivierbar.',
      },
      buttons: {
        addToCart: 'Zum Warenkorb hinzufügen',
        owned: 'Bereits gekauft',
        inCart: 'Bereits im Warenkorb',
      },
      toast: {
        addedtoCart: 'Add-on wurde zum Warenkorb hinzugefügt.',
      },
    },
    subscriptionExpiredAt: (endDate) => `Leider ist Dein Abo am ${endDate} abgelaufen.`,
    trialExpirationText: 'Wähle jetzt einen Plan und Du kannst weiterhin',
    trialExpiredAt: (endDate) => `Dein Testzeitraum ist am ${endDate} abgelaufen.`,
    upgradeNow: 'Weiter durchstarten',
    upgradePerks: [
      'Rechnungen schreiben',
      'Buchhaltung abwickeln',
      'Geschäftszahlen überblicken',
      'von vielen weiteren Funktionen profitieren',
      'eine Menge Zeit sparen',
    ],
    greetingText: (firstName, lastName) => (firstName || lastName) ? `Hallo ${`${firstName} ${lastName}`.trim()},` : 'Hallo,',
    vatHint: 'zzgl. USt.',
    overview: 'Deine Bestellübersicht',
    multiProductOverview: 'Deine Bestellung',
    subtitles: {
      s_2021_2: 'Buchhaltung Kompakt',
      m_2021_2: 'Buchhaltung Standard',
      l_2021_2: 'Buchhaltung Pro & Lager',
      xl_2021: 'Die Individuallösung für komplexe Anforderungen',
      // new main subs
      free_2025: 'Dein Einstieg in die E-Rechnungen',
      xs_2025: 'E-Rechnungen & Angebote',
      s_2025: 'Buchhaltung Kompakt',
      m_2025: 'Buchhaltung Standard',
      l_2025: 'Buchhaltung Pro & Lager',
      document_s: 'Gründer & Soloselbständige',
      document_m: 'Kleine Teams & Betriebe',
      document_l: 'Größere Teams & Mittelstand',
      staff: (employees) => `Bis ${employees} Mitarbeitende`,
    },
    supportHint: {
      s_2021_2: 'Kostenloser Support per E-Mail',
      m_2021_2: 'Kostenloser Support per E-Mail, Telefon und Live-Chat',
      l_2021_2: 'Kostenloser Support per E-Mail, Telefon und Live-Chat',
      xl_2021: 'Kostenloser Support per E-Mail, Telefon und Live-Chat',
    },
    moduleDescription: {
      s_2021_2: 'Bis 22.000 € Jahresumsatz',
      m_2021_2: 'Unbegrenzter Jahresumsatz',
      l_2021_2: 'Unbegrenzter Jahresumsatz',
      document_s: '50 Dokumente pro Monat',
      document_m: '250 Dokumente pro Monat',
      document_l: '500 Dokumente pro Monat',
      staffMonthly: (price) => `Nur ${price} pro MitarbeiterIn`,
      staffYearly: (price) => `Nur ${price} pro MitarbeiterIn`,
    },
    addedFunctionsTitleBasic: 'Alle Funktionen',
    addedFunctionsTitleSmart: 'Alle Funktionen von Basic',
    addedFunctionsTitleBusiness: 'Alle Funktionen von Smart',
    addedFunctionsTitleXL: 'Alle Funktionen von Business',

    // upgrade modal v2
    featurePoints: {
      point1: 'Käuferschutz',
      point2: 'Datenschutz',
      point3: 'Sichere Zahlung mit SSL',
    },
    backButtonLabel: '< Zurück zum vorherigen Schritt',
    backButtonLabelMobile: '< Zurück',
    continueToNextStep: 'Weiter zum nächsten Schritt',
    orderNowOnFree: 'FREE Plan aktivieren',
    orderSecurelyAndPay: 'Sicher & zahlungspflichtig bestellen',
    upgradeSteps: {
      button: {
        subscriptionMain: 'Zur Buchhaltung',
        subscriptionDocument: 'Zu Dokumente',
        subscriptionStaff: 'Zu Personal',
        extensions: 'Weiter zur Bestellübersicht',
        withoutExtensions: 'Weiter ohne Add-ons',
        billing: 'Weiter zur Zahlungsweise',
        overview: 'Zur Bestellübersicht',
      },
    },
    overviewSummary: {
      title: 'Bestellübersicht',
      subscription: {
        thead: {
          product: 'Produkt',
          plan: 'Plan',
          period: 'Zahlweise',
          monthlyPrice: 'Preis monatlich (zzgl. USt.)',
          yearlyPrice: 'Preis jährlich (zzgl. USt.)',
        },
        months: 'Monate',
      },
      modules: {
        thead: {
          extension: 'Erweiterung',
          addOn: 'Add-On',
          count: 'Anzahl',
          period: 'Zahlweise',
          monthlyPrice: 'Preis monatlich (zzgl. USt.)',
          yearlyPrice: 'Preis jährlich (zzgl. USt.)',
        },
      },
      periods: {
        yearly: 'Jährlich',
        monthly: 'Monatlich',
      },
      couponCodeLabel: 'Füge einen Gutschein oder Aktionscode hinzu:',
      couponCodePlaceholder: 'Code eingeben',
      netTotal: 'Zwischensumme',
      discount: 'Gesamtrabatt',
      vatHint: 'USt.',
      termsAndConditions: {
        our: 'Alle Transaktionen sind sicher und verschlüsselt. Es gelten die ',
        agb: 'AGB',
        and: ' & ',
        privacyPolicy: 'Datenschutzerklärung',
      },
      dueNow: 'Jetzt fällig',
      nextBilling: 'Nächste Abrechnung:',
      showOrderDetails: 'Bestelldetails anzeigen',
      totalDiscount: 'Gesamtersparnis',
    },
    choosePlan: 'Wähle einen Plan aus',
  },
  featureLockedModal: {
    scheduleMeeting: 'Beratungstermin vereinbaren',
    selectPlan: () => 'Jetzt upgraden',
    features: {
      todo: {
        title: 'Aufgaben und To-dos erfassen',
        description: 'Aufgaben dort erfassen, wo sie anfallen',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Übersicht behalten:', description: 'Alle To-dos an einem Ort' },
        feature2: { title: 'Richtig verzahnt:', description: 'Aufgaben mit Kontakten verknüpfen' },
        feature3: { title: 'Deadlines einhalten:', description: 'Fristen, Status und Erinnerungen definieren' },
        feature4: { title: 'Wiederkehrendes automatisieren:', description: 'Regelmäßige Aufgaben einmal anlegen' },
      },
      letters: {
        title: 'Geschäftsbriefe schreiben',
        description: 'Briefe direkt in orgaMAX schreiben - ganz ohne Word',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Professionell auftreten:', description: 'Briefe im eigenen Layout & mit Absenderdaten' },
        feature2: { title: 'Weniger Tipparbeit:', description: 'Textvorlagen für wiederkehrende Schreiben nutzen' },
        feature3: { title: 'Alles zentral:', description: 'Briefe direkt beim Kontakt oder Vorgang ablegen' },
        feature4: { title: 'Kein Dateichaos:', description: 'Kein Suchen mehr in Ordnern oder Mail-Anhängen' },
        feature5: { title: 'Sofort versenden:', description: 'Per E-Mail verschicken, drucken oder als PDF speichern' },
      },
      'orders-and-delivery-note': {
        title: 'Aufträge & Lieferscheine erstellen',
        description: 'Vom Auftrag zum Lieferschein in wenigen Klicks',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Perfekt verzahnt:', description: 'Auftrag, Lieferschein & Rechnung greifen nahtlos ineinander' },
        feature2: { title: 'Fehlerfrei:', description: 'Artikel, Preise & Kundendaten automatisch übernommen' },
        feature3: { title: 'Ohne Umwege:', description: 'Lieferschein direkt aus dem Auftrag erstellen' },
        feature4: { title: 'Individuell:', description: 'Eigene Layouts für professionelle Dokumente nutzen' },
        feature5: { title: 'Sofort versenden:', description: 'Per E-Mail verschicken, drucken oder als PDF speichern' },
      },
      'recurring-invoice': {
        title: 'Abo-Rechnungen schreiben',
        description: 'Wiederkehrende Rechnungen automatisieren',
        subscriptionTypeLabel: 'Als Add-on jederzeit hinzubuchbar',
        feature1: { title: 'Zeit sparen:', description: 'Einmal anlegen, beliebig oft nutzen' },
        feature2: { title: 'Routine einführen:', description: 'Rechnungen in festen Intervallen ganz ohne Aufwand' },
        feature3: { title: 'Kundentreu:', description: 'Daueraufträge pünktlich und zuverlässig abrechnen' },
        feature4: { title: 'Alles im Blick:', description: 'Nächste Fälligkeit direkt im System sehen' },
      },
      projects: {
        title: 'Abschlagsrechnung erstellen',
        description: 'Teilzahlungen ganz einfach abrechnen',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Flexibel fakturieren:', description: 'Abschläge nach Baufortschritt oder Projektstand berechnen' },
        feature2: { title: 'Klar nachvollziehbar:', description: 'Auflistung aller Abschläge in der Schlussrechnung' },
        feature3: { title: 'Weniger Aufwand:', description: 'Beträge und Leistungen aus Angeboten übernehmen' },
        feature4: { title: 'Zahlungseingänge sichern:', description: 'Frühzeitig abrechnen, Liquidität steigern' },
      },
      timetracking: {
        title: 'Projektzeiten erfassen',
        description: 'Arbeitszeiten projektbezogen erfassen und direkt abrechnen',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Besser planen:', description: 'Genaue Übersicht über Projektlaufzeiten und Aufwand' },
        feature2: { title: 'Direkt abrechnen:', description: 'Erfasste Zeiten direkt in die Rechnung übernehmen' },
        feature3: { title: 'Transparent bleiben:', description: 'Leistungen nachvollziehbar dokumentieren' },
        feature4: { title: 'Einfach erfassen:', description: 'Zeiten bequem im Büro oder mobil eintragen' },
      },
      banking: {
        title: 'Onlinebanking & Kassenbuch',
        description: 'Zahlungen verwalten und Kassenbuch digital führen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Buchhaltung beschleunigen:', description: 'Umsätze automatisch Belegen zuordnen' },
        feature2: { title: 'Fehlbuchungen vermeiden:', description: 'Kontoauszüge einfach abgleichen' },
        feature3: { title: 'Kassenbuch führen:', description: 'Bargeschäfte GoBD-konform dokumentieren' },
        feature4: { title: 'Alles zentral:', description: 'Bank und Kassenbuch in einem System verwalten' },
      },
      expenses: {
        title: 'Belege und Ausgaben verwalten',
        description: 'Belege digital erfassen und Ausgaben automatisch verbuchen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Zettelchaos vermeiden:', description: 'Belege einfach hochladen oder per App scannen' },
        feature2: { title: 'Schnell gefunden:', description: 'Mit Tags, Ordnern und Volltextsuche' },
        feature3: { title: 'Direkt verbucht:', description: 'Belegpositionen automatisch übernehmen' },
        feature4: { title: 'Mehr Überblick:', description: 'Alle Ausgaben zentral an einem Ort' },
      },
      dunning: {
        title: 'Zahlungserinnerungen & Mahnungen versenden',
        description: 'Säumige Zahlungen professionell nachfassen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Automatisch mahnen:', description: 'Mahnungen nach festen Fristen versenden' },
        feature2: { title: 'Zahlungsausfälle reduzieren:', description: 'Durch systematisches Nachfassen' },
        feature3: { title: 'Professionell auftreten:', description: 'Mit individuellen Vorlagen und Textbausteinen' },
        feature4: { title: 'Liquidität sichern:', description: 'Schnellere Zahlungseingänge durch klare Prozesse' },
      },
      mailbox: {
        title: 'orgaMAX Postfach',
        description: 'E-Mails empfangen, zuordnen und ablegen direkt in Deiner Buchhaltung',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Alles an einem Ort:', description: 'Rechnungen und Belege landen direkt im Postfach' },
        feature2: { title: 'Direkt weiterverarbeiten:', description: 'E-Mail-Anhänge mit einem Klick verbucht' },
        feature3: { title: 'Zeit sparen:', description: 'Automatische Belegerkennung und Zuordnung' },
        feature4: { title: 'Digital dokumentieren:', description: 'Belege rechtssicher im System ablegen' },
      },
      inventory: {
        title: 'Lagerverwaltung & Bestände verwalten',
        description: 'Artikelbestände immer aktuell halten',
        subscriptionTypeLabel: 'Ab Plan Warenwirtschaft',
        feature1: { title: 'Bestände im Blick:', description: 'Lagerzugänge und -abgänge automatisch dokumentiert' },
        feature2: { title: 'Direkt verbuchen:', description: 'Artikelbewegungen mit Rechnungen und Lieferscheinen verknüpfen' },
        feature3: { title: 'Einfach nachbestellen:', description: 'Mindestbestände und Lieferzeiten definieren' },
        feature4: { title: 'Lagerkosten senken:', description: 'Besser planen dank transparenter Artikelhistorie' },
      },
      'auto-dunning': {
        title: 'Automatisches Mahnwesen aktivieren',
        description: 'Offene Rechnungen automatisch verfolgen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Fristen im Griff:', description: 'Zahlungserinnerungen und Mahnungen laufen automatisch' },
        feature2: { title: 'Mehr Liquidität:', description: 'Schnellere Zahlungseingänge' },
        feature3: { title: 'Kundenschonend:', description: 'Freundliche Vorlagen und abgestufte Mahnstufen' },
        feature4: { title: 'Voll integriert:', description: 'Mahnungen direkt aus den Rechnungsdaten erstellen' },
        feature5: { title: 'Zeit sparen:', description: 'Keine manuelle Nachverfolgung mehr nötig' },
      },
      'e-signing': {
        title: 'Angebote digital unterschreiben (eSigning)',
        description: 'Angebote online verschicken & unterschreiben lassen',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Schneller beauftragt:', description: 'KundInnen unterschreiben digital' },
        feature2: { title: 'Kein Medienbruch:', description: 'Angebot erstellen, versenden und freigeben in orgaMAX' },
        feature3: { title: 'Rechtssicher:', description: 'Unterschrift mit Zeitstempel und Prüfprotokoll' },
        feature4: { title: 'Besser nachverfolgen:', description: 'Rückmeldungen in Echtzeit über shareHUB einsehen' },
      },
      'e-invoices': {
        title: 'E-Rechnung erstellen und empfangen',
        description: 'E-Rechnungen gesetzeskonform versenden - fit für den B2B-Standard',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Pflicht erfüllt:', description: 'Rechnungen im XRechnung- und ZUGFeRD-Format' },
        feature2: { title: 'Gewohnter Ablauf:', description: 'Klassische Rechnung schreiben & zu E-Rechnung umwandeln' },
        feature3: { title: 'Belege auslesen:', description: 'Eingehende E-Rechnungen direkt digital erfassen' },
        feature4: { title: 'Zeit sparen & Fehler vermeiden:', description: 'Keine manuelle Formatierung oder doppelte Dateneingabe' },
      },
      'optional-positions': {
        title: 'Optionale Positionen im Angebot',
        description: 'Zusatzangebote klar kennzeichnen - ohne Einfluss auf die Gesamtsumme',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Flexibler anbieten:', description: 'KundInnen mehrere Varianten zur Auswahl geben' },
        feature2: { title: 'Besser beraten:', description: 'Leistungen sichtbar machen, ohne sie direkt mitanzubieten' },
        feature3: { title: 'Individuell kalkulieren:', description: 'Optionale Positionen nicht in Gesamtsumme einrechnen' },
        feature4: { title: 'Klar gekennzeichnet:', description: 'Zusatzangebote im Angebot als optional markiert' },
      },
      'alternative-positions': {
        title: 'Alternativpositionen im Angebot',
        description: 'Lass Deine KundInnen entscheiden',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Individueller anbieten:', description: 'Mit verschiedenen Ausführungen oder Preismodellen' },
        feature2: { title: 'Klar strukturiert:', description: 'Alternativpositionen deutlich vom Hauptangebot abgrenzen' },
        feature3: { title: 'Ohne Einfluss auf Summe:', description: 'Preise werden nicht in die Gesamtsumme eingerechnet' },
        feature4: { title: 'Bessere Entscheidungsgrundlage:', description: 'KundInnen wählen direkt aus mehreren Möglichkeiten' },
      },
      'letter-papers': {
        title: 'Mehrere Briefpapiere verwenden',
        description: 'Dokumente mit persönlicher Note versenden',
        subscriptionTypeLabel: 'Als Add-on jederzeit hinzubuchbar',
        feature1: { title: 'Individuell auftreten:', description: 'Verschiedene Briefpapiere für unterschiedliche Zwecke nutzen' },
        feature2: { title: 'Eigene Designs:', description: 'PDF-Vorlagen einfach hochladen und zuordnen' },
        feature3: { title: 'Standard festlegen:', description: 'Wunschlayout dauerhaft voreinstellen' },
        feature4: { title: 'Anpassbar bleiben:', description: 'Briefpapier bei Bedarf jederzeit austauschen' },
      },
      'collective-invoice': {
        title: 'Sammelrechnungen erstellen',
        description: 'Mehrere Aufträge bündeln und Aufwand sparen',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Einmal abrechnen:', description: 'Offene Aufträge gesammelt in einer Rechnung zusammenführen' },
        feature2: { title: 'Weniger Einzelrechnungen:', description: 'Mehr Übersicht und weniger Klickarbeit' },
        feature3: { title: 'Schneller fertig:', description: 'Aufträge markieren und direkt gemeinsam abrechnen' },
        feature4: { title: 'Alles im Blick:', description: 'Noch nicht berechnete Leistungen automatisch anzeigen' },
      },
      'graduated-price': {
        title: 'Staffelpreise anlegen',
        description: 'Mengenrabatte automatisch berechnen und richtig ausweisen',
        subscriptionTypeLabel: 'Ab Plan Warenwirtschaft',
        feature1: { title: 'Flexibel kalkulieren:', description: 'Preise nach Abnahmemenge gestaffelt festlegen' },
        feature2: { title: 'Automatisch günstiger:', description: 'Passender Preis wird bei Angebotsmenge automatisch gezogen' },
        feature3: { title: 'Weniger Nachrechnen:', description: 'Staffelpreise direkt im Artikelstamm hinterlegen' },
        feature4: { title: 'Verkauf stärken:', description: 'Größere Mengen attraktiver anbieten' },
      },
      'invite-user': {
        title: 'Weitere Nutzer einladen',
        description: 'orgaMAX gemeinsam nutzen und Aufgaben gezielt verteilen',
        subscriptionTypeLabel: 'Als Add-on jederzeit hinzubuchbar',
        feature1: { title: 'Im Team arbeiten:', description: 'Mehrere Nutzer gleichzeitig im System verwalten' },
        feature2: { title: 'Steuerberater einbinden:', description: 'Zugriff kostenlos und ohne Zusatzkosten ermöglichen' },
        feature3: { title: 'Flexibel zubuchen:', description: 'Nutzerzugang einfach pro Person erweitern' },
        feature4: { title: 'Alles im Blick:', description: 'Verantwortlichkeiten klar aufteilen und Abläufe strukturieren' },
      },
      'public-api': {
        title: `${productName} API`,
        description: `Eigene Erweiterungen und der Zugriff auf die ${productName}-API sind mit dem Add-on „${productName} API“ (30 € pro Monat zzgl. USt.) möglich.`,
        feature1: { title: 'Eigene Erweiterungen:', description: 'Erweiterungen im Entwicklerbereich erstellen und verwalten' },
        feature2: { title: 'Zugangsdaten:', description: 'API-Key und Secret für Deine Erweiterungen erhalten' },
        feature3: { title: 'Systeme verbinden:', description: `Fremdsysteme per API mit ${productName} verbinden` },
      },
      revenue: {
        title: 'Sonstige Einnahmen verwalten',
        description: 'Zahlungseingänge außerhalb von Rechnungen schnell erfassen und verbuchen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Einfach verbucht:', description: 'Einnahmen ohne Rechnung direkt ins System eintragen' },
        feature2: { title: 'Beleg erkannt:', description: 'Dateien automatisch zuordnen lassen - ohne manuelle Eingabe' },
        feature3: { title: 'Für alles gewappnet:', description: 'Gutschriften, Zinserträge oder andere Einnahmen flexibel erfassen' },
        feature4: { title: 'Immer vollständig:', description: 'Auch Sonderfälle landen korrekt in der Buchhaltung' },
      },
      'tax-report': {
        title: 'Steuerauswertungen erstellen',
        description: 'UStVA und EÜR direkt aus Deiner Buchhaltung erzeugen',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Direkt aus den Daten:', description: 'EÜR und Umsatzsteuervoranmeldung automatisch erstellen' },
        feature2: { title: 'Ohne Excel-Chaos:', description: 'Alle Werte stammen direkt aus Deiner laufenden Buchhaltung' },
        feature3: { title: 'Schnell übermittelt:', description: 'Steuerdaten bequem per ELSTER-Schnittstelle versenden' },
        feature4: { title: 'Immer nachvollziehbar:', description: 'Klare Aufschlüsselung Deiner Einnahmen und Ausgaben' },
      },
      'economic-asset': {
        title: 'Abschreibungen & Anlagen verwalten',
        description: 'Anschaffungen als Anlage erfassen und automatisch abschreiben',
        subscriptionTypeLabel: 'Ab Plan Auftrag',
        feature1: { title: 'Einfach anlegen:', description: 'Anlagegüter mit wenigen Klicks erfassen und speichern' },
        feature2: { title: 'Direkt verbucht:', description: 'Ausgabe und Abschreibung automatisch im System hinterlegt' },
        feature3: { title: 'Wahlweise abschreiben:', description: 'Linear oder degressiv je nach Bedarf auswählen' },
        feature4: { title: 'Mehr Übersicht:', description: 'Alle laufenden Abschreibungen an einem Ort' },
      },
      purchases: {
        title: 'Lieferantenbestellungen schnell erledigt & organisiert',
        description: 'Ein zentraler Bestellprozess statt verteilter Einzellösungen',
        subscriptionTypeLabel: 'Ab Plan Warenwirtschaft',
        feature1: { title: 'Zentral organisiert: ', description: 'Bestellungen direkt in orgaMAX erfassen, verwalten und versenden - ohne externe Tools.' },
        feature2: { title: 'Einfacher Workflow:', description: 'Lieferanten und Artikel im Vorgang anlegen, speichern und wiederverwenden.' },
        feature3: { title: 'Klare Lieferstatus:', description: 'Auf einen Blick sehen, was offen, geliefert oder noch ausstehend ist.' },
        feature4: { title: 'Bessere Planung:', description: 'Durchgängige Prozesse sorgen für weniger Fehler und mehr Sicherheit im Alltag.' },
      },
      collectiveDeliveryNotes: {
        title: 'Teil- und Sammel-lieferscheine erstellen',
        description: 'Behalte auch bei mehreren Lieferungen den Überblick',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Einfach erstellen:', description: 'Erstelle Teil- und Sammellieferscheine direkt aus Deinen Aufträgen' },
        feature2: { title: 'Übersichtlich verwalten:', description: 'Behalte Lieferstatus und offene Restmengen jederzeit im Blick' },
        feature3: { title: 'Effizient arbeiten:', description: 'Spare Zeit bei der Auftragsabwicklung' },
      },
      partialInvoice: {
        title: 'Teilrechnungen erstellen',
        description: 'Rechne Aufträge Schritt für Schritt ab und behalte offene Beträge im Blick',
        subscriptionTypeLabel: 'Ab Plan Auftrag Pro',
        feature1: { title: 'Einfach erstellen:', description: 'Rechne genau das ab, was Du bereits geliefert oder geleistet hast, auch einzelne Teilmengen.' },
        feature2: { title: 'Übersichtlich verwalten:', description: 'Behalte bereits abgerechnete Beträge und offene Restmengen jederzeit im Blick.' },
        feature3: { title: 'Liquidität sichern:', description: 'Erhalte Zahlungen früher, schon während der Auftrag noch läuft.' },
      },
    },
  },

  // settings - section names
  settingSectionTitles: {
    transactionCreation: 'Vorgangserstellung',
    user: 'Benutzer',
    userAndTaxConsultant: 'Benutzer und Steuerberater',
    tax: 'Steuereinstellungen',
    dataTransfer: 'Datenübernahme zwischen Vorgängen',
    transactionProcessing: 'Verarbeitung von Vorgängen',
    titleAndUnits: 'Anrede und Einheiten',
    eventLog: 'Protokoll',
  },
  settingDocumentTitles: {
    myAccount: 'Mein Konto',
    interfaces: 'Schnittstellen',
    masterData: 'Kleinstammdaten',
    customFields: 'Individuelle Felder',
    moreSettings: 'Weitere Einstellungen',
  },

  attachhments: {
    title: 'Anhänge',
    invoceToggleLabel: 'Rechnungen/Abo-Rechnungen standardmäßig als PDF Anhang versenden',
    offerToggleLabel: 'Angebote standardmäßig als PDF Anhang versenden',
    updateSuccess: 'Deine Anhangseinstellungen wurden erfolgreich aktualisiert',
    modelTypes: {
      invoice: 'Rechnung',
      offer: 'Angebot',
      order: 'Auftrag',
      recurringInvoice: 'Abos',
    },
    modelTypeTitle: 'PDF-Dokument anhängen an',
    emailAttachmentDrawerTitle: 'E-Mail-Anhang',
    buttonTitle: 'PDF-Dokument',
    emailAttachmentDocumentTitle: 'E-Mail-Anhänge',
    emailAttachmentDocumentDescription: 'Hier hast Du die Möglichkeit zu definieren, welche PDF-Dokumente automatisch beim E-Mail-Versand eines Vorgangs angehängt werden sollen.',
  },

  tenantLogoSettings: {
    customerCenter: {
      description: `Lade Dein Logo hoch, um es im ${customerCenterName} anzuzeigen und somit Deinen ${customerCenterName} zu individualisieren. Ist bereits ein E-Mail-Logo hochgeladen, wird es hier angezeigt.`,
      title: `Logo für ${customerCenterName}`,
    },
    shareHub: {
      title: `${customerCenterName} einrichten`,
      explanationTitle: `${customerCenterName} - kurz erklärt`,
      explanationDescription1: `Mit dem orgaMAX ${customerCenterName} kannst Du bequem verschiedene Dokumente teilen. Dazu gehören Belege, Rechnungen, Angebote und Aufträge. Diese können nun digital über ein spezielles Portal an die Empfänger übermittelt werden.`,
      explanationDescription2: 'Dieses Portal bietet zahlreiche Möglichkeiten zur Ansicht und Interaktion. Hierzu gehören die Optionen zur Angebotsannahme, elektronischen Unterschrift und direkten Bezahlung. Durch die Nutzung dieser Funktionen wird die Kommunikation und Abwicklung Deiner Geschäftsprozesse effizienter gestaltet.',
      description: `Nutze die Vorteile des ${customerCenterName}s - mehr Zeit - weniger Kosten - ökologischen Fußabdruck verringern.`,
      progressBar: {
        logoLabel: 'Logo',
        logoSubLabel: 'hochladen',
        customerInfoLabel: 'E-Mail und Telefonnummer',
        customerInfoSubLabel: 'hinterlegt',
        payInfoLabel: 'Zahlungsmethoden',
        payInfoSubLabel: 'eingerichtet',
        fileInfoLabel: 'Zusätzliche Dokumente',
        fileInfoSubLabel: 'hinterlegt',
        counterLabel: 'von',
        counterSubLabel: 'Aufgaben erledigt',
        gamificationModal: {
          title: 'Danke für 1m² Wald',
          text1: `Mit der vollständigen Einrichtung des ${customerCenterName}s hast Du erreicht, dass 1m² Wald neu gepflanzt wird.`,
          text2: 'Gemeinsam mit unserem Partner WALDEMARIE unterstützen wir die großflächige Aufforstung von Kahlflächen.',
          text3: `Auch mit der Nutzung des ${customerCenterName}s kannst Du aktiv dazu beitragen.`,
          text4: 'Weitere Informationen hierzu findest Du unter folgendem Link:',
          text5: `Mit unserem ${customerCenterName} Digitalisierung vorantreiben, Zeit sparen und umweltbewusst handeln.`,
          moreLink: 'Mehr erfahren >',
          buttonTitle: 'Baumpflanz-Aktion',
          toastMessage: 'Danke für 1m² Wald',
        },
      },
    },
    email: {
      description: `Lade Dein Firmenlogo hoch, um es z.B in E-Mails oder ${customerCenterName} anzuzeigen und so ${productNameWithPrefix} zu individualisieren.`,
      title: 'Eigenes Logo hochladen',
    },
    toggleGroupTitle: 'Logo anzeigen in:',
    emailLabel: 'E-Mails',
    customerCenterLabel: customerCenterName,
    logo: {
      imageUploadSuccessMessage: 'Die Grafik wurde erfolgreich hochgeladen',
      imageUploadDefaultError: 'Beim Hochladen der Grafik ist ein Fehler aufgetreten',
      imageDeleteSuccessMessage: 'Die Grafik wurde erfolgreich gelöscht',
      imageDeleteDefaultError: 'Beim Löschen der Grafik ist ein Fehler aufgetreten',
      emailToggleActivationMessage: 'Das Logo wurde für den E-Mail-Versand aktiviert',
      emailToggleDeactivationMessage: 'Das Logo wurde für den E-Mail-Versand deaktiviert',
      customerCenterToggleActivationMessage: `Das Logo wurde für ${customerCenterName} aktiviert`,
      customerCenterToggleDeactivationMessage: `Das Logo wurde für ${customerCenterName} deaktiviert`,
      maxHeightImageError: 'Die Höhe von %filename überschreitet die zulässige maximale Höhe von %spx.',
      maxWidthImageError: 'Die Breite von %filename überschreitet die zulässige maximale Breite von %spx.',
      minHeightImageError: 'Die Höhe von %filename unterschreitet die zulässige minimale Höhe von %spx.',
      minWidthImageError: 'Die Breite von %filename unterschreitet die zulässige minimale Breite von %spx.',
      imageCropSelection: (height, width) => `Ausgewählte Bildgröße: ${width} x ${height} px`,
      imageCropError: 'Beim Zuschneiden der Grafik ist ein Fehler aufgetreten',
      imageUpload: 'Logo hochladen',
      selectUpload: 'Logo herziehen oder',
      selectUploadSpecial: ' auswählen',
      imageUploadHint: 'Bildgröße max. 1300px x 1300px, erlaubt sind jpg/jpeg, png Dateien. Das Bild wird für optimale Darstellung von 150px x 60px komprimiert.',
    },
  },

  customerCenterSettings: {
    contactInfo: {
      emailAddressLabel: 'Kontakt E-Mail-Adresse',
      phoneLabel: 'Kontakt Telefonnummer',
      emailAndPhoneUpdated: 'Telefonnummer und E-Mail erfolgreich gespeichert',
      emailUpdated: 'Die E-Mail-Adresse wurde erfolgreich gespeichert',
      phoneUpdated: 'Die Telefonnummer wurde erfolgreich gespeichert',
    },
    documentsUpload: {
      subText: `Du kannst Deinen Kunden AGB, Widerrufsbelehrung für Verbraucher oder einfach eine Preisliste im ${customerCenterName} zum Download zur Verfügung stellen. Vergib einen aussagekräftigen Namen für das Dokument z. B. AGB und lade es hoch, es steht dann automatisch im ${customerCenterName} zur Verfügung.`,
      uploadFilesCount: (count = 0, limit = 0) => `Hochgeladene Dokumente (${count}/${limit})`,
      uploadSuccessMessage: 'Dein Dokument wurde erfolgreich hochgeladen',
      deleteSuccessMessage: 'Das Dokument wurde erfolgreich gelöscht',
      invalidPdfFileTypeError: 'Bitte wähle ein .pdf Dokument aus',
      uploadButtonLabel: 'Weitere Dokumente',
      saveSucces: 'Dein Dokument wurde erfolgreich gespeichert',
    },
    autoEnablePaymentProviders: {
      headline: 'Zahlungsmethoden immer aktiv',
      subText: 'Damit Du in den Vorzug der schnellen Bezahlung kommst, kannst Du hier die aktivierten Zahlungsmethoden für neue Rechnungen und Aufträge direkt auf aktiv setzen.',
      toggleInvoicesText: 'Für <b>Rechnungen</b> immer als aktiv anzeigen',
      toggleOrdersText: 'Für <b>Aufträge</b> immer als aktiv anzeigen',
      activatePaymentProvidersSuccessMessage: 'Die Zahlungsmethoden wurden erfolgreich aktiviert.',
      deactivatePaymentProvidersSuccessMessage: 'Die Zahlungsmethoden wurden erfolgreich deaktiviert.',
      activatePaymentProvidersErrorMessage: 'Beim Aktivieren der Zahlungsmethoden ist ein Fehler aufgetreten.',
      deactivatePaymentProvidersErrorMessage: 'Beim Deaktivieren der Zahlungsmethoden ist ein Fehler aufgetreten.',
    },
    eSigning: {
      subText: 'Lass Deine Kunden Vorgänge, wie ein Angebot elektronisch unterschreiben - erhalte daraufhin eine Bestätigung inkl. Kopie der Unterschrift.',
      detailsHeadline: 'Elektronische Unterschrift für Angebote aktivieren',
      details: 'Sende die Angebote per Mail, bei aktivierter Funktion, wird bei Annahme des Angebots eine Unterschrift angefordert, diese kann direkt von Deinem Kunden digital gesetzt werden',
      buttonLabel: 'Aktivieren',
      toggleLabel: 'Als aktiv setzen',
    },
  },

  // settings - tax
  tax: {
    subHeadline: 'Diese Angaben, bis auf die USt.-IDNr. sind für die Abgabe der Umsatzsteuervoranmeldung relevant. Bitte gib Bundesland, Finanzamt und Steuernummer ein.',
    taxNumberOfficeInvalidHint: 'Die eingegebenen Daten sind nicht korrekt. Bitte überprüfe die Steuernummer und das Finanzamt.',
    taxOffice: 'Finanzamt',
    taxOfficeNumber: 'Finanzamtsnummer:',
    contact: 'Kontakt',
    federalState: 'Bundesland',
  },

  // settings - bank data
  bankDetailsSuccessMessage: 'Deine Bankverbindung wurde erfolgreich aktualisiert.',
  bankDetailsErrorMessage: 'Deine Bankverbindung konnte nicht aktualisiert werden!',

  // settings - document export
  documentExport: {
    createSuccess: 'Der Steuerberaterexport wurde gestartet.',
    createError: 'Der Steuerberaterexport konnte nicht gestartet werden.',
    hasClientError: ' Überprüfe bitte die Mandantennummer.',
    hasConsultantError: ' Überprüfe bitte die Beraternummer.',
    hasConsultantClientError: ' Überprüfe bitte Berater- und Mandantennummer.',
    sendSuccess: 'Der Steuerberaterexport wurde erfolgreich versendet.',
    sendError: 'Der Steuerberaterexport konnte nicht versendet werden. Bitte versuche es erneut.',
    fetchingErrorMessage: 'Beim Laden der Steuerberaterexporte ist ein Fehler aufgetreten',
    sendModalTitle: 'Daten übermitteln',
    heading: 'Steuerberaterexport',
    content: {
      documents: 'Belege',
      invoice: 'Ausgangsrechnungen & Rechnungskorrekturen',
      invoice_documents: 'Ausgangsrechnung',
      cancellation: 'Stornierungen & Gutschriften',
      expense: 'Ausgaben',
      expense_documents: 'Ausgaben',
      revenue: 'Sonstige Einnahmen',
      revenue_documents: 'Sonstige Einnahmen',
      batches_of_entries: 'Buchungsstapel',
      customers_vendors: 'Debitoren/Kreditoren',
      account_labels: 'Sachkontenbeschriftungen',
      [DatevExportType.invoices]: 'Rechnungen (Ausgangsrechnungen, Rechnungskorrekturen, Gutschriften & Storni)',
      [DatevExportType.expenses]: 'Ausgaben & Sonstige Einnahmen ',
      [DatevExportType.bankBookings]: 'Zahlungen (Bank, Kasse & Verrechnungskonto)',
      [DatevExportType.customersVendors]: 'Stammdaten (Kunden & Lieferanten)',
      documentTypes: {
        [DocumentExportType.receipt]: 'Keine Art zugewiesen',
        [DocumentExportType.incomingInvoice]: 'Dokumente Rechnungseingang',
        [DocumentExportType.invoice]: 'Dokumente Rechnungsausgang',
        [DocumentExportType.invoiceCorrection]: 'Dokumente Sonstiges',
        [DocumentExportType.withdrawal]: 'Dokumente Entnahme',
        [DocumentExportType.deposit]: 'Dokumente Einlage',
        [DocumentExportType.contract]: 'Dokumente Vertrag',
        [DocumentExportType.xReport]: 'Dokumente X-Bericht',
        [DocumentExportType.zReport]: 'Dokumente Z-Bericht',
        [DocumentExportType.cancellation]: 'Dokumente Stornorechnung',
      },
    },
    upgradeText: `${unlockViewUpgradeText} den Steuerberaterexport freizuschalten.`,
    transferProtocol: 'Übertragungsprotokoll',
    fixErrors: {
      withResolutionMessage: 'Bitte starte den Export erneut, um zu prüfen, ob alle Dokumente erfolgreich übertragen werden.',
      withoutResolutionMessage: 'Es wurden keine Änderungen vorgenommen.',
      fixNowButton: 'Jetzt beheben',
      drawerTitle: 'Fehler beheben',
      stepOfLabel: (current, total) => `Dokument ${current} von ${total}`,
      introText: `Einige Dokumente konnten nicht an DATEV übertragen werden.
</br></br>
Klicke auf „Weiter“, um die fehlgeschlagenen Dokumente und die jeweiligen Fehler anzuzeigen.
`,
      introHint: 'Nachdem Du die Fehler behoben hast, starte den Export erneut. So kannst Du prüfen, ob alle Dokumente erfolgreich übertragen werden.',
      failedDocumentsCount: (count) => `<b>fehlgeschlagene Dokumente:</b> ${count}`,
      categories: {
        expenses: 'Ausgaben',
        revenues: 'Sonstige Einnahmen',
        invoices: 'Rechnungen',
        others: 'Sonstige',
      },
      errorCodeLabel: 'Fehlercode',
      errorUriLabel: 'Weitere Informationen in der DATEV-Hilfe',
      errorDescriptionLabel: 'Fehlerbeschreibung',
      duplicateHint: 'Diese Datei ist bereits in DATEV vorhanden. Du kannst zwischen folgenden Optionen wählen:',
      renameCaption: 'Datei umbenennen',
      renameHint: 'Benenne die Datei um, wenn Du sie erneut an DATEV übertragen möchtest.',
      renameSuccess: 'Die Datei wurde erfolgreich umbenannt.',
      renameError: 'Die Datei konnte nicht umbenannt werden. Bitte versuche es erneut.',
      deleteButton: 'Datei löschen',
      deleteCaption: 'Datei löschen',
      deleteHint: 'Lösche die Datei, wenn sie versehentlich doppelt vorhanden ist.',
      deleteConfirmTitle: 'Datei löschen',
      deleteConfirmDescription: (name) => `Möchtest Du die Datei "${name}" wirklich löschen?`,
      deleteSuccess: 'Die Datei wurde gelöscht.',
      deleteError: 'Die Datei konnte nicht gelöscht werden. Bitte versuche es erneut.',
      deleteAssignedError: 'Die Datei kann nicht gelöscht werden, da eine Verknüpfung besteht.',
      openExpenseInNewTab: 'Ausgabe in neuem Tab öffnen',
      openRevenueInNewTab: 'Sonstige Einnahme in neuem Tab öffnen',
      flagButton: 'Von DATEV-Übertragung ausschließen',
      flagCaption: 'Von DATEV-Übertragung ausschließen',
      flagHint: 'Schließe die Datei von der DATEV-Übertragung aus, wenn Du sie nicht erneut übertragen möchtest.',
      flagSuccess: 'Die Datei wird nicht mehr an DATEV übertragen.',
      flagError: 'Die Datei konnte nicht ausgeschlossen werden. Bitte versuche es erneut.',
      fileExcluded: 'Von der DATEV-Übertragung ausgeschlossen.',
      alreadyExcludedHint: 'Diese Datei wird nicht an DATEV übertragen. Du kannst sie wieder für den DATEV-Sync aktivieren.',
      includeButton: 'Wieder für DATEV aktivieren',
      includeSuccess: 'Die Datei wird wieder an DATEV übertragen.',
      includeError: 'Die Datei konnte nicht wieder aktiviert werden. Bitte versuche es erneut.',
      manualFixHint: 'Bitte behebe den Fehler manuell und starte anschließend einen neuen Export.',
      fileNotFound: 'Die Datei wurde nicht gefunden. Möglicherweise wurde sie bereits gelöscht.',
      fileDeleted: 'Die Datei wurde gelöscht.',
      fileRenamed: 'Die Datei wurde umbenannt und wird erneut übertragen.',
      back: 'Zurück',
      next: 'Weiter',
      done: 'Fertig',
    },
    willBeCreated: 'Wird erstellt',
    noRowsToShow: 'Keine Exporte vorhanden',
    loadingOoo: 'Exporte werden geladen',
    forms: {
      exportDatev: {
        title: 'Steuerberaterexport',
        title2: 'DATEV-Format',
        list: 'Buchungssätze',
        subTitle: (link) => `Diese Auswahl exportiert Deine Buchungssätze als CSV-Dateien für den gewählten Zeitraum. Dein Steuerberater importiert diese CSV-Dateien in seinem Finanzbuchhaltungssystem (z.B. DATEV, Agenda, Simba, ADDISON oder Stotax). Deine Belege kannst Du im <a href="${link}">Bereich Exporte</a> zusätzlich herunterladen.`,
        subTitleTaxConsultant: 'Es können im DATEV-Format und angebunden an das DATEV-Rechenzentrum verschiedene Exporte zu gewünschten Zeiträumen erzeugt werden. Diese können in das verwendete Finanzbuchhaltungssystem eingelesen werden.',
        navtitle: 'Steuerberaterexport',
        navtext: 'Exportiere Deine Buchungssätze als CSV-Dateien im DATEV-Format für den gewählten Zeitraum.',
      },
      exportDatevView: {
        title: 'DATEV-Datenservices',
        subTitle: `Verbinde ${productNameWithPrefix} mit dem DATEV Belegbilderservice Rechnungswesen und dem DATEV Buchungsdatenservice. So überträgst Du Belege und Buchungsdaten direkt an Deinen Steuerberater. Du entscheidest, ob nur Belege oder zusätzlich die zugehörigen Buchungsdaten übertragen werden. Mit der automatischen Übertragung sparst Du Dir manuelle Arbeit.`,
        faqLabel: 'Weitere Informationen zur Einrichtung für Deinen Steuerberater findest Du in unserem',
        faqLink: 'FAQ-Beitrag',
        navtitle: 'DATEV-Datenservices',
        navtext: 'Mit einem Klick Belege, Buchungssätze und Banktransaktionen an Deinen DATEV-Steuerberater senden.',
        buttonTitle: 'DATEV-Datenservices aktivieren',
        documentTitle: 'DATEV-Cloud-DATEV-Datenservices Rechnungswesen',
      },
      export: {
        title: 'Dokumentenexport',
        subTitle: 'Erzeuge eine ZIP-Datei, die alle Dokumente für den gewählten Zeitraum beinhaltet.',
        navtitle: 'Dokumentenexport',
        navtext: 'Exportiere alle Dokumente je Art für den gewählten Zeitraum.',
        documentTypes: 'Art der Dokumente',
        periodLabel: 'Bestimme Deinen Export-Zeitraum',
        periodPlaceholder: 'Zeitraum wählen',
      },
      exportList: {
        title: 'Erzeugte Exporte',
        navtitle: 'Erzeugte Exporte',
        navtext: 'In dieser Liste kannst Du die Protokolle der DATEV-Übertragungen einsehen und Steuerberaterexporte herunterladen.',
        loadingTitle: 'Steuerberaterexporte werden geladen',
      },
      zReportList: {
        title: 'Kasse',
        navtitle: 'Kasse',
        cashInfo: {
          title: 'Kasseninformation',
          brand: 'Marke',
          model: 'Model',
        },
        cashClosings: {
          title: 'Kassenabschlüsse',
          filterTitle: 'Ansicht',
          loadingOoo: 'Kassenabschlüsse werden geladen',
          noRowsToShow: 'Keine Kassenabschlüsse gefunden',
          create: 'Erstellen',
          rebuild: 'Neu erstellen',
          columns: {
            name: 'Name',
            date: 'Datum',
            nr: 'Nr',
            userName: 'Ersteller',
            nominalBalance: 'Nominal',
            actualBalance: 'Soll',
            difference: 'Differenz',
            tssStatus: 'Status',
          },
        },
        dSFinVKExportList: {
          title: 'DSFinV-K Exporte',
          navtitle: 'DSFinV-K Exporte',
          loadingOoo: 'DSFinV-K Exporte werden geladen',
          noRowsToShow: 'DSFinV-K Exporte gefunden',
          columns: {
            state: 'Status',
            createdTime: 'Erstellt',
            startTime: 'Startdatum',
            complitionDate: 'Enddatum',
            expirationDate: 'Ablaufdatum',
            download: 'Herunterladen',
          },
        },
      },
    },
  },

  // settings - delivery conditions
  deliveryConditionDescriptionLine1: 'Lieferbedingungen sind Textbausteine, die Du in Angeboten und Aufträgen verwenden kannst.',
  deliveryConditionDescriptionLine2: 'Gib einfach den Text der jeweiligen Lieferbedingung hier ein und lege einen Namen für den Textbaustein fest, um diesen in Angeboten oder Aufträgen identifizieren zu können.',
  deliveryConditionDefaultDescription: 'Lege hier fest, welche Lieferbedingung standardmäßig in neuen Angeboten verwendet werden soll:',
  deliveryConditionChangeDefaultSuccessMessage: 'Die Standard Lieferbedingung wurde erfolgreich geändert.',
  deliveryConditionCreateSuccessMessage: 'Die neue Lieferbedingung wurde erfolgreich gespeichert.',
  deliveryConditionDeleteErrorMessage: 'Beim Löschen der Lieferbedingung ist ein Fehler aufgetreten.',
  deliveryConditionDeleteDefaultErrorMessage: 'Du kannst die Standard Lieferbedingung nicht löschen.',
  deliveryConditionDeleteSuccessMessage: 'Die ausgewählte Lieferbedingung wurde erfolgreich gelöscht.',
  deliveryConditionSaveSuccessMessage: 'Die Änderungen wurde erfolgreich gespeichert.',
  deliveryConditionHeading: 'Lieferbedingungen',
  deliveryConditionHeadingSingular: 'Lieferbedingung',

  // settings - pay conditions
  payConditionDescriptionLine1: 'Zahlungsbedingungen legen das Zahlungsziel und Textbausteine fest, die Du z. B. in Rechnungen verwenden kannst.',
  payConditionDescriptionLine2: 'Gib einfach das Zahlungsziel und den Text der jeweiligen Zahlungsbedingung hier ein und lege einen Namen dafür fest, um diese z. B. in Rechnungen identifizieren zu können.',
  payConditionTransactionDescription: 'Zahlungsbedingungen enthalten Angaben zur Zahlung. Hinterlegt werden können ein Name, ein Zahlungsziel, ein Text für Vorgänge sowie die Kennzeichnung als Standard-Zahlungsbedingung.',
  payConditionChangeDefaultSuccessMessage: 'Die Standard Zahlungsbedingung wurde erfolgreich geändert.',
  payConditionCreateSuccessMessage: 'Die neue Zahlungsbedingung wurde erfolgreich gespeichert.',
  payConditionDeleteDefaultErrorMessage: 'Du kannst die Standard Zahlungsbedingung nicht löschen.',
  payConditionDeleteSuccessMessage: 'Die ausgewählte Zahlungsbedingung wurde erfolgreich gelöscht.',
  payConditionDeleteErrorMessage: 'Beim Löschen der Zahlungsbedingung ist ein Fehler aufgetreten.',
  payConditionIsDefaultDeleteErrorMessage: 'Zahlungsbedingungen, die als Standard festgelegt sind, können nicht gelöscht werden. Bitte wähle zunächst eine andere Zahlungsbedingung als Standard aus.',
  payConditionSaveSuccessMessage: 'Die Änderungen wurden erfolgreich gespeichert.',
  paymentConditionHeading: 'Zahlungsbedingungen',
  paymentConditionHeadingSingle: 'Zahlungsbedingung',
  payConditionNotSelectedDescription: 'Du hast keine Zahlungsbedingung gewählt. Daher wird dieser Abschnitt nicht in der Ausgabe erscheinen.',
  payConditionNotDescriptionForSelected: (name) => `Du hast die Zahlungsbedingung "${name}" gewählt und keinen Text angegeben. Daher wird dieser Abschnitt nicht in der Ausgabe erscheinen.`,

  timeTrackingHourlyRatesHeader: 'Stundensatz',

  // settings - extensions
  extensionsSaveSuccessMessage: 'Die Änderungen wurden erfolgreich gespeichert.',
  extensionsHeading: 'Erweiterungen',
  extensionsLoading: 'Lade Erweiterungen',

  // settings - shops
  shopCreateSuccessMessage: 'Dein Webshop wurde erfolgreich angelegt',
  shopSaveSuccessMessage: 'Deine Webshop Einstellungen wurden erfolgreich aktualisiert',
  shopFetchErrorMessage: 'Beim Laden der Webshops ist ein Fehler aufgetreten.',
  shopSettingsDescription1: 'Verknüpfe orgaMAX mit Webshops, um Daten auszutauschen.',
  shopSettingsDescription2: 'Du benötigst dafür lediglich die webshop-spezifischen Zugangsdaten.',
  shopDeleteSuccessMessage: 'Der Webshop wurde erfolgreich gelöscht.',
  shopDeleteErrorMessage: 'Beim Löschen ist ein Fehler aufgetreten.',
  shopLoaderText: 'Lädt Webshops',
  shopAddCTA: 'Webshop',

  // settings - billing data form
  billingDataForm: {
    title: 'Abrechnungsinformationen',
    subTitle: 'Zahle bequem per Lastschrift. Deine Rechnung erhältst Du per E-Mail',
    accountHolderLabel: 'Kontoinhaber',
    ibanLabel: 'IBAN',
    bicLabel: 'BIC',
    sepaHint: 'Mit Klick auf "Speichern" ermächtige ich die deltra Business Software GmbH & Co. KG, Zahlungen von meinem Konto mittels SEPA-Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von Ihnen auf mein Konto gezogenen Lastschriften einzulösen. Ich kann innerhalb von 8 Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Dabei gelten die mit meinem Kreditinstitut vereinbarten Bedingungen. Gläubiger-Identifikationsnummer der deltra Business Software GmbH & Co. KG: DE96ZZZ00000024237.',
    saveSuccess: 'Deine Abrechnungsdaten wurden gespeichert',
    creditInstitution: 'Kreditinstitut',
    creditInstitutionUnknown: 'wird nur für deutsche IBANs ermittelt',
    billingAddressAndContactPersonTitle: 'Rechnungsadresse',
  },

  // ecommerce
  eCommerce: {
    menuLabel: 'E-Commerce',
    pageTitle: 'E-Commerce',
    noShopConnected: 'Aktuell sind keine Shopanbindungen vorhanden',
    toggle: {
      invoice: 'Automatische Rechnungserstellung',
      invoiceSubText: 'Nachdem Du in Deinen Aufträgen die Bestellungen aus Deinem E-Commerce Shop importiert hast, werden zu diesen Aufträgen automatisch Rechnungen erzeugt, ohne dass Du dies manuell anstoßen musst.<br/>Du siehst die erstellten Rechnungen wie gewohnt in der Auftragsliste und in der Rechnungsliste.',
      invoiceEmail: 'Automatischer Rechnungsversand',
      invoiceEmailSubText: 'Diese Funktion kann nur aktiviert werden, wenn Du auch die automatische Rechungsstellung aktiviert hast.<br/>Hierdurch werden Deine automatisch erstellen Rechnungen (aus Deinen E-Commerce Bestellungen) automatisch per E-Mail an Deine Shopkunden versendet, ohne dass Du hierfür manuell die Rechnungen einzeln bearbeiten musst.',
      deliveryNote: 'Automatische Lieferscheinerstellung',
      deliveryNoteSubText: 'Nachdem Du in Deinen Aufträgen die Bestellungen aus Deinem E-Commerce Shop importiert hast, werden zu diesen Aufträgen automatisch Lieferscheine erzeugt, ohne dass Du dies manuell anstoßen musst.<br/>Du siehst diese erstellten Lieferscheine wie gewohnt in der Auftragsliste und in der Lieferscheinliste.',
    },
    modal: {
      fieldLabels: {
        typeSelect: 'Typ',
        description: 'Bezeichnung',
      },
      activeToggleLabel: 'aktiv',
      createWebshop: 'Webshop erstellen',
      editWebshop: 'Webshop bearbeiten',
    },
    eCommerceForm: {
      edit: 'Identifikation bearbeiten',
      add: 'Identifikation hinzufügen',
      shopName: 'Webshop*',
      reference: 'Webshop-Referenz*',
      referenceArticle: 'Webshop-Referenz / SKU*',
    },
    updateScope: 'Update vorhanden',
    active: 'aktiv',
    inactive: 'inaktiv',
    shopNames: {
      shopify: 'Shopify',
      shopware: 'Shopware',
      amazon: 'amazon',
      kaufland: 'Kaufland',
      ebay: 'ebay',
      magento: 'Magento',
      woocommerce: 'WooCommerce',
    },
    shopDataInvalidErrorMessage: 'Beim Speichern der Daten ist ein Problem aufgetreten. Bitte überprüfe Deine Eingaben.',
    shopAlreadyExistsErrorMessage: 'Dieser Shop wurde bereits eingerichtet.',
    linkError: 'Dein Shop konnte nicht verknüpft werden.',
    upgradeText: `${unlockViewUpgradeText} E-Commerce freizuschalten.`,
    orderShopInformationBox: {
      labels: {
        customerReference: 'Kundenreferenz',
        deliveryAddress: 'Lieferadresse',
        orderDate: 'Auftragsdatum',
        orderNumber: 'Bestellnummer',
        payCondition: 'Zahlungsart',
        paymentState: 'Zahlungstatus',
        transactionId: 'Transaktions-Id',
      },
      linkToOrderInShopTooltip: 'Bestellung im Shop öffnen',
      fulfillmentStates: {
        fulfilled: 'Abgeschlossen',
      },
      syncedAtPrefix: 'Zul. aktual.: ',
      noData: 'Es sind keine Daten vorhanden.',
      pos: 'Ladenverkauf',
      sourcePrefix: 'Informationen von',
      paymentMethods: {
        bankDeposit: 'Vorkasse',
        cod: 'Nachnahme', // cod = cash on delivery
        exchangeCredit: 'Umtausch',
        giftcard: 'Gutschein',
        refund: 'Erstattung / Retoure',
        shopifyCashPOS: 'Barzahlung Shopify POS',
        shopifyPayments: 'Shopify Payments',
        paypal: 'Paypal',
      },
    },
  },

  // carriers
  carriers: {
    upgradeText: `${unlockViewUpgradeText} Versanddienstleister freizuschalten.`,
  },

  // settings - text modules
  textModulePlaceholderIntroductionText: 'Gib hier den Einleitungstext für %s ein',
  textModulePlaceholderConclusionText: 'Gib hier den Schlusstext für %s ein',
  textModulesPlaceholderEmailText: 'Gib hier den Standard-Text für den E-Mail-Versand ein',
  textModuleSaveSuccessMessage: 'Textbausteine erfolgreich aktualisiert',
  textModuleSaveErrorMessage: 'Beim Speichern der Textbausteine ist ein Fehler aufgetreten',
  textModuleHeading: 'Textbausteine',
  textModulesLoading: 'Lade Textbausteine',
  changeBccSelfSuccessMessage: 'Deine Einstellungen wurden erfolgreich aktualisiert.',
  textModules: {
    menuDescription1: 'Wähle aus unseren Textbausteinen und passe diese Deinen Bedürfnissen entsprechend an, oder erstelle neue Textbausteine pro Vorgangstyp wie zum Beispiel Angebot, Auftrag, Rechnung.',
    menuDescription2: 'Du kannst die Textbausteine direkt in den aktuellen Vorgang übernehmen. Textbausteine mit (System) können nicht gelöscht werden.',
    emailPlaceholder: 'E-Mail Betreff',
    titlePlaceholder: 'Betreff im Vorgang (z.B. Rechnung)',
    chooseTooltip: 'In aktuellen Vorgang übernehmen',
    saveAndApplyButtonLabel: 'Speichern & übernehmen',
    saveDisabledHint: 'Bitte fülle zuerst alle Pflichtfelder aus.',
    addNew: 'Neuen Textbaustein hinzufügen',
    title: 'Textbausteine',
    createSuccessMessage: 'Der neue Textbaustein wurde erfolgreich gespeichert.',
    updateSuccessMessage: 'Die Änderungen wurden erfolgreich gespeichert.',
    deleteSuccessMessage: 'Der ausgewählte Textbaustein wurde erfolgreich gelöscht.',
    deleteErrorMessage: 'Beim Löschen des Textbausteins ist ein Fehler aufgetreten.',
    deleteIsSystemErrorMessage: 'Du kannst systemseitige Textmodule nicht löschen.',
    deleteButtonTooltip: 'Systemseitige Textmodule können nicht gelöscht werden',
    edit: 'Textbaustein bearbeiten',
    create: 'Neuer Textbaustein',
    createButton: 'Textbaustein erstellen',
    loader: 'Lädt Textbausteine',
    defaultTypeChangeError: (typeLabel) => `Die Vorgangsart kann nicht geändert werden, da dieses Textmodul der Standard für ${typeLabel} ist.`,
    deleteModalTitle: 'Textbaustein löschen?',
    deleteModal: (name) => `Möchtest Du den Textbaustein <b>${name}</b> wirklich löschen ?`,
    typeLabel: 'Vorgangsart',
    description: 'Erstelle Vorlagen & individualisiere die Textbausteine für sämtliche Vorgangsarten',
    types: {
      [TextModuleTypes.DELIVERY_NOTE]: 'Lieferscheine',
      [TextModuleTypes.ORDER]: 'Aufträge',
      [TextModuleTypes.INVOICE]: 'Rechnungen',
      [TextModuleTypes.RECURRING_INVOICE]: 'Abo-Rechnungen',
      [TextModuleTypes.OFFER]: 'Angebote',
      [TextModuleTypes.CANCELLATION]: 'Stornorechnungen',
      [TextModuleTypes.CREDIT_NOTE]: 'Gutschriften',
      [TextModuleTypes.PAYMENT_REMINDER]: 'Zahlungserinnerung',
      [TextModuleTypes.FIRST_REMINDER]: '1. Mahnung',
      [TextModuleTypes.SECOND_REMINDER]: '2. Mahnung',
      [TextModuleTypes.LAST_REMINDER]: '3. Mahnung',
      [TextModuleTypes.LETTER]: 'Geschäftsbriefe',
      [TextModuleTypes.PURCHASE]: 'Bestellungen',
      [TextModuleTypes.PARTIAL_INVOICE]: 'Teilrechnungen',
      [TextModuleTypes.MANUAL_CREDIT_NOTE]: 'Rechnungskorrekturen/Gutschriften',
      [TextModuleTypes.DIRECT_DEBIT_MANDATE_ACTIVATED]: 'SEPA-Lastschrift aktiviert',
      [TextModuleTypes.DIRECT_DEBIT_MANDATE_PENDING]: 'SEPA-Lastschrift ausstehend',
      [TextModuleTypes.DIRECT_DEBIT_MANDATE_CANCELLED]: 'SEPA-Lastschrift storniert',
      [TextModuleTypes.DIRECT_DEBIT_PRE_NOTIFICATION]: 'SEPA-Lastschrift Vorankündigung',
      [TextModuleTypes.DIRECT_DEBIT_MANDATE_CREDITOR_IDENTIFIER_UPDATED]: 'SEPA-Lastschrift mit neuer Gläubiger-Identifikationsnummer',
    },
    inputLabels: {
      asDefault: 'Als Standard festlegen',
      bccSelf: 'Standardmäßig auch an mich versenden',
      name: 'Name',
      type: 'Vorgangsart',
      transactionTitle: 'Vorgangstitel',
      introduction: 'Einleitungstext',
      conclusion: 'Schlusstext',
      email: 'E-Mail-Versand',
      emailSubject: 'E-Mail-Betreff',
      useCustomerAddress: 'Direktlieferung an Kunden',
    },
    info: {
      useCustomerAddressTooltip: 'Diese Option sollte aktiviert werden, wenn die Adresse eines Kunden als Lieferadresse in der Bestellung verwendet werden soll. Die Auswahl von Kunde und Adresse erfolgt im Vorgang. Wird kein Kunde ausgewählt, erscheint diese Option nicht auf der Bestellung.',
    },
    emailPreviewSuccess: 'Die E-Mail wurde erfolgreich versendet.',
    emailPreviewError: 'Beim Versenden der E-Mail ist ein Fehler aufgetreten.',
    directDebitPlaceholders: {
      customerName: 'Kundenname',
      mandateReferenceNumber: 'Mandat Referenz',
      creditorIdentifier: 'Gläubiger-Identifikationsnummer',
      mandateType: 'Mandatstyp',
      replyToEmail: 'Antwort E-Mail-Adresse',
    },
  },

  // settings - dunningSettings
  dunningSettingsDescriptionLine1: 'Um überfällige Zahlungen anzumahnen, kannst Du bis zu 4 Mahnstufen aktivieren. Lege für jede Mahnstufe fest, nach wie vielen Tagen nach der letzten erstellten Mahnung die nächste Mahnstufe vorgeschlagen werden soll.',
  dunningSettingsDescriptionLine2: 'Darüber hinaus kannst Du für jede Mahnstufe die Höhe der Mahngebühren und die Textbausteine, die in der Mahnung verwendet werden sollen, individuell festlegen.',
  dunningSettingsSaveSuccessMessage: 'Die Änderungen wurden erfolgreich gespeichert.',
  dunningSettingsSaveErrorMessage: 'Beim Speichern der Änderungen ist ein Fehler aufgetreten.',
  dunningItem: {
    labels: {
      introduction: 'Einleitungstext',
      conclusion: 'Schlusstext',
      email: 'E-Mail-Versand',
    },
    dueDate: 'Fälligkeitsdatum',
    show: 'einblenden',
    hide: 'ausblenden',
    overdueFee: 'Mahngebühren',
    activateDunningLevel: (label) => `Mahnstufe für ${label} aktivieren`,
    createdAt: 'Erstellung',
    proposeAfter: (dateLabel) => `Tage nach ${dateLabel} vorschlagen`,
    introductionPlaceholder: (label) => `Gib hier Deinen Einleitungstext für die ${label} ein`,
    conclusionPlaceholder: (label) => `Gib hier Deinen Schlusstext für die ${label} ein`,
    emailPlaceholder: (label) => `Gib hier Deinen Schlusstext für die ${label} ein`,
    textModule: (textModuleToggleLabel) => `Textbausteine ${textModuleToggleLabel}`,
  },

  // settings - more settings
  moreSettingsHeading: 'Weitere Einstellungen',

  numerationStartvalueHeading: 'Startwert festlegen',
  numerationSaveSuccess: 'Die Nummernkreise wurden erfolgreich gespeichert.',
  numerationSaveError: 'Beim Speichern der Nummernkreise ist ein Fehler aufgetreten.',
  numerationSaveStartValueSuccess: 'Der Startwert wurde aktualisiert. Bitte speichere den Nummernkreis, um den Startwert auf dem Server zu aktualisieren!',
  changeInvoiceNumberSuccessMessage: 'Die Rechnungs- und Angebotsnummer wurden erfolgreich geändert.',
  changeInvoiceNumberErrorMessage: 'Die Rechnungs- oder Angebotsnummer konnte nicht geändert werden.',
  numerationCounterLengthTooSmall: 'Der Anzahl der ausgewählten Stellen der %s ist zu niedrig. Bitte wähle einen höheren Wert.',
  numerationStartValueTooSmall: 'Der Startwert der %s wurde bereits verwendet. Bitte wähle einen höheren Wert.',
  invoiceNumerationNumberError: 'Es existiert bereits eine Rechnung oder ein Storno mit einer höheren Nummer. Bitte wähle einen größeren Wert aus.',
  offerNumerationNumberError: 'Es existiert bereits ein Angebot mit einer höheren Angebots-Nr. Bitte wähle einen größeren Wert aus.',
  deliveryNoteNumerationNumberError: 'Es existiert bereits ein Lieferschein mit einer höheren Lieferschein-Nr. Bitte wähle einen größeren Wert aus.',
  orderNumerationNumberError: 'Es existiert bereits ein Auftrag mit einer höheren Auftrags-Nr. Bitte wähle einen größeren Wert aus.',
  purchaseNumerationNumberError: 'Es existiert bereits eine Bestellung mit einer höheren Bestell-Nr. Bitte wähle einen größeren Wert aus.',
  automatedDataTransfer: 'Datenübernahme zwischen Vorgängen',
  // numeration
  numerationValuesExceeded: {
    caption: (numerationType) => `Nummernkreis ${numerationType ? `für ${numerationType}` : ''} überschritten`,
    message: (numerationType) => `Der Nummernkreis für ${numerationType ? `${numerationType} ` : 'diesen Vorgang'} ist erschöpft. Es können keine weiteren Vorgänge (z.B. Angebote, Aufträge, Lieferscheine oder Rechnungen) erstellt werden. <br/><br/> Bitte erweitere den Nummernkreis in den Einstellungen, um wieder neue Vorgänge anlegen zu können.`,
    confirmButtonLabel: 'Nummernkreis anpassen',
  },
  numeration: {
    date: {
      empty: 'Keine',
      year: 'Jahr',
      month: 'Jahr + Monat',
      day: 'Jahr + Monat + Tag',
    },
    prefixError: 'Die letzte Stelle des Präfix darf nicht numerisch sein!',
    suffixError: 'Die erste Stelle des Suffix darf nicht numerisch sein!',
    lengthHint: {
      year: 'pro Jahr',
      month: 'pro Monat',
      day: 'pro Tag',
      hint: (count, relationKind, per) => {
        return `Mit dieser Nummernkreis-Einstellung kannst Du max. ${count} ${relationKind} ${per} schreiben.`
      },
    },
    invoices: 'Rechnungen',
    offers: 'Angebote',
    deliveryNotes: 'Lieferscheine',
    orders: 'Aufträge',
    purchases: 'Bestellungen',
    docType: {
      invoice: 'Rechnungs',
      offer: 'Angebots',
      deliveryNote: 'Lieferschein',
      order: 'Auftrags',
      purchase: 'Bestellungs',
    },
    numerationError: (numerationErrorPrefix, docType, numerationErrorSuffix) => `Beim Speichern des ${numerationErrorPrefix} ${docType}${numerationErrorSuffix}-Nummernkreises ist ein Fehler aufgetreten`,
  },

  // settings/mycompany/bookkeepingAccountRefresh
  bookkeepingAccount: {
    title: 'Buchungssätze erzeugen',
    subtitle: 'Hier kannst Du die Buchungssätze neu erzeugen.',
    recreateButtonLabel: 'Buchungssätze neu erzeugen',
    started: 'Neuaufbau der Buchungssätze gestartet',
    inProgress: 'Neuaufbau der Buchungssätze wurde bereits gestartet',
    done: 'Die Buchungssatzerzeugung ist erfolgreich abgeschlossen worden.',
    error: 'Neuaufbau der Buchungssätze leider fehlgeschlagen',
    longer: 'Neuaufbau der Buchungssätze dauert etwas länger,',
  },

  // select/tag input messages
  tagSaveErrorMessage: 'Beim Speichern ist ein Fehler aufgetreten',
  tagAddSuccessMessage: '%s %s wurde erfolgreich hinzugefügt',
  tagAddErrorMessage: 'Beim Hinzufügen ist ein Fehler aufgetreten',
  tagDeleteSuccessMessage: '%s %s wurde erfolgreich gelöscht',
  tagDeleteErrorMessage: 'Beim Löschen ist ein Fehler aufgetreten',
  tagUpdateSuccessMessage: '%s wurde erfolgreich geändert',
  tagUpdateErrorMessage: 'Beim Ändern ist ein Fehler aufgetreten',
  tagExistsMessage: '%s %s ist bereits vorhanden',
  tagDefaultHintMessage: 'Tippe einfach in das Feld, um neue %s hinzuzufügen. Mit einem Klick auf einen bestehenden Eintrag kannst Du diese umbenennen. Durch Ziehen eines Eintrags an eine andere Position änderst Du die Reihenfolge.',
  tagReplaceErrorMessage: (type, tagToDelete) => `Beim Ersetzen der ${type} ${tagToDelete} ist ein Fehler aufgetreten`,

  tagsSalutationAddSuccessMessage: 'Anrede <i>[tagName]</i> wurde hinzugefügt',
  tagsSalutationDeleteSuccessMessage: 'Anrede <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsSalutationExistsMessage: 'Die Anrede <i>[tagName]</i> ist bereits vorhanden',

  tagsTitleAddSuccessMessage: 'Titel <i>[tagName]</i> wurde hinzugefügt',
  tagsTitleDeleteSuccessMessage: 'Titel <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsTitleExistsMessage: 'Der Titel <i>[tagName]</i> ist bereits vorhanden',

  tagsPositionAddSuccessMessage: 'Position <i>[tagName]</i> wurde hinzugefügt',
  tagsPositionDeleteSuccessMessage: 'Position <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsPositionExistsMessage: 'Die Position <i>[tagName]</i> ist bereits vorhanden',

  tagsArticleUnitAddSuccessMessage: 'Einheit <i>[tagName]</i> wurde hinzugefügt',
  tagsArticleUnitDeleteSuccessMessage: 'Einheit <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsArticleUnitExistsMessage: 'Die Einheit <i>[tagName]</i> ist bereits vorhanden',

  tagsCustomerCategoryAddSuccessMessage: 'Kundenkategorie <i>[tagName]</i> wurde hinzugefügt',
  tagsCustomerCategoryDeleteSuccessMessage: 'Kundenkategorie <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsCustomerCategoryExistsMessage: 'Die Kundenkategorie <i>[tagName]</i> ist bereits vorhanden',

  tagsArticleCategoryAddSuccessMessage: 'Artikelkategorie <i>[tagName]</i> wurde hinzugefügt',
  tagsArticleCategoryDeleteSuccessMessage: 'Artikelkategorie <i>[tagName]</i> wurde erfolgreich gelöscht',
  tagsArticleCategoryExistsMessage: 'Die Artikelkategorie <i>[tagName]</i> ist bereits vorhanden',

  tag: {
    label: 'Tags',
    kindLabel: 'Art',
    placeholder: 'Tag hinzufügen',
    placeholderMultiple: 'Tags hinzufügen',
    assignTag: 'Tag zuweisen',
    enterTagName: 'Tagnamen eingeben',
    createLabel: (label) => `Tag '${label}' erstellen`,
    edit: 'Tag bearbeiten',
    editSuccessMessage: 'Das Tag wurde erfolgreich gespeichert.',
    editErrorMessage: 'Beim Speichern des Tags ist ein Fehler aufgetreten',
    editSystemLabelErrorMessage: 'Die Bearbeitung ist bei diesem Tag teilweise gesperrt',
    existingTagErrorMessage: 'Ein Tag mit derselben Bezeichnung existiert bereits. Bitte wähle eine andere Bezeichnung.',
    deleteTitle: 'Tag löschen',
    deleteConfirmText: (tag) => `Möchtest Du das Tag <strong>${tag}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
    removeSuccessMessage: 'Das Tag wurde erfolgreich entfernt.',
    removeErrorMessage: 'Beim Entfernen des Tags ist ein Fehler aufgetreten.',
    deleteSuccessMessage: 'Das Tag wurde erfolgreich gelöscht.',
    deleteErrorMessage: 'Beim Löschen des Tags ist ein Fehler aufgetreten.',
    addCondition: 'Bedingung hinzufügen',
    addNewTag: 'Neues Tag hinzufügen',
    newTagPlaceHolder: 'Neues Tag',
    selectAllTags: 'Alle Tags auswählen',
  },

  tagForm: {
    titles: {
      title: 'Titel',
      placeholder: 'Titel hinzufügen',
      description: 'Löschst Du bereits bestehende Titel aus dieser Liste, hat dies keine Auswirkungen auf Deine existierenden Kundendaten.',
      noOptionsMessage: 'Titel eingeben',
      createLabel: (label) => `Titel '${label}' erstellen`,
    },
    salutations: {
      title: 'Anrede',
      placeholder: 'Anrede hinzufügen',
      description: 'Löschst Du bereits bestehende Anreden aus dieser Liste, hat dies keine Auswirkungen auf Deine existierenden Kundendaten.',
      noOptionsMessage: 'Anrede eingeben',
      createLabel: (label) => `Anrede '${label}' erstellen`,
    },
    jobTitles: {
      title: 'Positionen',
      placeholder: 'Position hinzufügen',
      description: 'Positionen werden bei Ansprechpartnern verwendet, um die Rolle der Kontaktperson festzulegen. Wenn Du bestehende Positionen aus dieser Liste löschen, hat das keine Auswirkungen auf Deine existierenden Ansprechpartner.',
      noOptionsMessage: 'Position eingeben',
      createLabel: (label) => `Position '${label}' erstellen`,
    },
    customerCategories: {
      title: 'Kundenkategorien',
      placeholder: 'Kategorie hinzufügen',
      description: 'Mit der Kundenkategorie kannst Du Kundenumsätze gruppieren. Beim Löschen einer Kundenkategorie werden die Umsätze auf eine von Dir auswählbare Kategorie umgebucht.',
      type: 'Kundenkategorie',
      subject: 'Kunden',
      noOptionsMessage: 'Kundenkategorie eingeben',
      createLabel: (label) => `Kundenkategorie '${label}' erstellen`,
    },
    articleCategories: {
      title: 'Artikelkategorien',
      placeholder: 'Artikelkategorie hinzufügen',
      description: 'Mit der Artikelkategorie kannst Du Artikelumsätze gruppieren. Beim Löschen von Artikelkategorien werden die Umsätze auf eine von Dir wählbare Kategorie umgebucht.',
      type: 'Artikelkategorie',
      subject: 'Artikel',
      subjectPlural: 'Artikeln',
      noOptionsMessage: 'Artikel eingeben',
      createLabel: (label) => `Artikelkategorie '${label}' erstellen`,
    },
    articleUnits: {
      title: 'Artikeleinheiten',
      placeholder: 'Artikeleinheit hinzufügen',
      description: 'Einheiten werden in Artikeln und in Positionen verwendet. Löschst Du eine Einheit, hat dies keine Auswirkung auf abgeschlossene Rechnungen.',
      type: 'Artikeleinheit',
      subject: 'Artikel',
      subjectPlural: 'Artikeln',
      noOptionsMessage: 'Artikeleinheit eingeben',
      createLabel: (label) => `Artikeleinheit '${label}' erstellen`,
    },
    activityCategories: {
      title: 'Aktivitätskategorien',
      placeholder: 'Kategorie hinzufügen',
      description: ' Mit den Aktivitätskategorien kannst Du eigene Kategorien vergeben. Das Löschen einer Aktivitätskategorie hat keine Auswirkung auf bereits angelegte Aktivitäten.',
      type: 'Aktivitätskategorien',
      deleteSystemCategory: 'Eine System Aktivitätskategorie kann nicht gelöscht werden',
      deleteUsedCategory: 'Eine  verwendete Aktivitätskategorie kann nicht gelöscht werden',
      noOptionsMessage: 'Aktivitätskategorie eingeben',
      createLabel: (label) => `Aktivitätskategorien '${label}' erstellen`,
    },
    timeTrackingHourlyRates: {
      title: 'Stundensatz in €',
      placeholder: 'Stundensatz hinzufügen',
      description: 'Definiere die Stundensätze für die interne Zeiterfassung. Es können mehrere Stundensätze hinterlegt werden. Der Wert gilt auch bei der Kleinunternehmerregelung. Löschst Du einen Stundensatz, hat dies keine Auswirkungen auf bereits erstellte Rechnungen.',
      type: 'Stundensatz',
      noOptionsMessage: 'Stundensatz eingeben',
      createLabel: (label) => `Stundensatz '${label}' erstellen`,
    },
    replaceModal: {
      title: (type) => `${type} ersetzen`,
      submit: 'Ersetzen',
      description: (subject, type, tagToDelete) => `Es existieren ${subject}, denen die ${type} '<b>${tagToDelete}</b>' zugeordnet wurde.`,
      actionText: (subjectPlural, subject, type) => `Bitte wähle eine neue ${type}, die diesen ${subjectPlural || subject} zukünftig zugeordnet sein soll:`,
    },
  },

  // upgrade
  upgradeSubscriptionSelectionTitle: 'Plan wählen & durchstarten!',
  upgradeSubscriptionSelectionTitleAlternative: 'Wähle einen Plan aus',
  upgradeSubscriptionSelectionButtonLabel: 'Jetzt kaufen',
  upgradeSubscriptionSelectionButtonLabelCall: 'Jetzt anrufen',
  upgradeSubscriptionSelectionButtonLabelMoreDetails: 'Mehr Details',
  upgradeSubscriptionSelectionFunctions: 'Alle Funktionen',
  upgradeSubscriptionSepaMandateLabel: 'SEPA-Mandat erteilen',
  upgradeSubscriptionSepaHint: 'Mit Klick auf "Weiter" ermächtige ich die deltra Business Software GmbH & Co. KG, Zahlungen von meinem Konto mittels SEPA-Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von Ihnen auf mein Konto gezogenen Lastschriften einzulösen. Ich kann innerhalb von 8 Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Dabei gelten die mit meinem Kreditinstitut vereinbarten Bedingungen. Gläubiger-Identifikationsnummer der deltra Business Software GmbH & Co. KG: DE96ZZZ00000024237.',
  upgradeSubscriptionOrderOverviewSubscriptionTitle: 'Produkt',
  upgradeSubscriptionOrderOverviewPriceTitle: 'Preis',
  upgradeSubscriptionOrderOverviewPeriodTitle: 'Laufzeit',
  upgradeSubscriptionOrderOverviewBillingTitle: 'Rechnungsadresse',
  upgradeSubscriptionOrderOverviewContactTitle: 'Ansprechpartner',
  upgradeSubscriptionOrderOverviewPaymentTitle: 'Zahlung',
  upgradeSubscriptionOrderOverviewBuyButtonLabel: 'Zahlungspflichtig bestellen',
  upgradeSubscriptionSuccessTitle: 'Vielen Dank für Deine Bestellung!',
  upgradeSubscriptionSuccessSubtitle: 'Wir haben eine Rechnung an Deine E-Mail verschickt.',
  upgradeSubscriptionSuccessButton: 'Direkt loslegen',
  upgradeSubscriptionErrorIsMobile: 'Du hast Dein Abo auf einem mobilen Gerät abgeschlossen. Bitte verwalte Dein Abo auf dem entsprechenden mobilen Gerät.',
  upgradeSubscriptionSupportLabel: 'Support & Unterstützung',
  upgradeSubscriptionSupportMotto: 'Wir helfen gerne',
  upgradeSubscriptionSupportSeparator: ': ',
  upgradeSubscriptionSupportDescription: 'Telefon 9-21 Uhr, E-Mail, Chat, FAQs, Services (kostenpfl.), InApp-Kurse',
  upgradeSubscriptionHighlightText: 'Tipp',
  upgradeSubscriptionOnRequest: 'Ab 99,00 €',
  upgradeSubscriptionNeeded: 'Diese Funktion steht nur in einem Bezahlplan zur Verfügung',
  tryItForFree: 'Jetzt kostenlos testen',

  getSubscriptionStateErrorMessage: 'Beim Aktualisieren der noch verbleibenden Rechnungen und Angebote ist ein Fehler aufgetreten.',

  // notifications
  notificationSaveSuccessMessage: 'Die Benachrichtigungseinstellung wurde erfolgreich gespeichert.',
  notificationSaveErrorMessage: 'Beim Speichern der Benachrichtigungseinstellung ist ein Fehler aufgetreten.',

  // IBAN
  ibanErrorMessage: 'Bitte gib eine valide IBAN ein.',
  ibanErrorMessageOptional: 'Bitte gib eine valide IBAN ein (optional).',

  // supplier
  supplierDeleteConfirmCaption: multiple => `Lieferant${multiple ? 'en' : ''} löschen`,
  supplierDeleteConfirmText: multiple => `Möchtest Du ${multiple ? 'die ' : 'den'} Lieferanten wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  supplierDeleteSuccessMessage: multiple => `${multiple ? 'Die Lieferanten' : 'Der Lieferant'} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  supplierDeleteNotAllowedMessage: multiple => `${multiple ? 'Die Lieferanten können' : 'Der Lieferant kann'} nicht gelöscht werden, da Vorgänge für ${multiple ? 'sie' : 'ihn'} erfasst wurden.`,
  supplierAddressFormModalAdd: 'Lieferant anlegen',
  supplierContactPersonFormModalEdit: 'Ansprechpartner bearbeiten',
  supplierContactPersonFormModalAdd: 'Ansprechpartner hinzufügen',
  supplierEditSuccessMessage: 'Die Änderungen am Lieferanten wurden erfolgreich gespeichert.',
  supplierCreateSuccessMessage: 'Der Lieferant wurde erfolgreich gespeichert.',
  supplierEmptyDefaultAddressErrorMessage: 'Bitte füge eine Standardadresse hinzu.',
  supplierNotesInfoText: 'Hier kannst Du Anmerkungen zu diesem Lieferanten hinterlegen',
  supplierDefaultAddress: 'Standardadresse',
  supplierAdditionalAddresses: 'Weitere Adressen',
  supplierAddAdditionalAddress: 'Weitere Adresse hinzufügen',
  supplierAddressFormModalEdit: 'Adresse bearbeiten',
  supplierAddressFormModalCreate: 'Adresse hinzufügen',
  supplierAddressButtonLabel: 'Adresse',
  supplierAddressDescription: 'Lege hier die Standardadresse für Deinen Lieferanten fest.',
  supplierNotFoundErrorMessage: 'Der Lieferant wurde nicht gefunden.',
  supplierSearchCategory: 'Lieferanten',
  supplier: {
    aliases: {
      addAlias: 'Alias hinzufügen',
      uniqueAlias: (duplicates) => (`"${duplicates.aliases.join(', ')}" ${duplicates.aliases.length > 1 ? 'werden' : 'wird'} bereits bei ${duplicates.supplierNames.length > 1 ? 'folgenden' : 'folgendem'} Lieferanten verwendet: "${duplicates.supplierNames.join(', ')}"`),
      createLabel: (label) => `Alias '${label}' erstellen`,
      noOptionsMessage: 'Aliasnamen eingeben',
      labelKey: 'Aliasnamen',
    },
    list: {
      emptyStateText: 'Keine Lieferanten gefunden',
      supplierListEmptyStateTitle: 'Jetzt Lieferanten per Excel-Tabelle hochladen',
      loaderText: 'Lieferanten werden geladen',
      title: 'Lieferanten',
      listNoSearchResultTitle: 'Keine Lieferanten gefunden',
      listEmptyStateSubtitle: 'Jetzt den ersten Lieferanten hinzufügen',
      searchPlaceholder: 'Lieferanten durchsuchen',
      buttonLabel: 'Los geht´s',
    },
    new: 'Neu',
    edit: {
      title: {
        new: 'Neuen Lieferant anlegen',
        edit: 'Lieferant bearbeiten',
      },
    },
    detail: {
      title: 'Lieferanten-Nr.',
      customerReference: 'Eigene Kunden-Nr.',
    },
    model: {
      supplierNumberAbbreviation: 'Lf-Nr.',
    },

    forms: {
      accountingForm: {
        title: 'Fibukonten',
        selectLabel: 'Standardkategorie des Lieferanten',
        creditorNo: 'Kreditorennummer',
      },
      aliasesForm: {
        title: 'Aliasnamen',
      },
      bankAccountForm: {
        title: 'Bankverbindung',
        fieldLabels: {
          accountHolder: 'Kontoinhaber',
          iban: 'IBAN',
          bic: 'BIC',
          bankName: 'Bank',
          email: 'Kontakt E-Mail-Adresse',
          mandateReferenceNumber: 'Mandatsreferenz',
          mandateKind: 'Zahlungsverfahren',
          mandateKinds: {
            core: 'SEPA-Basislastschrift',
            b2b: 'B2B SEPA-Lastschrift',
          },
          mandateType: 'Zahlungsart',
          mandateState: 'Status',
          mandateValidityDate: 'Gültig bis',
          mandateStatesPending: 'Noch nicht beantragt',
          mandateStates: {
            active: 'Aktiv',
            pending: 'Beantragt',
            cancelled: 'Beendet',
            cancelling: 'Wird beendet',
            expired: 'Abgelaufen',
          },
          mandateTypes: {
            recurring: 'Wiederkehrend',
            oneOff: 'Einmalig',
          },
          signatureDate: 'Unterschrieben am',
          mandateAddress: 'Anschrift',
          mandateCancel: 'Lastschriftmandat widerrufen',
          mandateRevert: 'Änderung verwerfen',
          mandateChangeDescription: 'Der Kunde wird nach dem Speichern per E-Mail informiert.',
          mandateDelete: 'löschen',
          mandateResend: 'Lastschriftmandat erneut senden',
        },
        autoFilled: 'Wird automatisch ausgefüllt',
        signatureAlt: 'Signatur',
        mandateResendSuccess: 'Das Lastschriftmandat wurde erfolgreich gesendet.',
      },
      communicationForm: {
        title: 'Kommunikation',
        fieldLabels: {
          customerReference: 'Eigene Kd-Nr. beim Lieferant',
        },
      },
      conditionForm: {
        title: 'Konditionen',
        fieldLabels: {
          discount: 'Rabatt',
        },
      },
      informationForm: {
        title: 'Lieferanteninformationen',
        fieldLabels: {
          supplierNumber: 'Lieferantennummer',
          customerReference: 'Eigene Kd-Nr. beim Lieferant',
        },
      },
      notesForm: {
        title: 'Anmerkungen',
      },
      payConditionForm: {
        title: 'Zahlungskonditionen',
        fieldLabels: {
          dueDaysWithoutWithdrawal: 'Fälligkeit in Tagen',
          discountPercent: 'Skonto in %',
          withinDays: 'Skonto innerhalb von Tagen',
          payKindMethod: 'Zahlart',
        },
        toggleLabel: 'Skonto aktivieren',
        toggleTitle: 'Skonto',
      },
    },
    upgradeText: `${unlockViewUpgradeText} Lieferanten freizuschalten.`,
    jumpMarkLinkTexts: {
      addressForm: {
        headline: 'Adressen',
        text: 'Gib die Standardadresse des Lieferanten an und ergänze dies um weitere Adressen.',
      },
      contactPersonForm: {
        headline: 'Ansprechpartner',
        text: 'Du kannst Ansprechpartner des Lieferanten definieren und diese in den Vorgängen auswählen. Das Hinzufügen, sowie bearbeiten von Ansprechpartnern bleibt wie bisher bestehen.',
      },
      communicationForm: {
        headline: 'Kommunikation',
        text: 'E-Mail-Adresse, Telefon, Mobiltelefon. In diesem Bereich können die Kommunikationsmittel eingetragen werden.',
      },
      informationForm: {
        headline: 'Lieferanteninformationen',
        text: 'Weitere Informationen und Einteilung in Kategorien können hier vorgenommen werden, sollen weitere Kategorien zur Auswahl stehen, gehe in die Einstellungen.',
      },
      conditionsForm: {
        headline: 'Konditionen',
        text: 'Hast Du spezielle Zahlungskonditionen bei Deinem Lieferanten, kannst Du diese festlegen.',
      },
      notesForm: {
        headline: 'Anmerkungen',
        text: 'Du möchtest gerne bei Angebots- oder Rechnungserstellung Hinweise zu Deinem Lieferanten angezeigt bekommen oder Dir eigene Notizen machen. Dies kann hier vorgenommen werden.',
      },
      bankAccountForm: {
        headline: 'Bankverbindung',
        text: 'Damit Du Deine Lieferantenrechnungen direkt überweisen kannst, hinterlege die Bankdaten des Lieferanten.',
      },
      accountingForm: {
        headline: 'Fibukonten',
        text: 'Lege eine eigene Debitorennummer für Deinen Lieferanten fest.',
      },
    },
    nameExistsAsAlias: (alias) => `Dieser Name wurde bereits als Alias im Lieferanten ${alias} gesetzt und kann deshalb nicht vergeben werden.`,
  },

  // user
  userDeleteSuccessMessage: 'Der Benutzer wurde erfolgreich gelöscht.',
  userDeleteNotAllowedMessage: 'Der Benutzer kann nicht gelöscht werden.',
  userDeleteNotFoundMessage: 'Der Benutzer wurde nicht gefunden.',
  userDeleteConfirmCaption: 'Benutzer löschen',
  userDeleteConfirmText: 'Möchtest Du den Benutzer wirklich löschen? Dies kann nicht rückgängig gemacht werden! <br /> Ist der Benutzer der einzige Empfänger der Versandbestätigung beim automatischen Mahnen, wird die Versandbestätigung automatisch deaktiviert.',
  userNotFoundErrorMessage: 'Der Lieferant wurde nicht gefunden.',

  userCreateSuccessMessage: 'Einladung wurde erfolgreich verschickt.',
  userUpdateSuccessMessage: 'Der Benutzer wurde erfolgreich aktualisiert.',
  userLockedSuccessMessage: 'Der Benutzer wurde erfolgreich gesperrt.',
  userUnlockedSuccessMessage: 'Der Benutzer wurde erfolgreich entsperrt.',

  // field labels
  streetFieldLabel: 'Straße & Hausnummer',

  // tax categories
  generalTaxCategoryTitleOption1: 'Umsatzsteuer ausweisen: Soll-Besteuerung',
  generalTaxCategoryTitleOption2: 'Umsatzsteuer ausweisen: Ist-Besteuerung',
  generalTaxCategoryTitleOption3: 'Umsatzsteuer nicht ausweisen: Kleinunternehmerregelung',
  generalTaxCategoryTitleOption4: 'Umsatzsteuerbefreit',
  generalTaxCategoryDescriptionOption1: 'Bei der Soll-Besteuerung wird die Umsatzsteuer basierend auf dem Rechnungsdatum fällig, unabhängig davon, ob sie bereits bezahlt wurde oder nicht. Diese Methode ist für Unternehmen mit regelmäßigem Einkommen vorteilhaft, da sie die Liquiditätsplanung erleichtert.',
  generalTaxCategoryDescriptionOption2: 'Bei der Ist-Besteuerung wird die Umsatzsteuer erst fällig, wenn der Rechnungsbetrag tatsächlich bezahlt wurde. Diese Methode ist besonders für kleinere Betriebe oder Unternehmen mit Kunden, die längere Zahlungsziele haben, von Vorteil.',
  generalTaxCategoryDescriptionOption3: 'Wähle diese Option, wenn Du der Kleinunternehmerregelung gemäß § 19 UStG unterliegst und keine Umsatzsteuer ausweist. Als Kleinunternehmer gilt ein Unternehmen, das im letzten Kalenderjahr nicht mehr als 25.000 Euro Umsatz erzielt hat und im laufenden Jahr voraussichtlich nicht mehr als 100.000 Euro Umsatz erreichen wird.',
  generalTaxCategoryDescriptionOption4: 'Aktiviere diese Option, wenn Du nach §4 UStG von der Umsatzsteuer befreit bist. Hierzu gehören beispielsweise Heilbehandlungen mit medizinischem Hintergrund oder Umsätze aus Finanzdienstleistungen. Mehr Informationen findest Du hier: <a href="https://www.gesetze-im-internet.de/ustg_1980/__4.html" target="_blank">www.gesetze-im-internet.de</a>',
  generalTaxCategoryVatExemptReasonLabel: 'Grund der Umsatzsteuerbefreiung*',
  generalTaxCategoryTitle: 'Umsatzbesteuerung',
  taxCategorySaveError: 'Beim Speichern der Änderung ist ein Fehler aufgetreten.',
  taxCategoriesListFetchError: 'Beim Laden der Steuerkategorien ist ein Fehler aufgetreten.',
  taxAdjustmentsTitle: 'Steuereinstellungen 01.07.2020 - 31.12.2020',
  taxAdjustmentsToggleText: 'Bruttopreise in Artikeln und Abo-Rechnungen beibehalten',
  taxCategorySplitBookings: 'Splittbuchungen',

  taxCategoryPositions: {
    selectInputPlaceholder: 'Steuerkategorie suchen...',
    selectInputDisabledHint: 'In Deinem Plan nicht enthalten',
    // FLAG:useDocuAi — the tax category is locked because selecting it triggers booking, while
    // accounting can represent only German VAT.
    unbookableVatCategoryHint: (rates, isDomestic) => {
      const rateText = (rates || []).map(rate => `${String(rate).replace('.', ',')} %`).join(', ')
      return isDomestic
        ? `USt-Satz ${rateText} — zum Belegdatum gibt es diesen deutschen Steuersatz nicht. Der Beleg kann geprüft und gespeichert werden, eine Steuerkategorie lässt sich nicht zuordnen.`
        : `Ausländische Umsatzsteuer (${rateText}) — eine Buchung ist nur mit deutscher Umsatzsteuer möglich. Der Beleg kann geprüft und gespeichert werden, eine Steuerkategorie lässt sich nicht zuordnen.`
    },
    // FLAG:useDocuAi — rejected category assignment on an XML e-invoice: the line's VAT rate is
    // stated on the receipt and cannot be changed, but the category does not support it. The
    // server has long enforced the same rule (validatePostionBookkeepingAccount → NOT_ALLOWED);
    // this finally gives it a UI instead of failing silently.
    categoryVatMismatch: (rate, categoryName) => {
      const rateText = `${String(rate).replace('.', ',')} %`
      const category = categoryName ? `„${categoryName}"` : 'Diese Steuerkategorie'
      return `${category} führt keinen Steuersatz von ${rateText}. Der Satz steht so auf der E-Rechnung und lässt sich nicht ändern — bitte eine Kategorie mit ${rateText} wählen.`
    },
    // FLAG:useDocuAi — tooltip for the VAT amount column in the review row. The amount is rounded
    // per line; these exact line values are booked later.
    positionVatAmountHint: 'Umsatzsteuer dieser Position — je Zeile aus Betrag und Steuersatz berechnet.',
    newPosition: 'Neue Belegposition',
    totalNet: 'Summe netto',
    total: 'Gesamt',
    // FLAG:useDocuAi — whole-invoice document allowance (discount/reduction) breakdown.
    positionsTotal: 'Positionssumme',
    documentAllowance: 'Ermäßigung',
    documentSurcharge: 'Zuschlag',
    // FLAG:useDocuAi — in the review component the totals are what the DOCUMENT states (as DocuAI
    // read them), not a sum orgaMAX computes: the reviewer checks the documented values, and a
    // recomputed number would quietly hide a wrong extraction. Editable in place; a mismatch
    // between the positions and the stated totals is pointed out instead of being papered over.
    documentVat: 'USt.',
    // FLAG:useDocuAi — breakdown of the stated VAT per rate (EN 16931 BG-23) below the VAT line of
    // the review block; only XML receipts provide the map (documentVat).
    documentVatRate: (rate) => `davon ${String(rate).replace('.', ',')} %`,
    documentNet: 'Netto',
    documentTotal: 'Gesamt',
    totalsMismatch: {
      positions: (positionsSum, stated) => `Die Positionen ergeben ${positionsSum}, der Beleg nennt ${stated} netto.`,
      total: (computed, stated) => `Netto + USt. ergibt ${computed}, der Beleg nennt ${stated}.`,
      // The two checks above compare only PRINTED values with each other and therefore stay
      // silent when the three header amounts are internally consistent — even if the line items
      // claim a completely different tax amount. That was exactly the insurance premium invoice
      // (issue 7bf92ff5): receipt 599.73 = 599.73 + 0.00, but line items at 19% totaling 113.95 VAT.
      vat: (positionsVat, stated) => `Die Positionen ergeben ${positionsVat} USt., der Beleg nennt ${stated}.`,
      // FLAG:useDocuAi — WHY those two figures differ, appended to (never replacing) the sentence
      // above while the gap stays within what per-line rounding can produce. Without it the
      // reviewer cannot tell an unavoidable cent from a real error (Ralf, 2026-09-18: „in den
      // positionen wurde ust berechnet deshalb der rundungsfehler, sollten wir im hinweis
      // festhalten"). It also says which figure is BOOKED, and that is not the document's: the
      // booking payload carries the positions and the VAT map summed from them
      // (api buhlFinanceApiMapping/mapExpense — `positions`, `vat`, and totalNet derived from that
      // map), while the stated totals stay on the record for the reviewer. Measured on expense
      // 12518: vat {"7": 92.44} against vatAmount 92,43. Saying „der Beleg wird gebucht" here would
      // be plainly wrong, and it is exactly the question Ralf asked (2026-09-18).
      vatRoundingCause: 'Die Umsatzsteuer wird je Position berechnet und dabei gerundet – daraus '
        + 'entsteht die Cent-Abweichung. Gebucht werden die Positionen mit ihren Steuerkategorien, '
        + 'also der aus ihnen errechnete Betrag.',
      // Both replace the line-item notice while the line rates do not come from the receipt:
      // otherwise that notice would measure our own assumption, not the receipt.
      // No rate is available per line item -> the printed tax breakdown is booked.
      lineTaxByRate: rates => `Der Beleg weist ${rates} aus, nennt die Sätze aber nicht je Position. `
        + 'Gebucht wird deshalb je Steuersatz nach der Steueraufstellung des Belegs. '
        + 'Die Artikelzeilen bleiben auf dem Beleg erhalten, sie tragen die Buchung nur nicht.',
      // Only some lines have no rate -> the remaining rates are assumed and must be assigned.
      lineTaxUnknown: rates => `Der Beleg weist ${rates} aus, nennt die Sätze aber nicht je Position. `
        + 'Die Positionssumme beruht deshalb auf einer Annahme — bitte den Positionen den Satz zuordnen.',
    },
    drawer: {
      openAccountList: 'Steuerkonten-Liste öffnen',
      allAccounts: 'Alle',
      favorites: 'Favoriten',
    },
  },

  // revenue
  revenues: 'Einnahmen',
  revenueDeleteSuccessMessage: multiple => `Die Einnahme${multiple ? 'n' : ''} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
  revenuePartialDeleteErrorMessage: (deleted, failed) => `${deleted} Einnahme${deleted > 1 ? 'n' : ''} wurde${deleted > 1 ? 'n' : ''} gelöscht. ${failed} Einnahme${failed > 1 ? 'n' : ''} konnte${failed > 1 ? 'n' : ''} nicht gelöscht werden.`,
  revenueDeleteConfirmCaption: multiple => `Einnahme${multiple ? 'n' : ''} löschen`,
  revenueAndEconomicAssetDeleteConfirmCaption: multiple => (multiple
    ? 'Einnahmen können über diesen Weg nicht gelöscht werden.'
    : 'Einnahme kann über diesen Weg nicht gelöscht werden.'),
  revenueDeleteConfirmText: multiple => `Möchtest Du die ${multiple ? 'ausgewählten Einnahmen' : 'Einnahme'} wirklich löschen? Dies kann nicht rückgängig gemacht werden!`,
  revenueSaveSuccessMessage: 'Die Einnahme wurde erfolgreich gespeichert.',
  revenueSaveErrorMessage: 'Beim Speichern der Einnahme ist ein Fehler aufgetreten.',
  revenueSaveSupplierNotFoundError: 'Der ausgewählte Auftraggeber wurde nicht gefunden',
  revenueInvalidPositionsError: 'Deine Gesamtsumme stimmt nicht mit dem hinterlegten Betrag überein. Bitte überprüfe Deine Eingabe.',
  revenueSaveCashBalanceNegativeError: 'Der Betrag ist höher als der aktuelle Kassenbuchbestand. Das Kassenbuch darf nicht negativ sein.',
  revenueSavePaymentDatePastInitialBalanceDateError: 'Das Zahlungsdatum darf nicht vor dem initialen Bestandsdatum liegen.',
  revenueSavePaymentExpenseAmountExceeded: 'Der zugeordnete Betrag überschreitet den Ausgabenbetrag',
  revenueEditTitle: 'Einnahme bearbeiten',
  revenueConfirmTitle: 'Einnahme prüfen',
  revenueNewTitle: 'Neue Einnahme erstellen',
  revenueAndEconomicAssetDeleteConfirmText: multiple => `${multiple ? 'Mindestens eine der ausgewählten Einnahmen ist' : 'Diese Einnahme ist'} mit einem Anlagegut verbunden, daher muss die Löschung über das zugehörige Anlagegut veranlasst werden. Bitte gehe hierfür in den Bereich <b>Finanzen → Anlagenverwaltung</b> oder wähle <b>„Zur Anlagenverwaltung“</b> aus. Markiere dann das betreffende Anlagegut in der Liste und klicke auf <b>„Löschen“</b>. <br/><br/> Weitere Informationen findest Du in der <a target="_blank" href=${mainInfoUrl}>FAQ.</a> `,

  otherRevenues: 'Sonstige Einnahmen',
  otherRevenue: 'Sonstige Einnahme',

  // privacy
  privacySaveSuccessMessage: 'Die Datenschutzeinstellungen wurden erfolgreich gespeichert.',
  privacySaveErrorMessage: 'Beim Speichern der Datenschutzeinstellungen ist ein Fehler aufgetreten.',

  // chartOfAccounts
  chartOfAccountsEmptyCustomNameMessage: 'Bitte trage eine Kurzbezeichnung ein.',
  chartOfAccountsSaveSuccessMessage: 'Die Änderungen an dem Steuerkonto wurden erfolgreich gespeichert.',
  chartOfAccountsSaveErrorMessage: 'Beim Speichern der Änderungen des Steuerkontos ist ein Fehler aufgetreten',
  chartOfAccountsToggleLabel: 'Konto in Steuerkontenliste anzeigen',
  chartOfAccountsToggleIndividualLabel: 'Als individuelles Erlöskonto aktivieren',
  chartOfAccountsSaveSuccess: 'Das Konto wurde erfolgreich aktualisiert',
  chartOfAccountsAddedToFavorites: 'Das Konto wurde zu Favoriten hinzugefügt',
  chartOfAccountsRemovedFromFavorites: 'Das Konto wurde von Favoriten entfernt',
  chartOfAccountList: {
    drawerTitle: 'Steuerkonto bearbeiten',
    accountNumber: 'Kontonummer/Standardbezeichnung',
    individualDescription: 'Individuelle Bezeichnung',
    type: 'Art',
    group: 'Gruppe',
    text: 'Text',
  },

  // ocr
  ocrImageViewerLoaderText: 'Der Text wird analysiert...',
  ocrImageViewerErrorMessage: 'Beim Analysieren des Textes ist ein Fehler aufgetreten. Versuche es bitte erneut.',

  // office
  officeSubmenuDocumentsTitle: 'Dokumente',
  linkedDocuments: {
    title: 'Verknüpfte Dokumente',
    subtitle: 'Dokumente per Drag and Drop reinziehen, hochladen oder aus dem Dokumentencontainer verknüpfen.',
    dropzoneUploadLabel: 'Dokument hochladen',
    dropzoneDescription: 'oder per Drag and Drop reinziehen',
    allowedTypes: 'Erlaubte Dateitypen: PDF, Word, XML, PNG',
    linkButtonLabel: 'Dokument verknüpfen',
    uploadButtonLabel: 'Neues Dokument hochladen',
    linkPickerTitle: 'Dokument verknüpfen',
    linkSuccessMessage: 'Das Dokument wurde erfolgreich verknüpft.',
    linkErrorMessage: 'Das Dokument konnte nicht verknüpft werden.',
    unlinkSuccessMessage: 'Die Verknüpfung wurde erfolgreich aufgelöst.',
    unlinkErrorMessage: 'Die Verknüpfung konnte nicht aufgelöst werden.',
    columns: {
      name: 'Name',
      originalType: 'Typ',
      createdAt: 'Erstelldatum',
      unlink: 'Verknüpfung auflösen',
    },
  },
  documentDeleteSuccessMessageSingle: 'Das Dokument wurde erfolgreich gelöscht',
  documentDeleteSuccessMessageMulti: 'Die Dokumente wurden erfolgreich gelöscht',
  documentDeleteSuccessMessage: 'Das Dokument %s wurde erfolgreich gelöscht',
  documentDeleteConfirmCaption: 'Dokument löschen?',
  documentDeleteConfirmText: (count) => count > 1
    ? 'Möchtest Du das wirklich alle ausgewählten Dokumente löschen? Dies kann nicht rückgängig gemacht werden!'
    : 'Möchtest Du das Dokument {{document}} wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
  documentSubscriptionSubTile: 'Optimiere Deine Rechnungsverwaltung mit dem Modul Belege.',

  firstSteps: {
    title: 'Erste Schritte',
    welcome: {
      headline: 'Willkommen bei orgaMAX!',
      testDaysRemaining: (remainingDays) => `Du kannst orgaMAX noch ${remainingDays} Tage testen.`,
      trialExpired: 'Deine orgaMAX Testphase ist abgelaufen.',
      toProductSelection: 'Zur Produktauswahl',
      yourProducts: 'Zur Produktübersicht',
    },
    infoBoxes: {
      support: {
        title: 'Support & Hilfe-Center',
        description: 'Von Tutorials über FAQ, Blog und Guides ist alles dabei',
        openHelp: 'Hilfe öffnen',
      },
      consultation: {
        title: 'Persönliche Beratung',
        description: salesBusinessHours,
        openChat: 'Chat öffnen',
        tel: salesTelephoneNumber,
      },
      webinars: {
        title: 'Kostenlose Webinare',
        description: 'orgaMAX Wissenswelt-Akademie entdecken',
        secureSpots: 'Mehr erfahren',
        quickStartGuide: 'Schnellstart-Anleitung',
      },
    },
    steps: {
      title: 'Deine ersten Schritte',
      step1: (products) => `Produkt${products.length > 1 ? 'e' : ''} aktiviert`,
      step2: 'Eigene Daten oder Demo-Daten?',
      step3: 'Funktionen entdecken',
    },
    guides: {
      title: 'Welche Funktionen interessieren Dich?',
      error: 'Deine Anleitungen konnten nicht geladen werden. Bitte lade die Seite neu und versuche es erneut.',
    },
    dataSelection: {
      title: 'Wie möchtest Du starten?',
      ownDataTitle: 'Start mit Unternehmensdaten',
      ownDataDescription: 'Beginne direkt im Produktivmodus mit Deinen eigenen Unternehmensdaten – ideal, wenn Du sofort mit echten Projekten und Rechnungen arbeiten möchtest.',
      ownDataButton: 'Mit eigenen Daten starten',
      demoDataTitle: 'Demo-Modus',
      demoDataDescription: 'Entdecke orgaMAX im Demo-Modus – in einer sicheren Testumgebung, getrennt von Deinen echten Unternehmensdaten. Perfekt, um Funktionen auszuprobieren und Dir einen Überblick zu verschaffen.',
      demoDataButton: 'Mit Demo-Daten starten',
      infoTitle: 'Bitte beachte',
      infoText: 'Hinweis: Im Produktivmodus arbeitest Du mit echten Unternehmensdaten. Änderungen können nicht einfach zurückgesetzt werden – wähle diesen Modus, wenn Du direkt loslegen möchtest.',
      infoText2: 'Für einen unverbindlichen Test empfehlen wir den Demo-Modus. Du kannst jederzeit in den Produktivmodus wechseln und dort mit Deinen echten Daten weiterarbeiten.',
    },
  },

  // main menu
  menu: {
    firstSteps: 'Erste Schritte',
    // FLAG:docuAiEnhancements — menu entry for reported document issues.
    customerIssues: 'Meine Meldungen',
    apps: {
      title: 'Marktplatz',
      submenu: {
        extensions: 'Erweiterungen',
        marketPlace: 'Shop',
      },
      upgradeText: `${unlockViewUpgradeText} Erweiterungen freizuschalten.`,
    },
    marketPlace: {
      title: 'Shop',
    },
    backup: 'Datensicherung',
    dashboard: 'Dashboard',
    office: {
      title: 'Office',
      todos: 'Aufgaben',
      letters: 'Geschäftsbriefe',
    },
    masterData: {
      title: 'Stammdaten',
      customers: 'Kunden',
      suppliers: 'Lieferanten',
      articles: 'Artikel',
      dataImport: 'Datenimport',
      clients: 'Mandanten',
    },
    disposal: {
      title: 'Verkauf',
      invoices: 'Rechnungen',
      offers: 'Angebote',
      orders: 'Aufträge',
      deliveryNotes: 'Lieferscheine',
      recurringInvoices: 'Abos',
      projects: 'Abschläge',
      timetracking: 'Zeiterfassung',
    },
    finances: {
      title: 'Finanzen',
      banking: 'Bank/Kasse',
      expenses: 'Ausgaben',
      revenues: 'Sonstige Einnahmen',
    },
    booking: {
      title: 'Buchhaltung',
      documentExport: 'Steuerberaterexport',
      vatReturn: 'USt-Voranmeldung',
      booking: 'Buchungsübersicht',
      reporting: 'Auswertungen',
      economicAssets: 'Anlagenverwaltung',
    },
    documents: 'Dokumente',
    settings: {
      title: 'Einstellungen',
      electron: 'App-Einstellungen',
    },
    staff: {
      title: 'Personal',
    },
    newBadge: '(NEU)',

    profileSection: {
      heading: 'Gewählter Mandant:',
      viewTaxConsultant: 'Kanzleiansicht',
      noCompanyName: 'Kein Firmenname',
      clientNumber: 'Mandantennummer',
    },
    procurement: 'Auftragswesen',
    obtaining: {
      title: 'Einkauf',
      purchases: 'Bestellungen',
    },
  },

  backup: {
    title: 'Dashboard Datensicherung',
    alert: {
      activeTitle: 'Datensicherung aktiviert',
      inactiveTitle: 'Datensicherung nicht aktiv',
      subscriptionInactiveDescription: 'Die Datensicherung ist derzeit nicht lizenziert oder abgelaufen.',
      loadingTitle: 'Datensicherung wird geprüft',
      activeDescription: (dateLabel) => dateLabel
        ? `Letzte erfolgreiche Datensicherung am ${dateLabel}`
        : 'Die Verbindung zur Datensicherung wurde erfolgreich hergestellt.',
      inactiveDescription: (dateLabel) => dateLabel
        ? `Letzte erfolgreiche Datensicherung am ${dateLabel}`
        : 'Es liegt noch keine erfolgreiche Datensicherung vor.',
      loadingDescription: 'Verbindung zur Datensicherung wird hergestellt.',
    },
    recentBackupsTitle: 'Datensicherungen',
    recentBackupsSubtitle: 'Die letzten 3 Datensicherungs-Vorgänge',
    recentBackupsEmpty: 'Es liegen keine Sicherungen vor.',
    metricsPlaceholder: 'Weitere Kennzahlen stehen nach der Aktivierung der Datensicherung zur Verfügung.',
    metricsDashboard: {
      customers: 'Kontakte',
      invoices: 'Rechnungen',
      articles: 'Artikel',
      suppliers: 'Lieferanten',
      secured: 'gesichert',
      total: 'Gesamt',
      totalRecordsSecured: 'Datensätze gesichert',
    },
    sizeLabel: 'Größe',
    statuses: {
      success: 'Erfolgreich',
      warning: 'Unvollständig',
      failed: 'Fehlgeschlagen',
      running: 'In Bearbeitung',
    },
  },

  // onboarding

  onboarding: {
    general: {
      stepper: 'Schritt {current} von {totalSteps}',
      next: 'Weiter',
      back,
      finish: 'Fertig',
      abort: 'Abbrechen',
      cookies: 'Cookie Einstellungen',
    },
    avv: {
      tooltip: 'Weil Datenschutz für uns ein wichtiges Anliegen ist, schließen wir mit jedem der unsere Dienste nutzt, eine Vereinbarung zur Auftragsverarbeitung (AVV). Darin sind die Rechte und Pflichten beider Parteien gemäß Art. 28 DSGVO geregelt und Du kannst Dich darauf verlassen, dass die deltra Business Software GmbH & Co. KG Deine uns anvertrauten Daten sicher verarbeitet. Wir senden Dir den AVV nach erfolgter Registrierung per Mail zu.',
    },
    company: {
      heading: 'Kontakt',
      title: 'Deine Kontaktinformationen',
      text: 'Wie können Deine Kunden Dich erreichen?',
      toast: 'Bitte gib Deine Kontaktinformationen an, um mit der Einrichtung fortfahren zu können.',
      toastAVV: 'Bitte bestätige den AVV.',
      toastPhone: 'Bitte gib eine gültige Telefonnummer an.',
      inputs: {
        companyName: 'Firma*',
        street: 'Straße & Nr.*',
        zipCode: 'PLZ*',
        city: 'Ort*',
        phone: 'Telefon*',
        firstName: 'Vorname*',
        lastName: 'Name*',
      },
    },
    business: {
      heading: 'Firmendaten & Briefpapier',
      title: 'In welcher Branche bist Du tätig?',
      text: `Mit Angabe der Branche Deines Unternehmens kann ${productName} noch besser<br />auf Deine Bedürfnisse zugeschnitten werden.`,
      experienceMenuItem: 'Erfahrungen mit Bürosoftware',
      button1: 'IT & Medien',
      button2: 'Dienstleistungen',
      button3: 'Online-Handel',
      button4: 'Einzelhandel',
      button5: 'Handwerk',
      button6: 'Hotel & Gastro',
      search: 'Branche suchen',
      toast: 'Bitte gib Deine Branche an, um mit der Einrichtung fortfahren zu können.',
      taxSettingsMenuItem: 'Versteuerungsart',
      labelTax: '*IST-Besteuerung ist Standard. Versteuerungsart kann jederzeit geändert werden!',
      taxSettingsInput: {
        tooltipContent1: 'Umsatzsteuer wird erst nach Begleichung Deiner Rechnung an das Finanzamt abgeführt.',
        tooltipContent2: 'Umsatzsteuer wird mit der Rechnungserstellung an das Finanzamt abgeführt.',
        tooltipContent3: 'Du schreibst Rechnung ohne Ausweis der Umsatzsteuer gem. §19 UStG.',
      },
    },
    teamwork: {
      heading: 'Arbeiten im Team',
      title: 'Teamwork',
      text: `${productName} ist Teamwork! Wieviele Mitarbeiter Deines Unternehmens<br />arbeiten in Zukunft mit ${productName}?`,
      toast: 'Bitte gib Deine Mitarbeiterzahl an, um mit der Einrichtung fortfahren zu können.',
      inputs: {
        single: '1',
        multi: '2-3',
        more: 'mehr als 3',
        sub: 'Nutzer',
      },
    },
    vatChoice: {
      heading: 'Steuerrechtliche Daten',
      title: 'Umsatzsteuer',
      text: `Für die richtigen Einstellungen in Vorgängen wie Rechnungen und Co. benötigt ${productName} die Information, ob Du Umsatzsteuer ausweist.`,
      intro: 'Weist Du Umsatzsteuer aus?',
      inputs: {
        true: 'Nein',
        false: 'Ja',
      },
    },
    demoTenantChoice: {
      heading: 'Beispieldaten',
      title: `Starte ${productName} im Demo-Modus`,
      text: 'Im Demo Mandanten kannst Du alle Funktionalitäten anhand von bestehenden Beispieldaten überblicken sowie eigene Testdaten zum Ausprobieren erfassen. Du kannst jederzeit bequem in Deinen produktiven Mandanten wechseln, um Deine echten betrieblichen Daten zu erfassen.',
      intro: 'Möchtest Du jetzt mit Beispieldaten starten?',
      toast: 'Bitte gib an, ob Du mit Beispieldaten starten möchtest, um mit der Einrichtung fortfahren zu können.',
      inputs: {
        true: 'Ja',
        false: 'Nein',
      },
    },
    search: {
      searchTitle: 'Kein Suchergebnis',
      title: 'Branche suchen',
      placeholder: 'Branche eingeben',
      label: 'Branche *',
    },
    letterhead: {
      dropzone: {
        title: '<strong>Logo</strong>hier',
        text: 'Klicke hier, um Dein Logo hochzuladen oder nutze Drag&Drop',
      },
      buttons: {
        delete: 'Löschen',
      },
    },
  },

  onboardingV2: {
    general: {
      back,
      finish: 'Fertig',
      success: 'Erfolgreich abgeschlossen',
    },
    password: {
      title: 'Wähle Dein Passwort',
      text: 'So kannst Du einfach und sicher auf Dein Benutzerkonto zugreifen',
    },
    taxConsultantUser: {
      title: 'Ansprechpartner',
      text: 'Bitte nenne uns den Ansprechpartner für orgaMAX - Mandant. Dies kann entweder der Kanzleiinhaber oder ein verantwortlicher Mitarbeiter sein. Du kannst später weitere Ansprechpartner hinzufügen.',
      inputs: {
        firstName: 'Vorname*',
        lastName: 'Name*',
        phone: 'Telefonnummer*',
        email: 'E-Mail-Adresse',
      },
    },
    taxConsultantCompanyAddress: {
      title: 'Kanzleibasisinformationen',
      text: 'Bitte trage Deine Kanzleibasisinformationen ein, um eine reibungslose Identifikation durch Deine Mandanten zu ermöglichen.',
      inputs: {
        companyName: 'Wie heißt Deine Kanzlei?*',
        street: 'Straße & Hausnummer',
        zipCode: 'PLZ',
        city: 'Ort',
      },
      errors: {
        taxOffice: 'Bitte gib einen gültigen Kanzleinamen an.',
      },
    },
    user: {
      title: 'Los geht’s!',
      texts: {
        default: 'Für die bestmögliche Einrichtung Deines Accounts benötigen wir einige grundlegende Informationen.',
      },
      errorToast: 'Bitte gib Deine Kontaktinformationen an, um mit der Einrichtung fortfahren zu können.',
      toastPhone: 'Bitte gib eine gültige Telefonnummer an.',
      inputs: {
        companyName: 'Wie heißt Dein Unternehmen?',
        companyNamePlaceholder: '',
        firstName: 'Vorname*',
        firstNamePlaceholder: '',
        lastName: 'Name*',
        lastNamePlaceholder: '',
        phone: 'Telefonnummer',
        phonePlaceholder: '',
        source: 'Woher hast Du von orgaMAX erfahren?',
      },
      sourcePlaceholder: 'Bitte auswählen',
      errors: {
        name: 'Bitte gib einen gültigen Namen an.',
        phone: 'Bitte gib eine gültige Telefonnummer an.',
      },
    },
    companyAddress: {
      title: 'Wo ist Dein Firmensitz?',
      text: 'Mit der Angabe Deiner Firmenanschrift vervollständigst Du alle Kontaktdaten – für eine optimale Zusammenarbeit!',
      inputs: {
        street: 'Straße & Hausnummer',
        zipCode: 'PLZ',
        city: 'Ort',
      },
    },
    avv: {
      title: 'Datenschutz ist uns wichtig',
      text: 'Eine Auftragsverarbeitung liegt vor, wenn Daten weisungsgebunden im Auftrag von einem Auftragnehmer verarbeitet werden.',
      description: `Die Verarbeitung von Daten durch externe Dienstleister, die gemäß Deinen Anweisungen handeln, mag zunächst etwas abstrakt erscheinen. Doch wahrscheinlich hast Du bereits in vielen Fällen Erfahrungen mit dieser Art der Datenverarbeitung gemacht. Einige typische Beispiele, die für die meisten Unternehmen relevant sind, umfassen:</br>
      <ul style="list-style: outside; margin: 20px;">
        <li>Software-as-a-Service-Lösungen wie Newsletter- oder Buchhaltungstools</li>
        <li>Externe Lohnbuchhaltung</li>
        <li>Hosting Services für Deine Website</li>
      </ul>`,
      description2: 'Die Verwaltung von personenbezogenen Daten Deiner Interessenten und Kunden auf den Servern der Buhl Data Service GmbH erfordert laut Datenschutzgrundverordnung (DSGVO) den Abschluss einer Vereinbarung zur Auftragsverarbeitung.',
      texts: {
        avvText: (avv) => <>Ich stimme dem {avv} (AVV) zu.</>,
        avv: 'Auftragsverarbeitungsvertrag',
        avvShort: 'Auftragsvereinbarung',
        avvTextMobile: (avv) => <>Ich stimme der {avv} zu.</>,
      },
    },
    finished: {
      title: 'Herzlich Willkommen',
      text: 'Du hast Dein Unternehmen erfolgreich angelegt',
    },
    userExperience: {
      title: 'Dürfen wir eine letzte Frage stellen?',
      description: 'Wie bist Du auf orgaMAX aufmerksam geworden?',
    },
  },

  registration: {
    progress: (current, total) => `Schritt ${current} von ${total}`,
    close: 'Schließen',
    confirmEmail: {
      // word joiners (⁠) after the hyphens: on a phone the line breaks before "E-Mail-Adresse", never inside it
      title: 'Bestätige Deine E-⁠Mail-⁠Adresse',
      text: (email) => <>Wir haben Dir einen 4-stelligen Code an <b>{email}</b> geschickt.</>,
    },
    password: {
      text: 'Damit schützt Du den Zugang zu Deinem Benutzerkonto.',
      label: 'Passwort',
      next: 'Weiter',
    },
    avvInfo: {
      buttonLabel: 'Was ist der AVV?',
      title: 'Was ist der AVV?',
      text: (
        <>
          Wenn Du {productName} nutzt, speichern wir auch Daten Deiner Kunden für Dich.{' '}
          <b>Der AVV stellt sicher, dass wir diese Daten DSGVO-konform und nur in Deinem Auftrag und nach Deinen Weisungen verarbeiten.</b>
        </>
      ),
      hint: `Du schließt damit kein ${productName}-Abonnement ab.`,
      confirm: 'Verstanden',
      required: 'Bitte bestätige den AVV.',
    },
    userExperience: {
      title: `Wie bist Du auf ${productName} aufmerksam geworden?`,
      hint: `Deine Antwort ist freiwillig und hilft uns, ${productName} bekannter zu machen.`,
      finish: 'Test starten',
    },
    welcome: {
      expert: `Shereen Bornemann – ${productName} Expertin`,
      overline: 'Schön, dass Du da bist!',
      title: 'Schreib Deine erste Rechnung.',
      lead: 'Ich zeige Dir, wie.',
      startTour: 'Rundgang starten',
      firstInvoice: 'Erste Rechnung schreiben',
    },
  },

  createClient: {
    connection: {
      heading: '',
      alreadyConnected: 'Dieser Mandant ist bereits verknüpft.',
      title: 'Angaben zum Mandanten',
      text: 'Möchtest Du den Mandanten jetzt zur Zuammenarbeit einladen?',
      radio: {
        invite: 'Ja, Mandanten jetzt einladen',
        nonInvite: 'Nein, nur Stammdaten kanzleiintern anlegen',
        nonInviteHint: 'Du kannst Deinen Mandanten nachträglich zur Zusammenarbeit einladen',
      },
    },
    contactPerson: {
      heading: '',
      title: 'Lade einen Ansprechpartner ein',
      text: 'Der Ansprechpartner bekommt eine Einladung per E-Mail und wird als Administrator des Mandanten angelegt.',
      info: 'Dein Ansprechpartner kann später jederzeit Benutzer hinzufügen',
    },
    company: {
      heading: '',
      title: 'Stammdaten des Mandanten',
      tenantPresentTitle: 'Verknüpfte Unternehmen',
      tenantPresentText: (email) => `Unter der E-Mail ${email} sind mehrere Mandanten geführt:
      wähle den Mandanten aus, den Du verknüpfen möchtest`,
      text: 'Vervollständige wichtige Daten für die spätere Zusammenarbeit.',
      createNewTenant: 'Neuen Mandanten anlegen',
    },
    mail: {
      heading: '',
      title: 'Fast fertig',
      text: 'Schließe den Prozess ab. Im Anschluss wird eine Einladungsmail an Deinen Mandanten versendet.',
    },
    finish: {
      heading: '',
      titleOnline: 'Mandanten erfolgreich eingeladen',
      titleOffline: 'Mandant erfolgreich angelegt',
      textOnline: 'Sobald Dein Mandant die Einladung angenommen hat, kannst Du Aufgaben und Dokumente austauschen.',
      textOffline: 'Du kannst den Mandanten jederzeit nachträglich einladen, um Aufgaben und Dokumente mit ihm auszutauschen.',
    },
    companyForm: {
      person: 'Privatperson',
      ek: 'e. K.',
      trader: 'Gewerbetreibender',
      freelancer: 'Freiberufler',
      sole_trader: 'Sonstiger Einzelunternehmer',
      gbr: 'GbR',
      ohg: 'oHG',
      kg: 'KG',
      gmbh_cokg: 'GmbH & Co. KG',
      silent: 'Stille Gesellschaft',
      other_partnership: 'Sonstige Personengesellschaft',
      ug: 'Unternehmer Gesellschaft (UG)',
      gmbh: 'GmbH',
      ag: 'AG',
      legalFoundation: 'Rechtsfähige Stiftung',
      charitableFoundation: 'Gemeinnützige Stiftung',
      other_company: 'Sonstige Kapitalgesellschaft',
    },
    companyFormGroups: {
      person: 'Privatperson',
      onemanbusiness: 'Einzelunternehmen',
      partnerships: 'Personengesellschaften',
      corporations: 'Kapitalgesellschaften',
    },
    companyFormFields: {
      companyName: 'Name des Mandanten',
      clientNumber: 'Mandantennummer',
      companyForm: 'Rechtsform',
      responsibleUser: 'Zuständiger Sachbearbeiter',
    },
  },

  // documents and gmi
  documentTitle: `${productName} Belege`,
  documentTitleSlogan: 'Deine Rechnungen & Belege automatisiert im Überblick',
  documentAutomateMyDocumentsText: 'Meine Belege automatisieren',
  documentTryNowLabel: 'Jetzt Testen',
  documentBookNowLabel: 'Jetzt Buchen',
  documentNotBookable: 'Du kannst in Deinem aktuellen Paket keine automatisierten Belege buchen. Bitte wechsle Dein Paket',

  documentGMIButtonTitle: 'Belege synchronisieren',
  documentGMIExpiredButtonLabel: `Zu ${productName} Belege`,
  documentGMILoadText: 'Lade Dokumente ...',
  documentGMIExpiredDescription: `Dein ${productName} Belege Account ist nicht aktiv. Bitte aktiviere Deinen Account.`,

  documents: {
    showSignatureLabel: 'Zertifikat anzeigen',
    // FLAG:useDocuAi — tooltip for the AI badge on the document tile.
    aiProcessed: 'KI-analysiert',
    // FLAG:useDocuAi — title of the central review page (route handle).
    reviewPageTitle: 'Rechnung prüfen',
    // FLAG:useDocuAi — format details for a detected e-invoice; the syntax (CII/UBL …) comes from
    // the analysis result and is not translated.
    eInvoiceFormat: (syntax) => `E-Rechnung (${syntax})`,
    // FLAG:useDocuAi — header of the virtual „E-Rechnung" column in the expense list.
    eInvoiceColumnLabel: 'E-Rechnung',
    // Origin note on a child document (attachment) that came from another document.
    childOfParent: (parentName) => `aus: ${parentName}`,
    // FLAG:docuAiEnhancements — receipt chat in the document drawer (RAG knowledge base).
    ragChat: {
      title: 'Max – Belege-Chat — Fragen zu Prüfung & Analyse',
      emptyHint: 'Stelle eine Frage zur Belegprüfung, Rechnungsanalyse oder E-Rechnung — beantwortet aus der orgaMAX-Wissensbasis.',
      error: 'Der Chat ist gerade nicht erreichbar. Bitte versuche es später erneut.',
      // FLAG:docuAiEnhancements — without this button the error bubble is a dead end: users would
      // have to retype their question even though it is still in the history.
      retry: 'Erneut versuchen',
      retryNotice: 'Der Chat ist gerade nicht erreichbar. Ich versuche es automatisch erneut … ({attempt} von {max})',
      cancelled: 'Die Anfrage wurde abgebrochen. Du kannst sie erneut versuchen.',
      pending: 'Antwort wird erstellt …',
      inputPlaceholder: 'Frage zu Prüfen, Analyse, E-Rechnung …',
      // Context sent with the chat so the answer knows where the user is.
      viewContext: 'Dokument-Detailansicht (Drawer)',
      openFullView: 'Chat in Vollansicht öffnen',
      closeFullView: 'Vollansicht schließen',
      exportTranscript: {
        action: 'Chatverlauf exportieren',
        title: 'Max – Chatverlauf',
        exportedAt: 'Exportiert am',
        userRole: 'Deine Frage',
        assistantRole: 'Max',
        error: 'Fehler',
        sources: 'Quellen',
      },
      // FLAG:docuAiEnhancements — resize handle at the bottom right of the full view. Title rather
      // than tooltip: aiming at an 18px corner should not open a speech bubble as well.
      resizeHint: 'Ecke ziehen, um das Fenster zu vergrößern',
      // FLAG:docuAiEnhancements — report a knowledge-base gap from the chat (target 'ragchat').
      // Deliberately "knowledge base" rather than "analysis": the report concerns an incorrect or
      // missing ANSWER, not a misread receipt — the two go to different worklists.
      reportAnswer: 'Antwort melden',
      reportAnswerTitle: 'Was stimmt an dieser Antwort nicht?',
      reportAnswerPlaceholder: 'Was fehlt oder ist falsch? Die Frage und die Antwort werden mitgeschickt.',
      reportAnswerSubmit: 'Melden',
      reportAnswerSuccess: 'Danke — die Meldung ist eingegangen und fließt in die Wissensbasis ein.',
    },
    // FLAG:useDocuAi — caption for the invoice-review ("review") kind label (invoice / invoice correction).
    reviewInvoiceKindLabel: 'Art',
    // FLAG:useDocuAi — the reviewer's booking decision in the document review header (plan
    // `review-booking-target`). A document from the inbox has no upload intent; without the check
    // an expense is booked, with it a revenue. Deliberately phrased as a statement about the
    // RECEIPT ("is revenue"), not an instruction to the system ("book as revenue") —
    // the reviewer establishes what is in front of them; the outcome follows from that.
    reviewBookingTarget: {
      revenueLabel: 'Sonstige Einnahme',
      expenseLabel: 'Ausgabe',
      documentLabel: 'Nur Dokument',
      choiceLabel: 'Diesen Beleg speichern als',
      documentOnlyEffect: 'Es werden nur das Dokument und die Prüfdaten gespeichert. Es wird keine Ausgabe oder Einnahme angelegt und nichts gebucht.',
      // Rationale for a PRESELECTION. It appears only while the suggestion actually controls the
      // check box — neither after the reviewer's own click nor above an already stored decision.
      // A preselected check box without an explanation is worse than none; an explanation for
      // something the reviewer decided themselves is misleading.
      reason: {
        taxNumber: 'Vorbelegt: Deine Steuernummer steht als Rechnungssteller auf dem Beleg.',
        selfBilled: 'Vorbelegt: Gutschrift im Gutschriftverfahren — ausgestellt hat sie der Leistungsempfänger.',
      },
      // The server rejected the ledger switch because a receipt for this document already exists in
      // the other ledger. Say WHAT is blocking it and WHAT to do — "Saving failed" would
      // leave the reviewer with a check box they just selected deliberately. The convenient switch
      // is deliberately not implemented (plan T8).
      conflictError: 'Zu diesem Dokument gibt es bereits eine verknüpfte Ausgabe oder Einnahme. Lösche diesen Buchhaltungsbeleg zuerst — danach kannst Du eine andere Zuordnung oder „Nur Dokument“ wählen.',
    },
    // FLAG:useDocuAi — titles for the central invoice-review ("review") page.
    reviewIncomingInvoiceTitle: 'Eingangsrechnung prüfen',
    reviewCancellationInvoiceTitle: 'Stornorechnung prüfen',
    // FLAG:useDocuAi — label above the artifact/variant switcher on the review preview.
    reviewArtifactSelectLabel: 'Belegdokumente',
    // FLAG:useDocuAi — line-item list of the invoice review ("review"): filter, quick selection,
    // bulk assignment, and identification of special line-item types.
    reviewPositions: {
      filterPlaceholder: 'Positionen nach Artikelname filtern…',
      filterClear: 'Filter zurücksetzen',
      quickSelect: 'Schnellauswahl',
      selectAll: (count) => `Alle · ${count}`,
      clearSelection: 'Auswahl aufheben',
      reset: 'Zurücksetzen',
      selectedCount: (count) => `${count} ausgewählt`,
      assignCategory: 'Kategorie zuordnen',
      selectPosition: (number) => `Position ${number} auswählen`,
      positionPlaceholder: (number) => `Position ${number}`,
      editDescription: 'Klicken zum Bearbeiten',
      editDescriptionAria: (number) => `Bezeichnung von Position ${number} bearbeiten`,
      // Two distinct empty states: nothing MATCHES vs. nothing exists.
      noMatch: (filterText) => `Keine Position passt zu „${filterText}“.`,
      empty: 'Keine Positionen. Über „Neue Belegposition“ kannst Du eine anlegen.',
      // Tax rate retained during bulk assignment although the category does not normally support it.
      keptVatRate: (count) => `Steuersatz für ${count} Position(en) beibehalten — bitte prüfen, ob er zur Kategorie passt.`,
      // FLAG:useDocuAi — foreign currency: the tax category is locked because selecting it triggers
      // booking, while accounting accepts only euros.
      foreignCurrencyCategoryHint: 'Fremdwährung — eine Buchung ist nur in Euro möglich. Der Beleg kann geprüft und gespeichert werden, eine Steuerkategorie lässt sich erst nach der Umrechnung zuordnen.',
      kind: {
        deposit: 'Pfand/Leergut',
        credit: 'Gutschrift',
        depositHint: (reason) => `Pfand/Leergut — eigene steuerpflichtige Position mit eigenem USt-Satz, wird als eigene Buchungszeile gebucht${reason}`,
        creditHint: (reason) => `Gutschrift — gutgeschriebene bzw. zurückgegebene Position (negativ)${reason}`,
      },
    },
    // FLAG:useDocuAi — contact section of the invoice review ("review"): the counterparty's role,
    // how it is matched to master data, and what saving does.
    reviewPayee: {
      sectionTitle: 'Kontakt',
      // One term for BOTH the selection and prose, so the sentence about what saving creates names
      // exactly the role the reviewer clicked — never the technical term
      // "master record"/"business partner".
      roles: {
        supplier: 'Lieferant',
        companyCustomer: 'Kunde',
        privateCustomer: 'Privatkunde',
      },
      // Roles outside the detected direction remain selectable; the notice merely names the reason
      // instead of sending the reviewer into a bare required-field error.
      directionHint: {
        incoming: 'Erkannt als Eingangsrechnung — Du bist der Rechnungsempfänger, die Gegenseite also ein Lieferant.',
        outgoing: 'Erkannt als Ausgangsrechnung — Du bist der Aussteller, die Gegenseite also ein Kunde.',
      },
      // Under the private-customer role the company name is neither shown nor validated — say so
      // instead of silently dropping a detected company.
      privateCustomerCompanyHint: (company) => ` Auf dem Beleg steht die Firma „${company}“ — als Privatkunde wird sie nicht übernommen.`,
      assigned: (role) => `${role} zugeordnet.`,
      assignedNew: (role) => `Neuer ${role} erkannt.`,
      // FLAG:useDocuAi — die USt-IdNr vom Beleg liegt auf mehreren Lieferanten der Stammdaten.
      // Sie hat die Zuordnung dann NICHT entschieden; der Prüfer soll das wissen, statt sich zu
      // wundern, warum ein fremder oder gar kein Lieferant zugeordnet wurde.
      vatIdAmbiguousHeadline: 'USt-IdNr. mehrfach vergeben.',
      vatIdAmbiguousBody: 'Die USt-IdNr. auf diesem Beleg gehört mehreren Lieferanten in Deinen Stammdaten. Sie konnte den Kontakt deshalb nicht zuordnen — bitte prüfe die Zuordnung.',
      bodyWillUpdate: 'Die zugeordneten Stammdaten werden beim Speichern mit diesen Angaben aktualisiert.',
      bodyFromDirectory: 'Die Angaben stammen aus Deinen Stammdaten.',
      bodyWillCreate: 'Dieser Kontakt wird nach dem Speichern in Deine Stammdaten übernommen.',
      companyNameRequired: 'Firmenname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      noName: 'Ohne Namen',
      firstNameLabel: 'Vorname',
      lastNameLabel: 'Nachname*',
      companyNameLabel: 'Firmenname*',
      companyNameAffixLabel: 'Namenszusatz',
      assignLabel: 'Zuordnung ändern',
      assignPlaceholder: 'Kontakt suchen oder auswählen…',
      assignHint: 'Nach Auswahl werden die OCR-Daten durch die Stammdaten ersetzt.',
    },
    // FLAG:useDocuAi — paid-status hint on the review page (v1 display-only).
    // FLAG:useDocuAi — review marker for the review's HEADER FIELDS (receipt date, invoice number,
    // delivery date). Until 2026-08-14 only the „Bezahlung" card had a marker, although the receipt
    // chat promised the reviewer one for every flagged field — the reviewer searched for a marker
    // that did not exist (DMS issue a7285ab1, Casebook 0085). Wording deliberately matches
    // reviewPaymentStatus; it is the same statement.
    reviewFieldCheck: {
      checkHint: 'prüfen',
      lowConfidence: 'Bitte prüfen: Angabe unsicher erkannt',
      // Missing dates remain empty in document review and require the printed date before saving.
      dateNotDetermined: 'Die Analyse hat kein Belegdatum ermittelt. Bitte vom Beleg übernehmen.',
      // An empty required field with a red „prüfen" tells the reviewer only THAT
      // something is missing, not that we looked and found nothing. Some receipts (such as premium
      // invoices) simply have no number; leaving it empty is then correct.
      receiptNumberNotDetected: 'Auf dem Beleg konnten wir keine Rechnungsnummer sicher erkennen. '
        + 'Wir haben das Feld deshalb leer gelassen und zum Prüfen markiert — bitte trage die '
        + 'Nummer nach oder lasse das Feld leer, wenn der Beleg keine ausweist.',
    },
    reviewPaymentStatus: {
      lowConfidence: 'Bitte prüfen: Angabe unsicher erkannt',
      // FLAG:useDocuAi — shown under the IBAN field when the typed value fails the client
      // format/mod-97 check, so the reviewer fixes it before a save the server would reject.
      ibanInvalid: 'Bitte prüfen: keine gültige IBAN',
      // FLAG:useDocuAi — compact per-field "please check" marker next to a flagged
      // review field; the validationNote (why) is the tooltip.
      checkHint: 'prüfen',
      paymentMethods: {
        credit_transfer: 'Überweisung',
        direct_debit: 'Lastschrift',
        card: 'Karte',
        cash: 'Bar',
        paypal: 'PayPal',
      },
      debitAccountMatch: (account) => `Einzug passt zu Deinem hinterlegten Konto: ${account}`,
      // The old read-only direct-debit-account hint is gone: the debit account is a
      // reviewer-editable field now (fields.debitIban), so printing the value a second time
      // below its own input would just be a stale echo of what the reviewer is typing.
      // FLAG:useDocuAi — the document states more than one payee IBAN, so the reviewer
      // has to pick the account this invoice is actually paid to. Split into parts
      // because the count is emphasised inside the sentence.
      ibanChoice: {
        bannerPrefix: 'Im Dokument wurden ',
        bannerCount: (count) => `${count} IBANs`,
        bannerSuffix: ' erkannt. Bitte wähle das Zahlungskonto für diese Rechnung aus.',
        ariaLabel: 'Zahlungskonto',
        checkHint: 'Bitte IBAN prüfen',
        // The account scan reports every IBAN on the page, footer/imprint accounts included.
        // Removing one drops it from the list the save persists, so it stops being offered.
        deleteIban: 'IBAN löschen',
        // The save appends the confirmed IBAN to the linked supplier/customer
        // (receiptReviewCapabilities: attachReviewedBankAccount), so the next document
        // from the same party is recognized by its IBAN. Say so where the choice is made.
        storedWithParty: 'Die gewählte IBAN wird beim Speichern beim zugeordneten Lieferanten oder Kunden hinterlegt.',
        // A single recognized account needs no choice UI, but the reviewer must still be
        // able to get back to it after clearing or overtyping the field.
        adoptRecognized: (iban) => `Erkannte IBAN übernehmen: ${iban}`,
        // FLAG:useDocuAi — DocuAI detects the currency PRINTED beside an account (contract 1.7.0) and
        // marks an account whose currency differs from the invoice currency. The notice text normally
        // comes from DocuAI (validationNote) and names both currencies; this is the fallback when only
        // the code arrives.
        foreignCurrency: (currency) => `Konto in ${currency} – bitte prüfen`,
      },
      // FLAG:useDocuAi — editable review payment fields (v2: the banner values became
      // reviewer-editable fields that persist on save).
      fields: {
        sectionTitle: 'Zahlungsangaben',
        status: 'Bezahlt-Status',
        openAmount: 'Offener Betrag',
        method: 'Zahlungsart',
        // Names the SIDE, not just the field: on a Lastschrift the reviewer reads this
        // box expecting the account being debited, but it is (and must stay) the payee's.
        // The buyer's direct-debit account is shown below. Plain "IBAN" makes both
        // indistinguishable — the relabel was already made on 2026-07-05 and was lost
        // when the branch got rewritten into the 18./22.07. squash commits.
        iban: 'IBAN des Verkäufers',
        // The buyer-side account debited by the direct debit, as printed (usually X-masked).
        // Editable like every other review value — the scan misreads a masked account often
        // enough that a read-only display left the reviewer with nothing to do about it.
        debitIban: 'Einzugskonto',
        // FLAG:useDocuAi — bank and BIC of the payment account. Until 2026-08-11 these were display-only
        // values on the first detected IBAN; now they have separate review fields because a receipt
        // with multiple accounts can show the bank row on the wrong account, leaving the reviewer
        // unable to correct it (issue bc3e6bbc).
        bankInstitute: 'Bank',
        bic: 'BIC',
        cardNumber: 'Kartennummer',
        cardScheme: 'Kartenaussteller',
        none: '— keine Angabe —',
        statusOptions: {
          paid: 'Bezahlt',
          partially_paid: 'Teilweise bezahlt',
          unpaid: 'Offen',
        },
      },
    },
    reviewTaxCalculation: {
      grossToNetHint: 'Netto und Umsatzsteuer wurden aus Bruttobeträgen berechnet. Durch Rundung können Cent-Abweichungen entstehen.',
      // FLAG:useDocuAi — the receipt states gross line items (for example a Telekom mobile invoice).
      // We calculate net and VAT per line item ourselves and round per line, so the sum of the lines
      // can differ from the stated invoice totals by cents.
      grossPositionsRoundingHint: 'Die Positionsbeträge sind Bruttobeträge. Netto und Umsatzsteuer je Position werden berechnet – durch Rundung je Zeile können Cent-Abweichungen zu den ausgewiesenen Rechnungssummen entstehen.',
    },
    // FLAG:useDocuAi — foreign currency detected: the analysis flagged this invoice as issued in a
    // foreign currency (invoiceFacts.conversionRequired). The recognised amounts are NOT euros,
    // so they must not be booked as such until an EUR conversion rate is applied.
    reviewForeignCurrency: {
      title: 'Fremdwährungsbeleg erkannt',
      hint: (amountText) => `Dieser Beleg wurde in ${amountText} ausgestellt — die erkannten Beträge sind keine Euro-Beträge. Ordne den Beleg der Abbuchung auf Deiner Kreditkarte bzw. Deinem Konto zu ("Zuordnen"), sobald diese erscheint: Erst dort steht der tatsächlich belastete EUR-Betrag.`,
    },
    // FLAG:useDocuAi — final invoice with a fully offset advance payment. Only the title lives
    // here: DocuAI supplies the explanatory sentence with the finding (validationNote for
    // `settled_by_prepayment`) so detection and explanation cannot diverge.
    reviewSettledByPrepayment: {
      title: 'Anzahlung bereits verrechnet',
    },
    // FLAG:useDocuAi — "known benign situations": detected states in which the result may
    // legitimately differ from expectations. Deliberately NO text here: DocuAI supplies the title
    // and explanation in the contract (1.8.0, resolved against the editable catalog) so the same
    // situation is not described in two repositories. See modules/document/utils/analysis-notices.ts.
    // FLAG:useDocuAi — §3.5 XML lock: values mapped from an XML e-invoice are
    // legally binding and read-only in the review.
    xmlLockedValueHint: 'Bei XML-Rechnungen darf dieser Wert nicht geändert werden',
    // FLAG:useDocuAi — §4.2: assigning the tax account whose tax rate differs from
    // the detected one needs an explicit confirm; declining keeps the rate.
    vatChangeConfirm: {
      title: 'Steuersatz der Position ändern?',
      description: (currentRate, accountRate) => `Für diese Position wurden ${currentRate} % erkannt, das gewählte Steuerkonto verwendet ${accountRate} %. Soll der Steuersatz auf ${accountRate} % geändert werden?<br/>Bei „Nein“ wird nur das Konto zugeordnet und ${currentRate} % beibehalten.`,
    },
    previewCarouselWrapperTabs: {
      original: 'Original',
      xml: 'XML',
      report: 'Bericht',
      // FLAG:useDocuAi — KoSIT validation report + embedded-attachment variants in the preview switcher.
      validationReport: 'Prüfbericht',
    },
    signatureAssignmentLabel: (date) => `Unterschriftenzertifikat ${date}`,
    // FLAG:useDocuAi — embedded-image OCR stats (DocuAI)
    imageOcr: {
      title: 'Eingebettete Bilder',
      detected: (count) => `${count} ${count === 1 ? 'Bild' : 'Bilder'} erkannt`,
      large: (count) => `${count} groß`,
      ocrOff: 'OCR: aus',
      ocrDone: (ocred, chars) => `OCR: ${ocred} verarbeitet (+${chars} Zeichen)`,
      runOcr: 'OCR ausführen',
      running: 'OCR läuft…',
      requestStarted: 'OCR wurde gestartet. Die Ergebnisse erscheinen in Kürze.',
      requestFailed: 'OCR konnte nicht gestartet werden.',
    },
    noOcrResultsFound: 'Es wurden keine Ergebnisse gefunden',
    ocrAllValuesOverwritten: 'Alle gefundenen Werte wurden bereits manuell überschrieben und können nicht ersetzt werden',
    loadingFile: 'Dokument wird geladen...',
    // FLAG:useDocuAi — the two honest end states of the validation-report preview. Both used to show
    // `loadingFile`, which left the user waiting forever on a document that would never arrive.
    validationReportUnavailable: 'Der Prüfbericht konnte nicht geladen werden. Bitte versuche es später erneut.',
    validationReportNotRenderable: 'Für dieses Dokument liegt kein darstellbarer Prüfbericht vor.',
    // FLAG:useDocuAi — video/audio player in the document drawer (ported from the DMS admin
    // dashboard, MediaViewer/MediaControls).
    mediaPlayer: {
      play: 'Abspielen',
      pause: 'Pause',
      skipBackward: '10 Sekunden zurück',
      skipForward: '10 Sekunden vor',
      mute: 'Stummschalten',
      unmute: 'Ton an',
      fullscreen: 'Vollbild',
      exitFullscreen: 'Vollbild beenden',
      volume: 'Lautstärke',
      progress: 'Wiedergabeposition',
      loading: 'Medien werden geladen...',
      loadFailed: 'Die Datei konnte nicht geladen werden.',
      // The file is valid — the browser has no decoder for this format. This mainly affects
      // MPEG-1/MPEG-2 (.mpeg/.mpg) and AVI: no browser plays them, while MPEG-4/H.264 (.mp4) works
      // without issue. Therefore offer the download escape hatch here instead of a dead end.
      notPlayable: 'Dieses Videoformat (z. B. MPEG-1/MPEG-2 oder AVI) kann von keinem Browser abgespielt werden. Die Datei ist in Ordnung — lade sie herunter, um sie in einem lokalen Player zu öffnen.',
      download: 'Herunterladen',
    },
    zipDownloadStarted: 'Zip-Download wurde gestartet',
    signatureDrawerTitle: (name) => `Dokument "${name}" zur Unterschrift freigeben`,
    todo: 'Aufgabe',
    payedButtonLabel: 'Bezahlt',
    previousDocument: 'Vorheriges Dokument',
    nextDocument: 'Nächstes Dokument',
    drawer: {
      footer: {
        createDownloadLink: 'Downloadlink erstellen',
        dayCount: 'Gültigkeit in Tagen',
        reportProblem: 'Analyse-Problem melden',
        reportProblemAction: 'Problem melden',
        reportProblemTitle: 'Analyse-Problem melden',
        reportProblemPlaceholder: 'Beschreibe das Problem mit der Analyse dieses Dokuments …',
        reportProblemSubmit: 'Senden',
        reportProblemSuccess: 'Problem gemeldet. Vielen Dank!',
        // Accepted but not yet handed to analysis (most often because the document analysis has not
        // finished). The DMS retries delivery automatically.
        reportProblemQueued: 'Problem gemeldet. Die Meldung wird an die Analyse übermittelt.',
        reportProblemError: 'Problem konnte nicht gemeldet werden.',
        reportProblemNetworkError: 'Keine Verbindung zum Server. Deine Eingabe bleibt erhalten – bitte versuche es erneut.',
        reportProblemTooMany: 'Zu viele Tickets. Kontaktiere unseren Kundensupport.',
        reportSearchProblem: 'Such-Problem melden',
        reportSearchProblemTitle: 'Problem mit der Suche melden',
        reportSearchProblemPlaceholder: 'Beschreibe das Problem mit dieser Suche (z. B. erwartetes Dokument fehlt, falsche Treffer) …',
      },
      labels: {
        bankInstitute: 'Bankinstitut',
        name: 'Name',
        assignedTo: 'Verknüpft mit',
      },
      pager: (count, offset) => `Dokument ${offset} von ${count}`,
    },
    calculation: {
      tooltips: {
        cancel: 'Abbrechen',
      },
      label: 'wird verarbeitet...',
      error: 'konnte nicht verarbeitet werden...',
      errors: {
        mimetype: 'Dieser Dateityp ist nicht erlaubt',
      },
    },
    uploading: {
      tooltips: {
        cancel: 'Abbrechen',
        pause: 'Pausieren',
        resume: 'Fortsetzen',
        retry: 'Neu starten',
        remove: 'Aus Liste entfernen',
        open: 'Dokument anzeigen',
        openFolder: 'Ordner öffnen',
      },
      states: {
        done: 'Abgeschlossen',
        pausing: 'Wird pausiert...',
        paused: 'Pausiert',
        running: 'Wird hochgeladen...',
        error: 'Fehler beim Hochladen',
        // FLAG:useDocuAi — the step after the bytes reached the DMS. Without its own state the row
        // showed "Abgeschlossen" while complete-upload was still running and could still fail.
        completing: 'Wird abgeschlossen...',
        // FLAG:useDocuAi — an automatic retry is in progress; shown so a backoff pause does not
        // look like a stalled upload.
        retrying: (attempt, maxAttempts) => `Erneuter Versuch ${attempt}${maxAttempts ? ` von ${maxAttempts}` : ''}...`,
      },
    },
    uploadParseError: 'Datei konnte nicht eingelesen werden.',
    uploadError: 'Datei konnte nicht hochgeladen werden.',
    documentScanTitle: 'Dokumentenscans',
    receiptScanTitle: 'Belegescans',
    moveMenuLabel: 'Verschieben nach',
    openDocumentFolder: 'Ablageordner öffnen',
    favoritesFirst: 'Favoriten zuerst',
    selectedFirst: 'Markierte zuerst',
    createdBy: 'Hinzugefügt von',
    selectAllTooltip: 'Alle auswählen. Bitte beachte, dass nur die Dokumente ausgewählt werden, die Du angezeigt bekommst.',
    disabledIconToolTip: 'Du hast min. ein Dokument ausgewählt, das diese Aktion nicht unterstützt',
    setFavorite: 'Als Favorit markieren',
    removeFavorite: 'Als Favorit entfernen',
    unlinkReceiptTitle: (receiptType) => `${receiptType} entfernen`,
    linkDeleteConfirmText: 'Möchtest Du die Verknüpfung zur {{document}} wirklich aufheben?',
    erpEditError: {
      message: `Das Dokument ist mit ${windowsProductName} verknüpft und kann nur dort bearbeitet werden.`,
      title: 'Die Aktion konnte nicht ausgeführt werden.',
    },
    erpDeleteError: (num) => (num === 1 ? 'Das Dokument' : 'Mindestens eins der Dokumente') + ` ist mit ${windowsProductName} verknüpft und kann nur dort gelöscht werden.`,
    dashboard: {
      emptyStateDesc: 'Verwalte Dein erstes Dokument',
    },
    addFolder: 'Ordner erstellen',
    addDocument: 'Dokumente hochladen',
    widgets: {
      upgradeNow: 'Jetzt upgraden',
      scans: {
        title: 'Dokument-Analysen',
        usedLabel: 'Gescannt',
      },
      documentCount: {
        title: 'Dokumente',
        titleYear: 'Dokumente',
        usedLabel: 'Verbraucht',
        startDocuments: (count, end) => `inkl. ${count} Startdokumente bis zum ${end}`,
      },
      storageSpace: {
        title: 'Speicherplatz',
        usedLabel: 'Belegt',
      },
    },
    monthLabel: 'mtl.',
    yearLabel: 'jährl.',
    planUpgradeLabel: 'Plan wählen',
    planUpgradeCancelLabel: 'Jetzt nicht',
    upload: {
      interruptPendingUploadsDescription: 'Wenn Du den Mandanten wechselst, werden laufende Uploads unterbrochen',
      interruptPendingUploadsTitle: 'Uploads unterbrechen',
      uploadsPending: 'Es werden noch Uploads ausgeführt',
      errors: {
        wrongFile: 'Es wurde anscheinend nicht die richtige Datei ausgewählt',
        uploadFailed: 'Datei konnte nicht hochgeladen werden',
      },
      analyzeOnlyIncomingInvoices: 'Diese Funktion steht nur für die Art "Rechnungseingang" zur Verfügung.',
      status: {
        title: 'Uploads',
        progressSubtitle: ({ completedCount, fullCount }) => `${completedCount} von ${fullCount} fertig`,
      },
    },
    ocr: {
      header: 'OCR - Ergebnis',
      textLabel: 'Folgender Text wurde erkannt',
      selectLabel: 'Ordne den erkannten Text zu',
    },
    email: {
      menuItem: 'Per E-Mail versenden',
      header: 'Dokument per E-Mail versenden',
      subject: (name, eSign) => eSign ? `Dokument zur Unterschrift: ${name}` : `Freigegebenes Dokument ${name}`,
      text: (name, eSign) => eSign
        ? `Sehr geehrte Damen und Herren,</br></br>
            bitte unterzeichnen Sie das angehängte Dokument unter dem unten aufgeführten Link:`
        : `Sehr geehrte Damen und Herren,</br></br>
      es wurde für Sie ein Dokument von ${name} zur Ansicht freigegeben. Sie können das Dokument unter folgendem Link aufrufen und es sich in dem Portal anschauen:`,
    },
    enableSharingHeader: 'Dokument freigeben',
    enableSharing: `Du kannst dieses Dokument im ${customerCenterName} zur Ansicht freigeben.
    Mit Klick wird der Link zur Freigabe in den Zwischenspeicher gelegt.
    Des Weiteren ist es möglich diese Freigabe direkt via E-Mail zu senden.
    Die Freigabe kann jederzeit wieder entzogen werden.`,
    sharingNotAllowed: `Nur PDF Dateien können im ${customerCenterName} freigegeben werden.`,
    disableSharing: `Freigabe im ${customerCenterName} beenden`,
    sharingDisabled: 'Freigabe beendet. Der zuvor erstellte Link ist damit ungültig geworden.',
    shareHUBLinkCopied: 'Link erfolgreich in die Zwischenablage kopiert.',
    shareHUBLinkTooltip: 'Datei per Direktlink freigeben',
    copyShareHUBLink: 'Link kopieren',
    shareHub: customerCenterName,
    isShared: `Dokument ist im ${customerCenterName} freigegeben.`,
    isAssignedToWindows: 'Das Dokument kann nur in orgaMAX ERP bearbeitet werden.',
    // FLAG:useDocuAi — analysis-state tile badge labels (only rendered on the useDocuAi path).
    invoiceAnalysisReview: 'Zu prüfen',
    invoiceAnalysisComplete: 'Geprüft',
    invoiceAnalysisFailed: 'KI-Analyse fehlgeschlagen',
    invoiceAnalysisRetryExhausted: 'Die KI-Analyse ist erneut fehlgeschlagen. Bitte wende Dich an den Support.',
    // FLAG:useDocuAi — non-terminal transient-retry state (plan docuai-analysis-retrying):
    // DocuAI hit an infrastructure problem and retries by itself; the tile says so instead
    // of blinking endlessly.
    invoiceAnalysisRetrying: 'KI-Analyse verzögert',
    invoiceAnalysisRetryingTooltip: (retrying) => {
      const attempt = retrying?.attempt && retrying?.maxAttempts
        ? ` (Versuch ${retrying.attempt}/${retrying.maxAttempts})`
        : ''
      return `KI-Analyse verzögert — vorübergehende Störung, wird automatisch wiederholt${attempt}.`
    },
    invoiceAnalysisRetryNow: 'Jetzt erneut versuchen',
    invoiceAnalysisRetryNowStarted: 'Neuer Analyse-Versuch gestartet.',
    invoiceAnalysisRetryNowNotPossible: 'Sofortiger Versuch gerade nicht möglich — die automatische Wiederholung läuft weiter.',
    invoiceAnalysisPartial: 'Teilweise geprüft',
    invoiceAnalysisInvalid: 'E-Rechnung ungültig',
    invoiceAnalysisValid: 'E-Rechnung gültig',
    // FLAG:useDocuAi — terminal states without a result (plans/ausgaben-analyse-terminal-states)
    invoiceAnalysisAborted: 'Analyse abgebrochen',
    invoiceAnalysisNoInvoice: 'Kein Beleg erkannt',
    // FLAG:useDocuAi — terminal states for documents that produce no analysis result at all.
    // Both are answers, not shrugs: before them these documents showed nothing, which was
    // indistinguishable from an analysis that silently never started.
    analysisNotApplicable: 'Keine KI-Analyse für diesen Dateityp',
    analysisContentIndexed: 'Inhalt indexiert – Archiv ist durchsuchbar',
    // FLAG:useDocuAi — the analysis detected a different type from the one on this document and
    // REPLACED it. Informational, not a question: the change has already happened when this shows.
    // A user can set the type back manually; naming both types is what makes that possible.
    classificationMismatch: (detected, current) =>
      `Die KI-Analyse hat „${detected}“ erkannt. Die Art wurde von „${current}“ auf „${detected}“ geändert.`,
    // FLAG:useDocuAi — header for the list-view analysis-state column.
    invoiceAnalysisColumn: 'Prüfung',
    // FLAG:useDocuAi — "review again": re-open the review of an already checked invoice. The
    // document stays reviewed throughout; it never returns to the "Zu prüfen" list.
    reReviewInvoice: 'Erneut prüfen',
    openInvoiceReview: 'Prüfung öffnen',
    // FLAG:useDocuAi — the same entry once the linked receipt has left the editable states. The
    // review would still OPEN and still let the payee be typed over, but the save drops the
    // reviewed payee, totals and positions (`skipped: 'expenseNotDraft'`), so the edit never
    // arrives. Deliberately NOT a silently greyed-out row: a wordlessly disabled „Kategorie
    // zuordnen" once cost an hour of searching for a cause that was never on screen. The row says
    // in its own label that it is closed, the tooltip says what would be lost, and it names the
    // door that stays open — viewing the review data under „Dokumente" is legitimate.
    openInvoiceReviewLocked: {
      action: 'Prüfung öffnen (nicht mehr änderbar)',
      reason: 'Der Beleg ist nicht mehr änderbar: Lieferant, Beträge und Positionen aus der Prüfung würden beim Speichern verworfen. Unter „Dokumente“ lassen sich die Prüfdaten weiterhin ansehen.',
      checking: 'Wird geprüft, ob dieser Beleg noch geändert werden kann …',
    },
    // FLAG:useDocuAi — analysis has FINISHED and found no invoice. This is a result, not a failure:
    // receipt upload was the wrong door for this document, and the reviewer should either remove
    // it or deliberately keep it under „Dokumente". The question uses the same wording in both
    // places — immediately after upload and later in the list row menu — so the same situation is
    // not described differently twice.
    noInvoiceDetected: {
      title: 'Kein Rechnungsbeleg erkannt',
      // Deliberately WITHOUT the filename: the description is rendered as HTML
      // (awaitableConfirmationModal), and a document name is user input.
      description: 'Die KI-Analyse hat in diesem Dokument keine Rechnung gefunden. Es kann deshalb nicht geprüft und nicht als Beleg gebucht werden.<br /><br />Möchtest Du das Dokument wieder löschen? Wenn Du es behältst, bleibt es unter „Dokumente" gespeichert.',
      confirm: 'Dokument löschen',
      cancel: 'Behalten',
      // Receipt upload hosts retain the document and open the manual receipt form.
      manualEntryDescription: 'Die KI-Analyse hat in diesem Dokument keine Rechnung gefunden. Wenn Du es behältst, wird das Dokument angezeigt und Du kannst rechts die Belegdaten manuell eingeben und speichern.<br /><br />Möchtest Du das Dokument behalten oder löschen?',
      manualEntryKept: 'Das Dokument wurde behalten. Du kannst die Belegdaten jetzt manuell eingeben und speichern.',
      deleted: 'Das Dokument wurde gelöscht.',
      kept: 'Das Dokument bleibt unter „Dokumente" gespeichert.',
      error: 'Das Dokument konnte nicht gelöscht werden.',
    },
    // FLAG:useDocuAi — format/KoSIT validation judged the e-invoice INVALID. This is not an analysis
    // error but a verdict on the receipt: it should be rejected and must not be corrected anyway
    // (its values are locked in the review). Saving remains allowed — the reviewer should not lose
    // their work — but not casually: the notice explicitly says that the verdict remains.
    invalidEInvoiceSave: {
      title: 'E-Rechnung ist ungültig',
      // Deliberately WITHOUT the filename: the description is rendered as HTML
      // (awaitableConfirmationModal), and a document name is user input.
      description: 'Die Formatprüfung hat diese E-Rechnung als <b>ungültig</b> beurteilt. Eine ungültige E-Rechnung ist zurückzuweisen — sie darf nicht korrigiert werden, deshalb sind ihre Belegwerte hier gesperrt.<br /><br />Du kannst Deine Prüfung trotzdem speichern. Die Rechnung bleibt danach als ungültig gekennzeichnet.',
      // The same statement as PLAIN TEXT for the "review again" dialog: it appears in a MUI <Alert>,
      // which does not render HTML. Two versions of one statement — the alternative would have been
      // two consecutive dialogs, which is worse.
      alert: 'Die Formatprüfung hat diese E-Rechnung als ungültig beurteilt. Eine ungültige E-Rechnung ist zurückzuweisen und darf nicht korrigiert werden; sie bleibt auch nach dem Speichern als ungültig gekennzeichnet.',
      confirm: 'Trotzdem speichern',
      cancel: 'Abbrechen',
    },
    // FLAG:useDocuAi — generic terminal counterpart used for failed/aborted review rows. Unlike
    // noInvoiceDetected it must not claim that the analysis completed or found a particular Art.
    // FLAG:useDocuAi — the door back when analysis RAN but read the document as the wrong type (a
    // Telekom „Mobilfunkreport" is classified as a billing statement, so no invoice schema runs
    // and every field stays empty). The user sets the type to „Rechnungseingang" themselves; this
    // entry reruns analysis with exactly that assertion. Unlike „In Prüfung zurücksetzen", this is
    // a GENUINELY new run and costs accordingly — hence the confirmation and note that it takes a
    // moment.
    reanalyzeAsInvoice: {
      menuAction: 'Als Rechnung analysieren',
      title: 'Als Rechnung analysieren?',
      description: 'Die KI-Analyse hat in diesem Dokument keine Rechnung erkannt. Du hast die Art auf „Rechnungseingang" gesetzt — die Analyse wird jetzt mit dieser Vorgabe neu ausgeführt und liest die Rechnungsdaten aus.<br /><br />Das dauert einen Moment. Pro Dokument ist ein solcher Durchlauf möglich.',
      confirm: 'Analyse starten',
      cancel: 'Abbrechen',
      started: 'Die Analyse wurde gestartet.',
      notPossible: 'Die Analyse konnte nicht erneut gestartet werden — sie wurde für dieses Dokument bereits einmal wiederholt.',
    },
    // FLAG:useDocuAi — „Erneut analysieren": a GENUINELY new AI run for a receipt that was already
    // analyzed successfully as an invoice. The underlying case: the stored result is frozen and
    // no later DocuAI improvement ever reaches it.
    //
    // The text must say TWO things users would otherwise expect incorrectly: (1) the existing
    // review remains — the result is not applied automatically, and (2) it is applied only with
    // „In Prüfung zurücksetzen". Without (2), users look for the new result and find their old values.
    refreshAnalysis: {
      menuAction: 'Erneut analysieren',
      title: 'Erneut analysieren?',
      description: 'Die KI analysiert dieses Dokument komplett neu — zum Beispiel, wenn die Erkennung inzwischen verbessert wurde.<br /><br />Das neue Ergebnis wird anschliessend übernommen: es ersetzt die Werte in der Prüfung, Deine bisherigen Korrekturen an diesem Beleg gehen dabei verloren und der Beleg steht wieder auf „Zu prüfen".<br /><br />Das dauert einen Moment und verbraucht eine Deiner monatlich verfügbaren Neuanalysen.',
      descriptionUnreviewed: 'Die KI analysiert dieses Dokument komplett neu — zum Beispiel, wenn die Erkennung inzwischen verbessert wurde. Die neuen Werte ersetzen anschliessend die Werte in der Prüfung; noch nicht gespeicherte Eingaben gehen dabei verloren.<br /><br />Das dauert einen Moment und verbraucht eine Deiner monatlich verfügbaren Neuanalysen.',
      confirm: 'Analyse starten',
      cancel: 'Abbrechen',
      started: 'Die Analyse wurde gestartet.',
      // Say WHAT is happening and WHEN it will end. "Could not be started" left users
      // unsure whether the cause was them, the receipt, or a service disruption. The day count runs
      // until the next UTC month boundary — the same window the server enforces; a locally calculated
      // month boundary would be wrong on the first day of the month.
      quotaExceeded: (days) => (days <= 1
        ? 'Du hast Dein Kontingent für Neuanalysen aufgebraucht. Morgen beginnt der neue Monat und es steht wieder zur Verfügung.'
        : `Du hast Dein Kontingent für Neuanalysen aufgebraucht. In ${days} Tagen beginnt der neue Monat und es steht wieder zur Verfügung.`),
      analysisPending: 'Für dieses Dokument läuft bereits eine Analyse.',
      // The receipt has reached the maximum number of processing attempts; the DMS accepts no more
      // runs for it. Without this sentence this was the worst case of all: the click was accepted,
      // the tile spun, and only a watchdog stopped it minutes later — with no indication anywhere
      // that nothing would ever run. Deliberately no "try again later": waiting will
      // not change anything here.
      indexAttemptsExhausted: 'Dieser Beleg hat die maximale Anzahl an Verarbeitungsversuchen erreicht und kann nicht erneut analysiert werden. Bitte wende Dich an den Support.',
      notPossible: 'Die Analyse konnte nicht gestartet werden.',
    },
    // FLAG:useDocuAi — notice that a new run was accepted. Without it, „Erneut analysieren" ends
    // invisibly: the review correctly keeps its values, and nobody learns whether or when the new
    // result is available.
    freshAnalysis: {
      headline: 'Eine neuere KI-Analyse liegt vor.',
      body: 'Deine geprüften Werte sind unverändert. Du kannst die neuen Werte übernehmen — Deine Korrekturen gehen dabei verloren.',
      action: 'Werte übernehmen',
      confirmTitle: 'Neue Analyse übernehmen?',
      confirmDescription: 'Alle Werte werden auf das neue Analyse-Ergebnis gesetzt und die Rechnung geht zurück auf „Zu prüfen". Deine bisherigen Korrekturen an diesem Beleg gehen dabei verloren.',
      confirm: 'Übernehmen',
      cancel: 'Abbrechen',
      adopted: 'Die neuen Analyse-Werte wurden übernommen.',
    },
    // FLAG:useDocuAi — back to the original analysis result, so the review can start over. NOT a
    // re-analysis: the extraction is already stored, re-running it would only cost time and risk a
    // different answer.
    resetInvoiceReview: {
      action: 'Prüfung zurücksetzen',
      title: 'Prüfung zurücksetzen?',
      // Reset takes effect IMMEDIATELY (server round trip) — the earlier wording
      // "permanent only after saving" applied only to the removed client-side reset.
      menuAction: 'In Prüfung zurücksetzen',
      confirmDescription: 'Die Rechnung wird wieder auf „Zu prüfen" gesetzt und alle Werte auf das ursprüngliche Analyse-Ergebnis zurückgesetzt. Deine Korrekturen an diesem Beleg gehen dabei verloren.',
      // FLAG:useDocuAi — a review still open („Zu prüfen") has no status to fall back from; only
      // its values are restored, and only unsaved input is lost. Promising a status change here
      // would describe something that does not happen.
      confirmDescriptionUnreviewed: 'Alle Werte werden auf das aktuelle Analyse-Ergebnis zurückgesetzt. Noch nicht gespeicherte Eingaben an diesem Beleg gehen dabei verloren.',
      // TWO texts because „(Entwurf)" was simply wrong once the expense had status „Offen": since
      // the final review save registers immediately in bookingApi, that is the normal case for every
      // reviewed invoice. The old sentence also omitted that reset does NOT update accounting — it
      // writes back only locally; synchronization happens only when the next review is completed.
      confirmDescriptionWithExpense: 'Die Rechnung wird wieder auf „Zu prüfen" gesetzt und alle Werte auf das ursprüngliche Analyse-Ergebnis zurückgesetzt. Die zugehörige Ausgabe (Entwurf) wird ebenfalls auf diese Werte aktualisiert. Deine Korrekturen an diesem Beleg gehen dabei verloren.',
      confirmDescriptionWithBookedExpense: 'Die Rechnung wird wieder auf „Zu prüfen" gesetzt und alle Werte auf das ursprüngliche Analyse-Ergebnis zurückgesetzt. Die zugehörige Ausgabe wird ebenfalls auf diese Werte zurückgesetzt; in der Buchhaltung kommen sie erst an, wenn Du die Prüfung erneut abschliesst. Deine Korrekturen an diesem Beleg gehen dabei verloren.',
      confirm: 'Zurücksetzen',
      cancel: 'Abbrechen',
      success: 'Die Prüfung wurde zurückgesetzt.',
      error: 'Die Prüfung konnte nicht zurückgesetzt werden.',
      errorExpenseBooked: 'Die zugehörige Ausgabe ist bereits gebucht — die Prüfung kann nicht zurückgesetzt werden.',
      errorNoAnalysis: 'Zu diesem Beleg liegt kein Analyse-Ergebnis vor, auf das zurückgesetzt werden könnte.',
    },
    reReview: {
    // The same words as the review's role tabs (documents.reviewPayee.roles) — plus their declined
    // n-form because German has declension: `nom` for the subject ("the linked supplier …"),
    // `n` for BOTH accusative and dative ("the supplier …").
      roleWords: {
        supplier: { nom: 'Lieferant', n: 'Lieferanten' },
        companyCustomer: { nom: 'Firmenkunde', n: 'Firmenkunden' },
        privateCustomer: { nom: 'Privatkunde', n: 'Privatkunden' },
      },
      title: 'Änderungen übernehmen',
      intro: 'Diese Rechnung wurde bereits geprüft. Beim Speichern wird Folgendes aktualisiert:',
      loading: 'Auswirkungen werden ermittelt …',
      loadError: 'Die Auswirkungen konnten nicht ermittelt werden. Bitte versuche es erneut.',
      confirm: 'Speichern',
      cancel: 'Abbrechen',
      // Expense
      expenseUpdate: receiptNumber => receiptNumber
        ? `Die Ausgabe zu Rechnung ${receiptNumber} wird mit den geänderten Werten aktualisiert.`
        : 'Die zugehörige Ausgabe wird mit den geänderten Werten aktualisiert.',
      // "Ausgabe erstellen" is checked and no expense exists yet.
      // The preview does not claim a transient local status: a complete save immediately attempts
      // the Booking sync and reports its real result afterwards.
      expenseCreate: 'Es wird eine neue Ausgabe aus den geprüften Werten angelegt.',
      // The case that costs trust if it happens silently — say it plainly.
      expenseBooked: receiptNumber => receiptNumber
        ? `Die Ausgabe zu Rechnung ${receiptNumber} ist bereits gebucht und wird nicht geändert — die Buchung musst Du separat anpassen.`
        : 'Die zugehörige Ausgabe ist bereits gebucht und wird nicht geändert — die Buchung musst Du separat anpassen.',
      expenseNone: 'Zu dieser Rechnung gibt es keine Ausgabe — es wird auch keine angelegt.',
      // Kept for compatibility with older effects payloads. The dialog no longer renders an
      // expense row when revenue is the selected target: that row described an irrelevant
      // non-action and repeated the highlighted target notice.
      expenseBelongsToRevenue: 'Dieser Beleg wird als Einnahme gebucht — auf der Ausgabenseite ändert sich nichts.',
      // The target is certain before saving; the later Booking result is not. Do not promise a
      // booked state here — the save result owns that statement.
      targetRevenue: 'Ziel dieser Speicherung ist eine EINNAHME, keine Ausgabe.',
      // Revenue — counterpart sentences to the expense side above, so the dialog names both halves
      // equally concretely.
      revenueUpdate: receiptNumber => receiptNumber
        ? `Die Einnahme zu Beleg ${receiptNumber} wird mit den geänderten Werten aktualisiert.`
        : 'Die zugehörige Einnahme wird mit den geänderten Werten aktualisiert.',
      revenueCreate: 'Es wird eine neue Einnahme aus den geprüften Werten angelegt.',
      revenueBooked: receiptNumber => receiptNumber
        ? `Die Einnahme zu Beleg ${receiptNumber} ist bereits gebucht und wird nicht geändert — die Buchung musst Du separat anpassen.`
        : 'Die zugehörige Einnahme ist bereits gebucht und wird nicht geändert — die Buchung musst Du separat anpassen.',
      revenueNoAccess: 'Du hast keine Berechtigung für Einnahmen — es wird kein Beleg angelegt.',
      revenueNone: 'Zu diesem Beleg wird keine Einnahme angelegt.',
      // FLAG:useDocuAi — the second half of the truth about a locked receipt. "…is not changed"
      // says what stays; it does NOT say that the reviewed payee and the reviewed amounts are
      // DROPPED instead of half-written. Users read the first half as "it will be picked up later".
      // Fixed contract with getReviewSaveEffects (`payeeIgnored` / `amountsIgnored`, set only
      // together with the reasons expenseNotDraft / revenueNotDraft) and with the save itself.
      // The role is named concretely, as everywhere else in this dialog — never "Geschäftspartner".
      ignoredPayeeAndAmounts: role => `${role} und Beträge aus der Prüfung werden nicht übernommen.`,
      ignoredPayee: role => `Der ${role} aus der Prüfung wird nicht übernommen.`,
      ignoredAmounts: 'Die Beträge aus der Prüfung werden nicht übernommen.',
      transitionSwitch: (source, number, target) =>
        `Die ${source}${number ? ` ${number}` : ''} wird in eine ${target} umgewandelt.`,
      transitionTaxReportingWarning: 'Prüfe vor der Umwandlung, ob die bisherige Buchung bereits in einer abgegebenen USt-Voranmeldung, EÜR oder anderen steuerlichen Meldung berücksichtigt wurde. Falls ja, muss die betroffene Meldung gegebenenfalls fachgerecht korrigiert werden.',
      transitionSourceNotEditable: (source, number, target) =>
        `Die ${source}${number ? ` ${number}` : ''} ist nicht mehr editierbar und kann hier nicht in eine ${target} umgewandelt werden.`,
      transitionSourceHasMultipleDocuments: (source, number) =>
        `Die ${source}${number ? ` ${number}` : ''} ist mit mehreren Dokumenten verknüpft und kann deshalb hier nicht umgewandelt werden.`,
      transitionTargetAlreadyExists: (source, number, target) =>
        `Die ${source}${number ? ` ${number}` : ''} kann nicht umgewandelt werden, weil für dieses Dokument bereits eine ${target} existiert.`,
      transitionSourceAccessDenied: (source, number) =>
        `Du hast keine Berechtigung, die ${source}${number ? ` ${number}` : ''} umzuwandeln.`,
      transitionTargetAccessDenied: target =>
        `Du hast keine Berechtigung, eine ${target} anzulegen.`,
      // FLAG:useDocuAi — name the action that EXISTS. There is no separate "Entfernen" action for a
      // finance receipt; the sentence sent users looking for a button that was never built. The real
      // way out is deleting the Ausgabe/Einnahme — and since the delete no longer moves the document
      // to the Posteingang (it stays in its own folder, see receiptArchive.), the
      // sentence can say so and take the fear out of the only available action.
      transitionDocumentOnlyRequiresRemoval: (source, number) =>
        `Die ${source}${number ? ` ${number}` : ''} ist noch aktiv. Lösche zuerst die ${source} (der Beleg bleibt dabei unter „Dokumente" in seinem Ordner erhalten), bevor Du „Nur Dokument" speicherst.`,
      expenseCancellation: 'Für eine Stornorechnung wird keine Ausgabe angelegt oder geändert.',
      expenseNoAccess: 'Du hast keine Berechtigung für Ausgaben — an der Buchhaltung ändert sich nichts.',
      // Business partner — always name the concrete role (as on the review tabs: supplier / business
      // customer / private customer), never the technical term "master record". The dialog supplies the
      // role in the appropriate form: `role` = nominative, `roleN` = the declined n-form used in
      // German form used for BOTH accusative and dative of these words (supplier, customer …).
      partyWillCreate: (role, name) => `„${name}" wird als neuer ${role} angelegt.`,
      // The question the server cannot answer: correction or a different company?
      partyDivergedIntro: (roleN, linkedName, snapshotName) =>
        `Der Beleg ist mit dem ${roleN} „${linkedName}" verknüpft, im Formular steht jetzt „${snapshotName}". Was soll passieren?`,
      partyChoiceUpdate: (roleN, linkedName) => `Den ${roleN} „${linkedName}" korrigieren — es war ein Schreibfehler`,
      partyChoiceCreate: (roleN, snapshotName) => `„${snapshotName}" als neuen ${roleN} anlegen`,
      partyChoiceKeep: (roleN, linkedName) => `Verknüpfung mit „${linkedName}" behalten und den ${roleN} nicht ändern`,
      // FLAG:useDocuAi — the dialog's title when a FIRST review asks only the party question
      // (`mode='firstReview'`): no "Änderungen übernehmen", nothing else is changing.
      partyTitle: role => `${role} geändert`,
    },
    hasGeneratedFiles: 'Dokument hat generierte Dateien (z.B. XML oder Berichte)',
    hasSignature: 'Dokument wurde unterschrieben',
    readOnlyModeTooltipText: 'Für diesen Mandanten besteht nur eine Leseberechtigung',
    readOnlyModeTooltipBody: 'Für diesen Mandaten besteht derzeit kein aktiver Vertrag. Aus diesem Grund ist der Zugriff auf die Inhalte auf eine reine Leseansicht beschränkt.',
    shareHUBFolderDesc: `Im ${customerCenterName} Systemordner werden alle Vorgangsbelege automatisch abgelegt, die Du in orgaMAX ERP per E-Mail versendest. Vorausgesetzt wird, dass Du ${customerCenterName} eingerichtet und für den jeweiligen Vorgang, wie z. B. Angebot oder Rechnung, aktiviert hast.`,
    applyDataModal: {
      title: 'Erneut analysieren',
      titleApply: 'Werte übernehmen',
      description: 'Für dieses Dokument wurden bereits Werte erfasst. Möchtest Du das Dokument trotzdem erneut analysieren?',
      descriptionApply: 'Für diesen Beleg wurden bereits Werte erfasst. Möchtest Du diese übernehmen?',
      confirmYes: 'Ja, Werte übernehmen',
      confirmNo: 'Abbrechen',
      cancel: 'Nein',
      cancelAndScanAgain: 'Ja, erneut analysieren',
    },
    folder: {
      permissions: {
        [PERMISSION_TYPES.CREATE]: 'Du bist nicht berechtigt, Dokumente in diesen Ordner hochzuladen.',
        [PERMISSION_TYPES.UPDATE]: 'Du bist nicht berechtigt, Dokumente in diesem Ordner zu aktualisieren.',
        [PERMISSION_TYPES.MOVE]: 'Es können keine Dokumente in oder aus diesen Ordner verschoben werden.',
        [PERMISSION_TYPES.DELETE]: {
          folder: 'Du bist nicht berechtigt, Dokumente in diesem Ordner zu löschen.',
          document: 'Du bist nicht berechtigt, dieses Dokument zu löschen.',
        },
      },
      notAllowed: 'Du hast keine Berechtigung, Dokumente in diesen Ordner hochzuladen.',
      tooltips: {
        inbox: 'Posteingang. Die zentrale Anlaufstelle für alle neuen Dokumente.',
        bookkeeping: `Systemordner ${productNameBookkeeping}. Hier werden alle Vorgänge des Typs Rechnungsausgang, Stornorechnung und Kassenbelege (Einlage, Entnahme, X-Bericht, Z-Bericht) automatisch abgelegt.`,
        sharehub: `Im ${customerCenterName} Systemordner werden alle Vorgangsbelege automatisch abgelegt, die Du in ${windowsProductName} per E-Mail versendest. Vorausgesetzt wird, dass Du ${customerCenterName} eingerichtet und für den jeweiligen Vorgang, wie z. B. Angebot oder Rechnung, aktiviert hast.`,
      },
      markAsSeenFailed: 'Dokumente konnten nicht als gesehen markiert werden',
      permissionsUpdated: 'Ordner-Berechtigungen wurden aktualisiert.',
      showAllDocuments: 'Alle Dokumente anzeigen',
      allDocuments: 'Alle Dokumente',
      emptyStateText: 'Keine Ordner vorhanden.',
      accessDenied: 'Du hast keine Berechtigung diesen Ordner zu sehen.',
      rename: 'Umbenennen',
      create: 'Speichern',
      createNewFolder: 'Neuen Ordner erstellen',
      createTitle: 'Ordner erstellen',
      createSubTitle: 'Lege einen Ordnernamen fest.',
      createSubFolderTitle: 'Unterordner erstellen',
      createFolderMessage: (title) => `Der von Dir erstellte Ordner ${title} ist nicht sichtbar. Womöglich befindest Du Dich in einer gefilterten Ansicht. Entferne den Filter, um den Ordner sichtbar zu machen.`,
      createName: 'Neuer Ordner',
      createShared: 'Für weitere Benutzer freigeben',
      createSuccess: 'Ordner wurde erfolgreich erstellt.',
      createSuccessHome: 'Ordner wurde im Hauptverzeichnis erstellt.',
      createFailed: 'Ordner konnte nicht erstellt werden.',
      renameFailed: 'Ordner konnte nicht umbenannt werden.',
      nameTooLong: 'Ordnername zu lang. Maximal 247 Zeichen erlaubt.',
      moveNameTooLong: 'Ordner konnte nicht verschoben werden. Der neue Pfad überschreitet die zulässige Gesamtlänge.',
      moveFolderExists: 'Ordner konnte nicht verschoben werden. Ein Ordner mit dem gleichen Namen existiert bereits auf dieser Ebene.',
      name: 'Ordnername',
      nameTooShort: 'Ordner konnte nicht erstellt werden. Bitte lege einen Namen für den Ordner fest.',
      nameTooShortRename: 'Ordner konnte nicht umbenannt werden. Bitte lege einen Namen für den Ordner fest.',
      systemFolderNoChildren: 'Der Ordner konnte nicht erstellt werden. Du befindest Dich einem Systemordner und kannst keine weiteren Unterordner erstellen. Bitte wechsle zu "Alle Dokumente" um einen neuen Ordner zu erstellen.',
      moveSystemFolderNoChildren: 'Der Ordner konnte nicht verschoben werden, weil der Zielordner ein Systemordner ist. Bitte wähle einen anderen Ordner.',
      renameTitle: 'Ordner umbenennen',
      renameSubTitle: 'Gib einen neuen Namen ein.',
      renameButton: 'Speichern',
      renameSuccess: 'Ordner wurde erfolgreich umbenannt.',
      delete: 'Löschen',
      deleteTitle: 'Ordner löschen',
      deleteDescription: 'Willst Du den Ordner wirklich löschen?',
      deleteFailed: 'Dieser Ordner kann nicht gelöscht werden, da er mindestens 1 Dokument oder 1 Unterordner enthält.',
      deleteSuccess: 'Ordner wurde erfolgreich gelöscht.',
      openFolder: 'Ordner öffnen',
      openAccess: 'Freigabe verwalten',
      root: 'Home',
      rootDirectory: 'Hauptverzeichnis',
      noPath: 'Kein Ordner festgelegt',
      uploadPath: 'Wo sollen Deine Dokumente abgelegt werden?',
      selectLabel: 'Ordner festlegen',
      folderSelectLabel: 'Ordner',
    },
    addDocumentsTitle: 'Dokumente einfügen',
    addDocumentsSubTitle: 'Dateien hier ablegen oder über die App hochladen',
    detailsTitle: 'Informationen',
    ocrDetailsTitle: 'OCR-Analyse Ergebnis',
    // FLAG:useDocuAi — on the DocuAI path the values come from AI analysis, not OCR text detection;
    // „OCR" describes only the preliminary stage there. With the flag OFF (gini/natif), keep
    // ocrDetailsTitle deliberately so the baseline remains unchanged.
    aiDetailsTitle: 'Ergebnis der KI-Analyse',
    subTitle: (count, totalCount, selected) => {
      const prefix = count === totalCount ? `${count}` : `${count} von ${totalCount}`
      if (selected) {
        return `(${prefix} Dateien, ${selected} ausgewählt)`
      }
      return `(${prefix} Dateien)`
    },
    note: 'Notiz',
    notePlaceholder: 'Notiz eingeben',
    showDetails: 'Details ansehen',
    invoiceNumber: 'Rechnungsnummer',
    assignKind: 'Art zuweisen',
    assignTag: 'Tag zuweisen',
    searchPlaceholder: 'Alle Dokumente durchsuchen',
    fileLoaderText: 'Dokument wird geladen',
    loaderText: {
      fetching: 'Dokumente werden geladen',
      uploading: 'Dokumente werden hochgeladen',
      processing: 'Dokumente werden verarbeitet',
      movingFiles: 'Dokumente werden verschoben',
      movingFolder: 'Ordner wird verschoben',
    },
    upgradeText: `${unlockViewUpgradeText} Dokumente freizuschalten.`,
    showUnassignedOnly: 'Nur unverknüpfte anzeigen',
    latestDatevSync: 'Letzter DATEV-Sync',
    errors: {
      fileIdsCount: 'Es können maximal 25 Dokumente gleichzeitig heruntergeladen werden.',
      noValidForDelete: 'Die ausgewählten Dokumente dürfen nicht gelöscht werden',
      zipFolderPath: 'Fehler beim Download des Ordners',
      fileSkippedTooLarge: 'Mindestens eine Datei ist zu groß für den Upload. Maximale Größe 100 GB.',
      fileNotAllowed: (count) => `${count} ${count > 1 ? 'Dateien wurden übersprungen' : 'Datei wurde übersprungen'}, da der Dateityp nicht erlaubt ist.`,
      allFilesNotAllowed: (count) => `${count > 1 ? 'Die ausgewählten Dateitypen sind nicht erlaubt' : 'Der ausgewählte Dateityp ist nicht erlaubt'}`,
      default: 'Da ist etwas schiefgegangen.',
      invalidBic: 'Die angegebene BIC ist nicht gültig.',
      invalidIban: 'Die angegebene IBAN ist nicht gültig.',
      moveFilesSkipped: (count) => count === -1
        ? `Die Dokumente konnten nicht verschoben werden, weil sie mit ${windowsProductName} verknüpft sind.`
        : count > 1
          ? `Es wurden Dokumente übersprungen, weil sie mit ${windowsProductName} verknüpft sind.`
          : `Das Dokument konnte nicht verschoben werden, weil es mit ${windowsProductName} verknüpft ist.`,
      zipFileCount: 'Es dürfen maximal 20 Dateien auf einmal heruntergeladen werden.',
      fileSize: 'Mindestens eine Datei ist zu groß für den Download. Maximal 5 MB pro Datei sind erlaubt.',
      ERROR_UPDATING_COLUMNS: 'Speichern der Listeneinstellungen fehlgeschlagen.',
      ERROR_FETCHING_COLUMNS: 'Abrufen der Listeneinstellungen fehlgeschlagen.',
      ERROR_SETTING_IS_FAVORITE: 'Favorit ändern fehlgeschlagen.',
      documentDetailsFetch: 'Die Dokumentendetails konnten nicht abgerufen werden.',
      accessRevoked: 'Die Freigabe wurde beendet oder Dein Zugriff wurde entzogen.',
      drawerPageFetch: 'Die Drawer Navigation konnte nicht geladen werden.',
      unlinkReceiptError: 'Die Verknüpfung zum Beleg konnte nicht entfernt werden.',
    },
    scanButtonLabel: 'Analyse',
    scanAgainButtonLabel: 'Erneut analysieren',
    signDocumentLabel: 'E-Unterschrift einholen',
    scanning: 'Dokument wird gescannt',
    scanDocument: 'Dokument analysieren',
    orderByRecordDateDesc: 'Belegdatum absteigend',
    orderByRecordDateAsc: 'Belegdatum aufsteigend',
    orderByCreatedAtDesc: 'Hinzugefügt am absteigend',
    orderByCreatedAtAsc: 'Hinzugefügt am aufsteigend',
    orderByName: 'Sortierung von A-Z',
    navigationConfirmation: {
      title: 'Änderungen wurden vorgenommen!',
      description: 'Es wurden Änderungen am Dokument vorgenommen. Willst Du diese Änderungen speichern oder verwerfen und zum nächsten Dokument wechseln?',
    },
    manuallyPaidLabel: 'Bezahlt am',
    uploadSuccessTitle: 'Wichtiger Hinweis',
    uploadDocumentMessage: (title) => `Das von Dir hinzugefügte Dokument ${title} ist nicht sichtbar. Womöglich befindest Du Dich in einer gefilterten Ansicht. Entferne den Filter, um das Dokument sichtbar zu machen.`,
    multipleUploadErrors: (count, names) => `Folgende Datei${count > 1 ? 'en' : ''} konnte${count > 1 ? 'n' : ''} nicht hochgeladen werden: ${names}`,
    duplicateFileNameModalDesc: 'Es wurden mehrere Dokumente mit demselben Namen gefunden. Möchtest Du diese jetzt umbenennen oder trotzdem hochladen?',
    duplicateNameModalTitle: 'Dokumente umbenennen',
    // Duplicate-upload detection by CONTENT (checksum) — distinct from the filename flow above.
    duplicateChecksumModalTitle: 'Dokument bereits vorhanden',
    duplicateChecksumModalDesc: (name) => `Dieses Dokument wurde bereits hochgeladen: <strong>${name}</strong>. Möchtest Du das vorhandene Dokument verwenden oder trotzdem erneut hochladen?`,
    useExistingDocument: 'Vorhandenes Dokument verwenden',
    uploadDuplicateDocument: 'Trotzdem hochladen',
  },

  // documents > setup
  documentSetupInfoCallToActionText: ['Jetzt 14 Tage kostenlos testen!', 'Test endet automatisch – keine Kündigung notwendig.'],
  documentSetupInfoTryNowLabel: 'Jetzt kostenlos testen',

  documentSetupUserDataSubTitle: 'Es fehlen noch einige Nutzerdaten.',
  documentSetupSuccessTrialText: (documentTitle) => `Wir haben das Modul ${documentTitle} für Dich eingerichtet. Nach 14 Tagen wird Deine Testphase automatisch beendet. Mit Klick auf „Weiter“ gelangst Du zur Startseite von ${documentTitle}.`,
  documentSetupSuccessText: (documentTitle, accountTitle) => `Über ${documentTitle} werden ab sofort Belege automatisiert abgeholt. Mit Klick auf "Fertig" gelangst Du zurück in den Dokumentenbereich. Einstellungen kannst Du direkt in Deinem ${accountTitle}-Account vornehmen.`,

  documentSetupApiKeyLabel: 'API Schlüssel*',
  documentSetupStartDateFilterLabel: 'Ab welchem Zeitpunkt möchtest Du Deine Belege abrufen?',
  documentSetupInvalidApiKeyError: 'Ungültiger API Schlüssel',

  documentSetupTexts:
    [
      'Automatische Abholung Deiner Rechnungen & Belege',
      'Aus über 10.000 Portalen und Deinem E-Mail-Postfach möglich',
      'Zentraler Bereich für all Deine Rechnungen & Belege',
      'Automatische Zuordnung zu Deinen Ausgaben',
    ],

  documentSetupFooterTexts: [
    'Ein Service der fino data services GmbH',
    'Es gelten für diesen Bereich die <a target="_blank" href="https://bit.ly/Privacy-GMI-App">Datenschutzbestimmungen</a> der fino data services GmbH.',
  ],

  // states
  accepted: 'Angenommen',
  cancelled: 'Storniert',
  completed: 'Abgeschlossen',
  created: 'Erstellt',
  draft: 'Entwurf',
  toCheck: 'Zu prüfen',
  toAnalyze: 'In Analyse',
  analyzeError: 'Analyse-Fehler',
  notStarted: 'Nicht gestartet',
  deliveryNoteDelivered: 'Geliefert',
  deliveryNoteCreated: 'Lieferschein erstellt',
  depositInvoiceCreated: 'Abschlagsrechnung erstellt',
  closingInvoiceCreated: 'Schlussrechnung erstellt',
  partialInvoiceCreated: 'Teilrechnung erstellt',
  partiallyCharged: 'Teilw. verrechnet',
  notDelivered: 'Nicht geliefert',
  dunned: 'Angemahnt',
  goodsDelivered: 'Ware versendet',
  invoiced: 'In Rechnung gestellt',
  invoiceCreated: 'Rechnung erstellt',
  locked: 'Abgeschlossen',
  open: 'Offen',
  opened: 'Begonnen',
  paid: 'Bezahlt',
  imported: 'Importiert',
  markedAsPaid: 'Als bezahlt markiert',
  partlyPaid: 'Teilw. bezahlt',
  partiallyPaid: 'Teilweise bezahlt',
  posted: 'Verschickt',
  printed: 'Gedruckt',
  projectCreated: 'Abschlagsrechnung erstellt',
  projectCreatedFromOffer: 'Projekt erstellt',
  recurringTemplate: 'Noch nicht gestartet',
  rejected: 'Abgelehnt',
  sent: 'Versendet',
  delivered: 'Versendet',
  fullyDelivered: 'Geliefert',
  partiallyDelivered: 'Teilweise geliefert',
  purchaseNotDelivered: 'Bestellt / nicht geliefert',
  started: 'Aktiv',
  startedNotActive: 'Gestartet',
  finished: 'Beendet',
  credited: 'Gutgeschrieben',
  orderCreated: 'Auftrag erstellt',
  overdue: 'Überfällig',
  due: 'Fällig',
  inProgress: 'Angewiesen',
  printedLong: 'Ausgedruckt',
  toProcess: 'Zu prüfen',

  tagFilterView: {
    title: 'Filter',
  },

  // file-upload-chooser
  fileUploadChooser: {
    title: 'Dokument einfügen',
    uploadLabel: 'Dokument hochladen',
    fileContainerLabel: 'Dokumentencontainer öffnen',
    acceptedFileTypes: 'Akzeptierte Dateitypen',
  },

  documentUploadDrawer: {
    selectKind: 'Wie sollen Deine Dokumente kategorisiert werden?',
    upload: 'Hochladen',
    uploadAndScan: 'Hochladen & Analysieren',
    title: (number) => `${number} ${number > 1 ? 'Dokumente hochladen' : 'Dokument hochladen'}`,
    kindInputLabel: 'Art',
    toggleLabel: 'Dokumente für MeinBüro Kanzlei freigeben',
    excludeFromDatevLabel: 'Nicht an DATEV übertragen',
    excludeFromDatevError: 'Die Einstellung konnte nicht gespeichert werden. Bitte versuche es erneut.',
    includeInDatevButton: 'Wieder an DATEV übertragen',
    includeInDatevSuccess: 'Der Beleg wird beim nächsten DATEV-Export erneut übertragen.',
    pleaseSelectLabel: 'Bitte auswählen',
    notesHeader: 'Möchtest Du eine Notiz hinzufügen?',
    periodPlaceholder: 'Zeitraum wählen',
    businessPartnerLabel: 'Kunde/Lieferant',
    businessPartnerPlaceholder: 'Kunde oder Lieferant auswählen',
    businessPartnerHint: 'Das Dokument wird in den Stammdaten des ausgewählten Kunden oder Lieferanten angezeigt.',
    loaderLabel: (total, count) => `Dokumente werden vorbereitet (${total} von ${count})`,
  },

  // file uploader
  fileUploaderTypesError: 'Das Format der Datei \'{file}\' wird leider nicht unterstützt. Bitte verwende eins der folgenden Formate: %s.',
  fileUploaderTypeError: 'Das Format der Datei \'{file}\' wird leider nicht unterstützt. Bitte verwende das folgende Format %s.',
  fileUploaderSizeError: 'Die Dateigröße von \'{file}\' überschreitet die zulässige Maximalgröße von {sizeLimit}.',
  fileUploaderMaxHeightImageError: 'Die Höhe von {file} überschreitet die zulässige maximale Höhe von %spx.',
  fileUploaderMaxWidthImageError: 'Die Breite von {file} überschreitet die zulässige maximale Breite von %spx.',
  fileUploaderTooManyItemsError: (count) => count === 1 ? 'Es darf nur eine Datei ausgewählt werden' : `Es dürfen maximal ${count} Dokumente auf einmal hinzugefügt werden.`,
  fileUploaderNoMultipleUploadError: 'Es darf maximal eine Datei auf einmal hinzugefügt werden.',
  fileUploaderNoMultipleUploadError2: 'Es kann jeweils nur ein Anhang hochgeladen werden. Bitte lade die Anhänge einzeln hoch.',
  fileUploaderFailed: 'Die Datei \'%s\' konnte nicht hochgeladen werden.',
  fileUploaderWrongFormat: 'Die Datei \'%s\' ist entweder nicht vom angegebenen Typ oder fehlerhaft. Die Datei wurde nicht hochgeladen.',
  fileUploaderMinHeightImageError: 'Die Höhe von {file} unterschreitet die zulässige minimale Höhe von %spx.',
  fileUploaderMinWidthImageError: 'Die Breite von {file} unterschreitet die zulässige minimale Breite von %spx.',
  fileUploaderSignatureEncryptionError: 'Die Datei ist verschlüsselt und kann zum Anfordern von Unterschriften nicht genutzt werden.',
  fileUploaderSpecialCharactersError: (specialCharacters) => `Der Dokumentenname darf keines der folgenden Zeichen enthalten:  ${specialCharacters}`,

  // header contextMenu
  headerContextMenuItemColumnSetting: 'Spalteneinstellungen',
  headerContextMenuItemCsvExport: 'Aktuelle Ansicht exportieren',

  // csv export
  csvExportFileNameCustomer: 'Kunden',
  csvExportFileNameArticle: 'Artikel',
  csvExportFileNameDeliveryNote: 'Lieferscheine',
  csvExportFileNameExpense: 'Ausgaben',
  csvExportFileNameInvoice: 'Rechnungen',
  csvExportFileNameOffer: 'Angebote',
  csvExportFileNameOrder: 'Aufträge',
  csvExportFileNamePurchase: 'Bestellungen',
  csvExportFileNameProject: 'Projekte',
  csvExportFileNameRecurringInvoice: 'Abo-Rechnungen',
  csvExportFileNameRevenue: 'Sonstige_Einnahmen',
  csvExportFileNameChartOfAccounts: 'Steuerkonten',
  csvExportFileNameSupplier: 'Lieferanten',
  csvExportFileNameUser: 'Benutzer',
  csvExportFileNameTimetracking: 'Zeiten',
  csvExportFileNameBanking: 'Umsätze',
  csvExportFileNameBooking: 'Buchungen',
  csvExportFileNameCustomFields: 'Individuelle_Felder',
  csvExportFileNameClients: 'Mandanten',
  csvExportFileNameEconomicAssets: 'Anlagenverwaltung',

  // limitation messages
  limitations: {
    default: {
      title: 'Limit erreicht',
      description: `Du hast das Limit Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
    },
    carrier: {
      title: 'Du hast die maximale Anzahl an Versanddienstleistern erreicht, bitte erweitere Deinen Plan',
    },
    documentScanCount: {
      title: 'Maximale Anzahl an Dokumenten-Scans erreicht',
      subTitle: 'Schalte durch ein Upgrade weitere Scans frei!',
      description: `Du hast das Limit Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    documentScanCountInfo: {
      title: 'Mehr Dokument-Analysen freischalten',
      subTitle: `Du benötigst mehr Analysen? ${switchToBetterPlanRaiseSuccess}`,
      description: `Du hast das Limit Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    invoiceCount: {
      title: 'Anzahl der Rechnungen erreicht',
      description: `Du hast die Anzahl der maximalen Rechnungen Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
    },
    salesBorder: {
      title: 'Umsatzgrenze erreicht',
      description: `Du hast die Umsatzgrenze Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
    },
    storageSpace: {
      title: 'Speicherkontingent aufgebraucht',
      subTitle: 'Schalte durch ein Upgrade weiteren Speicherplatz frei!',
      description: `Du hast den maximalen Speicherplatz Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    storageSpaceInfo: {
      title: 'Mehr Speicherplatz freischalten',
      subTitle: `Du benötigst mehr Speicherplatz? ${switchToBetterPlanRaiseSuccess}`,
      description: `Du hast den maximalen Speicherplatz Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    documentCount: {
      title: 'Max. Anzahl an Dokumenten erreicht.',
      subTitle: (activePlan, documentCount) => `In Deinem aktuellen Plan <strong>${activePlan}</strong> ist Dein Dokumente-Kontingent von <strong>${documentCount}</strong> aufgebraucht. Schalte durch ein Upgrade Platz für weitere Dokumente frei!`,
      multiplePlanSubtitle: 'Schalte durch ein Upgrade Platz für weitere Dokumente frei!',
      description: `Du hast die maximale Anzahl an Dokumenten in Deinem Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    documentCountInfo: {
      title: 'Mehr Dokumente',
      subTitle: `Du willst mehr Dokumente hochladen? ${switchToBetterPlanRaiseSuccess}`,
      description: `Du hast die maximale Anzahl an Dokumenten erreicht. ${switchToBetterPlanRaiseSuccess}`,
      texts: ['So erreichst Du uns:', 'Mo. – Fr. 08:00 – 12:00 / 13:00 – 18:00 Uhr unter'],
      action: {
        buttonLabel: 'E-Mail schreiben',
      },
    },
    shop: {
      title: 'Maximale Anzahl an Shops erreicht',
      description: `Du hast die maximale Anzahl an Shops Deines Plans erreicht. ${switchToBetterPlanRaiseSuccess}`,
    },
    // test subscription model dependencies
    notInPlan: {
      finance: {
        title: `${planUpgradeNeeded}`,
        description: `Diese Funktion aus dem Modul 'Finanzen' ist in Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      default: {
        title: `${planUpgradeNeeded}`,
        description: `Die von Dir aufgerufene Funktion ist in Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      banking: {
        title: `${planUpgradeNeeded}`,
        description: `Diese Funktion aus Bank/Kasse steht in Deinem aktuellen Plan nicht zur Verfügung. ${switchToBetterPlan}`,
      },
      statistics: {
        title: `${planUpgradeNeeded}`,
        description: `Das Dashboard ist in Deinem aktuellen Plan nicht enthalten.  ${switchToBetterPlan}`,
      },
      articles: {
        title: `${planUpgradeNeeded}`,
        description: `Artikel sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      dataImport: {
        title: `${planUpgradeNeeded}`,
        description: `Der Datenimport sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      multiUser: {
        title: `${planUpgradeNeeded}`,
        description: `Mehrere Benutzer sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      shopConnection: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan sind Shop-Anbindungen nicht enthalten. ${switchToBetterPlan}`,
      },
      shippingConnection: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan sind Versanddienstleister nicht enthalten. ${switchToBetterPlan}`,
      },
      cashRegister: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist keine Kasse POS enthalten. ${switchToBetterPlan}`,
      },
      cashTSS: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist keine TSE für die Kasse POS enthalten. ${switchToBetterPlan}`,
      },
      documentScans: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist keine OCR-Funktionalität enthalten. ${switchToBetterPlan}`,
      },
      documentScansIntern: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist keine OCR-Funktionalität (intern) enthalten. ${switchToBetterPlan}`,
      },
      documentScansTrial: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist keine OCR-Funktionalität (Trial) enthalten. ${switchToBetterPlan}`,
      },
      documentsGmi: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist das Modul ${productName} Belege nicht enthalten. ${switchToBetterPlan}`,
      },
      timetracking: {
        title: `${planUpgradeNeeded}`,
        description: `Die Zeiterfassung ist in Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      suppliers: {
        title: `${planUpgradeNeeded}`,
        description: `Lieferanten sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      recurringInvoices: {
        title: `${planUpgradeNeeded}`,
        description: `Sonstige Einnahmen sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      revenues: {
        title: `${planUpgradeNeeded}`,
        description: `Sonstige Einnahmen sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      createBankAccount: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Konten hinzufügen. ${switchToBetterPlan}`,
      },
      offers: {
        title: `${planUpgradeNeeded}`,
        description: `Angebote sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      ordersanddeliverynotes: {
        title: `${planUpgradeNeeded}`,
        description: `Aufträge und Lieferscheine sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      stockKeeping: {
        title: `${planUpgradeNeeded}`,
        description: `Lagerhaltung ist Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      graduatedPrices: {
        title: `${planUpgradeNeeded}`,
        description: `Staffelpreise ist Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      dunning: {
        title: `${planUpgradeNeeded}`,
        description: `Das Mahnwesen ist Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      projects: {
        title: `${planUpgradeNeeded}`,
        description: `Abschläge sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      todos: {
        title: `${planUpgradeNeeded}`,
        description: `Aufgaben sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      documents: {
        title: `${planUpgradeNeeded}`,
        description: `Dokumente sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      expenses: {
        title: `${planUpgradeNeeded}`,
        description: `Ausgaben sind Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      bookingSummary: {
        title: `${planUpgradeNeeded}`,
        description: `Die Buchungsübersicht ist in Deinem aktuellen Plan nicht enthalten. ${switchToBetterPlan}`,
      },
      vatReturn: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist die USt-Voranmeldung nicht enthalten. ${switchToBetterPlan}`,
      },
      taxReports: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan sind die Steuerauswertungen nicht enthalten. ${switchToBetterPlan}`,
      },
      documentExport: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan ist der Steuerberaterexport enthalten. ${switchToBetterPlan}`,
      },
      apps: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan sind keine Apps enthalten. ${switchToBetterPlan}`,
      },
      setting: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Einstellungen vornehmen. ${switchToBetterPlan}`,
      },
      account: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Einstellungen in Deinem Account vornehmen. ${switchToBetterPlan}`,
      },
      users: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Einstellungen an Benutzern vornehmen. ${switchToBetterPlan}`,
      },
      paymentConditions: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du die Zahlungsbedingungen nicht anpassen. ${switchToBetterPlan}`,
      },
      deliveryConditions: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du die Lieferbedingungen nicht anpassen. ${switchToBetterPlan}`,
      },
      textCodules: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du die Textbausteine nicht anpassen. ${switchToBetterPlan}`,
      },
      ecommerce: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Einstellungen für E-Commerce. ${switchToBetterPlan}`,
      },
      extensions: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan hast Du keinen Zugriff auf Erweiterungen. ${switchToBetterPlan}`,
      },
      carriers: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Versanddienstleister hinzufügen. ${switchToBetterPlan}`,
      },
      taxSettings: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du Steuereinstellungen nicht anpassen.  ${switchToBetterPlan}`,
      },
      smtpImapAccount: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine eigenen Email-Accounts benutzen oder weitere Einstellungen vornehmen.  ${switchToBetterPlan}`,
      },
      moreSettings: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine weiteren Einstellungen vornehmen. ${switchToBetterPlan}`,
      },
      reporting: {
        title: `${planUpgradeNeeded}`,
        description: `In Deinem aktuellen Plan kannst Du keine Auswertungen erstellen. ${switchToBetterPlan}`,
      },
    },
  },
  userLimitReachedErrorMessage: 'Du hast die maximale Anzahl an Benutzern erreicht. Erweitere Deinen Plan um fortzufahren.',
  roleLimitReachedErrorMessage: 'Du hast für diese Rolle keine Nutzer mehr in Deinen aktiven Plänen.',

  // Bookings
  bookings: {
    title: 'Buchungsübersicht',
    datePickers: {
      fromDatePlaceholder: 'Startdatum',
      toDatePlaceholder: 'Enddatum',
    },
  },

  label: {
    allCapital: 'Alle',
    duration: 'Dauer',
  },

  // gmi
  gmi: {
    productName: `${productName} Belege`,
    companyName: 'GetMyInvoices',
    ctaButton: `${productName} Belege importieren`,
    loaderText: 'Lade Dokumente ...',
    existingAccount: 'Bestehender Account erkannt',
    existingAccountDescription: ({ gmiProductName, companyName, productName }) => `${gmiProductName} (ein Service von ${companyName}) hat zu Deiner E-Mail-Adresse einen bereits bestehenden Account gefunden. Um all Deine Belege in ${productName} automatisiert griffbereit zu haben, verknüpfe diesen Account mit ${gmiProductName}. Bitte lies dafür in Deinem ${gmiProductName} Account Deinen API Schlüssel aus und gib ihn hier ein:`,
    apiKeyHint: '*Du findest Deinen API Schlüssel in Deinem',
    syncStarted: 'Synchronisierung der Belege gestartet',
  },

  // file
  file: {
    list: {
      isAssigned: 'Verknüpft',
      emptyStateText: 'Keine Dokumente vorhanden.',
    },
    analyze: {
      loaderText: 'Das Dokument wird analysiert...',
      errorMessage: 'Beim Analysieren des Dokuments ist ein Fehler aufgetreten.',
      xmlError: 'Das XML Format des Dokuments ist nicht kompatibel',
      confirmTitle: 'Dokument analysieren',
      confirmMessage: (used, limit, date, scannedAlready) => (
        used >= limit
          ? <div>Du hast Deine verfügbaren Scans für diesen Monat bereits aufgebraucht. Dein Kontingent wird am
            <strong> {date} </strong>
            wieder aufgefüllt.
            <br /><br />
            Kontaktiere unseren Support um mehr zu erfahren.</div>
          : <div>Möchtest Du das Dokument von unserer intelligenten Texterkennung (OCR){scannedAlready ? ' erneut' : ''} analysieren und direkt Daten erfassen lassen?
            <br /><br />
            Dir stehen noch <strong>{Math.max(limit - used, 0)}</strong> von <strong>{limit}</strong> Dokumentenanalysen zur Verfügung.
          </div>
      ),
    },
  },

  avvModal: {
    title: 'Vertrag zur Auftragsverarbeitung (AVV)',
    acknlowedgeButton: `Abschließen und mit ${productName} weiterarbeiten`,
    sections: ['Bei der Nutzung unserer Produkte und Dienstleistungen werden personenbezogene Daten verarbeitet bzw. in Einzelfällen eingesehen.',
      'Um Deiner und unserer Verantwortung zum Schutz personenbezogener Daten nachzukommen, ist es laut DSGVO, Artikel 28, notwendig, dass Du für folgende Anwendungsfälle einen ',
      'Auftragsverarbeitungsvertrag (AVV)',
      ' mit uns schließen:',
      'Support per Fernzugriff',
      `Nutzung der ${productName} Cloud`,
      `Nutzung von ${productNameLong}`],
    cbTexts: ['Ich bestätige, dass ich zum Abschluss berechtigt bin und schließe hiermit einen ',
      'Auftragsverarbeitungsvertrag (AVV)',
      ' mit deltra Business Software ab.'],
  },

  settings: {
    jumpMarks: {
      userData: {
        title: 'Kontodaten',
        headline: 'Nutzerdaten',
        text: 'Hier kannst Du Deine Nutzerdaten hinterlegen, einsehen und anpassen.',
      },
      eInvoice: {
        headline: 'E-Rechnung',
        text: 'Pflichtangaben zur E-Rechnung im XRechnungsformat, wie IBAN und Ansprechpartner, können in diesem Bereich definiert werden. Sie sind dann als Standard für Rechnungen gültig.',
      },
      support: {
        headline: 'Support',
        text: 'Erteile die Genehmigung zur Fernwartung, um unsere Supportmitarbeiter beim Lösen Deiner Anfragen zu unterstützen.',
      },
      layout: {
        headline: 'Layoutvorlagen',
        text: 'Du kannst hier festlegen, welches Layout für Angebot, Auftrag, Rechnung verwendet werden soll.',
      },
      letterPaper: {
        headline: 'Briefpapier',
        text: 'Hinterlege Dein Briefpapier',
      },
      logo: {
        headline: 'Kopf-, Absender-, und Fußzeile',
        text: 'Du kannst hier für die Kopfzeile ein Logo hinterlegen, Texte einbinden und die Fußzeile nach Deinen Bedürfnissen anpassen.',
      },
      abos: {
        headline: 'Abonnements',
        text: 'Passe Deinen gewählten Plan und Deine Abrechnungsinformationen an.',
      },
      abosInfo: {
        headline: 'Abrechnungsinformationen',
        text: 'Passe Deine Abrechnungsinformationen an.',
      },
      email: {
        headline: 'E-Mail-Versand und -Abruf',
        text: 'Du willst Deine eigene E-Mail-Adresse für den Versand nutzen, dann können hier Deine SMTP Angaben gemacht werden. Des Weiteren kann auch ein IMAP Konto für den Abruf hinterlegt werden, welches Deinen Kunden zugeordnet wird.',
      },
      tenantLogo: {
        headline: 'Eigenes Logo hochladen',
        text: 'Dein Logo Upload für die Verwendung in unterschiedlichen Bereichen.',
      },
      tenantLogoTaxConsultant: {
        headline: 'Eigenes Kanzlei Logo hochladen',
        text: 'Dein Logo Upload für die Verwendung in unterschiedlichen Bereichen.',
      },
      emailAttachmentDocument: {
        headline: 'E-Mail Anhänge',
        text: 'Lade Dokumente hoch, welche pro Vorgang automatisch mit versandt werden, z.B. AGB bei Angeboten, Widerrufsbelehrung bei Auftragsbestätigung.',
      },
      password: {
        title: 'Passwort ändern',
      },
      contactData: {
        headline: 'Kontaktdaten',
        text: 'Hier kannst Du die Kontaktdaten Deiner Firma hinterlegen, einsehen und anpassen.',
      },
      tax: {
        headline: 'Steuereinstellungen',
        text: 'Gib Deine Steuerdaten und Deine Umsatzbesteuerung ein.',
      },
      bookkeeping: {
        headline: 'Buchhaltung',
        text: 'Hinterlege Angaben zu Deinem Steuerberater und passe Deinen Kontenplan an.',
      },
      numeration: {
        headline: 'Nummernkreise',
        text: 'Definiere individuelle Nummernkreise für Angebote, Rechnungen, Lieferscheine und Aufträge.',
      },
      customerLogo: {
        headline: `Logo für ${customerCenterName}`,
        text: `Hier kannst Du Dein Logo für ${customerCenterName} hochladen und aktivieren.`,
      },
      contactInfo: {
        headline: 'Kommunikation',
        text: `Gib hier die E-Mail-Adresse und Telefonnummer an, die im ${customerCenterName} als Kontaktmöglichkeit für Deine Kunden verwendet werden soll.`,
      },
      paymentProviders: {
        headline: 'Zahlungsdienstanbieter',
        text: 'Biete Deinen Kunden die Möglichkeit der Vielzahl an Bezahlmethoden.',
      },
      documentsUpload: {
        headline: 'Dokumente',
        text: `AGB, Widerrufsbelehrung, Preislisten, Werbeflyer direkt im ${customerCenterName} zur Verfügung stellen.`,
      },
      eSigning: {
        headline: 'Elektronische Unterschrift',
        text: 'Vorgänge digital unterschreiben lassen',
      },
      webshops: {
        headline: 'Webshops',
        text: 'Verbinde Deine Webshops und übertrage Deine Bestellungen.',
      },
      shipping: {
        headline: 'Versand',
        text: 'Verbinde Dein DHL-Dienstleisterkonto und erzeuge Versandlabels.',
      },
      paymentAndDeliveryConditions: {
        headline: 'Zahlungs- und Lieferbedingungen',
        text: 'Lege individuelle Einstellungen zu Deinen Zahlungs- und Lieferbedingungen fest.',
      },
      creditorIdentifier: {
        headline: 'SEPA-Lastschrift',
        text: 'Relevante Einstellungen, rund um das Einziehen von SEPA-Lastschriften findest Du in diesem Bereich.',
      },
      textModules: {
        headline: 'Textbausteine',
        text: 'Individualisiere die Textbausteine für sämtliche Vorgangsarten.',
      },
      dunning: {
        headline: 'Mahnwesen',
        text: 'Lege für ausbleibende Zahlungen bis zu vier Mahnstufen sowie individuelle Versanddaten fest.',
      },
      titleAndUnits: {
        headline: 'Anrede und Einheiten',
        text: 'Passe Anreden, Artikeleinheiten, Tags und vieles mehr für sämtliche Vorgangsarten individuell an.',
      },
      dataTransfer: {
        headline: 'Datenübernahme zwischen Vorgängen',
        text: 'Lege für Vorgänge fest, ob Infofelder übernommen werden sollen.',
      },
      transactionProcessing: {
        headline: 'Verarbeitung von Vorgängen',
        text: 'Hier kannst Du bestimmen, ob Deine aus Aufträgen gewandelten Rechnungen im Entwurfsmodus bleiben oder direkt abgeschlossen sind.',
      },
      datevExport: {
        headline: 'DATEV-Steuerberaterexport',
        text: 'Lege fest, welche Daten an Deinen Steuerberater übermittelt werden sollen.',
      },
      menuSettings: {
        headline: 'Menüdarstellung',
        text: 'Individualisiere die Hauptnavigation für Dein optimales Erlebnis.',
      },
      mailbox: {
        headline: 'Postfach-Einstellungen',
        text: 'Richte hier Dein Postfach ein und nimm die notwendigen Einstellungen vor.',
      },
      transactionCreation: {
        headline: 'Vorgangserstellung',
        text: ' Du kannst hier globale Einstellungen für Deine Vorgänge treffen.',
      },
      aiSettings: {
        headline: 'KI-Agent MAX Einstellungen',
        text: 'Lege hier Einstellungen zu Deinem persönlichen KI-Agent MAX fest.',
      },
      tenantExport: {
        headline: 'Datenexport',
        text: 'Exportiere hier Deine Daten für externe Verwendung oder Weiterverarbeitung.',
      },
    },
    pageTitles: {
      myAccount: 'Mein Konto',
      myCompany: 'Meine Firma',
      myOffice: 'Meine Kanzlei',
      letterPaper: 'Briefpapier',
      customerCenter: customerCenterName,
      interfaces: 'Schnittstellen',
      masterdata: 'Kleinstammdaten',
      customFields: 'Individuelle Felder',
      more: 'Weitere Einstellungen',
      mailbox: `${productName} Postfach`,
    },
    user: {
      remoteSettingsTitle: 'Genehmigung zur Fernwartung',
      remoteSettingsDescription: companyName => `Hiermit genehmige ich der ${companyName} sich in meinen Account einzuloggen, um etwaige Probleme oder Fehler analysieren und beheben zu können.`,
      emptyPasswordMessage: 'Bitte gib Dein Passwort ein',
      wrongPasswordMessage: 'Das eingegebene Passwort ist falsch',
      newPassword: 'Neues Passwort',
      oldPassword: 'Altes Passwort',
      change: 'Ändern',
      password: 'Passwort',
      repeatPassword: 'Passwort wiederholen',
      repeatPasswordErrorMessage: 'Die Passwörter müssen identisch sein',
      menu: {
        description: 'Es stehen Dir Optionen zur Personalisierung Deines Navigationsmenüs zur Verfügung. Nutze die vorhandenen Optionen, um ein optimales Ergebnis für Dich zu erzielen.',
        types: {
          comfortable: {
            title: 'Komfortable Menüversion',
            description: 'Icons neben Menüpunkten anzeigen.',
          },
          compact: {
            title: 'Kompakte Menüversion',
            description: 'Menüpunkte ohne Icons anzeigen',
          },
        },
        darkMode: {
          label: 'Darkmode aktivieren',
          description: 'Das Untermenü, die globale Suche und die Benachrichtigungen werden in dunklen Blautönen dargestellt',
        },
        showUnavailableFeatures: {
          label: 'Nicht verfügbare Funktionen ausblenden',
          description: 'Wenn diese Option aktiviert ist, werden in der Hauptnavigation alle Menüpunkte ausgeblendet, die in Deinem aktuellen Plan nicht enthalten sind. Wir empfehlen, diese Einstellung deaktiviert zu lassen, damit Du weiterhin über zusätzliche Funktionen und Mehrwerte höherer Pläne informiert bleibst.',
        },
      },
    },
    account: {
      customerCenterTooltipTitle: `${customerCenterName} kurz erklärt`,
      customerCenterTooltipDesc: `Mit Hilfe des ${customerCenterName} bietest Du Deinen Kunden die Möglichkeit, ihre Angebote, Aufträge und Rechnungen direkt online einzusehen und mit den Vorgängen zu interagieren – von der Angebotsannahme bis zur direkten Weiterverarbeitung zum Auftrag.</br></br>Lass Aufträge und Rechnungen direkt über das Mobiltelefon bezahlen – mit nur wenigen Klicks. Digitalisiere Deine Prozesse und schone die Umwelt, indem Du auf das Drucken verzichtest.</br></br>Aber wie funktioniert das? Ganz einfach: Du versendest das Angebot, den Auftrag und die Rechnung einfach per E-Mail, und ${customerCenterName} steht Deinen Kunden als direkter Link in der E-Mail zur Verfügung.</br></br>Die Einstellungsmöglichkeiten findest Du <a href="/settings/customer-center">hier</a>.`,
      directPaymentMethod: 'Direkte Zahlungsmethoden',
      paymentMethodDescriptionBegin: 'Ermögliche Deinen Kunden die einfache und schnelle Begleichung von Aufträgen oder Rechnungen direkt im ',
      paymentMethodDescriptionEnd: ' – jederzeit und von überall aus.',
      paymentDrawerDescriptionBegin: 'Durch den Einsatz des ',
      paymentDrawerDescriptionEnd: 's verkürzt Du die durchschnittliche Zeitspanne bis zur Bezahlung Deiner Rechnungen und Aufträge um 3 Tage. Nutze unsere vielfältigen Zahlungsmethoden sowie die Möglichkeit, Rechnungen und Aufträge unkompliziert von überall und jederzeit zu begleichen. Dadurch hast Du schneller Zugriff auf Dein Geld.',
      description: 'Richte ein Stripe oder PayPal Business-Konto ein, verknüpfe Deinen bestehenden Account, um Deinen Kunden die Möglichkeit der Vielzahl an Bezahlmethoden zu bieten.',
      paypal: {
        title: 'PayPal',
        paypalTooltipText: 'Es fallen die üblichen <a href="https://www.paypal.com/de/webapps/mpp/paypal-fees">PayPal-Gebühren</a> zzgl. 0,75% Bearbeitungsgebühr an.',
        description: 'Hier hast Du die Möglichkeit, eine Verknüpfung zu Deinem PayPal Business-Konto herzustellen. Dadurch können Deine Kunden nicht nur Rechnungen, sondern auch Aufträge bequem online über PayPal bezahlen – sei es per PayPal-Guthaben, Lastschrift, Kreditkarte oder anderen verfügbaren Zahlungsmethoden. Zur Nutzung der Funktion benötigst Du ein PayPal Business-Konto. Es fallen die üblichen PayPal-Gebühren an.',
        descriptionCancel: 'Hier kannst Du die Verknüpfung zu Deinem PayPal Business-Konto aufheben. Dadurch können Deine Kunden Aufträge, sowie Rechnungen nicht mehr online bezahlen. Du kannst die Verknüpfung später wiederherstellen.',
        PaypalMerchantIdLabel: 'PayPal-Händler-ID: ',
        PaypalMerchantEmailLabel: 'PayPal-Händler-Email:',
        buttonUnLinkLabel: 'Verknüpfung aufheben',
        buttonLinkLabel: 'Aktivieren',
        loadingLabel: 'Du wirst jeden Moment zu PayPal weitergeleitet...',
        linkageSuccess: 'Dein PayPal Business-Konto wurde erfolgreich verknüpft. Deine Kunden können Deine Rechnungen und Aufträge jetzt online bezahlen.',

        teaser: {
          title: 'Ermögliche Deinen Kunden, diese Rechnung online zu bezahlen.',
          subTitle: 'Verknüpfe jetzt Dein PayPal Business-Konto',
          yourBenefits: 'Deine Vorteile:',
          benefits: [
            'Deine Kunden bezahlen bequem per PayPal, Kreditkarte, Lastschrift oder vielen weiteren Möglichkeiten.',
            'Du bekommst schneller Dein Geld.',
            'Du trittst als modernes und digitalisiertes Unternehmen auf.',
          ],
          later: 'Später',
          callToAction: 'Jetzt Online-Zahlung einrichten',
        },
        activate: 'Paypal ist aktiviert',
        deactivate: 'Paypal ist deaktiviert',
      },
      stripe: {
        stripeTooltipText: 'Es fallen die üblichen <a href="https://stripe.com/de/pricing">Stripe-Gebühren</a> zzgl. 0,5% Bearbeitungsgebühr an.',
        description: 'Nutze die Möglichkeit, nicht nur Deine Rechnungen, sondern auch Aufträge über verschiedene Zahlungsmethoden wie Google Pay, Apple Pay, Sofortüberweisung und Kreditkarten abwickeln zu können. Richte ganz unkompliziert einen Account ein, um diese Vielfalt an Zahlungsmöglichkeiten Deinen Kunden zur Verfügung zu stellen.',
        descriptionCancel: 'Hier kannst Du die Verknüpfung Deines Kontos beim Zahlungsanbieter Stripe aufheben. Dadurch können Deine Kunden Aufträge, sowie Rechnungen nicht mehr online bezahlen. Du kannst die Verknüpfung später wiederherstellen.',
        buttonUnlinkLabel: 'Verknüpfung aufheben',
        buttonLinkLabel: 'Aktivieren',
        loadingLabel: 'Du wirst jeden Moment zu Stripe weitergeleitet...',
        onboardingCompleted: 'Onboarding abgeschlossen:',
        chargesEnabled: 'Zahlungen aktiviert:',
        logos: {
          applePay: 'Apple_Pay',
          googlePay: 'Google_Pay',
          sepa: 'SEPA_Lastschrift',
          mastercard: 'Mastercard',
          visa: 'Visa',
          giropay: 'Giropay',
          sofort: 'Sofort',
        },
        activate: 'Stripe ist aktiviert',
        deactivate: 'Stripe ist deaktiviert',
      },
    },
    dataTransfer: {
      dataTransferDescriptionLine: 'Wenn Du diese Option aktivierst, werden die Werte der Infofelder in Deinen Vorgängen (Rechnungen, Angebote, Aufträge, Lieferscheine) übernommen, wenn Du einen anderen Vorgang daraus erstellst (z.B. Rechnung aus Auftrag erstellen). Damit die Datenübernahme funktioniert, musst Du exakt gleich benannte Infofelder in beiden Vorgangstypen erstellt haben.',
      successMessage: 'Die Einstellung zur Datenübernahme zwischen Vorgängen wurde erfolgreich gespeichert.',
    },
    letterPaperSettings: {
      description: 'Du kannst Dein individuelles Briefpapier hochladen und für alle Vorgänge als Standard festlegen. Mehrseitiges Briefpapier wird automatisch erkannt und als zweite Seite gesetzt.',
      addNewLetterPaperTitle: 'Neues Briefpapier anlegen',
      uploadLabel: 'Briefpapier hochladen',
      downloadLetterPaperLabel: 'Briefpapier herunterladen',
      downloadLetterPaperPreviewLabel: 'Vorschau herunterladen',
      uploadSubLabel: 'PDF optional - 1- oder 2-seitig',
      addressBlockPosLabel: 'Adressfeld positionieren',
      infoBlockPosLabel: 'Informationsblock positionieren',
      printOptionsLabel: 'Druckoptionen',
      printOptionSender: 'Absenderzeile drucken',
      printOptionHeader: 'Kopfzeile und Logo drucken',
      printOptionFooter: 'Fußzeile drucken',
      printOptionFoldMarks: 'Falzmarken drucken',
      printTitleOnAllPages: 'Vorgangstitel ab Seite 2 drucken',
      addSuccessMessage: 'Dein Briefpapier wurde erfolgreich hinzugefügt.',
      updateSuccessMessage: 'Dein Briefpapier wurde erfolgreich aktualisiert.',
      deleteSuccessMessage: (name) => `Das Briefpapier '${name}' wurde gelöscht.`,
      letterPaperPreview: 'Briefpapier Vorschau',
      letterPaperLayoutPreview: 'Layout Vorschau',
      showOnSecondPage: 'auf Seite 2 ff. aktivieren',
      page2: 'Seite 2 und folgende',
      headerModalTitle: 'Logo bzw. Kopfzeile',
      senderModalTitle: 'Absenderzeile',
      footerModalTitle: 'Fußzeile',
      senderModalHeadline: 'Abesenderzeile anpassen',
      logoUploadModalHeadline: 'Kopfzeile anpassen',
      footerModalHeadline: 'Abesenderzeile anpassen',
      printTitleOnAllPagesTooltip: 'Deaktivieren, wenn kein "Vorgangstitel" (Beispiel: "Rechnung") auf Folgeseiten gedruckt werden soll.',
      adjustmentBlock: {
        verticalInCm: 'Vertikal (in cm)',
        horizontalInCm: 'Horizontal (in cm)',
      },
      layout: {
        description: 'Hier kannst Du für Deine Rechnungen, Angebote etc. ein individuelles Layout festlegen.',
        types: {
          [LetterPaperLayoutTypes.DOCUMENT_MAIN]: 'Standard',
          [LetterPaperLayoutTypes.DOCUMENT_MAIN_PAGEBREAK]: 'Position ohne auto. Seitenumbruch',
          [LetterPaperLayoutTypes.DOCUMENT_MAIN_BORDER]: 'Position mit Rahmen',
          [LetterPaperLayoutTypes.DOCUMENT_MAIN_BORDER2]: 'Position mit Rahmen angep.',
          [LetterPaperLayoutTypes.DOCUMENT_INFO_BLOCK]: 'Informationsblock mit Rahmen',
          [LetterPaperLayoutTypes.DOCUMENT_INFO_BLOCK2]: 'Informationsblock mit Rahmen angep.',
          [LetterPaperLayoutTypes.DOCUMENT_MAIN_ENGLISH]: 'Standard Layout Englisch',
        },
        tooltipHeader: 'Layout als Standard setzen',
        tooltipDescription: 'Das ausgewählte Layout wird mit Aktivierung des “Grafik von Toogle” als Standard gesetzt für Deine Vorgangsarten. Es kann dennoch pro Vorgang ein anderes Layout ausgewählt werden.',
        tooltipHeader1: 'Vorlage auswählen',
        tooltipDescription1: 'Du hast unter Layoutvorlagen die Möglichkeit, zwischen mehreren Vorlagen für Deinen Vorgang auszuwählen. Die Vorlage "Position ohne auto. Seitenumbruch" kann zum Beispiel genutzt werden, wenn Du sehr lange Beschreibungen hast, dann wird kein Seitenumbruch erzeugt, der sich anhand der Position ausrichtet.',
        editTooltip: 'Briefpapier editieren (Logo, Absender, Fußzeile)',
        addTooltip: 'Briefpapier hochladen (PDF Vorlage ein- und mehrseitig)',
        updateSuccessMessage: 'Layout erfolgreich als Standard gesetzt',
        updateErrorMessage: 'Layout konnte nicht aktualisiert werden!',
      },
      headers: {
        title: 'Kopf-, Absender-, und Fußzeile',
        header: 'Kopfzeile',
        sender: 'Absenderzeile',
        footer: 'Fußzeile',
      },
      descriptions: {
        header: 'Du kannst Deine Kopfzeile frei gestalten. Lade Dein Logo hoch, setze und gestalte Texte für Dein individuelles Briefpapier.',
        sender: 'Du kannst Deine Absenderzeile individuell einpflegen. Standardmäßig wird Deine angegebene Firmenadresse angezeigt.',
        footer: 'Du kannst Deine individuelle Fußzeile für Dein Briefpapier einpflegen. Diese wird dann für alle Vorgänge übernommen.',
      },
    },
    notifications: {
      title: 'Benachrichtigungen',
      description: 'Möchtest Du Benachrichtigungen für Deine Account-Aktivitäten einstellen?',
      viaEmail: 'Per E-Mail',
      viaSmartphone: 'Per Smartphone',
    },
    eInvoice: {
      description1: 'Im folgenden Abschnitt kannst Du die erforderlichen Einstellungen für die Erstellung einer E-Rechnung nach XRechnung-Standard vornehmen. Diese Einstellungen lassen sich jederzeit hier oder direkt wahrend der Rechnungserstellung anpassen. Falls Du keine E-Rechnungen benötigst, kannst Du die Erstellung von E-Rechnungsdokumenten deaktivieren.',
      description2: `Wenn Du diese Einstellung deaktivierst, erstellt ${productName} keine Dokumente, die den Anforderungen für elektronische Rechnungen entsprechen. Weder ZUGFeRD- noch XRechnungen stehen Dir nach Abschluss des Rechnungsvorgangs zur Verfügung. Stattdessen wird lediglich ein Rechnungs-PDF ohne XML-Daten erzeugt.`,
      toggleLabel: 'E-Rechnungen erzeugen',
      toggleTooltipContent: 'Privatkunden sind von der automatischen Validierung ausgenommen, da für sie keine E-Rechnung erstellt wird.',
      successMessage: 'Deine E-Rechnungseinstellungen wurden erfolgreich aktualisiert.',
      errorMessage: 'Beim Aktualisieren der E-Rechnungseinstellungen ist ein Fehler aufgetreten.',
      bankAccount: {
        title: 'Bankverbindung',
        successMessage: 'Deine Bankverbindung wurde erfolgreich aktualisiert.',
        errorMessage: 'Beim Erstellen der Bankverbindung ist ein Fehler aufgetreten.',
        accountHolder: 'Kontoinhaber',
        iban: 'IBAN',
        bic: 'BIC',
        descriptionField: 'Bezeichnung',
        subText: 'Für die Erstellung von E-Rechnungen musst Du mindestens eine Bankverbindung hinterlegen - ähnlich der Bankverbindung auf Deinem Briefpapier. Solltest Du bereits eine Online-Bank eingerichtet haben, steht diese Dir auch zur Auswahl.',
        description: 'Du kannst hier eine Bankverbindung zur Verwendung in E-Rechnungen erstellen, es wird keine Bankverbindung innerhalb unseres Bank/Kasse Bereichs eingerichtet.',
        createSuccessMessage: 'Deine Bankverbindung wurde erfolgreich erstellt.',
        updateErrorMessage: 'Beim Aktualisieren der Bankverbindung ist ein Fehler aufgetreten.',
        deleteSuccessMessage: 'Deine Bankverbindung wurde erfolgreich gelöscht.',
        deleteErrorMessage: 'Beim Löschen der Bankverbindung ist ein Fehler aufgetreten.',
        deleteDefaultErrorMessage: 'Die Standard Bankverbindung kann nicht gelöscht werden.',
        addNew: 'Neue Bankverbindung hinzufügen',
        manage: 'Bankverbindung verwalten',
        delete: 'Bankverbindung löschen',
        deleteConfirm: 'Möchtest Du die Bankverbindung wirklich löschen?',
        emptyState: 'Keine Bankverbindung vorhanden',
        apply: 'In aktuellen Vorgang übernehmen',
        editOfflineData: 'Offline-Bankkonto bearbeiten',
        editActiveData: 'Online-Bankkonto bearbeiten',
        offlineDataListTitle: 'Offline-Bankkonten',
        activeDataListTitle: 'Online-Bankkonten',
      },
      contactPerson: {
        title: 'Ansprechpartner',
        description: 'Du kannst hier einen Ansprechpartner zur Verwendung in E-Rechnungen erstellen und verwalten.',
        createSuccessMessage: 'Dein Ansprechpartner wurde erfolgreich erstellt.',
        errorMessage: 'Beim Erstellen des Ansprechpartners ist ein Fehler aufgetreten.',
        successMessage: 'Dein Ansprechpartner wurde erfolgreich aktualisiert.',
        updateErrorMessage: 'Beim Aktualisieren des Ansprechpartners ist ein Fehler aufgetreten.',
        deleteSuccessMessage: 'Dein Ansprechpartner wurde erfolgreich gelöscht.',
        deleteErrorMessage: 'Beim Löschen des Ansprechpartners ist ein Fehler aufgetreten.',
        deleteDefaultErrorMessage: 'Der Standard Ansprechpartner kann nicht gelöscht werden.',
        firstName: 'Vorname',
        lastName: 'Nachname',
        phone: 'Telefonnummer',
        email: 'E-Mail-Adresse',
        subText: 'Ein Ansprechpartner dient als Absender respektive Ersteller der E-Rechnung und ist auch eine Pflichtangabe, zur Erstellung einer gültigen E-Rechnung.',
        addNew: 'Neuen Ansprechpartner hinzufügen',
        manage: 'Ansprechpartner verwalten',
        delete: 'Ansprechpartner löschen',
        deleteConfirm: 'Möchtest Du den Ansprechpartner wirklich löschen?',
        emptyState: 'Aktuell keine Ansprechpartner vorhanden',
        apply: 'In aktuellen Vorgang übernehmen',
        edit: 'Ansprechpartner anpassen',
      },
      taxInformation: {
        title: 'Steuerangaben',
        description: 'Für eine gesetzeskonforme E-Rechnung sind bestimmte Steuerangaben erforderlich. Diese unterscheiden sich je nach Rechnungsformat:',
        descriptionListItem1: '<strong>XRechnung:</strong> Erfordert die Angabe sowohl der <strong>Steuernummer</strong> als auch der <strong>Umsatzsteuer-Identifikationsnummer (USt-ID)</strong>.',
        descriptionListItem2: '<strong>ZUGFeRD-PDF:</strong> Hier genügt die Angabe der <strong>Umsatzsteuer-Identifikationsnummer (USt-ID)</strong>.',
        subText: 'Für eine gesetzeskonforme E-Rechnung ist standardmäßig die Angabe Deiner USt.-IDNr. erforderlich. Alternativ ist auch die Angabe Deiner Steuernummer in Kombination mit der Kennung des Verkäufers zulässig.',
        manage: 'Steuerangaben verwalten',
        tooltip: {
          vatNumber: {
            showInfoIcon: true,
            title: 'Steuernummer',
            description: 'Wenn keine USt.-ID vorliegt, müssen <b>Steuernummer</b> und <b>Kennung des Verkäufers</b> angegeben werden.',
          },
          taxNumber: {
            showInfoIcon: true,
            title: 'Umsatzsteuer-ID',
            description: 'Mit USt.ID entfallen Steuernummer und Kennung des Verkäufers als Pflichtfelder.',
          },
        },
      },
      additionalInvoiceFields: {
        title: 'Zusätzliche Rechnungsfelder',
        description: 'Hier können individuelle Rechnungsinformationen eingegeben werden, die in die XML-Datei der E-Rechnung übertragen werden.',
        invoiceInformation: {
          title: 'Rechnungsinformationen',
          inputs: {
            projectReference: {
              label: 'BT-11: Projektreferenz',
              tooltip: 'Die Kennung des Projektes, auf das sich die Rechnung bezieht. <br/><br/> Hinweis: Wenn die Projektreferenz gefüllt ist, muss auch der Projektname gefüllt sein und umgekehrt.',
            },
            projectName: {
              label: 'BT-11-0: Projektname',
              tooltip: 'Der Name des Projektes, auf das sich die Rechnung bezieht. <br/><br/> Hinweis: Wenn der Projektname gefüllt ist, muss auch die Projektreferenz gefüllt sein und umgekehrt.',
            },
            contractReference: {
              label: 'BT-12: Vertragsreferenz',
              tooltip: 'Die Kennung eines Vertrags <br/><br/> Hinweis: Die Vertragsreferenz sollte im Kontext der spezifischen Handelsbeziehung und für einen definierten Zeitraum einmalig vergeben sein.',
            },
            purchaseOrderReference: {
              label: 'BT-13: Bestellreferenz',
              tooltip: 'Eine vom Käufer ausgegebene Kennung für eine referenzierte Bestellung.',
            },
            salesOrderReference: {
              label: 'BT-14: Verkaufsauftragsreferenz',
              tooltip: 'Eine vom Verkäufer ausgegebene Kennung für einen referenzierten Verkaufsauftrag.',
            },
            receivingAdviceReference: {
              label: 'BT-15: Referenz auf die Wareneingangsmeldung',
              tooltip: 'Eine Kennung für eine referenzierte Wareneingangsmeldung.',
            },
            despatchAdviceReference: {
              label: 'BT-16: Lieferavisreferenz',
              tooltip: 'Eine Kennung für ein referenziertes Lieferavis.',
            },
            tenderReference: {
              label: 'BT-17: Ausschreibungs- oder Losreferenz',
              tooltip: 'Die Kennung der Ausschreibung oder des Loses, auf die bzw. das sich die Rechnung bezieht <br/><br/> Hinweis: In manchen Ländern muss eine Referenz zu der Ausschreibung angegeben werden, die zu dem Vertrag geführt hat.',
            },
            objectIdentifier: {
              label: 'BT-18: Objektkennung',
              tooltip: 'Eine vom Verkäufer angegebene Kennung für ein Objekt, auf dem die Rechnung basiert <br/><br/> Hinweis: Das kann je nach Anwendung eine Abonnementnummer, eine Telefonnummer, ein Zählerstand, ein Fahrzeug, eine Person usw. sein.',
            },
            accountingReference: {
              label: 'BT-19: Buchungsreferenz des Käufers',
              tooltip: 'Ein Textwert, der angibt, an welcher Stelle die betreffenden Daten in den Finanzkonten des Käufers zu verbuchen sind.',
            },
            globalIdentifierSeller: {
              label: 'BT-29-0: Globaler Identifier des Verkäufers',
              tooltip: 'Globaler Identifier des Verkäufers: GLN, DUNS, BIC, ODETTE, ... <br/><br/> Hinweis: Das Identifikationsschema der Kennung des Verkäufers ist eine von einer globalen Registrierungsorganisation eindeutig einem Verkäufer zugewiesene Kennzeichnung.',
            },
            globalIdentifierScheme: {
              label: 'BT-29-1: Kennung für das Identifikationsschema',
            },
            legalRegistrationIdentifier: {
              label: 'BT-30: Kennung der rechtlichen Registrierung des Verkäufers',
              tooltip: 'Eine von einer offiziellen Registrierungsstelle ausgegebene Kennung, die den Verkäufer als Rechtsträger oder juristische Person identifiziert <br/><br/> Hinweis: Wird kein Identifikationsschema angegeben, sollte sie dem Käufer und Verkäufer bekannt sein.',
            },
            legalRegistrationScheme: {
              label: 'BT-30-1: Kennung des Schemas',
            },
          },
        },
        invoicePeriodInformation: {
          title: 'Rechnungszeitraum',
          inputs: {
            startDate: {
              label: 'BT-73: Anfangsdatum des Rechnungszeitraums',
              tooltip: 'Das Datum, an dem der Rechnungszeitraum beginnt <br/><br/> Hinweis: Das ursprüngliche Datum der Warenlieferung oder Dienstleistungserbringung.',
            },
            endDate: {
              label: 'BT-74: Enddatum des Rechnungszeitraums',
              tooltip: 'Das Datum, an dem der Rechnungszeitraum endet <br/><br/> Hinweis: Das Datum, an dem die Warenlieferung oder Dienstleistungserbringung abgeschlossen wurde.',
            },
          },
        },
        buyerInfo: {
          title: 'Käuferinformationen',
          inputs: {
            buyerNumber: {
              label: 'BT-46: Kennung des Käufers',
              tooltip: 'Eine Kennung des Käufers <br/><br/> Hinweis: Wird kein Schema angegeben, sollte sie dem Käufer und Verkäufer bekannt sein, z. B. eine zuvor ausgetauschte, vom Verkäufer zugewiesene Kennung des Käufers.',
            },
            buyerContactName: {
              label: 'BT-56: Kontaktstelle des Käufers',
              tooltip: 'Eine Kontaktstelle für einen Rechtsträger oder eine juristische Person wie z. B. Personenname, Bezeichnung der Kontaktperson oder der Abteilung oder des Büros',
            },
            buyerContactPhone: {
              label: 'BT-57: Telefonnummer der Kontaktstelle des Käufers',
              tooltip: 'Eine Telefonnummer der Kontaktstelle',
            },
            buyerContactEmail: {
              label: 'BT-58: E-Mail-Adresse der Kontaktstelle des Käufers',
              tooltip: 'Eine E-Mail-Adresse der Kontaktstelle',
            },
          },
        },
      },
    },
    banking: {
      creditorIdentifier: {
        title: 'Gläubiger-Identifikationsnummer',
        description: 'Trage hier Deine Gläubiger-Identifikationsnummer ein. Achte darauf, die Korrektheit der Nummer zu überprüfen, da diese für den Zahlungsverkehr entscheidend ist.',
        description1: 'Falls Du noch keine Gläubiger-Identifikationsnummer besitzt, kannst Du diese online unter <a href="https://extranet.bundesbank.de/scp" target="_blank">extranet.bundesbank.de</a> beantragen.',
        descriptionLeadingDays: `
        <div>
        <p>
          Die <strong>Vorlaufzeit</strong> beschreibt den Zeitraum zwischen dem Einreichen einer SEPA-Lastschrift bei der Bank und dem <strong>Fälligkeitstag</strong>.
        </p>

        <p>
          Der <strong>Fälligkeitstag</strong> ist der Tag, an dem der Betrag tatsächlich vom Konto Deines Kunden abgebucht wird.
        </p>

        <p>
          Damit die Bank die Lastschrift rechtzeitig verarbeiten kann, muss sie <strong>1 bis 5 Banktage vor dem Fälligkeitstag</strong> eingereicht werden – je nach Art der Lastschrift. Die Wertebereiche werden ggf. von Deiner Bank vorgegeben.
        </p>

        <p>
          <strong>Banktage</strong> sind <strong>Montag bis Freitag</strong>, ausgenommen <strong>Wochenenden und gesetzliche Feiertage</strong>, an denen Banken keine Zahlungen bearbeiten.
        </p>

        <p><strong>Arten von SEPA-Lastschriften:</strong></p>

        <ul style="list-style-type: disc;padding-left: 20px;">
          <li><strong>Erstlastschrift:</strong> Die erste Abbuchung zu einem neuen SEPA-Mandat.</li>
          <li><strong>Folgelastschrift:</strong> Alle weiteren, regelmäßigen Abbuchungen unter demselben Mandat.</li>
          <li><strong>Einzellastschrift:</strong> Eine einmalige Abbuchung (z. B. bei Einzelzahlungen).</li>
          <li><strong>Letzte Lastschrift:</strong> Die abschließende Abbuchung, wenn das SEPA-Mandat beendet wird.</li>
        </ul>
        </div>
        `,
        descriptionCutOffTime: `
        <p>Die Cut-Off-Zeit ist die tägliche Annahmeschlusszeit der Bank für den Zahlungsverkehr.</p>
        <p>Alle SEPA-Lastschriften oder Überweisungen, die vor dieser Uhrzeit eingereicht werden, gelten noch am selben Banktag als eingereicht. Die genaue Cut-Off-Zeit kann je nach Bank unterschiedlich sein</p>
        <p>Zahlungen, die nach der Cut-Off-Zeit übermittelt werden, werden erst am nächsten Banktag verarbeitet.</p>
        <p>Für eine sichere Planung sollte die Einreichung daher <strong>immer rechtzeitig vor Ablauf der Cut-Off-Zeit</strong> erfolgen, um Verzögerungen im Zahlungsverkehr zu vermeiden.</p>
        `,
        companyDataMissing: 'Bitte trage zunächst Deine <a href="settings/mycompany#contactData">Kontaktdaten</a> ein.',
        bankAccountMissing: 'Für einen SEPA-Einzug wird ein lastschriftfähiges <a href="/banking">Bankkonto</a> benötigt.',
        successMessage: 'Deine SEPA-Lastschrift-Information wurde erfolgreich gespeichert.',
        errorMessage: 'Beim Speichern der SEPA-Lastschrift-Information ist ein Fehler aufgetreten.',
        confirmationModalTitle: 'Gläubiger-Identifikationsnummer bestätigen',
        confirmationModalDescription: 'Bist Du sicher, dass Du die Gläubiger-Identifikationsnummer aktualisieren möchtest?',
        infoFields: [
          'Die Gläubiger-Identifikationsnummer besitzt folgendes Schema:',
          '<span>CC</span> <span>KK</span> <span>BBB</span> <span>…bel. alphanumerisch…</span>',
          '<span>│ </span> <span>│ </span> <span>│</span>',
          '<span>│ </span> <span>│ </span> <span>└─ 3-stelliger alphanumerischer Code</span>',
          '<span>│ </span> <span>└────</span> <span>2-stelliger Prüfcode</span>',
          '<span>└────────</span> <span>2-stelliger ISO Länder-Code</span>',
          '<span>Leerzeichen sind nicht zugelassen</span>',
        ],
        bankAccount: 'Verbundenes Konto:',
        changedCreditorIdInfo: (savedCreditorIdentifier) => `Aktuelle Gläubiger-Identifikationsnummer: ${savedCreditorIdentifier}`,
        changedCreditorIdWarning: 'Falls bereits Mandate erteilt wurden, werden alle betroffenen Kunden automatisch über die neue Gläubiger-Identifikationsnummer informiert.',
        sepaTransactions: {
          title: 'Verfügbare Aktionen:',
          sepaAddMultiPostdatedDebit: 'Sammellastschrift einreichen',
          sepaAddSinglePostdatedDebit: 'Einzellastschrift einreichen',
          sepaChangeSinglePostdatedDebit: 'Terminlastschrift ändern',
          sepaGetSinglePostdatedDebit: 'Einzellastschrift abrufen',
        },
        leadingDays: 'Vorlaufzeiten in Banktagen',
        FRST: 'Erstlastschrift:',
        RCUR: 'Folgelastschrift:',
        FNAL: 'Letzte Lastschrift:',
        OOFF: 'Einzellastschrift:',
        cutOffTime: 'Cut-Off-Uhrzeit:',
        purposeOfUseText: 'Standardverwendungszweck bei Lastschriften:',
        textModuleLink: 'Der finale Verwendungszweck ist auf 140 Zeichen begrenzt und wird ggf. gekürzt auf dem Kontoauszug dargestellt.<br />Du kannst die SEPA-Lastschrift-Vorlagen für Dein Unternehmen in <a href="/settings/masterData#textModules">Kleinstammdaten</a> anpassen.',
        purposeOfUseTextInvalid: 'Der eingegebene Verwendungszweck enthält ungültige Zeichen.',
      },
    },
    company: {
      communication: 'Kommunikation',
      updateSuccessMessage: 'Deine Firmendaten wurden erfolgreich aktualisiert.',
      errors: {
        isDemo: 'Diese Funktion ist im Demo-Modus nicht verfügbar.',
      },
    },
    payConditions: {
      dateOfPayment: 'Zahlungsziel',
      days: 'Tage',
      default: 'Standard',
      system: 'System',
      protectedSystemTooltip: 'Systemische Zahlungsbedingungen sind nicht löschbar.',
      addNew: 'Neue Zahlungsbedingung hinzufügen',
      deleteModalTitle: 'Zahlungsbedingung löschen?',
      deleteModal: (name) => `Möchtest Du die Zahlungsbedingung <b>${name}</b> wirklich löschen ?`,
    },
    deliverySettings: {
      deliveryCondition: 'Lieferbedingung',
      loaderText: 'Lädt Lieferbedingungen',
      addNew: 'Neue Lieferbedingung hinzufügen',
      default: 'Standard',
    },
    transactionProcessing: {
      title: 'Verarbeitung von Vorgängen',
      lockConvertedInvoice: 'Aus Aufträgen generierte Rechnungen werden vom System automatisch abgeschlossen.</br>Bei deaktivierter Einstellung verbleiben die Rechnungen im Entwurfsstatus.',
      successMessage: 'Deine Einstellungen wurden erfolgreich gespeichert',
    },
    taxes: {
      chartOfAccounts: 'Kontenplan',
      chartOfAccountsDescription: 'Hier geht es zur Übersicht der verschiedenen Steuerkonten. Du kannst Dir hier einen Überblick verschaffen oder Änderungen an der Kurzbeschreibung oder den Favoriten vornehmen. Des Weiteren ist eine Auswahl der Standardkontenrahmen möglich.',
      editChartOfAccounts: 'Kontenplan bearbeiten',
      chartsOfAccountsSkrType: {
        title: 'Standardkontenrahmen wählen:',
        changeTypeSuccessfullMessage: (skrTypeCaption) => `Dein Standardkontenrahmen wurde auf das Schema ${skrTypeCaption} gesetzt.`,
        changeTypeErrorMessage: 'Der Standardkontenrahmen konnte nicht auf den geänderten SKR gesetzt werden.',
        options: {
          skr03: {
            caption: 'SKR03',
            tooltip: {
              title: 'Standardkontenrahmen (SKR) 03',
              text: 'Die Konten werden anhand der Betriebsabläufe sortiert. Die Reihenfolge der einzelnen Konten soll der Reihenfolge der Abläufe im Unternehmen entsprechen.'
                + 'Hier findet das sogenannte Prozessgliederungsprinzip Anwendung.'
                + 'Wenn Du Dir unsicher bist, welchen Plan Du verwenden sollst, frage Deinen Steuerberater oder Buchhalter.'
                + 'Solltest Du ohne buchhalterische Unterstützung arbeiten - bleibt es Dir überlassen.',
            },
          },
          skr04: {
            caption: 'SKR04',
            tooltip: {
              title: 'Standardkontenrahmen (SKR) 04',
              text: 'Die Sortierung der Konten erfolgt in Anlehnung an die Gliederung des Jahresabschlusses (Bilanz und Gewinn- und Verlustrechnung).'
                + 'Wenn Du Dir unsicher bist, welchen Plan Du verwenden sollst, frage Deinen Steuerberater oder Buchhalter.'
                + 'Solltest Du ohne buchhalterische Unterstützung arbeiten - bleibt es Dir überlassen.',
            },
          },
        },
      },
      grossNetSwitch: {
        title: 'Preisart',
        netDescription: 'Wähle diese Einstellung, wenn Du hauptsächlich mit Geschäftskunden (B2B) handelst.',
        grossDescription: 'Wähle diese Einstellung, wenn Du hauptsächlich mit Endkunden (B2C) handelst.',
        grossNetSwitchTaxLabel: 'Hier kannst Du festlegen, ob Du Angebote, Rechnungen und Aufträge standardmäßig mit Brutto- oder Netto-Preisen erstellen möchtest. Du kannst das Verhalten stets pro Vorgang gesondert anpassen.',
      },
      calculationSwitch: {
        title: 'Umsatzsteuerberechnung',
        label: 'In diesem Bereich hast Du die Möglichkeit, zwischen der vertikalen und horizontalen Darstellung der Umsatzsteuerberechnung für Deine Vorgänge zu wählen.',
        options: {
          horizontal: {
            label: 'Horizontale (zeilenweise) Berechnung',
            descriptionBegin: 'Bei der',
            descriptionTooltip: 'horizontalen Berechnung',
            descriptionEnd: 'der Umsatzsteuer wird für jede Position im Vorgang die Steuer separat ermittelt. Für jedes Produkt oder jede Dienstleistung wird die Umsatzsteuer individuell berechnet. Bei mehreren Positionen werden die Steuerbeträge summiert, um den Gesamtbetrag der Umsatzsteuer für den Vorgang zu bestimmen, der am Ende separat ausgewiesen wird.',
            tooltip: {
              title: 'Horizontale Berechnung',
              subTitle: 'Beispiel',
              description: `
Wenn die Umsatzsteuer 19% beträgt, wird sie für jede Position separat berechnet:
<ol>
  <li>Produkt A: 100€ x 19% = 19€ Umsatzsteuer</li>
  <li>Produkt B: 150€ x 19% = 28,50€ Umsatzsteuer</li>
  <li>Dienstleistung C: 50€ x 19% = 9,50€ Umsatzsteuer</li>
</ol>
Die gesamte Umsatzsteuer für den Vorgang beträgt dann:
</br>
19€ + 28,50€ + 9,50€ = 57€
</br>
Somit ergibt sich ein Gesamtbetrag (inkl. Umsatzsteuer) von:
</br>
100€ + 150€ + 50€ + 57€ = 357€
</br>
In diesem Beispiel wird die Umsatzsteuer für jede Position horizontal berechnet und am Ende summiert, um den Gesamtbetrag der Umsatzsteuer für den Vorgang zu ermitteln.
              `,
            },
          },
          vertical: {
            label: 'Vertikale (positionsbezogene) Berechnung',
            descriptionBegin: 'Bei der',
            descriptionTooltip: 'vertikalen Berechnung',
            descriptionEnd: 'der Umsatzsteuer werden die Nettobeträge der Positionen je Steuersatz zusammengefasst. Anschließend wird die Umsatzsteuer auf Basis dieser kumulierten Nettobeträge berechnet. Dadurch werden die Steuerbeträge auf Summenebene ermittelt statt positionsweise gerundet. <br/><br/> Diese Berechnungsart ist für E-Rechnungen empfohlen bzw. erforderlich, da XRechnung und ZUGFeRD die Summen auf Netto-/Steuersatzebene prüfen. Außerdem entspricht sie der Berechnungslogik des DATEV-Exports und reduziert mögliche Cent-Abweichungen durch Rundung.',
            tooltip: {
              title: 'Vertikale Berechnung',
              subTitle: 'Beispiel',
              description: `
              Angenommen, Du hast denselben Vorgang mit den folgenden drei Positionen:
<ol>
  <li>Produkt A: 100€ (netto)</li>
  <li>Produkt B: 150€ (netto)</li>
  <li>Dienstleistung C: 50€ (netto)</li>
</ol>
Zuerst wird der Gesamtnettobetrag aller Positionen berechnet:
</br>
100€ + 150€ + 50€ = 300€ (Gesamtnettobetrag)
</br>
Wenn die Umsatzsteuer 19% beträgt, wird sie auf den Gesamtnettobetrag angewendet:
</br>
300€ x 19% = 57€ Umsatzsteuer
</br>
Somit ergibt sich ein Gesamtbetrag (inkl. Umsatzsteuer) von:
</br>
300€ + 57€ = 357€
</br>
Bei der vertikalen Berechnung wird also zuerst der Gesamtnettobetrag aller Positionen ermittelt und darauf basierend die gesamte Umsatzsteuer berechnet.
              `,
            },
          },
        },
      },
      taxRateToggle: {
        title: 'Steuersätze',
        description: 'Bestimme welche Umsatzsteuersätze (Umsatzsteuer) in Deinen Angeboten, Rechnungen, Ausgaben usw. zur Auswahl stehen sollen.',
        tooltipTitle: 'Steuersätze:',
        tooltipDescription: 'Einer der beiden Steuersätze muss immer aktiviert sein.',
        toggleDE: '19% | 7%',
        toggleAT: '20% | 10%',
      },
      noTaxAccountCategoriesFound: 'Keine passende Steuerkategorie gefunden!',
      fiscalYearTitle: 'Wirtschaftsjahr',
      fiscalYearText: 'Hier definierst Du den Beginn des Wirtschaftjahres, also den Beginn des Zeitraums Deiner Geschäftstätigkeit im laufenden Kalenderjahr.',
      beginningOfFiscalYearLabel: 'Beginn des Wirtschaftsjahres',
      taxAccountantTitle: 'Steuerberaterexport',
      taxAccountantSubtitle: 'Damit der Export im DATEV-Format durchgeführt werden kann gib bitte die Beraternummer und Deine Mandantennummer ein. Bei Fragen, wende Dich bitte an Deine Kanzlei.',
      companyFormInput: 'Rechtsform',
      companyFormInputPlaceholder: 'Rechtsform auswählen',
      companyFormInputTooltipHeader: 'Rechtsform:',
      companyFormInputTooltipDescription1: 'Welche Unternehmensform hast Du gewählt? Es stehen Gewerbetreibende, Einzelunternehmen, Kapital- oder Personengesellschaften zur Auswahl. Bist Du Dir nicht sicher, dann frage Deinen Steuerberater.',
      vatReturnInput: 'Umsatzsteuervoranmeldung (definiert für USt-VA)',
      vatReturnInputPlaceholder: 'Umsatzsteuervoranmeldung auswählen',
      vatReturnInputTooltipHeader: 'Umsatzsteuervoranmeldung:',
      vatReturnInputTooltipDescription1: 'Solltest Du verpflichtet sein, eine Umsatzsteuervoranmeldung an Dein zuständiges Finanzamt zu senden, kannst Du hier festlegen, zu welchem Intervall Du verpflichtet bist. Als Kleinunternehmer bist Du von der regelmäßigen Voranmeldung befreit. ',
      vatReturnInputTooltipDescription2: 'Abgabe Termin immer der 10. Tag im Monat.',
      profitDerminationInput: 'Gewinnermittlung',
      profitDerminationInputPlaceholder: 'Gewinnermittlung auswählen',
      profitDerminationInputTooltipHeader: 'Gewinnermittlung:',
      profitDerminationInputTooltipDescription1: 'Bist Du zur doppelten Buchführung verpflichtet, wähle die Bilanzierung (GuV) aus. Bei Vorgabe zur einfachen Buchführung ist die Einnahmen-Überschuss-Rechnung (EÜR - §4 Abs. 3) die richtige Wahl, im Zweifel den Steuerberater kontaktieren.',
      consultantLabel: 'Beraternummer',
      clientLabel: 'Mandantennummer',
      taxAccountantEmailLabel: 'E-Mail-Adresse',
      recurringWarningModal: {
        title: 'Abo-Rechnungen prüfen',
        description: 'Aktuell hast Du aktive Abo-Rechnungen, die als Kleinunternehmer erstellt wurden. Prüfe jetzt diese Abo-Rechnungen, damit zukünftige Rechnungen mit der korrekten Umsatzsteuer versendet werden. Außerdem ist bei einem unterjährigen Wechsel eine Nachversteuerung sämtlicher Umsätze erforderlich.',
        confirmButtonLabel: 'Zu den Abo-Rechnungen',
        ignoreButtonLabel: 'Ignorieren',
      },
      graduatedPriceUpdateModal: {
        title: 'Du hast Staffelpreise in Artikeln hinterlegt!',
        description: 'Willst Du die Staffelpreise gemäß der neuen Umsatzbesteuerungsart neu berechnen lassen?',
        confirmButtonLabel: 'Ja',
        ignoreButtonLabel: 'Nein',
      },
    },
    avv: {
      title: 'Datenschutz',
      avvHeader: 'Vertrag zur Auftragsverarbeitung (AVV)',
      accepted: ['Du hast unserem ', 'Vertrag zur Auftragsverarbeitung', ' am ', ' mit der E-Mail-Adresse ', ' zugestimmt.'],
      notAccepted: 'Du hast uns noch keine Freigabe zur Auftragsverarbeitung (AVV) erteilt.',
      buttonAccept: 'Freigabe erteilen',
      buttonResend: 'AVV erneut zusenden',
      avvSend: 'Anfrage verschickt',
      avvAccepted: 'AVV gespeichert und verschickt',
      error: 'Leider ist ein Fehler aufgetreten',
    },
    electron: {
      header: 'App-Einstellungen',
      title: 'Einstellungen',
      autostart: 'App automatisch bei Anmeldung starten',
      minimize: 'App in den Infobereich der Taskleiste minimieren',
    },
    paused: {
      title: 'Gesperrter Vertrag',
      paragraphs: [
        'Dein Vertrag wurde aufgrund von Unstimmigkeiten automatisch gesperrt.',
        'Bitte wende Dich an unseren Kundenservice. Wir klären den Fall dann schnell und unbürokratisch für Dich.',
        'Du erreichst uns Mo. - Fr. 08:00 - 12:00 / 13:00 - 18:00 Uhr unter der Telefonnummer +49 (0) 5231 7090-0 oder per E-Mail an <a href="mailto:info@orgamax.de">info@orgamax.de</a>.',
        'Vielen Dank für Deine Mithilfe!',
      ],
      buttonLabel: 'Abmelden',
    },
    communication: {
      email: 'E-Mail',
      website: 'Webseite',
      phone1: 'Telefon 1',
      phone2: 'Telefon 2',
      mobile: 'Mobil',
      fax: 'Fax',
    },
    mailbox: {
      description: `
        Das <b>${productName} Postfach</b> ermöglicht den automatischen Import und die Verarbeitung von E-Rechnungen (ZUGFeRD, XRechnung) sowie PDF-Dokumenten. Du kannst E-Mails mit entsprechenden Anhängen an dieses Postfach weiterleiten, damit nur die Anhänge automatisch in die Anwendung eingelesen und verarbeitet werden.<br/><br/>
        Mit diesem Postfach sparst Du Zeit, da Rechnungen und Dokumente effizient organisiert und weiterverarbeitet werden.
      `,
      link: 'Detaillierte Anleitung zur Verwendung des Postfachs',
      empty: {
        title: 'Es ist noch kein Postfach eingerichtet.',
        description: `
          Du hast noch kein Postfach eingerichtet. Wähle im Dropdown-Menü die gewünschte Domain aus und bestätige die Nutzungsbedingungen.<br/><br/>
          Nach der Bestätigung wird Dein Postfach erstellt. Dieses kannst Du für den <b>automatischen Import</b> von E-Mails mit Anhängen vom Typ E-Rechnung (ZUGfERD, XRechnung) oder PDF-Dokumenten nutzen.
        `,
        checkbox: 'Hiermit bestätige ich die ',
        button: 'Postfach erstellen',
        hint: {
          title: 'Wichtiger Hinweis',
          description1: 'Dieses Postfach ist kein <b>klassisches E-Mail-Postfach</b> und sollte nicht öffentlich weitergegeben werden.',
          point1: '<b>Nur Anhänge:</b> Es werden ausschließlich die Anhänge (z. B. PDF, XRechnung) eingelesen, nicht die E-Mails selbst.',
          point2: '<b>Kein Zugriff:</b> Du hast keinen direkten Zugriff auf den Posteingang oder die E-Mails.',
          point3: '<b>Automatische Löschung:</b> E-Mails werden alle 14 Tage gelöscht.',
          description2: 'Das Postfach dient ausschließlich dazu, Anhänge wie E-Rechnungen oder PDF-Dokumente automatisch zu importieren und zu verarbeiten.',
        },
        errors: {
          get: 'Domänen konnten nicht abgerufen werden',
        },
      },
      item: {
        title: 'Aktuelles Postfach',
        additionalSettings: 'Zusätzliche Einstellungen',
        tooltip: {
          button: {
            copy: 'In Zwischenablage kopieren',
            delete: 'Postfach löschen',
            sync: 'Postfach jetzt abrufen',
          },
          toggle: {
            hintTitle: 'Hinweis',
            convertTo: {
              title: 'Automatische Erstellung von Ausgaben oder Einnahmen',
              description: 'Aktiviere diese Funktion, um festzulegen, ob der Anhang einer eingelesenen E-Mail direkt als Ausgabe erstellt oder im Bereich „Dokumente“ abgelegt wird. Dies gilt für E-Rechnungen (ZUGfERD, XRechnung), Eingangsrechnungen im PDF-Format und andere Dokumente (im PDF-Format).',
              hint: `Diese Funktion erfordert die Produkte „${productName} ${!isOrgaMax ? 'Rechnungen' : 'Buchhaltung'}“ und „${productName} Dokumente“.`,
            },
            active: {
              title: 'Postfach deaktivieren oder aktivieren',
              description: 'Mit dieser Funktion kannst Du Dein Postfach vorübergehend deaktivieren. Während der Deaktivierung werden keine E-Mails und Anhänge angenommen oder verarbeitet. Dies kann sinnvoll sein, wenn Probleme mit dem Postfach auftreten oder von unserem Supportteam behoben werden müssen.',
            },
          },
        },
        toggle: {
          active: 'Postfach aktiv',
          attachmentsConvertTo: 'Automatische Erstellung von Ausgaben oder Einnahmen',
          notify: 'Benachrichtigungen bei ausgelesenen E-Mails',
        },
        delete: {
          confirm: {
            title: 'Willst Du das Postfach wirklich löschen?',
            description: 'Das Postfach inkulsive aller nicht abgerufenen Dokumente wird vollständig gelöscht.',
          },
        },
      },
      toast: {
        create: {
          success: 'Dein Postfach wurde erfolgreich erstellt.',
          error: {
            default: 'Beim Erstellen des Postfachs ist ein Fehler aufgetreten.',
            exists: (email) => `Es existiert schon ein Postfach mit der E-Mail: "${email}".`,
          },
        },
        delete: {
          success: 'Dein Postfach wurde erfolgreich gelöscht.',
          error: 'Beim Löschen des Postfachs ist ein Fehler aufgetreten.',
        },
        sync: {
          success: 'Die Synchronisation wurde erfolgreich gestartet.',
          error: 'Beim Starten der Synchronisation ist ein Fehler aufgetreten.',
        },
        activate: {
          success: (active) => `Das Postfach wurde erfolgreich ${active ? 'aktiviert' : 'deaktiviert'}`,
          error: (active) => `Beim ${active ? 'Aktivieren' : 'Deaktivieren'} ist ein Fehler aufgetreten`,
        },
        updateAttachmentConvert: {
          success: 'Die Änderung wurde erfolgreich übernommen.',
          error: 'Beim Ändern der Einstellung ist ein Fehler aufgetreten.',
        },
        notify: {
          success: (active) => `Benachrichtigungen wurden erfolgreich ${active ? 'aktiviert' : 'deaktiviert'}`,
          error: (active) => `Beim ${active ? 'Aktivieren' : 'Deaktivieren'} ist ein Fehler aufgetreten`,
        },
      },
      form: {
        domain: 'Domain',
      },
    },
    transactionCreation: {
      description: ' Du kannst hier globale Einstellungen für Deine Vorgänge treffen.',
      laborShare: {
        title: 'Lohnanteile',
        switchLabel: 'Lohnanteile auf Rechnungen ausweisen.',
      },
    },
    aiSettings: {
      title: 'KI-Agent MAX aktivieren',
      description: 'Lege fest ob MAX Dir bei der Arbeit mit der Anwendung helfen soll.',
      errors: {
        default: 'Beim Aktivieren des KI-Agent MAX ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
      },
    },
  },
  vatPrompt: {
    title: 'Hast Du die Umsatzsteuersätze geprüft?',
    description: 'Bitte prüfe, ob Du die korrekten Umsatzsteuersätze verwendet hast. Für ein Leistungsdatum / Lieferdatum ab dem 01.01.2021 gelten wieder die regulären Sätze von 19% und 7%. Eine Kombination aus regulären und reduzierten Umsatzsteuersätzen ist nicht zulässig.',
    okButtonLabel: 'Ja, speichern',
    cancelButtonLabel: 'Nochmal prüfen',
  },

  timeIntervals: {
    yearlyPayment: 'Jährliche Zahlweise',
    monthlyPayment: 'Monatliche Zahlweise',
    monthly: 'monatlich',
    yearly: 'jährlich',
  },
  noRole: {
    modal: {
      owner: {
        title: 'Benutzer mit nicht zugewiesener Rolle',
        description: 'Es sind Benutzer angelegt, denen noch keine Rolle zugewiesen wurde. Dies kann verschiedene Gründe haben: Entweder wurde bei der Anlage keine Benutzer-Rolle hinzugefügt, oder es wurden aufgrund von Abweichungen zwischen erworbenen Benutzern und angelegten Benutzern die Rollen entzogen. Bitte weise den aktiven Benutzern die entsprechenden Rollen zu und entferne gegebenenfalls Benutzer, die nicht benötigt werden.',
      },
      user: {
        title: 'Bitte wende Dich an Deinen Administrator',
        description: 'Du hast aktuell keine Rolle zugewiesen, bitte wende Dich an Deinen Administrator für weitere Informationen. Du hast keine Berechtigungen, Funktionen zu nutzen.',
      },
    },
  },
  roles: {
    ADMIN: { short: 'Administrator', description: 'Hat Zugriff auf alle Bereiche der Anwendung.' },
    TAX_CONSULTANT_ADMIN: { short: 'Administrator', description: 'Hat Zugriff auf alle Kanzleibereiche inkl. Nutzerverwaltung.' },
    OWNER: { short: 'Besitzer', description: 'Du hast den Haupt-Account erstellt und besitzt alle Rechte.' },
    FINANCE: { short: 'Finanzen', description: 'Lese & Schreibrechte im Banking, Kasse, Ausgaben und Steuerbereichen.' },
    SALES: { short: 'Vertrieb & Einkauf', description: 'Lese & Schreibrechte für Kunden, Artikel, Angebote, Rechnungen, Aufträge, Lieferanten, Bestellungen, Projekte, Zeiterfassung und Abos.' },
    TAX_CONSULTANT: (product) => {
      return {
        short: 'Kanzlei-Sachbearbeiter',
        description: product === 'main'
          ? 'Lese- & Schreibrechte im Bereich Finanzen, Vertrieb. Darf Steuerberaterexport ausführen.'
          : product === 'document'
            ? 'Lese- & Schreibrechte in Dokumenten, Steuerberaterexport darf ausgeführt werden.'
            : product === 'taxconsultant'
              ? 'Lese- & Schreibrechte im Bereich Finanzen, Vertrieb. Darf Steuerberaterexport ausführen.'
              : 'Lese- & Schreibrechte Personal Funktionen',
      }
    },
    DOCUMENTS: { short: 'Dokumente', description: 'Lese & Schreibrechte im Dokumente Bereich.' },
    STAFF: { short: 'Personal', description: 'Zugriff auf den Personal Bereich.' },
  },
  userList: {
    addUser: 'Benutzer hinzufügen',
    showAllUser: 'Alle ansehen',
    showUser: 'ansehen',
    status: {
      active: 'Aktiv',
      invited: 'Eingeladen',
      isLocked: 'Gesperrt',
    },
    listEmptyStateTitle: 'Keine Benutzer vorhanden',
    emptyStateText: 'Keine Benutzer vorhanden',
    searchCategory: 'Benutzer und Steuerberater',
    searchCategoryTaxTenant: 'Benutzer',
    searchPlaceholder: 'Benutzer durchsuchen',
    loaderText: 'Benutzer werden geladen',
    password: {
      successMessage: 'Dein Passwort wurde erfolgreich gesetzt',
      description: (email) => `Bitte lege für Deinen Benutzernamen <b>${email}</b> ein Kennwort fest, um sowohl die Webanwendung als auch die Mobile App von ${productName} Dokumente nutzen zu können.`,
      tooltipTitle: 'Passwort vergeben',
      tooltipDescription: (email) => `Bitte gib einmalig ein Passwort für Deinen Account mit der E-Mail-Adresse <b>${email}</b> ein.</br></br>
      Dieses Passwort wird für den Zugriff auf die Webanwendung sowie für die Nutzung der Mobile App ${productName} Dokumente benötigt.</br></br>
      Beachte bitte, dass die Vergabe des Passwortes nur einmal möglich ist und danach nicht mehr angezeigt wird.`,
    },
    button: {
      invite: 'Einladen',
      inviteUser: 'Benutzer',
      setPassword: 'Passwort vergeben',
    },
    edit_modal: {
      title: (isEdit, selectableRoles) => {
        let entityTitle = 'Benutzer'

        if (selectableRoles.length === 1 && selectableRoles[0] === TAX_CONSULTANT_ROLE) {
          entityTitle = 'Steuerberater'
        }

        return `${entityTitle} ${isEdit ? 'bearbeiten' : 'einladen'}`
      },
      subTitle: 'Benutzerdaten',
      rolesSubTitle: 'Produkte, Rollen und Rechte zuweisen',
      pleaseChooseRole: 'Bitte wähle eine Rolle aus.',
      buttons: {
        update: 'Benutzer löschen',
        save: 'Speichern',
        invite: 'Benutzer einladen',
        inviteAndOtherUsers: 'Einladen & weiterer Benutzer',
      },
      startTrial: 'Jetzt Testphase starten',
      purchasePlan: 'Testphase abgelaufen - Jetzt Buchen!',
      forgotPasswordText: 'Mit Klick auf den Link wird eine E-Mail versendet. Darüber lässt sich das alte Passwort zurücksetzen und ein neues Passwort vergeben.',
      forgotPasswordLink: 'Passwort zurücksetzen',
      deactivateUserText: 'Benutzer sperren/deaktivieren',
      availableUsers: ' Verfügbare Benutzer: ',
      errors: {
        connectedTaxConsultant: 'Der Benutzer ist bei Dir als Steuerberater hinterlegt und kann nicht bearbeitet werden.',
      },
    },
    upgradeModal: {
      upgradeModalTitle: 'Verfügbare Benutzer aufgebraucht',
      upgradeModalMessage: `Du hast das Kontingent für Benutzer leider aufgebraucht.<br/>Möchtest Du weitere Benutzer dazubuchen?<br/><br/>Rufe uns dazu einfach unter <strong>${salesTelephoneNumber}</strong> an!`,
    },
  },

  agGrid: {
    filteredRows: 'Gefiltert',
    selectedRows: 'Ausgewählt',
    totalRows: 'Insgesamt',
    totalAndFilteredRows: 'Zeilen',
    more: 'Mehr',
    to: 'bis',
    of: 'von',
    page: 'Seite',
    nextPage: 'nächste Seite',
    lastPage: 'letzte Seite',
    firstPage: 'erste Seite',
    previousPage: 'vorherige Seite',
    listed: 'angezeigt',
    selected: 'ausgewählt',
    copyCell: 'Zelle in Zwischenablage',
  },

  dashboard: {
    title: 'Dashboard',
    widgetsDrawerTitle: 'Widgets anpassen',
    updatedWidgetSettingsMessage: 'Widget-Einstellungen erfolgreich aktualisiert',
    createClient: 'Mandant einladen',
    quickActions: {
      invoice: {
        title: 'Rechnung erstellen',
        description: 'Leistungen abrechnen und versenden.',
      },
      // Short forms for the split button's own menu: the button beside it already
      // says "Rechnung erstellen", so repeating the verb in every entry only makes
      // the dropdown wider than the tile it hangs off.
      invoiceMenu: {
        // Spaces around the slash on purpose: it gives the line a break
        // opportunity, so a narrow menu wraps between the two words instead of
        // inside one of them.
        creditNote: 'Rechnungskorrektur / Gutschrift',
        deposit: 'Abschlagsrechnung',
        recurring: 'Abo-Rechnung',
      },
      documents: {
        title: 'Dokumente hochladen',
        description: 'Belege & Dokumente digital erfassen.',
      },
      offer: {
        title: 'Angebot erstellen',
        description: 'Angebote vorbereiten und versenden.',
      },
      customer: {
        title: 'Kunden anlegen',
        description: 'Kundendaten erfassen und verwalten.',
      },
      article: {
        title: 'Artikel anlegen',
        description: 'Artikel hinterlegen und verwalten.',
      },
      client: {
        title: 'Mandant einladen',
        description: 'Mandanten einladen und zusammenarbeiten.',
      },
      todo: {
        title: 'Aufgabe erstellen',
        description: 'Aufgaben planen und im Blick behalten.',
      },
    },
    dueTodos: {
      columns: {
        status: 'Status',
        date: 'Frist',
        creator: 'Von',
        title: 'Aufgabe',
      },
      today: 'heute',
      tomorrow: 'morgen',
      overdueSince: days => `überfällig seit ${days} ${days === 1 ? 'Tag' : 'Tagen'}`,
      noDueDate: 'ohne Frist',
      showAll: 'Alle Aufgaben ansehen',
    },
    edit: {
      start: 'Dashboard anpassen',
      save: 'Speichern',
      discard: 'Abbrechen',
      reset: 'Standard wiederherstellen',
      hiddenToggle: 'Ausgeblendet',
      hiddenTitle: 'Ausgeblendete Widgets',
      hiddenEmpty: 'Alle Widgets werden angezeigt',
      rowCount: (count, max) => `${count} von ${max}`,
      rowFull: {
        title: 'Diese Reihe ist voll',
        text: max => `In eine Reihe passen höchstens ${max} Widgets.`,
        tipLabel: 'Tipp:',
        tip: 'Lege das Widget in einer anderen oder in einer neuen Reihe ab.',
      },
      hideWidget: 'Widget ausblenden',
      showWidget: 'Widget einblenden',
      show: 'Einblenden',
      savedMessage: 'Dashboard-Layout gespeichert',
      saveFailedMessage: 'Dashboard-Layout konnte nicht gespeichert werden',
      sizes: {
        quarter: '¼',
        third: '⅓',
        half: '½',
        full: '1',
      },
      sizeLabels: {
        quarter: 'Viertel der Zeile – vier Widgets nebeneinander',
        third: 'Drittel der Zeile – drei Widgets nebeneinander',
        half: 'Halbe Zeile – zwei Widgets nebeneinander',
        full: 'Ganze Zeile – Widget allein',
      },
    },
    widgets: {
      documentScans: 'Dokumentenscans',
      storageSpace: 'Speicherplatz',
      documentCount: 'Dokumentenanzahl',
      todo: 'Fällige Aufgaben',
      lastUsedDocuments: 'Zuletzt bearbeitete Dokumente',
      lastUsedCustomers: 'Zuletzt angesehene Kunden',
      documentStorageSpace: 'Speicherplatz',
      salesExpensesStats: 'Umsatzstatistik',
      topSalesStats: 'Umsatzauswertung',
      taxformUstvaSummary: 'Umsatzsteuervoranmeldung (UStVA)',
      openTransactionStats: 'Offene Vorgänge',
      bankAccounts: 'Konten Salden',
      cashFlow: 'Cash Flow',
      lastLoggedInClients: 'Letzter Mandant',
      lastUsedReceipts: 'Zuletzt angesehene Vorgänge',
      userListTaxAccount: 'Benutzer',
      userList: 'Benutzer und Steuerberater',
      staffTimer: 'Zeiterfassung',
      staffTimerHistory: 'Erfasste Zeiten',
      invitations: 'Offene Einladungen',
    },
    widgetDetails: {
      callToAction: 'Jetzt loslegen',
      lastLoggedInClients: {
        heading: 'Zuletzt eingeloggter Mandant',
        emptyListPlaceholder: 'Noch nicht eingeloggt',
      },
      cashflowWidget: {
        title: 'Cashflow',
        invoices: 'bez. Rechnungen',
        expenses: 'bez. Ausgaben',
        revenues: 'bez. sonstige Einnahmen',
        disclaimer: '* nur vollständig bezahlte Vorgänge werden berücksichtigt.',
        empty: year => `Noch keine bezahlten Vorgänge in ${year}`,
      },
      bankAndCashWidget: {
        account: 'Konten',
        allAccounts: 'Alle Konten',
        balance: 'Saldo',
        lastUpdatedDate: 'Aktualisiert am:',
      },
      clientInvitations: {
        title: 'Offene Einladungen',
        subTitle: 'Folgende Einladungen zur Zusammenarbeit sind unbeantwortet:',
        editPlaceholder: 'Hier erscheinen offene Einladungen zur Zusammenarbeit. Solange keine offen sind, wird das Widget nicht angezeigt.',
      },
    },
    taxConsultantPlaceholder: {
      heading: 'Steuerung der Mandantenprozesse aus der Ferne leicht gemacht!',
      paragraph: {
        one: 'Hast Du Fragen zur optimalen Nutzung von orgaMAX Berater? Keine Sorge, wir sind hier, um Dir zur Seite zu stehen und all Deine Fragen zu beantworten.',
        two: 'Zögere nicht, uns eine E-Mail zu schreiben, oder noch besser, vereinbare direkt einen Rückruftermin, der perfekt in Deinen Zeitplan passt. Wir freuen uns darauf, Dir behilflich zu sein!',
      },
      contactTiming: 'Mo. - Fr. 08:00 - 12:00 / 13:00 - 18:00 Uhr',
      contactEmail: 'kanzlei@orgamax.de',
      contactEmailSubject: 'Fragen zu orgaMAX Steuerberater',
      callButton: 'Rückruf erbeten',
      emailButton: 'E-Mail senden',
    },
    personalTutorial: {
      right: {
        title: 'Wie Du Dein Personal verwaltest',
      },
      left: {
        title: `Willkommen bei ${productNameWithPrefix} Personal`,
      },
      description: `
        Mit diesen kurzen Tutorials wirst Du optimal auf orgaMAX Personal vorbereitet. Schritt für Schritt erleichtern Dir unsere Experten den Einstieg.
        <br />
        <br />
        Du benötigst für den Start weitere Unterstützung, buche einen Termin und wir stehen Dir zur Seite.
      `,
      links: {
        invite: 'Mitarbeitende einladen zu orgaMAX',
        contract: 'Arbeitsverträge hinterlegen',
        vacation: 'Urlaube und Abwesenheiten',
      },
      actions: {
        open: 'Jetzt Personal starten',
        appointment: 'Termin buchen',
      },
    },
  },

  // dasboard stats
  dashboardStats: {
    emptyStateText: 'Erstelle Deine erste Rechnung und schließe sie ab.',
    prePeriod: 'Vorperiode',
    revenue: 'Umsatz',
    profit: 'Überschuss',
    actualPeriod: 'Aktueller Zeitraum',
    expense: 'Ausgabe',
    profitExpenseStatistic: 'Umsatzstatistik',
    defaultErrorText: 'Umsatzdaten konnten nicht geladen werden',
    generateRevenue: 'Jetzt Umsatz generieren',
    loaderText: 'Umsatzdaten werden geladen',
    noStatsData: 'Es liegt noch kein Umsatz für den ausgewählten Zeitraum vor.',
    refresh: 'Jetzt aktualisieren',
    title: 'Umsatzauswertung',
    topSalesTypes: {
      customers: (top) => `${top} Kunden`,
      articles: (top) => `${top} Artikel`,
      articleCategories: (top) => `${top} Artikelkategorien`,
      customerCategories: (top) => `${top} Kundenkategorien`,
      top: 'Top 5 ',
    },
  },

  massImport2: {
    buttons: {
      reset: {
        tooltip: 'Zuordnungen zurücksetzen',
      },
    },
    requiredWarning: {
      one: '1 Pflichtangabe',
      multiple: '{{count}} Pflichtangaben',
    },
    dragLabel: 'XLSX/XLS-Datei hier ablegen oder Datei auswählen',
    importKind: {
      create: 'Nur neue {{items}} importieren',
      createUpdate: 'Neue {{items}} importieren, vorhandene aktualisieren',
      update: 'Nur vorhandene {{items}} aktualisieren',
    },
    importDefs: {
      articles: {
        categories: {
          information: 'Informationen',
          price: 'Preise',
          description: 'Beschreibung',
          category: 'Kategorie',
          cashRegister: 'Kasse',
          ecommerce: 'E-Commerce',
          stock: 'Lager',
          bookkeeping: 'Individuelles Erlöskonto',
          customFields: 'Individuelle Felder',
        },
        fields: {
          number: 'Artikel-Nr.',
          title: 'Bezeichnung',
          weight: 'Gewicht',
          unit: 'Einheit',
          price: 'Verkaufspreis (netto)',
          priceGross: 'Bruttopreis',
          laborShare: 'Lohnanteil',
          vatPercent: 'USt.',
          purchasePrice: 'Einkaufspreis (netto)',
          description: 'Beschreibung',
          category: 'Artikelkategorie',
          notes: 'Anmerkungen',
          notesAlert: 'Bei neuen Vorgängen anzeigen?',
          stockAmount: 'aktueller Lagerbestand',
          isStockManaged: 'Lagerbestandsgeführt',
          isPosArticle: 'Kassenartikel',
          tags: 'EAN\'s',
          kind: 'Artikelart',
          bookkeepingAccountId: 'Erlöskonto',
          minimumStockAmount: 'Mindestbestand',
        },
      },
      customers: {
        categories: {
          communication: 'Kommunikation',
          information: 'Informationen',
          conditions: 'Konditionen',
          other: 'Sonstiges',
          billingAddress: 'Rechnungsadresse',
          deliveryAddress: 'Lieferadresse',
          contactPerson: 'Ansprechpartner',
          bankAccount: 'Bankverbindung',
          customFields: 'Individuelle Felder',
        },
        fields: {
          category: 'Kundenkategorie',
          discount: 'Rabatt auf Listenpreise in %',
          number: 'Kunden-Nr.',
          salutation: 'Anrede',
          title: 'Titel',
          firstName: 'Vorname',
          lastName: 'Nachname',
          companyName: 'Firmenname',
          companyNameAffix: 'Namenszusatz',
          email: 'E-Mail',
          fax: 'Fax',
          mobile: 'Mobil',
          phone1: 'Telefon 1',
          phone2: 'Telefon 2',
          website: 'Webseite',
          vatNumber: 'USt.-IDNr.',
          netPriceAsDefault: 'Steuerfreie Rechnung?',
          notes: 'Anmerkungen',
          notesAlert: 'Bei neuen Vorgängen anzeigen?',
          // contactPerson
          cpSalutation: 'Anrede',
          cpTitle: 'Titel',
          cpFirstName: 'Vorname',
          cpLastName: 'Nachname',
          cpJob: 'Position',
          cpEmail: 'E-Mail',
          cpFax: 'Fax',
          cpMobile: 'Mobil',
          cpPhone1: 'Telefon 1',
          cpPhone2: 'Telefon 2',
          cpBirthday: 'Geburtstag',
          cpIsMainContact: 'Hauptansprechpartner?',
          // billingAddress
          baSalutation: 'Anrede',
          baTitle: 'Titel',
          baFirstName: 'Vorname',
          baLastName: 'Nachname',
          baCompanyName: 'Firmenname',
          baCompanyNameAffix: 'Namenszusatz',
          baStreet: 'Straße & Hausnummer',
          baCity: 'Ort',
          baZipCode: 'PLZ',
          baCountryIso: 'Land',
          // deliveryAddress
          daSalutation: 'Anrede',
          daTitle: 'Titel',
          daFirstName: 'Vorname',
          daLastName: 'Nachname',
          daCompanyName: 'Firmenname',
          daCompanyNameAffix: 'Namenszusatz',
          daStreet: 'Straße & Hausnummer',
          daCity: 'Ort',
          daZipCode: 'PLZ',
          daCountryIso: 'Land',
          // customer fields
          accountNo: 'Debitorennummer',
          buyerReference: 'Leitweg-ID',
          customerOwnSupplierNumber: 'Lieferantennummer des Kunden',
          hourlyRate: 'Stundensatz in € (netto)',
          // bankAccount
          accountHolder: 'Kontoinhaber',
          iban: 'IBAN',
          bic: 'BIC',
        },
      },
      suppliers: {
        categories: {
          communication: 'Kommunikation',
          conditions: 'Konditionen',
          payConditions: 'Zahlungskonditionen',
          bankAccount: 'Bankverbindung',
          bookkeepingAccounts: 'Fibukonten',
          information: 'Informationen',
          other: 'Sonstiges',
          contactPerson: 'Ansprechpartner',
        },
        fields: {
          number: 'Lieferanten-Nr.',
          name: 'Lieferantenname',
          street: 'Straße & Hausnummer',
          zipCode: 'PLZ',
          city: 'Ort',
          countryIso: 'Land',
          email: 'E-Mail',
          fax: 'Fax',
          mobile: 'Mobil',
          phone1: 'Telefon 1',
          phone2: 'Telefon 2',
          website: 'Webseite',
          customerReference: 'Eigene Kd-Nr. beim Lieferanten',
          // conditions
          discount: 'Rabatt in %',
          // payConditions
          dueDays: 'Zahlungsziel in Tagen',
          payKind: 'Zahlart',
          cashDiscount: 'Skonto aktivieren?',
          cashDiscountPercent: 'Skonto in %',
          cashDiscountDays: 'Rabatt innerhalb von Tagen',
          // bankAccount
          accountHolder: 'Kontoinhaber',
          iban: 'IBAN',
          bic: 'BIC',
          bookkeepingAccountNo: 'Standard Buchhaltungskonto',
          creditorAccountNo: 'Kreditorennummer',
          // contactPerson
          cpSalutation: 'Anrede',
          cpTitle: 'Titel',
          cpFirstName: 'Vorname',
          cpLastName: 'Nachname',
          cpJob: 'Position',
          cpEmail: 'E-Mail',
          cpFax: 'Fax',
          cpMobile: 'Mobil',
          cpPhone1: 'Telefon 1',
          cpPhone2: 'Telefon 2',
          // other
          notes: 'Anmerkungen',
          notesAlert: 'Bei neuen Vorgängen anzeigen?',
          nameAffix: 'Namenszusatz',
          cpBirthday: 'Ansprechpartner Geburtstag',
          cpIsMainContact: 'Hauptansprechpartner?',
          vatNumber: 'USt-IDNr.',
        },
      },
    },
  },

  massImport: {
    noImportsAvailable: 'Es sind für Dich keine Datenimporte freigeschaltet',
    topBarTitle: 'Datenimport',
    boolean: 'Wahrheitswert für Boolean-Elemente',
    boolLabelTrue: 'Trifft zu / Ist wahr',
    boolLabelFalse: 'Trifft nicht zu / Nicht wahr',
    assignment: 'Zuordnung',
    columnInFile: 'Spalte in Importdatei',
    fieldInData: 'Feld in Stammdaten',
    massSuccess: 'Massenverarbeitung wurde erfolgreich angestoßen',
    massError: 'Massenverarbeitung konnte nicht angestoßen werden',
    importStarted: 'Der Import wurde gestartet. Du erhältst eine Mail mit allen relevanten Informationen inkl. Fehlerbericht, sobald der Import abgeschlossen ist.',
    importRunning: 'Import läuft',
    faqLink: 'FAQ zum Kundenimport',
    confirmTitle: 'Wichtige Info zur Massenverarbeitung',
    confirmDescription: '<p>Beachte, dass die Änderungen nach dem Import nicht rückgängig gemacht werden können. Während des Imports bitten wir Dich, keine Änderungen an den {{items}} vorzunehmen, da dies zu Fehlern in der Datenbank führen könnte.</p><p>Der Import wird im Hintergrund ausgeführt. Im Anschluss erhältst Du eine Meldung und eine E-Mail mit dem Protokoll.</p>',
    confirmButton: 'Ok, importieren',
    cancelButton: 'Nochmal überprüfen',
    itemsArticles: 'Artikeln',
    itemsCustomers: 'Kunden',
    itemsSuppliers: 'Lieferanten',
    preview: 'Vorschau',
    genericTitle: 'Datenimport',
    title: '{{type}}-Massenverarbeitung',
    uploadTitle: '{{type}}-Dokument einfügen',
    dragLabelA: 'XLSX-Datei hier ablegen oder',
    dragLabelB: ' Datei auswählen',
    uploadDescription: 'Datei hier ablegen oder Dialog öffnen',
    templateDescription: 'XLSX-Vorlage herunterladen',
    templateA: 'Unsere ',
    templateB: 'Muster-XLSX',
    templateC: ' mit Demodatensatz kannst Du zur Erstellung Deiner individuellen XLSX-Datei nutzen.',
    customer: 'Kunde',
    customers: 'Kunden',
    supplier: 'Lieferant',
    suppliers: 'Lieferanten',
    article: 'Artikel',
    articles: 'Artikel',
    fileLabel: 'Importdatei*',
    importKind: 'Importart',
    options: 'Optionale Einstellungen',
    importLine: 'Import ab Zeile',
    customersLabel: 'Kunden-Import',
    suppliersLabel: 'Lieferanten-Import',
    articlesLabel: 'Artikel-Import',
    articlesMassLabel: 'Artikel-Import starten',
    customersMassLabel: 'Kunden-Import starten',
    suppliersMassLabel: 'Lieferanten-Import starten',
    importSuccess: '{{count}} {{type}} importiert',
    loading: 'Lade Datenimport',
    importing: 'Daten werden importiert',
    validation: 'Daten werden geprüft',
    noRowsToShow1: 'Es konnten keine Daten importiert werden.',
    noRowsToShow2: 'Bitte prüfe, ob Du eine korrekt befüllte Datei hochgeladen hast!',
    abortImportModal: {
      title: 'Import abbrechen?',
      description: 'Wenn Du den Vorgang abbrichst, werden Deine Daten nicht importiert.',
      buttons: {
        abort: 'Vorgang Abbrechen',
        proceed: 'Fortsetzen',
      },
    },
    button: {
      validating: 'Prüfe',
      import: 'Importieren',
      control: 'FEHLER PRÜFEN',
      importConfirm: 'Nur {{validCount}} {{validHintLabel}} importieren',
      correction: 'Fehler korrigieren',
      backToList: 'Zur {{typeLabel}}-Liste',
    },
    someThingWrong: 'Fehler prüfen',
    header: {
      link: 'Verknüpfen',
      unlink: 'Verknüpfung aufheben',
      title: 'Verfügbare Spalten',
      empty: 'Keine weiteren Spalten verfügbar',
    },
    importDefs: {
      errorMessage: 'Zeile {{index}},{{field}} {{reason}}',
      invalid: 'ungültig',
      errorNumber: 'keine Zahl',
      errorUsed: 'bereits vergeben',
      errorToLong: 'zu lang',
      labels: {
        article: {
          nr: 'Artikel-Nr.',
          title: 'Bezeichnung',
          description: 'Beschreibung',
          vat: 'USt.',
          retailPrice: 'Verkaufspreis (netto)',
          purchasingPrice: 'Einkaufspreis (netto)',
          unit: 'Einheit',
          cat: 'Artikelkategorie',
          isPosArticle: 'Kasse POS Artikel',
          tags: 'EAN',
          stockAmount: 'aktueller Lagerbestand',
          isStockManaged: 'Lagerbestandsgeführt',
          stock: 'Lager',
          notes: 'Anmerkungen',
          notesAlert: 'Bei neuen Vorgängen anzeigen?',
          bookkeepingAccountId: 'Erlöskonto',
          minimumStockAmount: 'Mindestbestand',
        },
        customer: {
          company: 'Firma',
          person: 'Person',
          kind: 'Art',
          nr: 'Kunden-Nr.',
          companyName: 'Firmenname',
          salutation: 'Anrede',
          title: 'Titel',
          firstname: 'Vorname',
          lastName: 'Nachname',
          street: 'Straße',
          postcode: 'PLZ',
          city: 'Ort',
          country: 'Land',
          email: 'E-Mail',
          website: 'Webseite',
          tel1: 'Telefon1',
          tel2: 'Telefon2',
          fax: 'Fax',
          mob: 'Mobil',
          cat: 'Kategorie',
          discount: 'Rabatt',
        },
        supplier: {
          company: 'Firma',
          person: 'Person',
          kind: 'Art',
          nr: 'Lieferanten-Nr.',
          name: 'Firmenname',
          companyName: 'Firmenname',
          salutation: 'Anrede',
          title: 'Titel',
          firstname: 'Vorname',
          lastName: 'Nachname',
          street: 'Straße',
          postcode: 'PLZ',
          city: 'Ort',
          country: 'Land',
          email: 'E-Mail',
          website: 'Webseite',
          tel1: 'Telefon1',
          tel2: 'Telefon2',
          fax: 'Fax',
          mob: 'Mobil',
          discount: 'Rabatt',
          customerReference: 'Eigene Kd-Nr. beim Lieferant',
          accountHolder: 'Kontoinhaber',
          iban: 'IBAN',
          bic: 'BIC',
          nameAffix: 'Namenszusatz',
          dueDays: 'Zahlungsziel in Tagen',
          payKind: 'Zahlart',
          cashDiscount: 'Skonto aktivieren?',
          cashDiscountPercent: 'Skonto in %',
          cashDiscountDays: 'Rabatt innerhalb von Tagen',
          bookkeepingAccountNo: 'Standard Buchhaltungskonto',
          creditorAccountNo: 'Kreditorennummer',
          vatNumber: 'USt-IDNr.',
          notes: 'Anmerkungen',
          notesAlert: 'Bei neuen Vorgängen anzeigen?',
          cpSalutation: 'Anrede (Ansprechpartner)',
          cpTitle: 'Titel (Ansprechpartner)',
          cpFirstName: 'Vorname (Ansprechpartner)',
          cpLastName: 'Nachname (Ansprechpartner)',
          cpJob: 'Position (Ansprechpartner)',
          cpEmail: 'E-Mail (Ansprechpartner)',
          cpFax: 'Fax (Ansprechpartner)',
          cpMobile: 'Mobil (Ansprechpartner)',
          cpPhone1: 'Telefon 1 (Ansprechpartner)',
          cpPhone2: 'Telefon 2 (Ansprechpartner)',
          cpBirthday: 'Geburtstag (Ansprechpartner)',
          cpIsMainContact: 'Hauptansprechpartner?',
        },
      },
      constants: {
        customer: {
          company: 'company',
          person: 'person',
        },
        supplier: {
          company: 'company',
          person: 'person',
        },
      },
    },
    errors: {
      massImport: {
        booelanValueMissing: 'Wahrheitswert nicht angegeben',
        booelanValueEqual: 'Wahrheitswerte dürfen nicht identisch sein',
        importKindMissing: 'Importart nicht angegeben',
        articles: {
          number: '"Artikel-Nr." nicht zugewiesen',
          title: '"Bezeichnung" nicht zugewiesen',
        },
        customers: {
          number: 'Das Feld Kundennummer ist ein Pflichtfeld. Bitte weise dieses entsprechend zu.',
          billingAddressNameMissing: 'Die Felder Nachname bzw. Firmenname der Rechnungsadresse sind Pflichtfelder. Bitte weise mindestens eins der beiden Felder entsprechend zu.',
        },
        suppliers: {
          number: '"Lieferantennummer" nicht zugewiesen',
          name: '"Lieferantenname" nicht zugewiesen',
        },
      },
      title: 'Import nicht möglich',
      colHeaders: {
        col: 'Spalte',
        error: 'Fehler',
        action: 'Aktion',
      },
      required: 'Dies ist ein Pflichtfeld.',
      duplicate: 'Alle Werte müssen eindeutig sein.',
      articles: {
        vat: 'Importierte USt. nicht zulässig.',
        number: 'Ein oder mehrere Artikelnummern sind bereits im System vergeben.',
        retail: 'Der Verkaufspreis muss eine Zahl sein.',
        purchase: 'Der Einkaufspreis muss eine Zahl sein.',
        tags: 'Eine oder mehrere EAN Nummern sind bereits im System vergeben.',
        stockAmount: 'Der Lagerbestand muss als Zahl erfasst werden.',
        isStockManaged: 'Der Wert für die Lagerbestandsführung ist nicht zulässig.',
      },
      customers: {
        art: 'Die Art muss entweder Person oder Firma entsprechen.',
        number: {
          exists: 'Eine oder mehrere Kundennummern sind bereits im System vergeben.',
          format: 'Die Kundennummer muss eine Zahl sein',
        },
        countryIso: 'In dieser Spalte befinden sich Werte, die nicht dem ISO 3166 Format entsprechen.',
        email: 'In dieser Spalte befinden sich einige E-Mail-Adressen im falschen Format.',
      },
      suppliers: {
        countryIso: 'In dieser Spalte befinden sich Werte, die nicht dem ISO 3166 Format entsprechen.',
        email: 'In dieser Spalte befinden sich einige E-Mail-Adressen im falschen Format.',
        number: {
          exists: 'Eine oder mehrere Lieferanten-Nummern sind bereits im System vergeben.',
          format: 'Die Lieferanten-Nummer muss eine Zahl sein',
        },
      },
    },
    fieldsIgnored: {
      article: {
        noPOS: 'In Deinem Plan ist die Kasse POS nicht enthalten. Beim Import werden entsprechende Daten ignoriert.',
        noStockManagement: 'In Deinem Plan ist die Lagerhaltung nicht enthalten. Beim Import werden entsprechende Daten ignoriert.',
        noPOSNoStockManagement: 'In Deinem Plan sind weder die Kasse POS noch Lagerhaltung enthalten. Beim Import werden entsprechende Daten ignoriert.',
      },
    },
    actions: {
      required: 'Wähle eine Spalte zum Verknüpfen aus!',
      duplicate: 'Vergib eindeutige Werte!',
      inputNecessary: 'Bitte gib einen passenden Wert ein!',
      inputNecessaryFirstName: 'Bitte gib einen Vornamen ein!',
      inputNecessaryLastName: 'Bitte gib einen Nachnamen ein!',
      inputNecessaryCompanyName: 'Bitte gib einen Firmennamen ein!',
      inputNecessaryCustNo: 'Bitte gib eine Kundennummer ein!',
      articles: {
        vat: 'Wähle in den betreffenden Zellen eine zulässige USt. aus!',
        number: 'Ändere in den betreffenden Zellen die Artikelnummer!',
        retail: 'Ändere den Verkaufspreis in das benötigte Format!',
        purchase: 'Ändere den Einkaufspreis in das benötigte Format!',
        tags: 'Ändere in den betreffenden Zellen die EAN Nummer(n)!',
        stockAmount: 'Ändere in den betreffenden Zeilen den Lagerbestand!',
        isStockManaged: 'Bitte gib nur "Ja" oder "Nein" ein',
      },
      customers: {
        art: 'Wähle in den betreffenden Zellen "Person" oder "Firma" aus!',
        number: {
          exists: 'Ändere in den betreffenden Zellen die Kundennummer!',
          format: 'Ändere die Kundennummer in das benötigte Format!',
        },
        countryIso: 'Wähle in den betreffenden Zellen andere Werte aus!',
        email: 'Ändere in den betreffenden Zellen die E-Mail-Adresse!',
      },
      suppliers: {
        number: {
          exists: 'Ändere in den betreffenden Zellen die Lieferanten-Nummer.',
          format: 'Ändere die Lieferanten-Nummer in das benötigte Format.',
        },
        countryIso: 'Wähle in den betreffenden Zellen andere Werte aus.',
        email: 'Ändere in den betreffenden Zellen die E-Mail-Adresse.',
      },
    },
  },
  marketplace: {
    headline: productName => `${productName} Marketplace`,
    subtitle: productName => `Verknüpfe Deine Lieblingstools mit ${productName}`,
    headlines: {
      own: productName => `Meine Erweiterungen für ${productName}`,
      popular: productName => `Beliebte Erweiterungen für ${productName}`,
      new: productName => `Neue Erweiterungen für ${productName}`,
      featured: productName => `Von ${productName} entwickelte Erweiterungen`,
      all: productName => `Alle Erweiterungen für ${productName}`,
      published: (productName, isPublisher) => !isPublisher ? `Erstelle Deine eigene Erweiterung für ${productName}` : `Eigene Erweiterungen für ${productName}`,
      publisher: productName => 'Meine Herausgeberdaten',
    },
    menuitems: {
      own: 'Meine Erweiterungen',
      popular: 'Beliebte Erweiterungen',
      new: 'Neue Erweiterungen',
      features: 'von MeinBüro entwickelt',
      all: 'Alle Erweiterungen',
      published: isPublisher => `${isPublisher ? 'Eigene Erweiterungen' : 'Erweiterung erstellen'}`,
      publisher: 'Herausgeberdaten anpassen',
    },
    search: 'Erweiterungen durchsuchen',
    form: {
      toolbar: isEdit => isEdit ? 'Erweiterung bearbeiten' : 'Neue Erweiterung erstellen',
      save: rejected => rejected ? 'Neue Version anlegen' : 'Als Entwurf speichern',
      publish: 'Erweiterung einreichen',
      reviewHint: 'Deine App befindet sich im Review und kann währenddessen nicht bearbeitet werden.',
      testApp: 'Erweiterung testen',
      create: {
        headline: 'Generiere Deinen API-Key',
        text: 'Gib bitte eine Weiterleitungs-URL an.<br />Nutzer werden nach der Installation auf diese URL weitergeleitet.<br /><strong>Beispiel: https://deine-app.de/auth/callback</strong>',
        inputs: {
          button: 'API-Key generieren',
        },
      },
      intro: {
        headline: 'Dein persönlicher API-Key',
        text: 'Du musst mindestens eine Weiterleitungs-URL angeben, bevor Du Deine Erweiterungen veröffentlichen kannst.<br />Händler werden nach der Installation der Erweiterung zu diesen URLs weitergeleitet, die sich auf einer Whitelist befinden.',
        inputs: {
          key: 'API-Key',
          secret: 'Secret API-Key',
          callback: 'Callback-URL',
        },
      },
      info: {
        name: 'Name der Erweiterung',
        version: 'letzte Version',
      },
      general: {
        headline: '1. Allgemeine Daten',
        sections: {
          info: {
            headline: 'Informationen',
            text: 'Wähle einen kundenbezogenen Namen und eine kundenbezogene Kurzbeschreibung für Deine Erweiterung. Die Kurzbeschreibung verdeutlicht Deinem Kunden den Nutzen und die Vorteile Deiner Erweiterung. Sie erscheint im Marketplace, den Suchergebnissen und an anderen geeigneten Stellen.',
            inputs: {
              name: maxLength => `Erweiterungs-Name (max. ${maxLength} Zeichen)`,
              summary: maxLength => `Kurzbeschreibung (max. ${maxLength} Zeichen)`,
            },
          },
          icon: {
            claim: 'Lade Dein Icon hoch',
            dependencies: (allowedExtensions, minHeight, minWidth, maxSize) => `${allowedExtensions.join(' & ')}, mind. ${minWidth}x${minHeight} und max. ${maxSize / 1000 / 1000}MB`,
          },
        },
      },
      details: {
        headline: '2. Details',
        sections: {
          screenshots: {
            headline: 'Screenshots',
            text: maxCount => `Lade Screenshots Deiner Erweiterung hoch, die Deinen Kunden die Funktionsweise und die Vorteile Deiner Erweiterung erläutern. Deine Screenshots sollten hochauflösend und gut zu erkennen sein. Wenn Du Text zu Deinen Screenshots hinzufügen möchtest, bringe diese auf Deinen Bildern unter. Maximale Anzahl an Screenshots: ${maxCount}`,
            add: {
              claim: 'Screenshots hinzufügen',
              dependencies: (allowedExtensions, maxSize) => `${allowedExtensions.join(' & ')}, max. ${maxSize / 1000 / 1000}MB`,
            },
          },
          description: {
            headline: 'Detaillierte Beschreibung',
            text: 'Erkläre Deinen Kunden, welche Vorteile Deine Erweiterung hat und wie sie Deine Kunden erfolgreicher macht. Hier kannst Du Funktionen Deiner Erweiterung aufzählen und beschreiben, wie sie funktioniert.',
            inputs: {
              description: 'Beschreibungstext eingeben',
            },
          },
          scopes: {
            headline: 'API-Zugriffe',
            text: 'Gib an, auf welche Scopes der API Deine Erweiterung zugreift. Anhand Deiner Angaben werden wir im nächsten Schritt das Review durchführen. Die Angabe hilft Deinen Kunden zu erkennen, auf welche ihrer Daten Deine App zugreift. Du kannst Deine Scopes im Nachhinein jederzeit mit einer neuen Version Deiner App anpassen.',
            inputs: {
              button: 'Zugriffe bearbeiten',
            },
          },
          review: {
            headline: 'Review Anmerkungen (optional)',
            text: 'Gib unserem Qualitätsteam alle wichtigen Informationen, die sie zum Überprüfen Deiner Erweiterung brauchen. Z.B. eine Anleitung zur Benutzung Deiner Erweiterung, Login Daten für einen Testaccount oder andere wichtige Informationen, die für das Testen Deiner Erweiterung relevant sind.',
            inputs: {
              note: productName => `Beispiel:\r\n\r\nBitte benutzt diesen Testaccount: Username: „Testuser“, Passwort „meinpasswort?!2345“\r\n\r\nTest Szenario 1\r\nWählt zwei Datensätze ein\r\nÜbertragt diese Datensätze\r\nErwartetes Ergebnis: Die Datensätze sind in ${productName} gespeichert und sichtbar.`,
            },
          },
        },
      },
    },
    status: {
      inDevelopment: 'Entwurf',
      pending: 'Eingereicht',
      inReview: 'In Review',
      rejected: 'Abgelehnt',
      approved: 'Genehmigt',
    },
    detail: {
      toolbar: 'Zurück zum Marketplace',
      moreInformation: 'Weitere Informationen',
    },
    errors: {
      409: 'Eine Erweiterung mit diesem Namen existiert bereits',
    },
    publisherForm: {
      fullName: 'Herausgeber (vollständiger Name oder Firma)',
      street: 'Straße + Hausnummer',
      postcode: 'PLZ',
      postcodeValidationError: 'Bitte gib eine gültige Postleitzahl an',
      city: 'Ort',
      email: 'Support E-Mail',
      website: 'Webseite (optional)',
      phone: 'Telefon (optional)',
      agbUrl: 'AGB (optional)',
    },
    publisherinfo: {
      workflow: [
        {
          title: 'Gib Deine Herausgeberinformationen an',
          text: productName => 'Diese Daten werden benötigt, damit die Nutzer wissen, vom wem diese Erweiterung entwickelt wurde.',
        },
        {
          title: 'Generiere den API-Key für Deine Erweiterung',
          text: productName => `Mit dem API-Key kannst Du Deine Erweiterung in ${productName} einbinden.`,
        },
        {
          title: 'Gib die Metadaten für die Erweiterung ein',
          text: productName => 'Trage alle wichtigen Informationen zu Deiner Erweiterung ein.',
        },
        {
          title: 'Reiche Deine Erweiterung zum Review ein',
          text: productName => 'Übergib Deine fertige Erweiterung zur Überprüfung an unser Qualitätsteam, bevor Du sie veröffentlichst.',
        },
      ],
      button: 'Herausgeberinformationen angeben',
    },
    empty: {
      text: 'Du hast keine aktiven Erweiterungen',
      button: 'Erweiterung hinzufügen',
    },
    messages: {
      appActivatedSuccess: 'Die Erweiterung wurde erfolgreich aktiviert',
      appDeactivatedSuccess: 'Die Erweiterung wurde erfolgreich deaktiviert',
      appActivating: 'Aktiviere App...',
      appDeactivating: 'Deaktiviere App...',
      appLoading: 'Lade App ...',
      appSaving: 'Speichere App ...',
      appSubmit: 'Reiche App ein ...',
      saveIcon: 'Speichere Icon...',
      saveScreenshots: 'Speichere Screenshots...',
    },
    publicApi: {
      textReadOnly: `Deine Erweiterungen sind schreibgeschützt, weil das Add-on „${productName} API“ nicht mehr gebucht ist. Bereits veröffentlichte Erweiterungen bleiben im Marketplace verfügbar. Buche das Add-on (30 € pro Monat zzgl. USt.), um Deine Erweiterungen wieder zu bearbeiten und Deinen API-Key zu sehen.`,
      notBookedMessage: `Dafür benötigst Du das Add-on „${productName} API“.`,
    },
    openApp: 'Erweiterung öffnen',
    removeApp: 'Erweiterung entfernen',
    activateNow: 'Jetzt aktivieren',
    learnMoreDrawer: {
      titles: {
        main: [
          'Angebote & Aufträge',
          'Rechnungen & Lieferservice',
          'Belege',
          'Übersichtliches Zahlungswesen',
          'Steuerdaten-Export',
          'Steuerauswertung',
          'Blick in die Software',
        ],
        document: [
          'Dein ganzes Büro in einer App',
          'Im Webbrowser effektiv verwalten',
          'Alles im Blick',
          'Steuerbelege leicht exportieren',
          'Unterschriften schnell einholen',
        ],
        staff: [
          'Urlaube & Abwesenheiten effizient managen',
          'Arbeitszeiten sicher erfassen',
          'Digitale Personalakte',
          'Wir haben noch mehr zu bieten',
        ],
      },
      youtubeVideoDescription: 'Wie Du Deine Buchhaltung schnell und einfach erledigst, zeigt Dir Marius Wolter von orgaMAX',
    },
  },
  transactionSendEmail: {
    recipientName: 'Empfängername',
    attachmentDeleteFailed: 'Beim Löschen der Datei ist ein Problem aufgetreten',
    attachmentDeleteSuccess: 'Die Datei wurde erfolgreich gelöscht',
    dunningToggleLabel: 'Zukünftige Mahnungen automatisch versenden',
    loaderText: 'E-Mail wird versendet',
    maxAttachmentHint: 'Es können maximal 10 Anhänge hochgeladen werden',
    messageCustomer: 'Nachricht an den Kunden',
    messageSupplier: 'Nachricht an den Lieferanten',
    emailSignature: 'E-Mail-Signatur',
    updateEmailSignatureInSettings: 'Signatur in Einstellungen aktualisieren',
    sendEmail: 'E-Mail senden',
    sendEmailWithSignature: 'E-Mail mit Unterschrift senden',
    sendCopy: 'Kopie an mich',
    subject: 'Betreff',
    uploading: 'Die Datei wird hochgeladen',
    uploaderText: 'Dokument hochladen',
    uploaderHint: 'oder per Drag and Drop reinziehen',
    otherAttachments: 'Weitere Anhänge',
    removeUploadedAttachmentTooltip: 'Hochgeladenen Anhang entfernen',
    downloadUploadedAttachmentTooltip: 'Hochgeladenen Anhang herunterladen',
    previewOnlyAvailableForPdfTooltip: 'Vorschau nur für PDF-Dateien verfügbar',
    emailSelectInput: 'Absender',
    adjustDisplayedSender: 'Angezeigten Absender anpassen',
    missingSenderNameTooltip: 'Kein Absendername in den Einstellungen hinterlegt.',
    addOwnEmailAccount: 'Eigenes E-Mail-Konto hinzufügen',
    sectionRecipientsAndSending: 'Empfänger und Versand',
    sectionMessage: 'Nachricht',
    sectionAttachments: 'Anhänge',
    mainDocumentBadge: 'Aktuelles Dokument',
    previewMainDocumentBadge: 'Aktuelles Dokument',
    removeAttachmentTooltip: 'Anhang entfernen',
    downloadAttachmentTooltip: 'Anhang herunterladen',
    allowedFileTypesHint: types => `Erlaubte Dateitypen: ${types}`,
    recipientLabel: 'Empfänger',
    ccLabel: 'CC',
    bccLabel: 'BCC',
    addBcc: 'BCC hinzufügen',
    duplicateRecipientError: 'E-Mail Adresse mehrfach hinterlegt.',
    duplicateRecipientSendError: 'Eine E-Mail Adresse darf nicht gleichzeitig in "Empfänger", CC und BCC hinterlegt sein.',
    duplicateRecipientButtonTooltip: 'Ein oder mehrere E-Mail Adressen sind mehrfach hinterlegt.',
    missingRequiredFieldsTooltip: 'Bitte fülle alle Pflichtfelder aus, um fortzufahren.',
    documentsTab: 'Dokumente',
    emailPreviewTab: 'E-Mail-Vorschau',
    previewHint: 'Diese Vorschau zeigt beispielhaft, wie die E-Mail beim Empfänger aussehen kann. ',
    previewHintBold: 'Buttons wie z.B. „Antworten“ oder „Rechnung ansehen“ werden erst in der versendeten E-Mail angezeigt.',
    previewFrom: 'Von',
    previewTo: 'An',
    previewAttachmentsCount: count => `${count} ${count === 1 ? 'Anhang' : 'Anhänge'}`,
  },

  recipientEmailInput: {
    add: 'hinzufügen',
    email: 'E-Mail-Adresse',
    placeholder: 'E-Mail-Adresse eingeben oder auswählen',
  },
  ownEmailRestriction: {
    label: {
      default: 'E-Mail Adresse',
      signature: 'Unterzeichner',
    },
    hint: {
      default: 'Im Testzeitraum werden E-Mails unabhängig von der Eingabe nur an die eigenen hinterlegten E-Mail Adressen versendet.',
      signature: 'Im Testzeitraum können E-Mails nur an die eigene E-Mail-Adresse gesendet werden. Daher lassen sich hier keine anderen Unterzeichner auswählen.',
      combined: 'Während des Testzeitraums kannst Du E-Mails nur an Deine eigenen Benutzer-E-Mail-Adressen senden. Das gilt auch für CC und BCC.',
    },
  },

  // general list translations
  list: {
    selectAll: '(Alle auswählen)',
    selectAllSearchResults: '(Alle Suchergebnisse auswählen)',
    searchOoo: 'Suche...',
    blanks: '(Leerzeichen)',
    noMatches: 'Keine Treffer',
    filterOoo: 'Suchen',
    equals: 'Gleich',
    notEqual: 'Ungleich',
    empty: 'Wähle eins',
    lessThan: 'Kleiner als',
    greaterThan: 'Größer als',
    lessThanOrEqual: 'Kleiner oder gleich',
    greaterThanOrEqual: 'Größer oder gleich',
    inRange: 'Von ... bis',
    inRangeStart: 'von',
    inRangeEnd: 'bis',
    contains: 'Enthält',
    notContains: 'Enthält nicht',
    blank: 'Leer',
    notBlank: 'Nicht leer',
    startsWith: 'Beginnt mit',
    endsWith: 'Endet mit',
    dateFormatOoo: 'JJJJ-MM-TT',
    andCondition: 'UND',
    orCondition: 'ODER',
    applyFilter: 'Fertig',
    resetFilter: 'Filter löschen',
    clearFilter: 'Filter löschen',
    filterDateRange: 'Zeitraum filtern',
    cancelFilter: 'Abbrechen',
    textFilter: 'Textfilter',
    numberFilter: 'Zahlenfilter',
    dateFilter: 'Datumsfilter',
    setFilter: 'Filter einstellen',
    columns: 'Spalten',
    filters: 'Filter',
    pivotMode: 'Pivot-Modus',
    groups: 'Gruppen',
    rowGroupColumnsEmptyMessage: 'Hier ziehen, um Spaltengruppen festzulegen',
    values: 'Werte',
    valueColumnsEmptyMessage: 'Zum Aggregieren hierher ziehen',
    pivots: 'Spaltenbeschriftungen',
    pivotColumnsEmptyMessage: 'Hier ziehen, um Spaltenbeschriftungen festzulegen',
    group: 'Gruppe',
    loadingOoo: 'Lädt',
    noRowsToShow: 'Keine Einträge gefunden',
    enabled: 'Aktiviert',
    pinColumn: 'Spalte anheften',
    pinLeft: 'Links anheften',
    pinRight: 'Rechts anheften',
    noPin: 'Lösen',
    valueAggregation: 'Wertaggregation',
    autosizeThiscolumn: 'Auto-Skalierung Spalte',
    autosizeAllColumns: 'Alle Spalten automatisch skalieren',
    groupBy: 'Gruppiere nach',
    ungroupBy: 'Gruppierung aufheben',
    resetColumns: 'Spalten zurücksetzen',
    expandAll: 'Alles erweitern',
    collapseAll: 'Alles schließen',
    copy: 'Kopieren',
    ctrlC: 'Strg + C',
    copyWithHeaders: 'Mit Überschriften kopieren',
    paste: 'Einfügen',
    ctrlV: 'Strg + V',
    export: 'Exportieren',
    csvExport: 'CSV Export',
    excelExport: 'Excel Export (.xlsx)',
    excelXmlExport: 'XML Export (.xml)',
    sum: 'Summe',
    min: 'Mindest',
    max: 'Max',
    none: 'Keiner',
    count: 'Anzahl',
    avg: 'Durchschnittlich',
    filteredRows: 'Gefiltert',
    selectedRows: 'Ausgewählt',
    totalRows: 'Total',
    totalAndFilteredRows: 'Reihen',
    more: 'Mehr',
    of: 'von',
    to: 'bis',
    page: 'Seite',
    nextPage: 'Nächste Seite',
    lastPage: 'Letzte Seite',
    firstPage: 'Erste Seite',
    previousPage: 'Vorherige Seite',
    pivotChartAndPivotMode: 'Pivot Chart & Pivot Mode',
    pivotChart: 'Pivot-Diagramm',
    chartRange: 'Chart Bereich',
    columnChart: 'Spalte',
    groupedColumn: 'Gruppiert',
    stackedColumn: 'Gestapelt',
    normalizedColumn: '100% gestapelt',
    barChart: 'Bar',
    groupedBar: 'Gruppiert',
    stackedBar: 'Gestapelt',
    normalizedBar: '100% gestapelt',
    pieChart: 'Pie-Chart',
    pie: 'Pie',
    doughnut: 'Donut',
    line: 'Linie',
    xyChart: 'X Y (Streuung)',
    scatter: 'Streuen',
    bubble: 'Bubble',
    areaChart: 'Bereich',
    area: 'Bereich',
    stackedArea: 'Gestapelt',
    normalizedArea: '100% gestapelt',
    histogramChart: 'Histogramm',
    pivotChartTitle: 'Pivot-Diagramm',
    rangeChartTitle: 'Bereich Chart',
    settings: 'Einstellungen',
    data: 'Daten',
    format: 'Format',
    categories: 'Kategorien',
    defaultCategory: '(Keiner)',
    series: 'Serie',
    xyValues: 'X Y-Werte',
    paired: 'Paired Mode',
    axis: 'Achse',
    navigator: 'Navigator',
    color: 'Farbe',
    thickness: 'Dicke',
    xType: 'X-Typ',
    automatic: 'Automatisch',
    category: 'Kategorie',
    number: 'Nummer',
    time: 'Zeit',
    xRotation: 'X Rotation',
    yRotation: 'Y Rotation',
    ticks: 'Zecken',
    width: 'Breite',
    height: 'Höhe',
    length: 'Länge',
    padding: 'Polsterung',
    spacing: 'Abstand',
    chart: 'Diagramm',
    title: 'Titel',
    titlePlaceholder: 'Diagrammtitel - Doppelklick zum Bearbeiten',
    background: 'Hintergrund',
    font: 'Schriftart',
    top: 'Oben',
    right: 'Richtig',
    bottom: 'Unterseite',
    left: 'Links',
    labels: 'Etiketten',
    size: 'Größe',
    minSize: 'Mindestmaß',
    maxSize: 'Maximale Größe',
    legend: 'Legende',
    position: 'Position',
    markerSize: 'Markergröße',
    markerStroke: 'Marker Stroke',
    markerPadding: 'Marker Padding',
    itemSpacing: 'Gegenstandsabstand',
    itemPaddingX: 'Item Padding X',
    itemPaddingY: 'Item Padding Y',
    layoutHorizontalSpacing: 'Horizontaler Abstand',
    layoutVerticalSpacing: 'Vertikaler Abstand',
    strokeWidth: 'Strichbreite',
    offset: 'Offset',
    offsets: 'Offsets',
    tooltips: 'Tooltips',
    callout: 'Aufbieten, ausrufen, zurufen',
    markers: 'Marker',
    shadow: 'Schatten',
    blur: 'Verwischen',
    xOffset: 'X Offset',
    yOffset: 'Y Offset',
    lineWidth: 'Linienbreite',
    normal: 'Normal',
    bold: 'Fett gedruckt',
    italic: 'Kursiv',
    boldItalic: 'Fett Kursiv',
    predefined: 'Vordefiniert',
    fillOpacity: 'Deckkraft füllen',
    strokeOpacity: 'Linienopazität',
    histogramBinCount: 'Bin count',
    columnGroup: 'Säule',
    barGroup: 'Bar',
    pieGroup: 'Kuchen',
    lineGroup: 'Linie',
    scatterGroup: 'X Y (Streuung)',
    areaGroup: 'Bereich',
    histogramGroup: 'Histogramm',
    groupedColumnTooltip: 'Gruppiert',
    stackedColumnTooltip: 'Gestapelt',
    normalizedColumnTooltip: '100% gestapelt',
    groupedBarTooltip: 'Gruppiert',
    stackedBarTooltip: 'Gestapelt',
    normalizedBarTooltip: '100% gestapelt',
    pieTooltip: 'Kuchen',
    doughnutTooltip: 'Donut',
    lineTooltip: 'Linie',
    groupedAreaTooltip: 'Bereich',
    stackedAreaTooltip: 'Gestapelt',
    normalizedAreaTooltip: '100% gestapelt',
    scatterTooltip: 'Streuen',
    bubbleTooltip: 'Tip',
    histogramTooltip: 'Histogramm',
    noDataToChart: 'Es sind keine Daten zum Zeichnen verfügbar.',
    pivotChartRequiresPivotMode: 'Für das Pivot-Diagramm muss der Pivot-Modus aktiviert sein.',
  },

  customListFilter: {
    greaterThanDate: 'Später als',
    lessThanDate: 'Früher als',
    chooseDate: 'Datum wählen',
  },

  // booking list
  bookingList: {
    title: 'Buchungsübersicht',
    emptyStateText: 'Keine Buchungssätze gefunden',
    searchPlaceholder: 'Buchungssätze durchsuchen',
    loaderText: 'Buchungssätze werden geladen',
    upgradeText: `${unlockViewUpgradeText} die Buchungsübersicht freizuschalten.`,
  },

  // invoice list
  invoiceList: {
    syncInvoiceState: 'Rechnungsstatus synchronisieren',
    syncInvoiceStateStarted: 'Synchronisierung gestartet',
    syncInvoiceStateEnded: 'Synchronisierung beendet',
    emptyStateText: 'Keine Rechnungen gefunden',
    loaderText: 'Rechnungen werden geladen',
    new: 'Rechnung erstellen',
    newDeposit: 'Abschlagsrechnung erstellen',
    newRecurring: 'Abo-Rechnung erstellen',
    newManualCreditNote: 'Rechnungskorrektur/Gutschrift erstellen',
    searchPlaceholder: 'Rechnungen durchsuchen',
    stateLabels: {
      isOverDue: 'Überfällig',
      partlyPaid: 'Teilw. bezahlt',
      draft: 'Entwurf',
      lockedCreditNote: 'Offen (Gutschrift)',
      open: 'Offen',
      dunned: 'Angemahnt',
      paidCreditnote: 'Bezahlt (Gutschrift)',
      paid: 'Bezahlt',
      canceled: 'Storniert',
      inProgress: 'Angewiesen',
    },
    tooltips: {
      editAndDelete: (type) => `Abgeschlossene ${type} dürfen aus rechtlichen Gründen nicht gelöscht oder bearbeitet werden.`,
    },
    dunningRows: {
      charge: 'Mahngebühr',
      loading: 'Mahnungen werden geladen',
      empty: 'Keine Mahnungen gefunden',
      error: 'Mahnungen konnten nicht geladen werden',
    },
    directDebitStates: {
      cancelled: 'Storniert',
      expired: 'Abgelaufen',
      initiated: 'Initiiert',
      notified: 'Benachrichtigt',
      processing: 'Verarbeitet',
      refunded: 'Erstattet',
      ready: 'Bereit',
      rejected: 'Abgelehnt',
      returned: 'Zurückgegeben',
      settled: 'Abgeschlossen',
      submitted: 'Eingereicht',
    },
    cancellationModal: {
      cancel: 'Stornieren',
      loadingTextManualCreditNote: 'Rechnungskorrektur/Gutschrift wird storniert...',
      loadingTextInvoice: 'Rechnung wird storniert...',
      headerTextManualCreditNote: (number) => `Rechnungskorrektur/Gutschrift ${number} stornieren`,
      headerTextInvoice: (number) => `Rechnung ${number} stornieren`,
      cancelReason: 'Stornogrund (optional):',
    },
  },

  // delivery note list
  deliveryNoteList: {
    emptyStateText: 'Keine Lieferscheine gefunden',
    loaderText: 'Lieferscheine werden geladen',
    upgradeText: `${unlockViewUpgradeText} Lieferscheine freizuschalten.`,
    new: 'Neu',
    searchPlaceholder: 'Lieferscheine durchsuchen',
    markAsDelivered: 'Als geliefert markieren',
  },

  // letter list
  letterList: {
    emptyStateText: 'Keine Geschäftsbriefe gefunden',
    loaderText: 'Geschäftsbriefe werden geladen',
    upgradeText: `${unlockViewUpgradeText} Geschäftsbriefe freizuschalten.`,
    new: 'Neu',
    searchPlaceholder: 'Geschäftsbriefe durchsuchen',
  },

  // project list
  projectList: {
    emptyStateText: 'Keine Projekte gefunden',
    loaderText: 'Projekte werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Projekte durchsuchen',
    listEmptyStateTitle: 'Keine Projekte vorhanden',
    listNoSearchResultTitle: 'Keine Projekte gefunden',
    listEmptyStateSubtitle: 'Jetzt Projekt und erste Abschlagsrechnung anlegen',
    listEmptyStateButtonLabel: 'Los geht\'s',
  },

  // customer list
  customerList: {
    emptyStateText: 'Keine Kunden gefunden',
    loaderText: 'Kunden werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Kunden durchsuchen',
    privateCustomers: 'Privatkunden',
    businessCustomers: 'Firmenkunden',
  },

  // client list
  clientList: {
    invitationStatus: {
      accepted: 'Angenommen',
      requested: 'Angefragt',
      paused: 'Pausiert',
      rejected: 'Abgelehnt',
      invited: 'Eingeladen',
      not_invited: 'Nicht eingeladen',
      offline: 'Nicht eingeladen',
    },
    connectionStatus: {
      accepted: 'Angenommen',
      connected: 'Verbunden',
      disconnected: 'Nicht verbunden',
      paused: 'Pausiert',
      rejected: 'Abgelehnt',
      requested: 'Angefragt',
      offline: 'Offline',
    },
    emptyStateText: 'Keine Mandanten gefunden',
    loaderText: 'Mandanten werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Mandanten durchsuchen',
    title: 'Mandanten',
    emptyStateTitle: 'Keine Mandanten vorhanden',
    listNoSearchResultTitle: 'Keine Mandanten gefunden',
    buttonLabel: 'Los geht´s',
  },

  // client Details
  clientDetail: {
    title: 'Mandanten-Details',
    jumpMarkLinkTexts: {
      overview: {
        headline: 'Stammdaten & Login',
        text: 'Stammdaten bearbeiten und direkt in Deinen Mandanten einloggen.',
      },
      todo: {
        headline: 'Aufgaben',
        text: 'Fordere Erledigungen, Auskünfte und Freigaben Deines Mandanten an.',
      },
      eSigning: {
        headline: 'Elektronische Unterschriften',
        text: 'Fordere digitale Unterschriften Deines Mandanten an.',
      },
      taxForm: {
        headline: 'Steuereinstellungen',
        text: 'Nimm hier Angaben bzgl. Umsatzbesteuerung, Standardkontenrahmen und Wirtschaftsjahr vor.',
      },
      taxAccountantExport: {
        headline: 'DATEV-Steuerberaterexport',
        text: 'Hier steht der klassische CSV-Export im DATEV-Format zur Verfügung, der in verschiedene Systeme importiert werden kann.',
      },
      documentExport: {
        headline: 'Dokumente-Export',
        text: 'Hier können Dokumente nach Art und Zeitraum in eine ZIP-Datei exportiert werden.',
      },
      createdExports: {
        headline: 'Erzeugte Exporte',
        text: 'In dieser Liste kannst Du alle zuvor erstellten DATEV- und andere Exporte herunterladen.',
      },
    },
    widgets: {
      eSigningWidget: {
        headline: 'Elektronische Unterschrift',
        description: 'Fordere digitale Unterschriften Deines Mandaten an.',
        tooltip: 'Du interessierst Dich dafür, wie Du digitale Unterschriften anfordern kannst? Dann kontaktiere uns über die bekannten Wege und wir schalten Dich dafür frei.',
        requestSignature: 'Unterschrift anfordern',
        errors: {
          create: 'Beim Erstellen der Aufgabe ist ein Fehler aufgetreten.',
        },
      },
      overviewWidget: {
        headline: 'Stammdaten & Login',
        description: 'Du kannst die Stammdaten bearbeiten, mit denen Dein Mandant in der Kanzlei-Verwaltung angelegt ist. Dies hat keine Auswirkungen auf die Stammdaten Deines Mandanten. Des Weiteren kannst Du Dich direkt in den Mandanten einloggen.',
        offlineHeadline: 'Mandant einladen',
        loginHeadline: 'In Mandanten einloggen',
        invitedHeadline: 'Einladung verschickt',
        deleteClient: 'Mandant löschen',
        deleteClientDescription: name => `Willst Du ${name} unwiderruflich löschen?`,
        endCollaborationTitle: 'Bist Du Dir sicher?',
        endCollaborationDescription: () => `Möchtest Du die Zusammenarbeit mit Deinem Mandanten wirklich beenden? Die Verbindung wird damit aufgehoben und es ist kein Austausch mehr über ${productNameWithPrefix} möglich.`,
        offlineDescription: `Versende eine Einladung an Deinen Mandanten, um ihn zur Zusammenarbeit auf ${productNameWithPrefix} einzuladen.`,
        loginDescription: 'Logge Dich in Deinen Mandanten ein, um sämtliche buchhalterische sowie steuerliche Sachverhalte einzusehen und zu bearbeiten.',
        tooltip: 'Ein Login in Offline Mandanten ist nicht möglich. Lege hierfür einen neuen Mandanten an.',
        offlineInviteButton: 'Einladung versenden',
        resendInvitationButton: 'Einladung erneut versenden',
        loginButton: 'Einloggen',
        drawer: {
          headline: 'Mandanten-Stammdaten ändern',
          contantPerson: 'Ansprechpartner Mandant',
          companyName: 'Name des Mandanten',
          clientNumber: 'Mandantennummer',
          clerk: 'Sachbearbeiter',
          success: 'Änderungen wurden erfolgreich gespeichert.',
          errors: {
            companyNameRequired: 'Bitte gib einen Firmennamen ein.',
            companyFormRequired: 'Bitte gib eine Rechtsform ein.',
            clerkRequired: 'Bitte wähle einen Sachbearbeiter aus.',
          },
        },
        clientRequested: `Wir haben Deinem Mandanten eine Einladung zur Zusammenarbeit auf ${productNameWithPrefix} geschickt.`,
        clientPaused: 'Der Mandant hat die Verknüpfung deaktiviert.',
        activate: 'Bitte Verknüpfung zum Mandanten aktivieren.',
        paused: 'Verknüpfung zum Mandanten deaktiviert.',
        disconnected: 'Verknüpfung zum Mandanten wurde entfernt.',
      },
      todoWidget: {
        headline: 'Aufgaben',
        description: 'Du siehst hier alle offenen Aufgaben inkl. Details zu Deinem Mandanten.',
        viewAll: 'Alle Aufgaben ansehen',
        noRowsToShow: 'Keine offenen Aufgaben',
        loading: 'Aufgaben werden geladen',
        columns: {
          status: 'Status',
          title: 'Titel',
          dueToDate: 'Frist',
        },
      },
      taxFormWidget: {
        headline: 'Steuereinstellungen',
        baseDrawerHeader: (companyName = '') => `Steuereinstellungen ${companyName} bearbeiten.`,
        smallBusinessTitle: 'Kleinunternehmerregelung',
        taxTypeTitle: 'Soll- und Ist-Versteuerung',
        taxNumberTitle: 'Steuernummer',
        taxOfficeTitle: 'Zuständiges Finanzamt',
        vatNumberTitle: 'Umsatzsteuer-ID',
        fiskalYearTitle: 'Wirtschaftsjahr',
      },
      notAllowed: 'Du hast noch keine Berechtigung diese Informationen einzusehen.',
    },
  },

  // order list
  orderList: {
    emptyStateText: 'Keine Aufträge gefunden',
    loaderText: 'Aufträge werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Aufträge durchsuchen',
    manualProcess: 'Manueller Vorgang',
    syncButtonLabel: 'Bestellungen abrufen',
    stateLabels: {
      invoiced: 'Verrechnet',
      delivered: 'Geliefert',
      partiallyCharged: 'Teilw. verrechnet',
      partiallyDelivered: 'Teilw. geliefert',
    },
    deliveryStatus: {
      notDelivered: 'Nicht geliefert',
      partiallyDelivered: 'Teilw. geliefert',
      delivered: 'Geliefert',
    },
    typeLabels: {
      order: 'Standard',
      refundOrder: 'Retoure',
    },
  },

  // purchase list
  purchaseList: {
    emptyStateText: 'Keine Bestellungen gefunden',
    loaderText: 'Bestellungen werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Bestellungen durchsuchen',
  },

  // expense list
  expenseList: {
    emptyStateText: 'Keine Ausgaben gefunden',
    loaderText: 'Ausgaben werden geladen',
    new: 'Neu',
    uploadEInvoices: 'E-Rechnungen hochladen',
    searchPlaceholder: 'Ausgaben durchsuchen',
    empty: {
      filter: {
        title: 'Keine Ausgaben gefunden',
      },
      default: {
        title: 'Keine Ausgaben vorhanden',
        searchTitle: 'Keine Ausgaben gefunden',
        subtitle: 'Jetzt die erste Ausgabe erfassen',
        button: 'Los geht\'s',
      },
    },
    upgradeText: `${unlockViewUpgradeText} Ausgaben freizuschalten.`,
    deleteTooltips: {
      noSelection: 'Bitte markiere die Ausgaben, die Du löschen möchtest.',
      notDeletable: 'Bezahlte, angewiesene und teilweise bezahlte Ausgaben können nicht gelöscht werden.',
    },
  },

  // acticle list
  articleList: {
    emptyStateText: 'Keine Artikel gefunden',
    listNoSearchResultTitle: 'Keine Artikel gefunden',
    loaderText: 'Artikel werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Artikel durchsuchen',
    shopSync: {
      activateSync: 'Sync aktivieren',
      activateSyncSuccess: 'Lagersynchronisation wurde erfolgreich aktiviert',
      activateSyncError: 'Beim aktivieren der Lagersynchronisation ist ein Fehler aufgetreten',
      deactivateSync: 'Sync deaktivieren',
      deactivateSyncSuccess: 'Lagersynchronisation wurde erfolgreich deaktiviert',
      deactivateSyncError: 'Beim deaktivieren der Lagersynchronisation ist ein Fehler aufgetreten',
      startManualSync: 'Manuellen Sync starten',
      startManualSuccess: 'Lagersynchronisation wurde erfolgreich gestartet',
      startManualError: 'Beim starten der Lagersynchronisation ist ein Fehler aufgetreten',
      addShop: 'Weiteren Shop hinzufügen',
    },
    number: 'Artikel-Nr.',
    title: 'Name',
    filters: {
      salesAndPurchase: 'Verkaufs- und Einkaufsartikel',
      sales: 'Verkaufsartikel',
      purchase: 'Einkaufsartikel',
    },
  },

  // economic asset list
  economicAssetSearchCategory: 'Anlagenverwaltung',
  economicAssetTitle: 'Anlagen',
  economicAssetList: {
    emptyStateText: 'Keine Anlagen gefunden',
    listNoSearchResultTitle: 'Keine Anlagen gefunden',
    loaderText: 'Anlagen werden geladen',
    new: 'Anlagegut hinzufügen',
    searchPlaceholder: 'Alle Anlagen durchsuchen',
    buttonLabel: 'Los geht\'s',
    modal: {
      economicAssetDeleteSuccessMessage: multiple => `${multiple ? 'Die Anlagegüter' : 'Das Anlagegut'} wurde${multiple ? 'n' : ''} erfolgreich gelöscht`,
      economicAssetDeleteConfirmCaption: multiple => `${multiple ? 'Anlagegüter' : 'Anlagegut'} löschen`,
      economicAssetDeleteConfirmText: multiple => `Möchtest Du ${multiple ? 'die Anlagegüter' : 'das Anlagegut'} wirklich löschen? Dies kann nicht rückgängig gemacht werden! Die Ausgabe zu dem Anlagegut wird ebenfalls gelöscht. Das Anlagegut und die Ausgabe kann nur gelöscht werden, wenn der Status der Ausgabe Offen ist.`,
      expenseNotOpenErrorMessage: (number) => `Das Anlagegut mit der Inventarnummer: <b>${number}</b> kann nicht gelöscht werden, da zu dem Anlagegut bereits eine verrechnete Ausgabe besteht.`,
    },
  },
  economicAsset: {
    status: {
      open: 'Aktiv',
      fullyDepreciated: 'Abgeschrieben',
      completed: 'vorz. abgeschrieben',
    },
    type: {
      linear: 'Linear',
      degressive: 'Degressiv',
      lowValueAsset: 'Sofortabschreibung',
      electricVehicleSpecial: 'Turbo E-Fahrzeug',
    },
    specialDepreciation: {
      lossOrDestruction: 'Abgang Verlust',
      sale: 'Abgang Verkauf',
      transferToPrivateOwnership: 'Abgang Entnahme',
    },
    quickFilter: {
      ltTwoYears: 'Weniger als 2 Jahre',
      gtTwoYears: 'Mehr als 2 Jahre',
      fullyDepreciated: 'Abgeschrieben',
      completed: 'vorzeitig Abgeschrieben',
    },
    drawer: {
      title: 'Anlagegut erstellen',
    },
    preview: {
      title: 'Details zum Abschreibungsverlauf',
      emptyStateDescription1: 'Bitte fülle alle Felder aus,',
      emptyStateDescription2: 'um Details zum Abschreibungsverlauf zu sehen',
      emptyLowValueDescription1: 'Anlagegut sofort abgeschrieben',
      emptyLowValueDescription2: '(Keine Abschreibungstabelle aufgrund Sofortabschreibung)',
      expenseButtonTitle: 'Ausgabe ansehen',
      exportButtonTitle: 'Excel-Datei herunterladen',
      economicAssetButtonTitle: 'Anlage öffnen',
      revenueButtonTitle: 'Verkauf ansehen',
    },
    view: {
      tooltip: {
        buttonTitle: 'Information',
        title: 'Lineare oder degressive Abschreibung',
        subTitle1: 'Lineare Abschreibung:',
        subTitle2: 'Degressive Abschreibung: ',
        subTitle3: 'Info:',
        subTitle4: 'Die degresssive Abschreibung',
        subTitle5: 'darf in folgenden Zeiträumen angewandt werden:',
        description1: 'Gleichmäßige Verteilung der Kosten über die Nutzungsdauer, konstante jährliche Abschreibung.',
        description2: 'Die degressive Abschreibung verteilt Anschaffungs- oder Herstellungskosten eines Vermögenswerts mit höheren Abschreibungsbeträgen in den ersten Jahren, um den schnelleren Wertverlust zu berücksichtigen. Dies bietet steuerliche Vorteile zu Beginn und eignet sich für schnell entwertende Vermögenswerte.',
        description3: '31.08.2008 - 31.12.2010',
        description4: '01.01.2020 - 31.12.2023',
        emptyStateDescription: 'Bitte fülle alle oben genannten Felder aus, um die Vorschau zum Abschreibungsverlauf zu sehen und das Anlagegut zu speichern.',
      },
      description: 'Bezeichnung',
      classLiveAndPeriod: 'Anlagetyp',
      dateOfPurchase: 'Anschaffungsdatum',
      economicLife: 'Abschreibungsdauer in Jahren',
      costOfPurchase: 'Anschaffungswert (Netto)',
      costOfPurchaseSmallBusiness: 'Anschaffungswert (Brutto)',
      accountNo: 'Steuerkategorie',
      inventoryNumber: 'Inventarnummer',
      depreciationMethod: 'Abschreibungsmethode',
      placeholderDescription: 'Bezeichnung (z.B. Fräsmaschine AKL)',
      placeholderSelectInput: 'Durchsuchen und auswählen',
      placeholderNumber: 'Eindeutige Nummer vergeben',
      placeholderMethod: 'Abschreibungsmethode',
      directDeprecationMethod: 'Sofortabschreibung',
      errorEconomicLife: (value) => `Die Abschreibungsdauer muss größer oder gleich ${value} sein`,
    },
    table: {
      cell1: 'Jahr',
      cell2: 'AfA linear',
      cell3: 'AfA degressiv',
      cell4: 'kumuliert',
      cell5: 'Restbuchwert',
      additionalInfoTime: 'Abschreibungsdauer:',
      additionalInfoYear: 'Jahre',
      additionalInfoCostOfPurchase: 'Anschaffungswert:',
      additionalInfoMethod: 'Abschreibungsmethode:',
      additionalInfoPeriodType: 'Anlagetyp:',
      additionalInfoDateOfPurchase: 'Anschaffungsdatum:',
      additionalInfoInventoryNumber: 'Inventarnummer:',
      additionalInfoDescription: 'Beschreibung:',
      linear: 'Lineare Abschreibung',
      degressive: 'Deggresive Abrschreibung',
      specialDepreciationType: 'Art:',
      specialDepreciationDate: 'Nutzungsende:',
      specialDepreciationComment: 'Begründung:',
    },
    toast: {
      success: 'Das Anlagegut wurde erfolgreich erstellt',
      error: 'Beim Speichern der Anlage ist ein Fehler aufgetreten',
      errorCalculate: 'Beim Berechnen der Abschreibung ist ein Fehler aufgetreten',
      idError: 'Die Inventarnummer ist bereits vergeben, bitte wähle eine neue Inventarnummer aus',
      finApiError: 'There is already a economic asset number',
      finApiError2: 'Creditor account number exists already.',
      bookingSuccess: 'Der Anlagenabgang wurde erfolgreich verbucht',
      bookingError: 'Der Anlagenabgang konnte nicht verbucht werden',
    },
    excelExport: {
      headerYear: 'Jahr',
      headerLinear: 'AfA linear',
      headerDegressive: 'AfA degressiv',
      headerCommunicates: 'Kommunliert',
      headerBookValue: 'Restbuchwert',
    },
    modal: {
      title: 'Anlagegut oder GWG erfassen?',
      subtitleHeader: 'Aufgrund der gewählten Steuerkategorie in Deiner Ausgabe kannst Du entweder ein Anlagegut oder ein geringwertiges Wirtschaftsgut (GWG) mit Aufzeichnungspflicht erfassen:',
      subtitleInfoHeader: 'Inhalt der Ausgabe:',
      subtitleSupplier: 'Lieferant:',
      subtitleCategory: 'Steuerkategorie:',
      subtitleTotalNet: 'Betrag:',
      subtitleCreate: 'Möchtest Du diese Ausgabe jetzt als Anlagegut oder GWG erfassen?',
      subtitleCreateInfo: 'Du kannst dies auch nachträglich durchführen, indem Du die Ausgabe öffnest und erneut speicherst.',
      subtitleLink: `Weitere Informationen zur Anlagenverwaltung findest Du <a target="_blank" href=${mainInfoUrl}>hier.</a>`,
      submitButton: 'Jetzt erfassen',
      abortButton: 'Später erfassen',
    },
    bookingModal: {
      title: 'Anlagenabgang buchen',
      radioInputTitle: 'Um welche Art von Anlagenabgang handelt es sich:',
      buttonTitle: 'Anlagenabgang',
      lostLabel: 'Verlust',
      salesLabel: 'Verkauf',
      pricePrivateTransferLabel: 'Teilwert',
      withdrawalLabel: 'Entnahme Privat',
      infoText1: 'Du wählst den Anlagenabgang mit Verlust, wenn etwas aus dem Unternehmen verschwindet oder zerstört wird und dabei kein Geld eingenommen wird, wie zum Beispiel bei einem Diebstahl oder wenn die Anlage verschrottet wird. In diesen Fällen wird der Restwert des Gegenstands als Verlust in der Buchhaltung erfasst.',
      infoText2: 'Du wählst den Typ Anlagenverkauf, wenn Du ein Anlagegut verkaufst und dafür Geld erhältst. In der Buchhaltung wird der Verkaufserlös erfasst, und der Restwert der Anlage wird von diesem Erlös abgezogen, um den Gewinn oder Verlust aus dem Verkauf zu berechnen.',
      infoText3: 'Eine Entnahme ins Privatvermögen bedeutet, dass Du etwas, das dem Unternehmen gehört, für Dich privat nutzt. Dieser Vorgang wird in der Buchhaltung so behandelt, als hättest Du es verkauft, und kann umsatzsteuerpflichtig sein, außer wenn Du Kleinunternehmer bist.',
      optionEditTitle: 'Notwendige Angaben:',
      datePickerLabel: 'Nutzungsende',
      notesLabel: 'Begründung für Nutzungsende',
      notesPlaceHolder: 'Gib hier die Begründung für das Nutzungsende ein.',
      submitButton: 'Abgang buchen',
      submitButtonTooltipTitle: 'Information',
      submitButtonTooltipDescription: 'Bitte fülle alle Felder bis auf <b>Begründung für Nutzungsende</b> (optional) aus, um den Abgang buchen zu können',
      vatSelectInputLabel: 'Umsatzsteuerpflichtig',
      priceLabel: 'Verkaufspreis',
      notesTooltip1: 'Du musst keine Begründung eingeben, wir empfehlen Dir aber dies durchzuführen. Somit ist später nachvollziehbar, warum das Anlagegut ausgebucht wurde.',
      notesTooltip2: 'Beispiele können sein:',
      notesTooltip3: 'Diebstahl, Verschrottung oder auch eine ausführlichere Begründung.',
      vatTooltip1: 'Beim Anlagenverkauf ist der Verkauf grundsätzlich umsatzsteuerpflichtig, das bedeutet, dass auf den Verkaufspreis Umsatzsteuer erhoben wird.',
      vatTooltip2: 'Eine Ausnahme gilt nur für Kleinunternehmer, die nach § 19 UStG von der Umsatzsteuer befreit sind.',
      vatTooltipPrivateTransfer: 'Wenn Du ein Anlagegut, das ohne Vorsteuerabzug erworben wurde (z.B. von privat oder einem Kleinunternehmer), ins Privatvermögen entnimmst, ist diese Entnahme nicht umsatzsteuerpflichtig, ansonsten immer umsatzsteuerpflichtig. Das liegt daran, dass beim Kauf keine Vorsteuer geltend gemacht wurde. Ein Beispiel wäre ein Firmen-Pkw, der ohne Umsatzsteuer gekauft wurde – in diesem Fall kann er umsatzsteuerfrei ins Privatvermögen übertragen werden, solange die Bedingungen erfüllt sind.',
      priceTooltip1: 'Im Feld "Verkaufspreis" gibst Du den Betrag ein, den Du für die verkaufte Anlage erhalten hast.',
      priceTooltip2: 'Daraus wird dann der Buchgewinn oder -verlust ermittelt, indem der Verkaufspreis mit dem Restbuchwert der Anlage verglichen wird.',
      priceTooltipPrivateTransfer: 'Der Teilwert eines Anlageguts ist der geschätzte Wert, den das Gut im Zeitpunkt der Entnahme hat, also der Marktwert, den es unter normalen Umständen beim Verkauf erzielen würde. Er stellt den wirtschaftlichen Wert dar, der bei einer fiktiven Veräußerung ermittelt werden kann, und spiegelt den Betrag wider, den ein Käufer unter normalen Bedingungen bereit wäre, für das Anlagegut zu zahlen.',
    },
    gwg: {
      infoTitle1: 'Kein Geringwertiges Wirtschaftsgut',
      infoDescription1: 'Geringwertige Wirtschaftsgüter (GWG) mit einem Betrag unter 250,01 € dürfen sofort abgeschrieben und somit direkt als Betriebsausgabe oder mit der Kategorie 4855/6260 Sofortabschreibung erfasst werden.',
      infoTitle2: 'Sammelposten für GWG',
      infoDescription2: 'Der Wert für dieses GWG ist über 800,00 € und müsste somit als GWG Sammelposten angelegt werden, dies ist noch nicht möglich, daher alternativ bitte als aufzeichnungspflichtiges GWG mit dem Konto 0480/0670 erfassen.',
      infoTitle3: 'Betrag ist zu hoch',
      infoDescription3: 'Der Betrag liegt über 1.000,00 € und muss daher als Anlagegut und nicht als geringwertiges Wirtschaftsgut erfasst werden. Bitte wähle die entsprechende Steuerkategorie, z. B. 320/520.',
    },
    errors: {
      costOfPurchaseTooLowErrorMessage: 'Der Anschaffungswert muss höher als 250,00 € sein',
      costOfPurchaseTooHighErrorMessage: 'Der Anschaffungswert darf nicht höher als 1.000,00 € sein',
      costOfPurchaseDefaultErrorMessage: 'Der Anschaffungswert muss höher als 0,00 € sein',
    },
  },

  // revenue list
  revenueList: {
    emptyStateText: 'Keine Einnahmen gefunden',
    loaderText: 'Einnahmen werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Einnahmen durchsuchen',
    empty: {
      filter: {
        title: 'Keine Einnahmen vorhanden',
        searchTitle: 'Keine Einnahmen gefunden',
        subtitle: 'Es wurde keine Einnahmen für die aktive Filterung gefunden',
      },
      default: {
        title: 'Keine Einnahmen vorhanden',
        searchTitle: 'Keine Einnahmen gefunden',
        subtitle: 'Jetzt die erste Einnahmen erfassen',
        button: 'Los geht\'s',
      },
    },
    title: 'Sonstige Einnahmen',
    upgradeText: `${unlockViewUpgradeText} Sonstige Einnahmen freizuschalten.`,
    deleteTooltips: {
      noSelection: 'Bitte markiere die Einnahmen, die Du löschen möchtest.',
      notDeletable: 'Bezahlte und teilweise bezahlte Einnahmen können nicht gelöscht werden.',
    },
  },

  // recurring invoice list
  recurringInvoiceList: {
    title: 'Abos',
    emptyStateText: 'Keine Abos gefunden',
    loaderText: 'Abos werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Abos durchsuchen',
    listEmptyStateTitle: 'Keine Abos vorhanden',
    listNoSearchResultTitle: 'Keine Abos gefunden',
    listEmptyStateSubtitle: 'Jetzt das erste Abo anlegen',
    listEmptyStateButtonLabel: 'Los geht\'s',
    upgradeText: `${unlockViewUpgradeText} Abos freizuschalten.`,
  },

  signature: {
    documentSelection: {
      description: 'Bitte lade das Dokument zur Unterschrift hoch, oder wähle ein bereits vorhandenes Dokument aus dem Dokumentencontainer aus',
    },
    form: {
      signer: {
        label: 'Unterzeichner',
        add: 'Weiteren Unterzeichner hinzufügen',
        info: {
          heading: 'Information zur Auswahl der Unterzeichner',
          description: 'Je Aufgabe kann nur ein Mandat ausgewählt werden. Nach Auswahl eines Mandanten werden andere Mandanten ausgeblendet.',
        },
      },
      button: {
        placeSignature: 'Unterschriftsfelder platzieren',
      },
      input: {
        message: {
          label: 'Nachricht an alle Unterzeichner',
        },
        dueToDate: {
          label: 'Frist',
        },
      },
    },
    placement: {
      title: 'Ausfüllbare Felder platzieren',
      subTitle: 'Platzierte Felder',
      button: {
        signature: 'Unterschrift hinzufügen',
        date: 'Datum hinzufügen',
        text: 'Text hinzufügen',
        nextSigner: 'Nächster Unterzeichner',
        finish: 'Anfordern',
      },
      tooltip: {
        signature: {
          title: 'Unterschriftsfeld platzieren',
          description: 'Positioniere Unterschriftenfelder an den gewünschten Stellen - flexibel auf jeder Seite des Dokuments.<br/> Fordere bequem Unterschriften an.',
        },
        date: {
          title: 'Datumsfeld platzieren',
          description: 'Lege ein Datum selbst fest oder überlasse die Auswahl dem Unterzeichner.<br/> Das Datum kann jederzeit angepasst werden.',
        },
        text: {
          title: 'Textfeld platzieren',
          description: 'Gib einen Text vor oder überlasse die Eingabe dem Unterzeichner.<br/> Der Textinhalt kann jederzeit angepasst werden.',
        },
      },
      steps: {
        firstStep: {
          label: 'Schritt 1',
          description: 'Wähle die Seite aus, auf der mindestens ein Unterschriftsfeld platziert werden soll.',
        },
        secondStep: {
          label: 'Schritt 2',
          description: 'Erstelle ein oder mehrere Unterschriftsfelder und positioniere diese an den gewünschten Stellen, indem Du sie ziehst.',
        },
        optional: {
          label: 'Optional',
          description: 'Füge bei Bedarf Textfelder oder Datumsfelder hinzu und lasse diese vom Unterzeichner ausfüllen oder anpassen.',
        },
      },
    },
    signing: {
      defaultSignerName: 'Keinen Namen angegeben',
      success: {
        sendEmail: 'E-Mail wurde erfolgreich verschickt',
      },
      errors: {
        sign: 'Beim Unterzeichnen des Dokumentes ist ein Fehler aufgetreten.',
        reject: 'Beim Ablehnen ist ein Fehler aufgetreten.',
        fetchDocument: 'Beim Laden des Dokumentes ist ein Fehler aufgetreten.',
        downloadDocument: 'Beim Herunterladen der Datei ist ein Fehler aufgetreten.',
        sendEmail: 'Beim Versenden der E-Mail ist ein Fehler aufgetreten',
      },
      state: {
        notSigned: 'Offen',
        partiallySigned: 'Teilw. ausgefüllt',
        declined: 'Abgelehnt',
        done: (date, type) => {
          let actionText
          switch (type) {
            case SIGNATURE_FIELD_TYPES.DATE: {
              actionText = 'Ausgewählt'
              break
            }
            case SIGNATURE_FIELD_TYPES.TEXT: {
              actionText = 'Ausgefüllt'
              break
            }
            default: {
              actionText = 'Unterschrieben'
            }
          }
          return `${actionText}${date ? ' am ' + `<b>${date}</b>` : ''}`
        },
      },
      info: {
        signers: {
          label: 'Unterzeichner',
          tooltipLabel: 'weitere Person(en)',
          resendTooltip: 'E-Mail zur Unterschrift erneut senden',
        },
        type: {
          label: 'Typ',
          value: 'Zur Unterschrift',
        },
        dueDate: {
          label: 'Fällig',
        },
      },
      message: {
        label: 'Nachricht',
        defaultMessage: 'Es wurde keine Nachricht hinterlegt.',
      },
      signatures: {
        title: (count, total) => `Erforderliche Feld(er) (${count} von ${total})`,
        hint: 'Die Unterschrift(en) sind mit Klick auf „Abschließen“ rechtskräftig.',
      },
      moreButton: {
        items: {
          edit: 'Unterschrift korrigieren',
          applyToAll: 'Unterschrift für alle Unterschriftsfelder übernehmen',
          delete: (typeLabel) => `${typeLabel} löschen`,
          show: 'Seite anzeigen',
        },
      },
      documents: {
        title: 'Hinzugefügte Dokumente',
      },
      buttons: {
        reject: 'Ablehnen',
        submit: 'Abschließen',
      },
    },
    page: 'Seite',
    date: 'Datum',
    text: 'Text',
    textField: {
      description: {
        label: 'Hinweislabel für den Unterzeichner',
        hint: '(Gib an, welcher Text hier eingetragen werden soll, z.B. Ort oder Name)',
        placeholder: 'z.B. Hier Ort eintragen',
      },
      text: {
        defaultLabel: 'Hier bitte Text eingeben',
        label: 'Vorgabetext für den Unterzeichner',
        hint: '(Dieser Text wird als Standardwert übernommen und kann vom Unterzeichner angepasst werden, z. B. "Berlin", "Max Mustermann")',
        placeholder: 'z.B. Berlin',
      },
    },
    textLabel: 'Bitte Text eingeben',
    dateLabel: 'Datum wählen',
    signature: 'Unterschrift',
    sign: 'Unterschreiben',
    use: 'Übernehmen',
    subtitle: 'Du kannst mit der Maus oder auf Touchscreens mit dem Finger unterschreiben.',
    required: 'Bitte füge eine Unterschrift hinzu',
    confirmationRequired: 'Bitte bestätigen',
    checkbox: 'Unterschrift für alle Positionen übernehmen',
    checkboxSignedByClient: (name) => `Ich bestätige, dass die Unterschrift durch ${name} geleistet wurde`,

    moduleModal: {
      title: 'Elektronische Unterschrift',
      subtitle: (type) => `${type} digital unterschreiben lassen`,
      text1: (type) => `Deine ${type} im orgaMAX ${customerCenterName} elektronisch und nachweisbar\nvon Deinen Kunden unterschreiben lassen - Druckkosten und Zeit einsparen.`,
      text2: 'Interesse? Jetzt anrufen und freischalten lassen oder per E-Mail anfragen.',
      text3: 'So erreichst Du uns\nMo. - Fr. 08:00 - 12:00 / 13:00 - 18:00 Uhr unter',
      telephone: '+49 (0) 5231 7090-0',
      email: {
        buttonLabel: 'Anfrage per E-Mail versenden',
        emailId: 'info@orgamax.de',
        subject: 'Anfrage zur elektronischen Unterschrift',
        body: 'Hi orgaMAX-Team,\n\nich habe Interesse an der elektronischen Unterschrift.',
      },
    },
  },

  // todo list
  todoList: {
    emptyStateText: 'Keine Aufgaben gefunden',
    loaderText: 'Aufgaben werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Aufgaben durchsuchen',
    stateLabels: {
      due: 'Fällig',
      done: {
        [TodoTypes.PROCESS]: 'Erledigt',
        [TodoTypes.RELEASE]: 'Freigegeben',
        [TodoTypes.ATTENTION]: 'Zur Kenntnis genommen',
        [TodoTypes.SIGNATURE]: 'Unterschrieben',
      },
      declined: 'Abgelehnt',
      overdue: 'Überfällig',
    },
    typeLabels: {
      [TodoTypes.PROCESS]: 'Zu erledigen',
      [TodoTypes.RELEASE]: 'Zur Freigabe',
      [TodoTypes.ATTENTION]: 'Zur Kenntnisnahme',
      [TodoTypes.SIGNATURE]: 'Zur Unterschrift',
      [TodoTypes.CONTACT]: 'Kontaktieren',
    },
  },

  pagination: {
    label: 'Einträge pro Seite',
  },

  // booking selection list
  bookingSelectionList: {
    searchPlaceholder: 'Buchungen durchsuchen',
    title: 'Welche Buchung soll zugeordnet werden?',
    emptyStateText: 'Keine Buchungen gefunden',
    loaderText: 'Buchungen werden geladen...',
    columns: {
      bankAccount: 'Konto',
      bookingContactName: 'Zahlungsempfänger',
      senderContactName: 'Auftraggeber',
    },
    quickFilter: {
      all: 'Alle',
      matching: 'Passend',
      amount: 'Betrag +/- 10%',
      payee: 'Zahlungsempfänger',
      sender: 'Auftraggeber',
      date: 'Datum +/- 3 Tage',
    },
  },

  dashboardTransactionStatsComponent: {
    refreshButtonText: 'Jetzt Aktualisieren',
    openInvoices: 'Offene Rechnungen',
    openOffers: 'Offene Angebote',
    openOrders: 'Offene Aufträge',
    amount: 'Betrag',
    count: 'Anzahl',
  },

  // time tracking list
  timeTrackingList: {
    title: 'Zeiterfassung',
    emptyStateText: 'Keine Zeiten gefunden',
    loaderText: 'Zeiten werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Zeiten durchsuchen',
    openEffort: 'Aufwand offen',
    openEfforts: 'Aufwände offen',
    listEmptyStateTitle: 'Keine Zeiten vorhanden',
    listNoSearchResultTitle: 'Keine Zeiten gefunden',
    listEmptyStateSubtitle: 'Jetzt die erste Zeit erfassen',
    listEmptyStateButtonLabel: 'Los geht\'s',
    upgradeText: `${unlockViewUpgradeText} die Zeiterfassung freizuschalten.`,
    settleBill: 'Abrechnen',
  },

  // offer list
  offerList: {
    emptyStateText: 'Keine Angebote gefunden',
    loaderText: 'Angebote werden geladen',
    new: 'Neu',
    searchPlaceholder: 'Angebote durchsuchen',
    upgradeText: `${unlockViewUpgradeText} Angebote freizuschalten.`,
  },

  // activity list
  activityList: {
    defaultTitle: 'Aktivitäten',
    defaultEmptyHint: 'Keine Einträge gefunden',
    deleteActivity: 'Aktivität löschen',
    deleteConfirmText: 'Möchtest Du diese Aktivität wirklich löschen?',
    deleteErrorMessage: 'Beim Löschen der Aktivität ist ein Fehler aufgetreten',
    deleteSuccessMessage: 'Die Aktivität wurde erfolgreich gelöscht',
    inputPlaceHolder: 'Aktivität hinzufügen und per Enter bestätigen',
    createErrorMessage: 'Beim Erstellen der Aktivität ist ein Fehler aufgetreten',
    saveSuccessMessage: 'Die Aktivität wurde erfolgreich gespeichert!',
  },

  salesOverview: 'Umsatzübersicht',
  salesOverviewSubtitle: 'Umsatz der letzten 6 Monate',
  expenseOverview: 'Ausgabenübersicht',
  expenseOverviewSubtitle: 'Ausgaben der letzten 6 Monate',
  readEmail: 'E-Mail ansehen',
  emptyHere: 'Ganz schön leer hier!',
  customerEmptyHereHint: 'Hier kannst Du sehen, welche Aktivitäten Du zu Deinem Kunden vorgenommen hast.',
  supplierEmptyHereHint: 'Hier kannst Du sehen, welche Aktivitäten Du zu Deinem Lieferanten vorgenommen hast.',
  customerEmptyListTitle: 'Hier kannst Du Dokumente anlegen',
  supplierEmptyListTitle: 'Hier kannst Du eine Ausgabe anlegen',
  emailModalTitleRecipient: 'Empfänger',
  emailModalTitleSubject: 'Betreff',
  emailModalTitleAttachment: 'Anhang',
  conditionsTitle: 'Konditionen',
  notesTitle: 'Anmerkungen',
  documentLabel: 'Dokument',
  originalDocumentLabel: 'Originaldatei',
  moreLabel: 'Weitere',

  filterLabels: {
    all: 'Alle',
    communication: 'Kommunikation',
    documents: 'Dokumente',
    todos: 'Aufgaben',
    activities: 'Aktivitäten',
  },

  salesTotalTitle: 'Umsatz gesamt',
  openInvoicesTitle: 'Offene Rechnungen',
  openOffersTitle: 'Offene Angebote',
  openTrackedTimesTitle: 'Nicht abgerechnete Zeiten',
  expenseTotalTitle: 'Ausgabe gesamt',
  expensesWithoutReceiptTitle: 'Ausgaben ohne Beleg',
  paidExpensesTitle: 'Bezahlte Ausgaben',
  unpaidExpensesTitle: 'Unbezahlte Ausgaben',
  taxFreeInvoice: 'steuerfr. Rechnung',

  columnHeaderNames: {
    receiptDateTitle: 'Belegdatum',
    receiptNumberTitle: 'Belegnummer',
    transactionStateTitle: 'Status',
    payDateTitle: 'Zahlungsdatum',
    totalGrossTitle: 'Brutto',
    totalNetTitle: 'Summe',
    dateTitle: 'Datum',
    typeTitle: 'Vorgang',
    numberTitle: 'Nr.',
    letterTitle: 'Betreff',
  },
  emailSettings: {
    description: 'Du kannst Deine erzeugten Belege und Dokumente über Dein eigenes E-Mail-Konto an Deine Kontakte senden, dazu musst Du die Daten Deines Postausgangsservers (SMTP) hinterlegen.<br />Es ist ebenso möglich Deinen Posteingangsserver (IMAP) zu hinterlegen, mit dieser Option kannst Du im Kunden selbst den bisherigen E-Mail Verlauf einsehen und über den integrierten Mailclient antworten.',
    portTitle: 'Port',
    encryptionTitle: 'Verschlüsselung',
    connectionSuccessTitle: 'Konto erfolgreich verknüpft',
    connectionSuccessImapTitle: 'Deine E-Mails können jetzt abgerufen werden',
    addAccountTitle: 'Konto hinzufügen',
    addAccountConfigure: 'Jetzt einrichten',
    contactAdressesTitle: 'Anmelde- und Kontakt-E-Mail',
    contactAdressesDescription: (email) => `Du bist mit der E-Mail-Adresse <strong>${email}</strong> angemeldet. Bevor Du Dich mit Deiner neuen E-Mail-Adresse anmelden kannst, musst Du diese bestätigen. Hierfür schickt orgaMAX Dir eine E-Mail mit einem Bestätigungs-Link.`,
    connectionFailedTitle: 'Leider konnten wir Dein E-Mail-Konto nicht verknüpfen',
    connectionOnloadFailedTitle: 'Leider konnten wir Dein E-Mail-Konto nicht automatisch verknüpfen',
    mayTakeFewMinuesTitle: 'Dieser Vorgang kann einige Minuten dauern',
    deleteAcknowledgmentTitle: 'Bist Du sicher, dass Du die Verknüpfung zu diesem E-Mail-Konto entfernen möchtest? Du kannst die Verknüpfung später erneut vornehmen.',
    asDefaultTitle: 'Als Standard einstellen',
    activateGrantTitle: 'Freischaltung aktivieren',
    activateGrantSubtitle1: `Google fordert unter Umständen eine zusätzliche Freischaltung zum Zugriff auf E${String.fromCodePoint(8209)}Mails.`,
    activateGrantSubtitle2: 'Diese Freischaltung kannst Du hier aktivieren:',
    issueApprovalTitle: 'Freigabe erteilen',
    issueApprovalSubtitle: 'Bei E-Mail-Accounts von Google kann eine zusätzliche Freigabe notwendig sein.\nHier kannst Du diese Freigabe bei Google erteilen:',
    allowAccessTitle: 'Zugriff erlauben',
    allowAccessSubtitle: 'Häufig muss bei Deinem Mailanbieter der Zugriff erlaubt werden.\nHier findest Du eine Anleitung dazu:',
    isConnectingTitle: 'Die Verbindung zu Deinem\nKonto wird hergestellt',
    senderTitle: 'Absender & Antwortadresse',
    accountName: 'Kontoname',
    senderName: 'Absendername',
    senderEmail: 'Antwortadresse',
    displayName: 'Anzeigename',
    senderDescription: (noreplyEmail) => `E-Mails werden standardmäßig über ${noreplyEmail} versendet. Der hier festgelegte Absendername wird in der E-Mail angezeigt. `
      + 'Die Antwortadresse wird verwendet, wenn in der E-Mail der Button „Antworten“ geklickt wird.',
    senderDescriptionOwnAccount: (noreplyEmail) => 'Ist weiter unten ein eigenes E-Mail-Konto verknüpft, kann beim Versand von Vorgängen ausgewählt werden, '
      + `ob die E-Mail über dieses Konto oder über ${noreplyEmail} versendet wird.`,
    accountAlreadyExists: (email) => `Das Konto mit der E-Mail-Adresse ${email} existiert bereits`,
    submitSuccess: (email) => `Die Verbindung zu Deinem Konto ${email} war erfolgreich`,
    errorMessageCreate: 'Die Verbindung zu Deinem Konto ist fehlgeschlagen',
    updateSuccess: 'Die Einstellungen wurden erfolgreich gespeichert',
    errorMessageUpdate: 'Die Einstellungen konnten nicht gespeichert werden',
    deleteSuccess: 'Die Verbindung zu Deinem Konto wurde erfolgreich aufgehoben',
    sendViaOwnAccount: 'Versand über eigenes E-Mail Konto (SMTP)',
    archiveImapEmails: `E-Mail-Verlauf in ${isOrgaMax ? `${productNameLong}` : 'MeinBüro'} bei Deinen Kunden ablegen (IMAP)`,
    emailAccountIsDefault: '(Standard)',
    reloadEmailButtonTitle: 'E-Mail-Verlauf abrufen',
    emailFetchingTitle: 'Dein E-Mail-Verlauf wird abgerufen',
    emailFetchedSuccessfullyTitle: 'Dein E-Mail-Verlauf wurde erfolgreich abgerufen.',
    emailFetchFailedTitle: 'Dein E-Mail-Verlauf konnte nicht abgerufen werden.',
    imapErrors: {
      authFailed: 'Die Anmeldung am E-Mail-Server ist fehlgeschlagen. Bitte prüfe Benutzername und Passwort Deines E-Mail-Kontos.',
      connectionTimeout: 'Der E-Mail-Server hat nicht rechtzeitig geantwortet. Bitte versuche es später erneut.',
      hostNotFound: 'Der E-Mail-Server wurde nicht gefunden. Bitte prüfe die IMAP-Server-Adresse.',
      connectionRefused: 'Der E-Mail-Server hat die Verbindung abgelehnt. Bitte prüfe IMAP-Server-Adresse und Port.',
      tlsFailed: 'Die verschlüsselte Verbindung zum E-Mail-Server ist fehlgeschlagen. Bitte prüfe die Verschlüsselungseinstellungen Deines E-Mail-Kontos.',
      unknown: 'Beim Abrufen Deines E-Mail-Verlaufs ist ein Fehler aufgetreten.',
    },
    startImapButtonTitle: 'E-Mails abrufen',
    keepEmails: 'E-Mails behalten',
    deleteEmails: 'E-Mails verwerfen',
    deleteEmailHeadline: 'E-Mail löschen',
    deleteEmailText: 'Möchtest Du Deine E-Mail wirklich löschen? Sie wird nur hier im Verlauf ausgeblendet. Du kannst sie durch ein erneuetes Verknüpfen des E-Mail-Kontos erneut synchronisieren.',
    emailDeletedSuccessfully: 'Die E-Mail wurde erfolgreich gelöscht',
    deleteEmailButtonLabel: 'Ausblenden',
    upgradeText: `${unlockViewUpgradeText} Dein eigenes E-Mail Konto anzubinden.`,
    mailAccountTitle: 'Anbindung E-Mail Konto',
    signatureTitle: 'E-Mail-Signatur',
    signature: 'Signatur',
    emailDescription: 'Hier kannst Du Deine eigene E-Mail Signatur eingeben und editieren. Diese Signatur wird beim E-Mail Versand von Angebot, Auftrag, Rechnung an die Email angefügt.',
    emailSettingsTitle: 'Daten zum E-Mail Versand',
    senderEmailLabel: 'Absender E-Mail',
    sendTestEmail: 'Test E-Mail versenden',
    form: {
      heading: 'Daten zum E-Mail Versand',
      subHeading: `Gib hier Deine E-Mail Postausgangsserver Anmeldedaten ein. Wichtig: Anbieter wie ${emailProviderFaqLabel('Gmail', gmailSetupFaqLink)} oder ${emailProviderFaqLabel('Microsoft Office 365', office365SetupFaqLink)} müssen im Vorfeld konfiguriert sein.`,
      detailsLinkLabel: 'Anleitungen zur Einrichtung',
      providerSelectorHeading: 'Wähle Deinen Anbieter aus',
    },
    incoming: {
      serverTitle: 'Posteingangs-Server (IMAP-Server-Adresse)',
      userNameTitle: 'Benutzername Posteingangs-Server (IMAP-Benutzername)',
    },
    outgoing: {
      serverTitle: 'Postausgangsserver (SMTP-Server-Adresse)',
      userNameTitle: 'Benutzername Postausgangsserver (SMTP-Benutzername)',
    },
    validationErrors: {
      displayNameRequired: 'Anzeigename ist erforderlich',
      senderNameRequired: 'Absendername ist erforderlich',
      hostRequired: 'Server-Adresse ist erforderlich',
      portRequired: 'Port ist erforderlich',
      userNameRequired: 'Benutzername ist erforderlich',
      passwordRequired: 'Passwort ist erforderlich',
      emailRequired: 'E-Mail ist erforderlich',
      emailInvalid: 'E-Mail ist ungültig',
    },
    deleteEmail: {
      headline: 'E-Mail-Konto löschen',
      description: 'Möchtest Du Dein E-Mail-Konto wirklich löschen? Du kannst es später erneut verknüpfen',
    },
    errors: {
      AUTH: 'Benutzername oder Passwort falsch',
      SERVER: 'Verbindungsfehler, bitte überpüfe Servername, Port und Verschlüsselung.',
    },
    oAuth: {
      failed: 'Die Verbindung wurde unterbrochen oder ist abgelaufen. Bitte starte die Verbindung erneut.',
      retry: 'Verbindung erneut herstellen',
    },
  },

  // global search
  globalSearch: {
    emptyStateText: 'Leider ergab Deine Suche keine Treffer',
    expenseStates: {
      open: 'Offen',
      partiallyPaid: 'Teilweise bezahlt',
      paid: 'Bezahlt',
      inProgress: 'Angewiesen',
    },
    numbers: {
      article: 'Artikel-Nr. {{number}}',
      cancellation: 'Storno-Nr. {{number}}',
      creditNote: 'Gutschrift-Nr. {{number}}',
      customer: 'Kunden-Nr. {{number}}',
      dunning: 'Rechnungs-Nr. {{number}}',
      offer: 'Angebots-Nr. {{number}}',
      deliveryNote: 'Lieferschein-Nr. {{number}}',
      invoice: 'Rechnungs-Nr. {{number}}',
      order: 'Auftrags-Nr. {{number}}',
      supplier: 'Lieferanten-Nr. {{number}}',
      trackedTime: 'Kunden-Nr. {{number}}',
      purchase: 'Bestell-Nr. {{number}}',
    },
    placeholder: `${productName} durchsuchen`,
    phone: 'Tel.',
    searchCategories: {
      article: 'Artikel',
      cancellation: 'Stornierungen',
      creditNote: 'Gutschriften',
      customer: 'Kunden',
      deliveryNote: 'Lieferscheine',
      dunning: 'Mahnungen',
      expense: 'Ausgaben',
      invoice: 'Rechnungen',
      offer: 'Angebote',
      order: 'Aufträge',
      project: 'Projekte',
      recurringInvoice: 'Abo-Rechnungen',
      revenue: 'Sonstige Einnahmen',
      supplier: 'Lieferanten',
      trackedTime: 'Zeiterfassungen',
      letter: 'Geschäftsbriefe',
      purchase: 'Bestellungen',
    },
  },

  notifications: {
    today: {
      title: 'Heute',
      dateFormat: 'HH:mm',
    },
    yesterday: {
      title: 'Gestern',
      dateFormat: '[Gestern] HH:mm',
    },
    week: {
      title: 'Diese Woche',
      dateFormat: 'dddd HH:mm',
    },
    older: {
      title: 'Älter',
      dateFormat: 'DD.MM.YYYY, HH:mm',
    },
    title: 'Benachrichtigungen',
    markAll: 'Alle als gelesen markieren',
    showMore: 'Mehr anzeigen',
    showLess: 'Weniger anzeigen',
  },

  dueDate: {
    today: 'Heute',
    tomorrow: 'Morgen',
    nextWeek: 'In einer Woche',
    nextMonth: 'In einem Monat',
    custom: 'Benutzerdefiniert',
  },

  todo: {
    todoTitle: 'Aufgabe',
    label: 'AUFGABE',
    type: 'Typ',
    todos: 'Deine Aufgaben',
    submenuTitle: 'Aufgaben',
    redate: 'Neu datieren',
    doneButtonLabels: {
      [TodoTypes.PROCESS]: 'Erledigen',
      [TodoTypes.RELEASE]: 'Freigeben',
      [TodoTypes.ATTENTION]: 'Zur Kenntnis nehmen',
      [TodoTypes.SIGNATURE]: 'Anfordern',
    },
    dueToDos: 'Fällige Aufgaben',
    myToDos: 'Meine Aufgaben',
    placeholder: 'Beschreibung hinzufügen',
    titlePlaceholder: 'Aufgabentitel eingeben',
    helpText: '<strong>Enter</strong> zum Hinzufügen, <strong>Umschalttaste (Shift) + Enter</strong> für eine neue Zeile',
    createErrorMessage: 'Beim Erstellen der Aufgabe ist ein Fehler aufgetreten',
    collaborationDeletedErrorMessage: 'Du hast keine Berechtigung diese Aufgabe einzusehen.',
    saveSuccessMessage: (interval) =>
      `Die ${interval !== TodoIntervalEnum.SINGLE ? 'Seriena' : 'A'}ufgabe wurde erfolgreich gespeichert`,
    updateErrorMessage: 'Beim Aktualisieren der Aufgabe ist ein Fehler aufgetreten',
    updateSuccessMessage: (interval) =>
      `Die ${interval !== TodoIntervalEnum.SINGLE ? 'Seriena' : 'A'}ufgabe wurde erfolgreich aktualisiert`,
    createdBy: 'Von:',
    addTodo: 'Aufgabe hinzufügen',
    createTodo: 'Aufgabe erstellen',
    editTodo: 'Speichern',
    createTaskErrorMessage: 'Beim Erstellen der Aufgabe ist ein Fehler aufgetreten',
    uploadFileErrorMessage: 'Beim Hochladen der Datei ist ein Fehler aufgetreten',
    newestTasks: 'Deine Aufgaben',
    showAllTasks: 'Alle Aufgaben ansehen',
    edit: 'Aufgabe bearbeiten',
    create: 'Neue Aufgabe erstellen',
    deleteConfirmCaption: 'Aufgabe ablehnen',
    deleteConfirmText: 'Möchtest Du dieses Aufgabe wirklich ablehnen?',
    deleteButtonLabel: 'Ablehnen',
    documentCopied: 'Dokument kopiert',
    maxLengthErrorMessage: 'Der Titel darf höchstens 100 Zeichen enthalten',
    detail: {
      title: 'Aufgabendetail',
      creator: 'Ersteller',
      dueToDate: 'Frist',
      dueFromDate: 'Fälligkeit',
      interval: 'Intervall',
      status: {
        [TodoTypes.PROCESS]: 'Erledigt',
        [TodoTypes.RELEASE]: 'Freigegeben',
        [TodoTypes.ATTENTION]: 'Zur Kenntnis genommen',
        [TodoTypes.SIGNATURE]: 'Abgeschlossen',
        rejected: 'Abgelehnt',
        deleted: 'Kommentar wurde gelöscht',
      },
      documents: 'Dokumente',
      accessDeniedTitle: 'Fehlende Zugriffsrechte',
      accessDeniedMessage: 'Einige Dokumente wurden in einen Ordner verschoben, auf den Du keinen Zugriff hast. Bitte wende Dich an Deinen Administrator, um Zugriff zu erhalten.',
      accessDeniedFileTooltip: 'Kein Zugriff',
      responsibles: count => count === 1 ? 'Verantwortlicher' : 'Verantwortliche',
      customers: count => count === 1 ? 'Kunde' : 'Kunden',
      suppliers: count => count === 1 ? 'Lieferant' : 'Lieferanten',
      deleteButtonDisabledLabel: 'Hinweis: Aufgaben können derzeit nur gelöscht werden, wenn sie noch nicht bearbeitet wurden, keine Anhänge enthalten und nicht an einen Steuerberaterin zugewiesen sind. Eine erweiterte Papierkorbfunktion befindet sich in Vorbereitung.',
      deleteModal: {
        title: 'Aufgabe löschen',
        deleteIncludingDocuments: 'Aufgabe inkl. Dokumente löschen',
        deleteConfirmation: 'Möchtest Du die Aufgabe wirklich löschen?',
        deleteConfirmationWithDocuments: 'Dieser Aufgabe wurden Dokumente hinzugefügt. Dokumente können entfernt oder im Dokumentencontainer behalten werden.',
        deleteConfirmationWithoutDocuments: 'Dieser Aufgabe wurden Dokumente hinzugefügt. Da für mindestens eins dieser Dokumente keine Löschrechte bestehen, bleiben alle Dokumente im Dokumentencontainer enthalten.',
      },
    },
    iconButtons: {
      move: 'In die Dokumente kopieren',
      download: 'Download',
      preview: 'Vorschau',
      unlink: 'Löschen',
      remove: 'Aufheben',
    },
    form: {
      title: 'Aufgabentitel',
      content: 'Beschreibung',
      dueDate: 'Fälligkeit',
      deadline: 'Frist',
      interval: 'Intervall',
      responsible: 'Verantwortlicher',
      customers: 'Kunden',
      suppliers: 'Lieferanten',
      collaborators: 'Mandanten',
      previewDisabled: 'Für noch nicht hochgeladene Dateien mit diesem Dateityp wird nach Erstellung der Aufgabe eine Vorschau verfügbar.',
    },
    filter: {
      open: 'Offen',
      due: 'Fällig',
      overdue: 'Überfällig',
      own: 'Meine',
      declined: 'Abgelehnt',
      done: 'Erledigt',
      approved: 'Freigegeben',
      noted: 'Zur Kenntnis genommen',
      signed: 'Unterschrieben',
      unseen: 'Ungelesen',
    },
    uploader: {
      title: 'Dokument auswählen',
    },
    messages: {
      title: 'Kommentare',
      inputPlaceHolder: 'Kommentar eingeben',
      loadMore: (count) => `${count} weitere${count === 1 ? 'n' : ''} Kommentar${count > 1 ? 'e' : ''} laden`,
    },

    list: {
      listEmptyStateTitle: 'Keine Aufgaben vorhanden',
      listNoSearchResultTitle: 'Keine Aufgaben gefunden',
      listEmptyStateSubtitle: 'Jetzt die erste Aufgabe erstellen',
      listEmptyStateButtonLabel: 'Los geht\'s',
    },

    intervals: {
      [TodoIntervalEnum.SINGLE]: 'Einmalig',
      [TodoIntervalEnum.DAILY]: 'Täglich',
      [TodoIntervalEnum.WEEKLY]: 'Wöchentlich',
      [TodoIntervalEnum.MONTHLY]: 'Monatlich',
      [TodoIntervalEnum.QUARTERLY]: 'Vierteljährlich',
      [TodoIntervalEnum.YEARLY]: 'Jährlich',
    },

    signing: {
      title: {
        default: 'Zur Unterschrift',
        document: 'Dokument',
      },
      from: 'von',
    },
    uploadInProgressMessage: (progress) => `Upload läuft. Bitte warte, bis alle Dateien hochgeladen sind, bevor Du dieses To-Do öffnest oder bearbeitest. ${progress ? `${progress.finished}/${progress.total} Dateien hochgeladen.` : ''}`,
    readOnlyMode: 'Mandant im Lesemodus',
    readOnlyModeTooltipText: 'Für diesen Mandanten besteht nur eine Leseberechtigung',
    readOnlyModeTooltipBody: 'Für diesen Mandaten besteht derzeit kein aktiver Vertrag. Aus diesem Grund ist der Zugriff auf die Inhalte auf eine reine Leseansicht beschränkt.',
  },

  activity: {
    label: 'AKTIVITÄT',
    create: 'erstellen',
    activityInputPlaceholder: 'Kategorie hinzufügen',
    activityCategoryOptionCreated: (option) => `Die Aktivitätskategorie ${option} wurde erfolgreich erstellt.`,
  },

  mite: {
    credentials: {
      domain: {
        heading: 'Wie lautet Deine mite Login-Adresse?',
        text: 'Du findest Deine mite-Adresse unter "Account" im Abschnitt "Login".',
        default: 'ihre-mite-adresse',
      },
      access: {
        heading: 'Zugriff aktivieren',
        text: 'Gehe auf ',
      },
      key: {
        heading: 'API-Schlüssel kopieren und einfügen',
        text: 'Setze das Häkchen bei “Zugriff aktivieren” und klicke auf “Sichern”. Klicke anschließend auf “API-Schlüssel anzeigen”. Kopiere Deinen API-Schlüssel und füge ihn hier ein.',
      },
      submit: 'Jetzt verbinden',
    },
    filter: {
      all: 'Alle',
      cleared: 'Abgerechnet',
      notCleared: 'Nicht Abgerechnet',
      search: 'Zeitbuchungen durchsuchen',
    },
    createInvoice: 'Rechnung erstellen',
    heading: 'Zeiterfassung aus mite abrechnen',
    noResult: 'Keine Zeitbuchungen gefunden',
    columns: {
      date: 'Datum',
      customer: 'Kunde',
      project: 'Projekt',
      service: 'Leistung',
      note: 'Bemerkung',
      hours: 'Stunden',
      revenue: 'Umsatz',
      cleared: 'Abgerechnet',
    },
    lockedStatus: {
      true: 'Ja',
      false: 'Nein',
    },
  },

  cleverreach: {
    topbarTitle: 'CleverReach Export',
    mainTitle: `Übertrage Deine ${productName} Kunden nach CleverReach und halte sie aktuell`,
    mainSubtitle: `Mit der CleverReach App kannst Du Deine ${productName}-Kontakte nach CleverReach übertragen.
    Solange die Verbindung aktiv ist, werden alle neuen Kunden mit E-Mail-Adresse und Änderungen in ${productName}
    automatisch in die CleverReach-Liste "${productName}-Kontakte" übertragen.`,
    subTitleTransmit: 'Übertrage jetzt Deine Daten.',
    connectToCleverreach: 'Zuerst verbinden wir Deinen CleverReach-Account.',
    syncInProgressTitle: 'Datenübertragung läuft',
    syncInProgressSubtitle: 'Der Vorgang läuft im Hintergrund. Du kannst diese Seite verlassen.',
    duplicatesTitle: 'Datenübertragung könnte fehlerhaft sein.',
    duplicatesSubtitle: 'Folgende E-Mail-Adressen werden mehrfach verwendet. Es ist keine eindeutige Zuordnung möglich.',
    buttonTextStart: 'Start',
    buttonTextDashboard: 'Zum Dashboard',
    buttonTextTryAgain: 'Erneut probieren',
    buttonTextTransmit: 'Daten übertragen',
    buttonTextTransmitWithDuplicates: 'Daten trotzdem übertragen',
    buttonTextConnectToCleverreach: 'Mit CleverReach verbinden',
    buttonTextEditCustomerData: 'Kundendaten anpassen',
    connectionSuccessful: 'CleverReach Verbindung erfolgreich!',
  },

  accounting: {
    resetConnectionSuccessMessage: 'Die Verbindung wurden erfolgreich zurückgesetzt!',
    resetConnectionErrorMessage: 'Beim Zurücksetzen der Verbindung ist ein Fehler aufgetreten.',
    connectionStateText: 'Verbindungsstatus',
    buttonLongTermConnectText: 'Langzeitige Verbindung aufbauen',
    notConnectedText: (appName) => `Keine Verbindung zu ${appName}`,
    connectedText: (appName) => `Mit ${appName} verbunden`,
    buttonTransferProcessingText: 'Übertragung läuft…',
    transferSuccessMessage: 'Übertragung gestartet',
    transferErrorMessage: 'Beim Übertragung ist ein Fehler aufgetreten',
    getSettingsErrorMessage: 'Beim Laden der Einstellungen ist ein Fehler aufgetreten',
    saveSettingsErrorMessage: 'Beim Speichern der Einstellungen ist ein Fehler aufgetreten',
    getTransferListErrorMessage: 'Beim Laden der Liste ist ein Fehler aufgetreten',
    saveSettingsSuccessMessage: 'Die Einstellung wurde erfolgreich gespeichert!',
    getProtocolErrorMessage: 'Beim Laden des Protokolls ist ein Fehler aufgetreten',
    allDocuments: 'alle Belege',
    redirectMessage: 'Du wirst jeden Moment weitergeleitet',

    getNewDocumentText: (count) => `${count} ${count === 1 ? 'neuer Beleg' : 'neue Belege'}`,
    getNewBookingEntriesText: (count) => `${count} neue ${count === 1 ? 'Buchung' : 'Buchungen'}`,

    documentTypes: {
      invoices: 'Rechnungen',
      expenses: 'Ausgaben & Sonstige Einnahmen',
      others: 'Sonstige Buchungen',
      bankBookings: 'Bankbuchungen',
      customersVendors: 'Stammdaten',
    },

    content: {
      documents: 'Belege',
      bookingEntries: 'Buchungen',
    },

    datev: {
      buttonConnectText: 'Anmelden mit DATEV',
      resendText: 'Bereits übertragene Belege bzw. Dateien erneut übertragen',
      sendDocumentType: 'Belegart übertragen',
      transferMonth: 'Übertragungsmonat',
      currentClientText: (consultantNo = '', clientNo = '') => `Beraternummer: ${consultantNo}, Mandantennummer: ${clientNo}`,
      getClientsErrorMessage: 'Beim Laden der Mandanten ist ein Fehler aufgetreten.',
      clientNotExistsErrorMessage: 'Der angeforderte Mandant existiert nicht. Bitte wende Dich an Deinen Steuerberater.',
      getTransferInfoErrorMessage: 'Beim ermitteln der zu übertragenden Dokumente ist ein Fehler aufgetreten.',
      modalTitleClient: 'Mandantenauswahl',
      modalDescriptionClient: 'Bitte wähle einen Mandanten aus',
      buttonTransferStartText: 'Daten jetzt an DATEV übertragen',
      lostDatevAccessErrorMessage: 'Keine Verbindung zu DATEV-Datenservices',
      automateText: 'Automatisch an DATEV übertragen:',
      buttons: {
        disconnect: {
          label: 'Verbindung trennen',
          description: '<b>Trennen:</b> Die Verbindung wird beendet und kann von Dir später ohne erneute Mandantenauswahl wiederhergestellt werden.',
        },
        reset: {
          label: 'Verbindung entfernen',
          description: '<b>Entfernen:</b> Die Verbindung wird vollständig gelöscht. Bei einer erneuten Einrichtung musst Du den Mandanten erneut auswählen.',
        },
      },
      searchClientsPlaceholder: 'Mandanten durchsuchen',
      connection: {
        userText: (name) => `${name} hat die Verbindung aufgebaut.`,
        activeTo: 'Aktiv bis ',
        datevAppsUrlText: 'DATEV Apps verwalten unter ',
      },
      selectButtonLabel: 'Auswählen',
      clientSelectionModal: {
        consultantNumber: 'Beraternummer',
        clientNumber: 'Mandantennummer',
        availableServices: 'Verfügbare Dienste',
      },
    },

    list: {
      noRowsToShow: 'Keine Übertragungen vorhanden',
      loadingOoo: 'Übertragungen werden geladen',
    },

    transferView: {
      content: 'Inhalt:',
      toTransfer: 'Übertragen werden:',
    },
  },

  payConditionForm: {
    create: 'Zahlungsbedingung erstellen',
    edit: 'Zahlungsbedingung bearbeiten',
    name: 'Name der Zahlungsbedingung*',
    dueDays: 'Zahlungsziel (in Tagen)*',
    offerText: 'Text auf Angeboten',
    invoiceText: 'Text auf Rechnungen',
    orderText: 'Text auf Aufträgen',
    default: 'Standard-Zahlungsbedingung',
    saving: 'Wird gespeichert',
    hints: {
      deleteButton: {
        defaultCondition: 'Zahlungsbedingung kann nicht gelöscht werden, da sie als Standard gespeichert ist.',
        inTransaction: 'Zahlungsbedingung kann nicht gelöscht werden, da sie im aktuellen Vorgang ausgewählt ist.',
        inTransactionAndDefault: 'Zahlungsbedingung kann nicht gelöscht werden, da sie als Standard gespeichert und im Vorgang ausgewählt ist.',
      },
    },
  },

  deliveryConditionForm: {
    create: 'Lieferbedingung erstellen',
    edit: 'Lieferbedingung bearbeiten',
    name: 'Name der Lieferbedingung*',
    deliveryDays: 'Liefertermin nach Auftrags-/Rechnungsstellung (in Tagen)*',
    text: 'Text auf Vorgängen',
    default: 'Standard-Lieferbedingung',
    saving: 'Wird gespeichert',
    saveAndApplyDisabledHint: 'Bitte fülle zuerst alle Pflichtfelder aus.',
    saveDisabledHint: 'Bitte fülle zuerst alle Pflichtfelder aus.',
  },

  shipment: {
    title: 'Paketschein erstellen',
    selection: 'Versandart',
    weightinput: 'Gewicht in KG (MIN 0.2KG)',
    noConfig: 'Bitte konfiguriere zunächst Deine Versandarten',
    errorMessage: 'Das Lieferdatum darf nicht in der Vergangenheit liegen',
    adressErrorMessage: 'Deine eigenen hinterlegten Adressdaten sind unvollständig',
    buttons: {
      save: 'Paketschein erstellen',
    },
  },
  carrierAccountList: {
    title: 'Versanddienstleister-Konten',
    description: 'Versanddienstleister-Konten werden verwendet um automatisch Versandetiketten drucken zu können. Gib hier beispielsweise Deine Zugangsdaten Deines DHL-Portals ein.',
    buttons: {
      add: 'Konto',
    },
    messages: {
      accountsCreateSuccess: 'Versanddienstleister erfolgreich angelegt',
      accountsSaveSuccessMessage: 'Versanddienstleister erfolgreich gespeichert',
      accountsDeleteSuccessMessage: 'Versanddienstleister erfolgreich gelöscht',
    },
  },
  carrierAccountModal: {
    title: 'Versanddienstleister-Konto hinzufügen',
    labels: {
      username: 'Benutzername*',
      password: 'Kennwort*',
      accountNumber: 'Kundennummer*',
    },
    authenticationError: 'Login-Daten inkorrekt.',
  },
  shipmentTypeList: {
    title: 'Versandarten',
    unknown: 'Unbekannt',
    description: 'Versandarten bestimmen, welche Versand-Etiketten im Verarbeitungsprozess erstellt werden (z.B. DHL Paket, DHL Paket Express, ...)',
    titleDefault: 'Standard-Versandart',
    descriptionDefault: 'Lege hier fest, welche Versandart standardmäßig verwendet werden soll:',
    messages: {
      typeCreateSuccessMessage: 'Versandart erfolgreich angelegt.',
      typeSaveSuccessMessage: 'Versandart erfolgreich aktualisiert.',
      typeDeleteSuccessMessage: 'Versandart wurde gelöscht.',
      typeSetDefaultSuccessMessage: 'Die Standardversandart wurde festgelegt.',
    },
  },
  shipmentTypeForm: {
    titleNew: 'Versandart hinzufügen',
    titleEdit: 'Versandart bearbeiten',
    labels: {
      name: 'Name der Versandart*',
      carrierAccountId: 'Versandkonto*',
      carrierProductId: 'Produkt*',
    },
  },
  emailModalHeadlines: {
    senderTitle: 'Von',
    recipientTitle: 'An',
    ccTitle: 'Cc',
    bccTitle: 'Bcc',
    furtherTitle: 'weitere',
  },
  customFields: {
    list: {
      listEmptyStateTitle: 'Jetzt individuelle Felder anlegen',
      listNoSearchResultTitle: 'Keine individuellen Felder gefunden',
      listEmptyStateButtonLabel: 'Los geht´s',
      emptyStateText: 'Keine Individuellen Felder gefunden',
      loaderText: 'Individuellen Felder werden geladen',
      new: 'Neues individuelles Feld anlegen',
      edit: 'Individuelles Feld bearbeiten',
      delete: 'Individuelle(s) Feld(er) löschen',
      searchPlaceholder: 'Felder durchsuchen',
    },
    types: {
      text: {
        label: 'Freies Eingabefeld',
        hint: 'Bei diesem Feldtyp kann ein Text bestehend aus Buchstaben, Zahlen, Sonderzeichen hinterlegt werden z.B. HTS-25B',
      },
      number: {
        label: 'Nummernfeld',
        hint: 'Bei diesem Feldtyp können nur Zahlen hinterlegt werden z.B. 12345',
      },
      date: {
        label: 'Datum',
        hint: 'Dieser Feldtyp ermöglicht es, die Eingabe in Form eines Datum zu hinterlegen z.B. 02.10.1981',
      },
      single_select: {
        label: 'Einfache Auswahlbox',
        hint: 'Dieser Feldtyp bietet die Möglichkeit, das Feld mit einer definierten Auswahl zu befüllen und in Stammdaten via Dropdown auszuwählen.',
      },
      multi_select: {
        label: 'Mehrfache Auswahlbox',
        hint: 'Dieser Feldtyp erweitert die “Einfache Auswahlbox” um die Möglichkeit mehr als eine Auswahl zu den Stammdaten hinzuzufügen.',
      },
      currency: {
        label: 'Währungsfeld',
        hint: 'Dieser Feldtyp setzt ein Stammdatenfeld mit einem Währungskennzeichen z.B. 52,35 €',
      },

    },
    title: 'Individuelle Felder',
    subTitle: 'Du kannst eigene Felder wie Textfelder, Datumsfelder etc. zur individuellen Stammdatenpflege erstellen. Die individuellen Felder können <a href="/settings/customFields">hier</a> festgelegt werden.',
    noFieldsAvailable: 'Aktuell keine individuellen Felder vorhanden',
    modelTypes: {
      article: 'Artikel',
      customer: 'Kunden',
    },
    inputs: {
      name: 'Feldname',
      type: 'Feldtyp',
      value: 'Werte',
    },
    addEdit: {
      hint: 'Achtung: Bedenke, dass es bei Änderung des Datentyps zu Datenverlusten kommen kann, da die vorhandenen Feldwerte nicht beliebig konvertiert werden können.',
      valuePlaceHolder: 'neuen Wert eintragen',
      modelTypeTitle: 'Wo soll das Feld verwendet werden?',
    },
    delete: {
      status: {
        pending: 'in Prüfung',
        notUsed: 'nicht verwendet',
      },
      titles: {
        field: 'Feldname',
        usedIn: 'Verwendet in',
      },
      successMessage: 'Erfolgreich gelöscht',
      deleteConfirmation: 'Hiermit bestätige ich den Löschvorgang',
      hint: 'Achtung: einige Felder sind in Benutzung und das Löschen führt zu Datenverlust. Bitte bestätige den Löschvorgang:',
    },
    buttons: {
      add: 'Feld hinzufügen',
    },
  },
  invoiceDetail: {
    confirmEmailTitle: 'Hallo, um orgaMAX vollständig nutzen zu können, gib bitte den erhaltenen Aktivierungscode ein.',
    dun: 'Mahnung erstellen',
    draftActions: {
      print: 'Entwurf drucken',
      download: 'Entwurf herunterladen',
      sendEmail: 'Entwurf per E-Mail versenden',
    },
    dunning: {
      table: {
        header: {
          actions: 'Aktionen',
        },
        actions: {
          send: 'Senden',
        },
      },
      state: {
        1: 'Zahlungserinnerung',
        2: '1. Mahnung',
        3: '2. Mahnung',
        4: 'Letzte Mahnung',
      },
      recipients: 'wird verschickt an:',
    },
    credit: {
      toast: {
        success: 'Gutschrift wurde erstellt',
      },
    },
    delete: {
      confirmation: {
        caption: 'Rechnung löschen',
        captionManualCreditNote: 'Rechnungskorrektur/Gutschrift löschen',
        text: 'Möchtest Du die Rechnung wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
        textManualCreditNote: 'Möchtest Du die Rechnungskorrektur/Gutschrift wirklich löschen? Dies kann nicht rückgängig gemacht werden!',
        confirmText: 'Löschen',
      },
      toast: {
        success: 'Die Rechnung wurde erfolgreich gelöscht',
      },
    },
    regenerate: {
      toast: {
        generating: 'Das PDF wird neu generiert.',
      },
    },
    carrier: {
      downloadFileName: (displayNumber) => `Versandetikett zu Rechnung ${displayNumber}.pdf`,
    },
    titles: {
      tags: 'Tags',
      payCondition: 'Zahlungsart',
      revenueAccount: 'Erlöskonten',
      correspondingBookings: 'Zugehörige Buchungen',
      eInvoice: 'E-Rechnung herunterladen',
    },
    openAmountLabel: 'Offener Betrag',
    amountLabel: 'Betrag',
    revenueAccountLabel: {
      vat0: 'USt.-frei',
      vat1: 'Volle USt.',
      vat2: 'Halbe USt.',
    },
    noConnectedTransaction: 'Keine verknüpften Vorgänge',
    qrCodeDescription: 'Bezahlen per QR-Code',
    qrCodeTooltipHeader: 'QR-Code bezahlen',
    qrCodeTooltipContent: 'Sind Zahlungsmethoden aktiviert, können Deine Kunden direkt mit einem QR-Code die Rechnung in 3 Schritten begleichen. Es wird ein zusätzliches Formular generiert, auf dem sich der QR-Code befindet.',
    eInvoice: {
      description: 'Lade Deine E-Rechnung schnell und bequem im gewünschten Format herunter. Wähle einfach das passende E-Rechnungsformat aus und profitiere von einer effizienten und digitalen Rechnungsabwicklung.',
      buttonTitle: 'Jetzt herunterladen',
      selectInputLabel: 'Dateityp auswählen',
      eInvoiceLink: 'Weitere Informationen zur E-Rechnung findest Du hier',

    },
    errors: {
      deliveryDateOnDirectDebitInvalid: 'Bei SEPA-Lastschriftrechnungen darf das Lieferdatum nicht heute oder in der Vergangenheit liegen.',
      directDebitMandateMissing: 'Der Kunde hat kein verfügbares SEPA-Lastschriftmandat.',
    },
  },
  offline: {
    headline: 'Es konnte leider keine Verbindung<br /> zum Server hergestellt werden',
    text: 'Bitte überprüfe Deine Internetverbindung<br /> und probiere es erneut',
  },

  transactionDetail: {
    currentState: 'Aktueller Status',
    source: 'Quelle',
    connected: 'Zugehörig',
    tag: 'Tags',
  },

  contactPersonFormModalEdit: 'Ansprechpartner bearbeiten',
  contactPersonFormModalAdd: 'Ansprechpartner hinzufügen',
  contactPersonTitle: 'Ansprechpartner',
  contactPersonCommunicationTitle: 'Kommunikation',
  contactPersonMiscDataTitle: 'Weitere Daten',
  notesAlertLabel: 'Anmerkungen bei Erstellung neuer Vorgänge anzeigen?',
  tenant: {
    addLabel: 'Hinzufügen',
    switchLabel: 'Wechseln',
    restoreLabel: 'Wiederherstellung',
    switchNotAllowed: 'Das Wechseln des Mandanten ist nicht erlaubt',
    demoTenant: 'Demo-Firma erstellen',
    creatingNewLabel: 'Mandant wird erstellt',
    creatingNewDemoLabel: 'Demo-Mandant wird erstellt',
    tenantNameUpdated: 'Mandantenname gespeichert',
    tenantColorsUpdated: 'Mandanteninitialen gespeichert',
    tenantColorsUpdateFailed: 'Mandanteninitialen konnten nicht gespeichert werden',
    tenantTypeExists: 'Eine Testversion besteht bereits!',
    tenantCreated: 'Dein neuer Mandant wurde erfolgreich erstellt. Der neue Mandant wird sofort für Dich aktiviert.',
    tenantActivated: tenantName => {
      if (tenantName) {
        return `Mandant ${tenantName} aktiviert!`
      }
      return 'Standard-Mandant aktiviert!'
    },
    newTenant: 'Neuer Mandant',
    createNewTenant: 'Neue Firma erstellen',
    creatingTenant: 'Mandant wird erstellt...',
    newOffice: 'Neue Kanzlei',
    collaboration: {
      accepted: (name) => `${name} hat Deine Anfrage akzeptiert`,
      requested: (name) => `${name} hat Dir eine Anfrage gesendet`,
      paused: (name) => `${name} hat die Zusammenarbeit pausiert`,
      rejected: (name) => `${name} hat Deine Anfrage abgelehnt`,
      disconnected: (name) => `${name} hat die Zusammenarbeit beendet`,
    },
    selection: {
      defaultCompanyName: 'Name des Unternehmens',
      backToTaxOffice: 'Zurück zur Kanzleiansicht wechseln',
    },
    switchConfirmation: {
      confirmButtonLabel: 'Jetzt erstellen',
      titleNewCompany: 'Neuen Mandanten erstellen?',
      titleDemoTenant: 'Demo-Mandant erstellen?',
      description: (userEmail) => `Mit dieser Aktion wird eine <strong>zusätzliche Firma</strong> für Deinen Account ${userEmail} angelegt.
            Dies ist sinnvoll, wenn Du <strong>mehrere Firmen oder Niederlassungen getrennt voneinander verwalten</strong> möchtest.</br></br>
            Der neue Mandant startet mit einer eigenständigen Testphase und neuen Daten.
            Weitere Informationen findest Du in unserem <a href=${addNewTenantFaqLink} target="_blank" rel="noopener noreferrer">FAQ-Bereich</a>`,
    },
    settings: {
      toasts: {
        success: {
          update: 'Einstellungen erfolgreich aktualisiert',
        },
        error: {
          update: 'Einstellungen konnten nicht aktualisiert werden',
        },
      },
    },
  },

  reporting: {
    documentTitle: 'Auswertungen',
    documentSubtitle: 'In diesem Bereich kannst Du unterschiedliche betriebswirtschaftliche Auswertungen, wie die allgemeine Geschäftsentwicklung, Gewinn- und Verlustrechnung (GuV) und weitere, erstellen. Bitte wähle dazu die gewünschte Auswertung im Dropdown-Menü aus.<br /> Die Umsatzsteuervoranmeldung (UstVA.) und Einnahmenüberschussrechnung (EÜR) findest Du <a href="/taxReports">hier</a>.',
    reportCreatedLabel: 'Die Auswertung wurde erfolgreich erstellt.',
    emptyReportText: 'Keine Daten vorhanden.',
    types: {
      euer: 'Einnahmen-Überschuss',
      guv: 'Gewinn und Verlust',
      incomeExpense: 'Einnahmen-Ausgaben',
      shortBwa: 'Allgemeine Geschäftsentwicklung',
      topArticle: 'Top Artikel',
      topCustomer: 'Top Kunden',
    },
    form: {
      placeHolderYear: 'Jahr',
      placeHolderInterval: 'Intervall',
      placeHolderPeriod: 'Zeitraum',
      placeHolderType: 'Auswertung',
      confirmButtonLabel: 'Erzeugen',
      intervalOptions: {
        month: 'Monat',
        quartal: 'Quartal',
      },
      toggleLabel: 'Kumuliert',
    },
    list: {
      title: 'Gespeicherte Auswertungen',
      loaderText: 'Auswertungen werden geladen',
      noReportsFound: 'Keine Auswertungen gefunden',
      columns: {
        date: 'Erstelldatum',
        period: 'Zeitraum',
        type: 'Auswertungstyp',
        actions: 'Aktionen',
      },
      cumulatedAddition: '(kumuliert)',
    },
    euer: {
      title: 'Einnahmen-Überschuss-Auswertung',
      fileName: 'Einnahmen-Überschuss',
      taxData: 'Steuerdaten',
      taxNumber: 'Steuer-Nr',
      taxOffice: 'Finanzamt',
      period: 'Zeitraum',
      operation: 'Transaktion',
      amount: 'Betrag',
      total: 'Summe',
      difference: 'Differenz',
      showAllValues: '0-Werte einblenden',
      infoText: 'Es fließen alle bezahlten Umsätze, sowie Ausgaben in diese Auswertung ein. Die Auswertung orientiert sich an der ',
      infoTextLink: '§ 4 Abs. 3 EStG',
      infoTextSuffix: ' Berechnung.',
      tooltipText: 'Die Einnahmenüberschussrechnung ist in § 4 Abs. 3 EStG, deshalb auch die 4-3-Rechnung genannt, ist die einfachste Art der Gewinnermittlung. Hier werden die Betriebseinnahmen den Betriebsausgaben gegenübergestellt.',
    },
    guv: {
      title: 'Gewinn / Verlust',
      fileName: 'Gewinn und Verlust',
      solidTitles: ['Zwischenergebnis', 'Betriebsergebnis', 'Finanzergebnis', 'Geschäftsergebnis', 'Einkommenssteuern', 'Steuerergebnis'],
      infoText: 'Kurz ',
      infoTextLink: 'GuV',
      infoTextSuffix: ' genannt, beinhaltet alle erzeugten Rechnungen, sonstigen Einnahmen, Ausgaben unabhängig von dem Zahlstatus.',
      tooltipText: 'Die Gewinn- und Verlustrechnung (GuV) ist ein wesentlicher Bestandteil des Jahresabschlusses eines Unternehmens und dient dazu, den Erfolg eines Unternehmens über einen bestimmten Zeitraum, in der Regel ein Geschäftsjahr, darzustellen. Sie zeigt, wie sich der Gewinn oder Verlust eines Unternehmens zusammensetzt.',
    },
    incomeExpense: {
      infoText: 'In dieser Auswertung werden die Einnahmen (brutto), Storno, Korrekturrechnungen, sowie Ausgaben (brutto) aufgelistet, ohne Unterteilung nach Steuerkategorien und einer Zahlungszuordnung.',
      fileName: 'Einnahmen-Ausgaben',
    },
    shortBwa: {
      infoText: 'Diese Auswertung beinhaltet alle Geschäftsvorfälle, wie Rechnungen und Ausgaben, unabhängig von ihrem Zahlstatus, es dient zur Übersicht und gleicht ein wenig einer kurzen Betriebswirtschaftlichen Auswertung.',
      fileName: 'Allgemeine Geschäftsentwicklung',
    },
    topArticle: {
      title: (date) => `Artikelauswertung Top Artikel - Zeitraum ${date}`,
      infoText: 'Die Auswertung zeigt Dir die Top Artikel gemessen an ihrer Stückzahl und Gesamtumsatz.',
      fileName: 'Top Artikel',
      columns: {
        number: 'Art.Nr.',
        title: 'Bezeichnung',
        sum_amount: 'Anzahl',
        sum_net: 'Nettoumsatz',
      },
    },
    topCustomer: {
      title: (date) => `Top Kunden - Zeitraum ${date}`,
      infoText: 'Deine Top-Kunden werden nach Umsatzvolumen sortiert angezeigt. Die Auswertung berücksichtigt erstellte Rechnungen, es ist nicht relevant, ob eine Zahlung hinterlegt wurde.',
      fileName: 'Top Kunden',
      columns: {
        number: 'Kunden-Nr.',
        name: 'Name',
        totalNet: 'Gesamtumsatz Netto',
        totalGross: 'Gesamtumsatz Brutto',
      },
    },
  },

  // messages, e.g. for toasts
  messages: {
    common: {
      toastHeadings: {
        success: 'Erfolgreich',
        error: 'Da ist etwas schief gelaufen',
        warning: 'Achtung!',
        info: 'Hinweis',
      },
      toastFinanceHeadings: {
        error: 'Da ist etwas schief gelaufen',
        messageHeaders: {
          success: 'Folgendes wurde von der Bank gemeldet',
          error: 'Folgender Fehler wurde von der Bank gemeldet',
          warning: 'Folgendes wurde von der Bank gemeldet',
          info: 'Folgender Hinweis wurde von der Bank gemeldet',
        },
        helpText: {
          success: '',
          error: 'Überprüfe Deine eingegebenen Login-Daten, solltest Du dennoch Hilfe benötigen wende Dich bitte an den Support.',
          warning: '',
          info: '',
        },
        unknowError: 'Unbekannter Fehler',
      },
      tokenInvalid: 'Token ist nicht gültig',
      imageUpload: {
        minWidthMessage: 'Das Bild muss mindestens 1 Pixel breit sein.',
        minHeightMessage: 'Das Bild muss mindestens 1 Pixel hoch sein.',
        maxWidthMessage: 'Das Bild darf maximal 1300 Pixel breit sein.',
        maxHeightMessage: 'Das Bild darf maximal 1300 Pixel hoch sein.',
        fileTypeMessage: 'Lade bitte eine .png/.jpg/.svg Datei hoch.',
      },
    },
  },

  appUpdateModal: {
    title: 'Wir haben gerade ein Update veröffentlicht',
    description: 'Damit Du weiter ohne Probleme arbeiten kannst, musst Du einmal die Seite aktualisieren.',
    buttonReloadLabel: 'Jetzt neu laden',
    buttonPostponeLabel: 'Später erneut erinnern',
  },

  agbHints: {
    privacyAndAgb: (privacyLink, agbLink) => <>Mit der Einrichtung des Kontos willigst Du in unsere {privacyLink} und {agbLink} ein.</>,
    privacy: 'Datenschutzrichtlinie',
    agb: 'AGB',
    mail: `Wir senden Dir Tipps & Infos zu ${productNameLong} per E-Mail. Durch Klick auf den Link am Ende jeder E-Mail kannst Du die Neuigkeiten wieder abbestellen.`,
  },

  staff: {
    columns: {
      recordDate: 'Datum',
      shouldTime: 'Soll',
      actualTime: 'Ist',
      differenceTime: 'Differenz',
    },
    errors: {
      forbidden: 'Deine Testphase oder Dein Abonnement ist abgelaufen.',
      companyExists: 'Diese Firma existiert bereits, bitte ändere Deinen Firmennamen.',
      passwordIncorrect: 'Das Passwort ist nicht korrekt.',
      failedToAction: 'Die Zeiterfassungsaktion konnte nicht ausgeführt werden.',
      colleagueNotFound: 'Der Mitarbeiter existiert nicht. Für die erste Nutzung besuche bitte das "Personal" Menü.',
      workingTimeModelMissing: 'Es wurde noch kein Arbeitszeitmodell hinterlegt.',
    },
    timeTracking: {
      timerStarted: 'Zeiterfassung erfolgreich gestartet.',
      timerStopped: 'Zeiterfassung erfolgreich beendet.',
      hours: 'Stunden',
      minutes: 'Minuten',
      trackFor: 'Zeit erfassen für:',
    },
    missingDataModal: {
      title: 'Bitte Angaben vervollständigen',
      subTitle: `Zur Nutzung Deines ${productNameLong} Personal`,
      required: 'Dies ist ein Pflichtfeld',
      submitButtonTooltip: '<b>Information:</b><br/><br/>Bitte fülle alle Felder aus um zu speichern.',
      submitButton: 'Jetzt starten',
      cancelButton: 'Abbrechen',
    },
    loading: 'Öffne Personal...',
  },

  pbbLog: {
    title: 'Error Log aktivieren',
    description: 'Zur Fehlerbehebung wird die Aufzeichnung des fehlerhaften Vorgangs in eine verschlüsselte Log-Datei gespeichert. Diese Log-Datei kann dann von unserem Support geprüft werden.',
    activated: 'Logging aktiv',
    avvTitle: 'Datenschutzbestimmungen',
    avv: 'Diese Datei kann abhängig vom aufgezeichneten Vorgang folgende personenbezogenen Daten enthalten: Umsatzabfrageergebnisse: Name, BIC, IBAN, Bankname des Auftraggebers und Empfängers, Wertstellungsdatum, Buchungsdatum, GV-Code, Verwendungszweck, Betrag, Anfangs- und Endsaldo, Geburtsdatum des Kontoinhabers. Überweisungen, Terminaufträge, Lastschriften, Daueraufträge: wie oben, zusätzlich Ausführungsdatum, die für die Transaktion genutzte und damit ungültige TAN. Wertpapiergeschäftsvorfälle: BIC, Bankname, Depotnummer, Depotinhaber, Depostand, Wertpapiere (Anzahl, ISIN, Wert, Wechselkurs). Zugang über HBCI: Benutzerkennung (meist identisch mit der Kontonummer), Kunden-ID. Kreditkartenkonten: wie Umsatzabfrageergebnisse, statt IBAN die Kreditkartennummer. PINs und gültige TANs sowie persönliche Zugangsdaten (wie HBCI-Schlüssel) werden in keinem Fall in der Log-Datei verzeichnet, sondern immer unkenntlich gemacht. Die übermittelten Daten werden vertraulich und ausschließlich zur Fehlerbehebung verwendet.',
    success: {
      [true]: 'Logging erfolgreich aktiviert',
      [false]: 'Logging erfolgreich deaktiviert',
    },
    modalTitle: 'Error Log ist aktiv',
    modalContent: 'Zur Zeit ist das Logging der Bankingfunktionen in Deinem Mandanten aktiv. Du kannst dies in den Einstellungen unter "Mein Konto" -> "Support" ändern',
    modalButton: 'Schließen',
  },
  ustIdVerification: {
    notInquired: 'nicht geprüft',
    notShared: 'nicht an die EU-Datenbank mitgeteilt',
    companyName: 'Firmenname',
    city: 'Ort',
    zipCode: 'PLZ',
    street: 'Straße / Hausnr.',
    error: 'USt. ID Prüfung nicht erfolgreich',
    title: 'Qualifizierte Bestätigungsanfrage USt. ID',
    description: {
      a: 'Mit der qualifizierten Bestätigungsanfrage wird abgefragt, ob die mitgeteilten Angaben (Firmenname, Ort, PLZ, Straße / Hausnr.) mit der hinterlegten',
      b: 'im jeweiligen EU-Mitgliedsstaat übereinstimmen. Im Vorfeld wurde direkt eine einfache Bestätigungsanfrage gestellt.',
    },
    columnLeft: 'USt. ID',
    columnRight: 'ist gültig',
  },
  myAccount: {
    title: 'Konto verwalten',
    myPlans: 'Meine Pläne',
    popover: {
      items: {
        manage: 'Konto verwalten',
        faq: 'FAQ',
        aiSettings: 'KI-Agent MAX',
        logout: 'Abmelden',
      },
    },
    userProfile: {
      drawerTitle: 'Nutzerdaten',
      info: 'Hier lassen sich neben Deinen Anmeldedaten auch Kontakt E-Mail-Adresse sowie Benachrichtigungen verwalten.',
      roles: {
        OWNER: {
          title: 'Besitzer',
          content: 'Du hast den Haupt-Account erstellt und besitzen alle Rechte.',
        },
        ADMIN: {
          title: 'Administrator',
          content: 'Du hast Zugriff auf alle Bereiche der Anwendung.',
        },
        FINANCE: {
          title: 'Finanzen',
          content: 'Du hast Lese & Schreibrechte im Banking, Kasse, Ausgaben und Steuerbereichen.',
        },
        SALES: {
          title: 'Vertrieb & Einkauf',
          content: 'Du hast Lese & Schreibrechte für Kunden, Artikel, Angebote, Rechnungen, Aufträge, Lieferanten, Bestellungen, Projekte, Zeiterfassung und Abos.',
        },
        TAX_CONSULTANT: {
          title: 'Steuerberater',
          content: 'Du hast Leserechte im Banking, Kasse, Ausgaben und Steuerbereichen. Darf Steuerberaterexport ausführen.',
        },
        DOCUMENTS: {
          title: 'Dokumente',
          content: 'Du hast Lese & Schreibrechte im Dokumente Bereich.',
        },
        STAFF: {
          title: 'Personal',
          content: 'Du hast Zugriff auf den Personal Bereich.',
        },
        NO_TENANT_ROLE: {
          title: 'Keine Rolle',
          content: 'Du bist momentan keiner Rolle zugewiesen.',
        },
      },
    },
    userSettings: {
      title: 'Nutzerdaten',
      headline: 'Kontodaten',
      accountInfo: {
        headline: 'Nutzerdaten & Passwort',
        text: 'Hier kannst Du die Nutzerdaten anpassen, ein Profilbild hinterlegen und Dein Passwort ändern.',
      },
      contact: {
        headline: 'E-Mail & Benachrichtigungen',
        text: 'Hinterlege hier Deine Kontaktadresse und stelle Deine Benachrichtigungen ein.',
        emailNotification: (email) => {
          if (email) return `Du erhältst die Benachrichtigungen an folgende E-Mail-Adresse: <b>${email}</b>`
        },
        title: 'E-Mail-Adresse',
      },
      avatar: {
        imageUploadSuccessMessage: 'Das Profilbild wurde erfolgreich hochgeladen',
        imageUploadDefaultError: 'Beim Hochladen des Profilbildes ist ein Fehler aufgetreten',
        imageDeleteSuccessMessage: 'Das Profilbild wurde erfolgreich gelöscht',
        imageDeleteDefaultError: 'Beim Löschen des Profilbildes ist ein Fehler aufgetreten',
        maxHeightImageError: 'Die Höhe von %filename überschreitet die zulässige maximale Höhe von %spx.',
        maxWidthImageError: 'Die Breite von %filename überschreitet die zulässige maximale Breite von %spx.',
        minHeightImageError: 'Die Höhe von %filename unterschreitet die zulässige minimale Höhe von %spx.',
        minWidthImageError: 'Die Breite von %filename unterschreitet die zulässige minimale Breite von %spx.',
        imageCropSelection: (height, width) => `Ausgewählte Bildgröße: ${width} x ${height} px`,
        imageCropError: 'Beim Zuschneiden des Profilbildes ist ein Fehler aufgetreten',
        imageUpload: 'Profilbild hochladen',
        selectUpload: 'Bild herziehen oder',
        selectUploadSpecial: ' auswählen',
        imageUploadHint: 'Bildgröße maximal 1300x1300px, erlaubt sind jpg/jpeg- und png-Dateien',
      },
    },
    informationBoxSettings: {
      description: 'Hier können Kontaktdaten Deiner Firma Steuereinstellungen Kontenplan und indiv. Nummernkreise angepasst werden.',
      edit: 'bearbeiten',
      initials: {
        title: 'Individuelle Kennzeichnung',
        hint: 'Hinweis: Die Kennzeichnung Deines Mandanten erscheint im Menü an Deinem Profilbild. Anhand dessen erkennst Du, in welchem Mandanten Du Dich derzeit befindest.',
        initialsLabel: 'Initialen',
        colorPickerLabel: 'Farbauswahl',
      },
      accountInfo: {
        headline: 'Kontakt & Kommunikation',
        text: 'Hier kannst Du die Kontakt- und Kommunikationsdaten Deiner Firma hinterlegen, einsehen und anpassen.',
        contactTitle: 'Kontaktdaten',
      },
      tax: {
        headline: 'Steuereinstellungen',
        text: 'Gib Deine Steuerdaten und Deine Umsatzbesteuerung ein.',
      },
    },
    banner: {
      trial: {
        remainingDaysText: (remainingDays) => `TAG${remainingDays === 1 ? '' : 'E'}`,
        text: 'bis Deine Testphase endet',
        description: 'Wähle jetzt einen Plan aus und starte direkt mit orgaMAX durch.',
        expired: 'Testphase abgelaufen - mehr Zeit benötigt, rufe uns an.',
      },
    },
    billingSettings: {
      title: 'Abrechnungsinformationen',
      text: 'Passe Deine Abrechnungsinformationen an.',
    },
    supportSettings: {
      title: 'Support',
      errorLog: 'ERROR-LOG',
      errorLogYes: 'Logging aktiv',
      errorLogNo: 'Logging nicht aktiv',
      remote: 'FERNWARTUNG',
      remoteYes: 'Genehmigung erteilt',
      remoteNo: 'Genehmigung nicht erteilt',
      teamViewer: 'TeamViewer',
      folderPermissions: {
        headline: 'Ordner-Berechtigungen',
        description: 'Setze alle Ordner-Berechtigungen dieses Mandanten zurueck, wenn Du den Testzustand wiederherstellen moechtest.',
        buttonLabel: 'Ordner-Berechtigungen zurücksetzen',
        loadingLabel: 'Wird zurueckgesetzt...',
        confirmTitle: 'Ordner-Berechtigungen zurücksetzen',
        confirmDescription: 'Moechtest Du wirklich alle Ordner-Berechtigungen dieses Mandanten zurücksetzen?',
        successMessage: 'Ordner-Berechtigungen wurden erfolgreich zurueckgesetzt.',
        errorMessage: 'Ordner-Berechtigungen konnten nicht zurueckgesetzt werden.',
        auditLog: {
          title: count => `Berechtigungsprotokoll (letzte ${count} Einträge)`,
          loadingState: 'Berechtigungsprotokoll wird geladen...',
          columns: {
            date: 'Datum',
            user: 'Benutzer',
            action: 'Aktion',
            folder: 'Ordner',
            targetUser: 'Zielbenutzer',
            permission: 'Berechtigung',
          },
        },
      },
      remoteInfoSettings: {
        headline: 'Fernwartung & Datenschutz',
        text: 'Hier kannst Du die Genehmigung zur Fernwartung aktivieren und der AVV zustimmen.',
      },
      privacyInfoSettings: {
        headline: 'Error Log',
        text: 'Hier kannst Du die zur Vereinfachung der Fehlerbehebung das Logging aktivieren.',
      },
    },
    addTenantModal: {
      title: 'Willst Du jetzt eine neue Firma anlegen?',
      description: 'Lege direkt eine neue Firma an, steuere Deine Geschäftsprozesse und optimiere Deine Buchhaltung.',
      confirmButton: 'Jetzt anlegen',
    },
    subscriptionUpgrade: {
      comingSoon: {
        title: 'Produkt nicht verfügbar',
        description: 'Dieses Produkt steht Dir in Kürze zur Verfügung.',
      },
      notAvailableInDemo: 'Im Demo Mandaten nicht verfügbar',
      startTrialVersion: 'Testversion starten',
      actualSubscription: 'Aktueller Plan:',
      trialVersion: 'Testversion',
      noPlan: 'keiner',
      trialExpiredAt: (end) => `Ablaufdatum: <b>${end}</b>`,
      title: 'Plan upgraden',
      hint: '* Alle Preise sind netto zzgl. USt. Das Angebot richtet sich nur an Unternehmer (§ 14 BGB) und nicht an Verbraucher (§ 13 BGB).',
      upgradePlan: 'Plan upgraden',
      buyPlan: 'Plan buchen',
      extendPlan: 'Plan verlängern',
      callSalesPhoneNumber: 'Jetzt anrufen',
      showDetails: 'Details/Pläne vergleichen',
      buyProduct: 'Produkt buchen',
      upgradeUnavailable: {
        title: 'Du möchtest Deinen Plan erweitern?',
        text: 'Dein Plan unterscheidet sich von unseren aktuellen Angeboten. Kontaktiere uns – gemeinsam finden wir die passende Lösung.',
        cta: 'Beratung vereinbaren',
        contact: 'Alternativ erreichst Du uns unter <a href="tel:+49523190000">05231 90 000</a> oder <a href="mailto:info@orgamax.de">info@orgamax.de</a>.',
      },
      currentSubscription: 'AKTIV',
      cancelledSubscription: (date) => `Gekündigt zum ${date}`,
      trialModal: {
        title: (type) => `Testversion ${productName} ${typeMap[type]}`,
        description: (type) => `Durch einen Klick auf "Testversion starten" kannst Du Deine 14-tägige Testphase für das Produkt "${typeMap[type]}" beginnen. Du brauchst Dir keine Sorgen um eine automatische Umwandlung in ein Abonnement zu machen – Dein Testzeitraum endet automatisch, ohne weitere Verpflichtungen.`,
        confirmButtonLabel: 'Testversion starten',
      },
      documentTrialModal: {
        title: 'Dokumente & Belege einfach digital organisieren',
        subTitle: 'Damit Du Papierchaos vermeidest & stets den Überblick behältst – über Deinen Webbrowser oder mobil in der App',
        bulletPoints: [
          { icon: 'documents', text: 'Organisiert', subText: 'Alle Dokumente an einem Ort – per App, Mail oder Drag & Drop' },
          { icon: 'brain', text: 'Intelligent', subText: 'Dank Texterkennung Dokumente schnell wiederfinden' },
          { icon: 'clock', text: 'Zeitsparend', subText: 'Dokumente per Scan-App aufnehmen & übertragen' },
          { icon: 'hand', text: 'Mühelos', subText: 'Digitale Übergabe an Steuerbüro inkl. DATEV-Cloud' },
        ],
        confirmButtonLabel: 'Jetzt Testversion starten',
        cancelButtonLabel: 'Später',
        eventCategory: 'Single-Testauswahl',
        eventLabel: (productType) => `Produktauswahl abgeschlossen ${productType}`,
      },
    },
    getSubscriptionTitle: (title) => `orgaMAX (${title})`,
    danglingSubscription: {
      modalTitle: 'Erworbenes Abonnement zuweisen',
      modalSubtitle: 'Du kannst hier die Firma oder Mandanten bestimmen, für welche das erworbene Produkt in der jeweiligen Planstufe gebucht werden soll. Du weist das gewählte Produkt dem entsprechenden Mandaten zu, Du kannst dies auch später vornehmen.',
      subscription: 'Erworbener Plan/Abo',
      tenant: 'Mandant/Unternehmen',
      startAt: 'Erworben am:',
      assignLater: 'Später zuweisen',
      assignOpenSubscriptions: 'Erworbene Abonnements zuweisen',
      assignSuccess: 'Das Abonnement wurde erfolgreich zugewiesen',
      assignError: {
        'subscriptionType.type': {
          EXISTS: 'Diesem Unternehmen wurde bereits ein Plan/Abo dieses Typs zugewiesen. Du kannst den Plan nur im Store updaten.',
        },
        subscription: {
          EXISTS: 'Das Abonnement wurde bereits zugewiesen.',
        },
        subscriptionType: {
          NOT_FOUND: 'Der Plan wurde nicht gefunden, bitte kontaktiere den Support',
        },
        package: {
          NOT_FOUND: 'Der Plan wurde nicht gefunden, bitte kontaktiere den Support',
        },
      },
    },
    subscriptionDetails: {
      title: 'In Deinem Abo enthalten',
      extensions: 'Erweiterungen',
      addExtensions: 'Erweiterungen hinzufügen',
      selectedPlan: 'Ausgewählter Plan',
      planOverview: 'Detaillierte Planübersicht',
      daysCountdown: (countDown) => `${countDown} Tage kostenlos`,
      testExpiry: (date) => `Testphase endet am ${date}`,
      testExpiredOn: (date) => `Testphase beendet am ${date}`,
      nextRenewal: (date) => `Nächste Erneuerung ${date}`,
      testVersion: 'Testversion',
      testExpired: 'Testphase ausgelaufen',
      paymentMethod: 'Gewählte Zahlweise',
      period: {
        month: 'Monatlich',
        year: 'Jährlich',
      },
      detailsBadge: {
        testExpired: 'Testversion abgelaufen',
        testEndsToday: 'Testphase endet heute',
        testEndsOn: (date) => `Testphase endet am ${date}`,
        expiredOn: (date) => `Gekündigt am ${date}`,
      },
      emptyModulesLabel: 'Aktuell leider keine Erweiterung verfügbar',
      billingDataTitle: 'Abrechnungsinformationen',
      descriptions: {
        main: 'Erledige Dein komplettes Auftragswesen und die vorbereitende Buchhaltung mit wenigen Klicks – inklusive App (iOS & Android).',
        document: 'Digitalisiere, organisiere und archiviere Dokumente bequem zu jeder Zeit und von überall – inklusive App (iOS & Android).',
        staff: 'Erledige Personalprozesse sicher digital – von der Personalakte über Urlaubsplanungen bis hin zu Reisekostenabrechnungen.',
      },
    },
    accountPopover: {
      upgradeButton: 'Jetzt upgraden',
      startTrialButton: 'Jetzt freischalten',
      trialVersionTitle: 'Testversion',
      planTitle: (type) => `Plan ${type.toUpperCase()}`,
      planSubtitle: (remainingDays) => `Noch ${remainingDays} ${remainingDays === 1 ? 'Tag' : 'Tage'} verfügbar`,
    },
  },

  changeDetectionDlg: {
    claim: 'Achtung, Änderungen nicht gespeichert',
    subClaim: 'Deine Änderungen sind noch nicht gespeichert. Wenn Du diesen Bereich verlässt, gehen Deine Eingaben verloren.<br /><br />Möchtest Du diesen Bereich verlassen und die Änderungen verwerfen?',
  },
  detailLabels: {
    openAmount: 'Offener Betrag',
    amount: 'Betrag',
    alreadyPaid: 'Bereits bezahlt',
    invoiceDate: 'Rechnungsdatum',
    credited: 'gutgeschrieben',
    cancelled: 'storniert',
    fullyPaid: 'vollst. bezahlt',
    partlyPaid: 'teilw. bezahlt',
    notPaid: 'nicht bezahlt',
    draft: 'Entwurf',
    number: 'Nr.',
    dateTitle: 'Datum',
    paymentState: 'Zahlungsstatus',
    offerDate: 'Angebotsdatum',
    title: 'Titel',
    start: 'Start',
    budget: 'Budget',
    restBudget: 'Restbudget',
    paid: 'Bezahlt',
    next: 'Nächste',
    recurringEnd: 'Abo-Ende',
    recurring: 'Wiederholung',
    recurringStart: 'Abo-Start',
    date: 'Datum',
    dunningAmount: 'Mahngebühr',
    articleCount: 'Artikelanzahl',
    customer: 'Kunde',
  },

  back,
  next: 'Vor',

  documentPaymentDrawer: {
    title: 'Dokument bezahlen',
    intro: 'Vergewissere Dich, dass die unten stehenden Informationen korrekt sind und wähle „Weiter“.',
    summary: 'Scanne den QR-Code aus Deiner Banking-App heraus, um das Überweisungsformular schnell und einfach auszufüllen.',
    labels: {
      accountHolder: 'Kontoinhaber',
      iban: 'IBAN',
      bic: 'BIC (optional)',
      bicSummary: 'BIC',
      totalAmount: 'Betrag',
      purpose: 'Verwendungszweck',
    },
    errors: {
      accountHolder: 'Der Kontoinhaber muss ausgefüllt werden',
      iban: 'Die IBAN muss ausgefüllt werden',
      negativeAmount: 'Der Betrag muss größer als 0 sein.',
    },
  },
  marketing: {
    trialButton: 'Produkt testen',
    purchaseButton: 'Jetzt buchen',
    upgradeButton: 'Jetzt upgraden',
    addToShoppingCartButton: 'In den Warenkorb',
    startTrialButton: 'Jetzt 14 Tage kostenlos testen!',
    selectSubscription: 'Plan auswählen',
    includedPlan: 'In Deinem Plan enthalten',
    extensions: 'Erweiterungen',
    headerVideoTitle: 'Das sagen unsere Kunden ...',
    headerMarketingTitle: 'Beratungstermin',
    marketingSubHeaderTitle: 'Wir beraten Dich gern!',
    marketingDescription: 'Deine Fragen rund um alle orgaMAX Produkte beantworten wir gerne persönlich. Wähle einfach einen für Dich passenden Termin aus.',
    purchaseMeetingButtonMain: 'Beratungstermin Buchhaltung/Personal',
    purchaseMeetingButtonDocuments: 'Beratungstermin Dokumente',
    learnMore: 'Mehr erfahren',
    moreInfo: 'Mehr Informationen',
    showDetails: 'Funktionsvergleich',
    trialSubscription: 'Testphase',
    reducedPriceDuration: (reducedPriceDuration, regularPrice, reducedPricePeriod) => `Ab dem ${reducedPriceDuration + 1}. ${reducedPricePeriod === dateConstants.YEAR ? 'Jahr' : 'Monat'} ${regularPrice}`,
    trialRemaining: (endDate) => endDate ? `Testphase endet am ${endDate}` : 'Testphase beendet',
    planStarted: (beginDate) => `Aktiver Plan seit ${beginDate}`,
    planCanceled: (endDate) => `Plan gekündigt zum ${endDate}`,
    itemUsageLimit: (used, limit) => `${used} von ${limit}`,
    main: {
      description: 'Rechnungen & Finanzen mühelos verwalten',
      subtitle: {
        subtitle0: '(E-)Rechnungen, Angebote & Mahnungen',
        subtitle1: 'Zahlungen & Belege immer im Blick',
        subtitle2: 'Steuerrelevante Daten aufbereiten & senden',
      },
    },
    document: {
      description: 'Dokumente sicher verwalten & schnell finden',
      subtitle: {
        subtitle0: 'Dateien einfach & schnell digitalisieren',
        subtitle1: 'Belege & Verträge sicher speichern',
        subtitle2: 'Automatische Texterkennung & smarte Suche',
      },
    },
    staff: {
      description: 'Alle Personaldaten an einem zentralen Ort',
      subtitle: {
        subtitle0: 'Digitale Personalakte',
        subtitle1: 'Arbeitszeiten rechtskonform erfassen',
        subtitle2: 'Urlaubszeiten transparent planen',
      },
    },
    taxconsultant: {
      description: 'Die Collaboration-Lösung für Dich und Deine Mandanten. Schaue nicht zurück, sondern gestalte die Zukunft aktiv mit. Belegaustausch, direkte Kommunikation und starke Bindung sind nur drei der vielfältigen Möglichkeiten, die Dir orgaMAX Berater bietet.',
    },
    desktop: {
      description: 'Mit orgaMAX shareHub ist es ein Leichtes, Informationen und Geschäftsdokumente elektronisch zu teilen, anstatt sie auszudrucken.',
    },
    purchaseContent: {
      plan: 'Aktueller Plan:',
      users: 'Benutzer:',
      beginDate: 'Beginn Plan:',
      expireDate: 'Nächste Verlängerung:',
      endTrialDate: 'Ende Testphase:',
      storage: 'Speicherplatz:',
    },
    buttons: {
      comparePlans: 'Pläne vergleichen',
    },
    modules: {
      title: 'Verfügbare Add-ons',
    },
  },
  eventLog: {
    empty: 'Für diese Filtereinstellungen sind keine Einträge vorhanden.',
    filter: {
      all: 'Alle',
      user: 'Benutzer',
      documents: 'Dokumente',
    },
    action: {
      approve: 'Konto bestätigt',
      acceptInvitation: 'Einladung angenommen',
      register: 'Registrierung',
      login: 'Anmeldung',
      create: 'Erstellen',
      delete: 'Löschen',
      update: 'Bearbeiten',
      rename: 'Umbenennen',
      switchTenant: 'Mandantenwechsel',
    },
    data: {
      firstName: 'Vorname',
      lastName: 'Nachname',
      fileId: 'Datei-ID',
      layoutSettings: 'Layout-Einstellungen',
      companyName: 'Mandant',
    },
    type: {
      user: 'Benutzer',
      document: 'Dokument',
    },
    labels: {
      datepicker: 'Zeitraum',
      typeSelect: 'Art',
    },
    messages: {
      noMessage: 'Keine Nachricht vorhanden',
      user: {
        approve: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat sein Konto bestätigt.`,
        acceptInvitation: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat eine Einladung angenommen.`,
        register: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat sich registriert.`,
        login: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat sich eingeloggt.`,
        rename: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat seinen Namen geändert.`,
        switchTenant: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat sich in Mandant ${data?.companyName} eingeloggt.`,
        delete: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat einen Benutzer (${data?.userId}) gelöscht.`,
        editLayoutSettings: ({ user }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat die Menü-Layout-Einstellungen geändert.`,
      },
      document: {
        create: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat ein Dokument (${data.fileId}) hochgeladen.`,
        update: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat ein Dokument (${data.fileId}) bearbeitet.`,
        delete: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat ein Dokument (${data.fileId}) gelöscht.`,
      },
      invoice: {
        create: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat eine Rechnung (${data.invoiceId}) erstellt.`,
        update: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat eine Rechnung (${data.invoiceId}) bearbeitet.`,
        delete: ({ user, data }) => `Benutzer ${user?.name || user?.email}(${user?.id}) hat eine Rechnung (${data.invoiceId}) gelöscht.`,
      },
    },
  },

  csat: {
    success: 'Vielen Dank für Dein Feedback',
    rating: {
      satisfied: 'Sehr zufrieden',
      unsatisfied: 'Unzufrieden',
    },
    inputs: {
      textInput: {
        placeholder: `Mit Deinem Feedback hilfst Du uns ${productName} zu verbessern.`,
      },
    },
    buttons: {
      confirm: 'Feedback absenden',
      doNotEvaluate: 'Nicht bewerten',
    },
    features: {
      createInvoice: {
        title: `Wie zufrieden bist Du mit dem Prozess der Rechnungserstellung bei ${productName} ${mainProductNaming}?`,
        subtext: 'Berücksichtige dabei Aspekte wie die Einfachheit des Verfahrens, die Klarheit der bereitgestellten Informationen und die Schnelligkeit, mit der Deine Rechnung erstellt wurde.',
      },
      uploadDocument: {
        title: 'Wie zufrieden bist Du mit dem Hochladen von Dokumenten?',
        subtext: 'Hast Du Verbesserungsvorschläge oder sind Dir Probleme aufgefallen, die wir beim Hochladen von Dokumenten beheben sollten?',
      },
      setDocumentFilter: {
        title: `Wie zufrieden bist Du mit den Filtereinstellungen in ${productNameDocument}?`,
        subtext: 'Hast Du Verbesserungsvorschläge oder sind Dir Probleme aufgefallen, die wir bei den Filtereinstellungen beheben sollten?',
      },
      analyzeDocument: {
        title: 'Wie zufrieden bist Du mit der Analyse Deines Dokuments?',
        subtext: 'Hast Du Verbesserungsvorschläge oder sind Dir Probleme aufgefallen, die wir bei der Analyse von Dokumenten beheben sollten?',
      },
      manageFolders: {
        title: 'Wie zufrieden bist Du mit der Möglichkeit, Deine Dokumente in Ordnern zu verwalten?',
        subtext: 'Hast Du Verbesserungsvorschläge oder sind Dir Probleme aufgefallen, die wir bei der Ordnerverwaltung beheben sollten?',
      },
      searchDocuments: {
        title: `Wie zufrieden bist Du mit der Suchfunktion in ${productNameDocument}?`,
        subtext: 'Hast Du Verbesserungsvorschläge oder sind Dir Probleme aufgefallen, die wir beim Suchen von Dokumenten beheben sollten?',
      },
    },
  },
  missingCompanyData: {
    title: 'Notwendige rechtliche Angaben',
    subTitle: 'Deine Kunden müssen natürlich wissen, wer der Absender der Rechnungen ist.',
    subTitle2: 'Diese Angaben werden auch automatisch in Deinem Briefkopf angezeigt.',
    submitButton: 'Speichern & Vorgang weiter bearbeiten',
    companyAddressContainer: '<b>Information:</b> Zur Erstellung eines ordnungsgemäßen Geschäftsdokuments fehlen noch relevante Angaben. <b>Jetzt ergänzen!</b>',
    submitButtonTooltip: '<b>Information:</b><br/><br/>Die eingegebenen Daten sind nicht korrekt. Bitte überprüfe die Steuernummer und das Finanzamt.',
    placeholder: 'Bitte eingeben',
  },
  onboardingTesterData: {
    steps: {
      title: 'Firmendaten vervollständigen',
      subTitle: 'Für die Erstellung ordnungsgemäßer Geschäftsdokumente musst Du Deinen Kunden mitteilen,',
      subTitle2: 'wer der Absender des Vorgangs ist. Deine Angaben werden automatisch im Briefpapier hinterlegt.',
      banking: {
        title: 'Fußzeile hinzufügen',
        subTitle: 'In der Fußzeile des Geschäftsdokumentes werden Deine Bankverbindung und',
        subTitle2: 'Steuerangaben hinterlegt.',
      },
      layout: {
        title: 'Layout auswählen',
        subTitle: 'Wähle aus mit welchem Layout Deine Geschäftsdokumente erstellt werden sollen.',
        subTitle2: 'Du kannst Deine Auswahl nachträglich und je Vorgang verändern.',
      },
      logo: {
        title: 'Logo hochladen',
        subTitle: 'Gestalte die Kopfzeile nach Deinen eigenen Wünschen. Lade Dein Logo hoch und platziere es an beliebiger Stelle. Du kannst Deine Gestaltung nachträglich jederzeit anpassen.',
      },
      taxSettings: {
        title: 'Umsatzsteuer ausweisen',
        subTitle: 'Wähle bitte abschließend aus, ob Du auf Deinen Geschäftsdokumenten die Umsatzsteuer',
        subTitle2: 'ausweist und wie diese fällig wird. Du kannst die Auswahl nachträglich verändern.',
      },
    },
    leftContent: {
      backButton: 'Zurück',
      skipButton: 'Überspringen',
      saveAndNextButton: 'Speichern und weiter',
      saveAndFinishButton: 'Speichern und fertigstellen',
    },
    startModal: {
      leftTitle: 'Dein erster Geschäftsvorgang',
      leftDescription: 'Mit orgaMAX schreibst Du Rechnungen, Angebote und weitere Geschäftsvorgänge mühelos.',
      rightTitle: 'Jetzt Vorlage einrichten',
      rightDescription1: 'Bitte richte zuerst Deine Vorlage ein, um professionelle und rechtskonforme Geschäftsdokumente zu erstellen.',
      rightDescription2: 'Unser Assistent führt Dich in wenigen Schritten durch Layout und Pflichtangaben, damit Deine Dokumente genau Deinen Vorstellungen entsprechen.',
      button: 'Vorlage einrichten',
    },
    companyDataTitle: 'Unternehmen',
    companyDataPhone: 'Telefonnummer',
    companyDataContact: 'Kontakt',
    bankAccount: 'Bankverbindung',
    taxTitle: 'Steuerangaben',
    bankingTooltip: 'Trage Deine Bankverbindung ein, um sie auf dem Briefpapier anzuzeigen und den Zahlungsprozess für Deine Kunden zu vereinfachen. Bei E-Rechnungen ist die Angabe gemäß § 14 UStG eine Pflichtangabe.',
    taxTooltip: 'Gemäß § 14 UStG musst Du für ordnungsgemäße Geschäftsvorgänge eine Steuernummer oder USt-ID angeben. Bei inländischen Geschäften reicht die Steuernummer, während für den EU-Handel eine USt-ID erforderlich ist. Beide Nummern findest Du auf Deinem Steuerbescheid oder beim Finanzamt. Fehlende Angaben kannst Du später ergänzen.',
    companyNameTooltip: {
      description: 'Trage Deinen Firmennamen ein, um Dein Unternehmen auf Dokumenten erkennbar zu machen. Falls Du keinen offiziellen Firmennamen hast, nutze stattdessen Deinen Vor- und Nachnamen.',
      showInfoIcon: true,
    },
    phoneNumberTooltip: {
      description: 'Füge Deine Telefonnummer ein, um Kunden die Kontaktaufnahme zu erleichtern. Die Rufnummer wird direkt auf dem Briefpapier vermerkt.',
      showInfoIcon: true,
    },
  },
  wsOffline: 'HINWEIS: Hintergrundaktualisierungen sind derzeit nicht verfügbar. Das kann an einem Netzwerkproblem liegen, beeinträchtigt Deine Arbeit jedoch nicht.',
  appDeviceModal: {
    sendLinkAsEmail: 'Link an E-Mail senden',
    sentLinkAsEmail: 'Link wurde per Mail verschickt',
    continue: 'Trotzdem weiter arbeiten',
    cookies: 'Cookies',
    mainAndDocument: {
      visit: 'Damit Du orgaMAX Buchhaltung und orgaMAX Dokumente optimal nutzen kannst, besuche <b>app.orgamax.de</b> an einem Computer oder Laptop.',
      download: 'Um die Anwendung auf Deinem Smartphone oder Tablet nutzen zu können, lade nun die jeweilige App herunter:',
    },
    main: {
      title: 'Womit willst Du weitermachen?',
      visit: 'Damit Du orgaMAX mit allen Funktionen optimal nutzen kannst, empfehlen wir die Verwendung eines Computers oder Laptops.',
      download: 'Als perfekte Ergänzung empfehlen wir die orgaMAX App. Abgestimmt auf den Einsatz unterwegs.',
    },
    document: {
      title: 'Womit willst Du weitermachen?',
      visit: 'Damit Du Dein DMS <b>schnell, einfach und mit allen Funktionen</b> erleben kannst, nutze orgaMAX am <b>Computer oder Laptop.</b>',
      download: 'Lade nun die App herunter, um orgaMAX Dokumente optimal auf Deinem Smartphone oder Tablet nutzen zu können:',
    },
    staff: {
      title: 'Jetzt in Browseransicht wechseln',
      visit: 'Damit Du Deine Personalverwaltung <b>schnell, einfach und mit allen Funktionen</b> erledigen kannst, nutze orgaMAX am <b>Computer oder Laptop.</b>',
    },
    productTitles: {},
    downloadLabels: {
      title: 'orgaMAX App herunterladen',
      download: 'Abgestimmt auf den Einsatz unterwegs.',
      everyThingYouNeed: 'Als perfekte Ergänzung empfehlen wir die orgaMAX App. ',
    },
  },
  multiSelect: {
    types: {
      label: {
        all: 'Alle',
        user: 'Benutzer',
        client: 'Mandant',
        customer: 'Kunden',
        supplier: 'Lieferant',
        external: 'Extern',
      },
    },
    addForm: {
      inputs: {
        name: 'Name',
        email: 'E-Mail',
      },
      error: {
        email: 'Bitte gib eine valide E-Mail ein.',
      },
    },
    add: 'hinzufügen...',
    placeholder: 'Durchsuchen oder hinzufügen',
    error: {
      default: 'Beim Laden der Daten ist ein Fehler aufgetreten.',
    },
  },
  htmlInput: {
    tooltipLabels: {
      pageBreak: 'Seitenumbruch einfügen',
      placeholder: 'Platzhalter einfügen',
      mjmlButton: 'Button einfügen',
      underline: 'Unterstrichen',
      bold: 'Fett',
      italic: 'Kursiv',
      fontSize: 'Schriftgröße',
      fontColor: 'Textfarbe',
      fontBackgroundColor: 'Texthervorhebung',
      link: 'Link hinzufügen/bearbeiten',
      alignment: 'Ausrichtung',
      bulletedList: 'Aufzählungsliste',
      numberedList: 'Nummerierte Liste',
      undo: 'Rückgängig',
      redo: 'Wiederherstellen',
      alignmentOptions: {
        left: 'Links',
        center: 'Zentriert',
        right: 'Rechts',
        justify: 'Blocksatz',
      },
    },
    fontColor: {
      removeColorButton: 'Keine',
      removeColorButtonTooltipTitle: 'Farbe entfernen',
      cancelButton: 'Abbrechen',
      saveButton: 'OK',
      textFieldPlaceholder: 'z.B. "#7cb5ec"',
    },
    fontBackgroundColor: {
      removeColorButton: 'Keine',
      removeColorButtonTooltipTitle: 'Hervorhebung entfernen',
      cancelButton: 'Abbrechen',
      saveButton: 'OK',
      textFieldPlaceholder: 'z.B. "#ffff00"',
    },
    link: {
      editLinkAddTitle: 'Link hinzufügen',
      editLinkEditTitle: 'Link bearbeiten',
      editLinkTextInputLabel: 'Text',
      editLinkHrefInputLabel: 'URL',
      editLinkCancelButtonLabel: 'Abbrechen',
      editLinkSaveButtonLabel: 'Speichern',
      viewLinkEditButtonLabel: 'Bearbeiten',
      viewLinkRemoveButtonLabel: 'Entfernen',
    },
    extensions: {
      pageBreak: {
        label: 'Seitenumbruch',
      },
      orderedList: {
        type: {
          decimal: 'Zahlen',
          decimalWithLeadingZero: 'Zahlen mit führender Null',
          upperAlpha: 'Buchstaben (groß)',
          lowerAlpha: 'Buchstaben (klein)',
          upperRoman: 'Römisch (groß)',
          lowerRoman: 'Römisch (klein)',
        },
      },
      mjmlButton: {
        edit: {
          title: 'MJML Button bearbeiten',
          label: 'Button Text',
        },
      },
    },
  },
  codeListSelect: {
    error: {
      default: 'Beim Laden der verfügbaren Optionen ist ein Fehler aufgetreten.',
    },
  },
  completionState: {
    legend: {
      title: 'Erklärung Komplettierungsgrad',
      description: 'Der Komplettierungsgrad dient Dir zur besseren Übersicht und Prüfung auf Vollständigkeit für Belege. Für eine optimale Buchführung sollten alle Bereiche gefüllt sein.',
      subtitle: 'Legende Grafik',
      allCompletedLabel: 'Alle buchhalterischen Inhalte sind vorhanden',
      verifyState: 'Durch KI Analyse erkannt, nicht geprüft',
      item: {
        done: 'Vorhandene Beleginhalte',
        verify: 'KI erkannte Beleginhalte, zu prüfen',
      },
    },
    receipt: (done) => `Beleg ${done ? 'wurde' : 'nicht'} hochgeladen`,
    applicant: (done) => `Lieferant ${done ? '' : 'nicht'} vorhanden`,
    number: (done) => `Beleg-Rechnungsnr. ${done ? '' : 'nicht'} vorhanden`,
    receiptDate: (done) => `Belegdatum ${done ? '' : 'nicht'} vorhanden`,
    deliveryDate: (done) => `Liefer-/Leistungsdatum ${done ? '' : 'nicht'} vorhanden`,
    taxCategory: (done) => `Steuerkategorie ${done ? '' : 'nicht'} ausgewählt`,
    total: (done) => `Betrag ${done ? '' : 'nicht'} vorhanden`,
  },
  navigationButtons: {
    previous: (type) => `Vorherige ${type === RECEIPT_TYPES.EXPENSE ? 'Ausgabe' : 'Sonstige Einnahme'}`,
    next: (type) => `Nächste ${type === RECEIPT_TYPES.EXPENSE ? 'Ausgabe' : 'Sonstige Einnahme'}`,
  },
  taxConsultantControl: {
    invite: {
      title: 'Steuerberater',
      subTitle: 'Lade Deinen Steuerberater ein',
      description: 'Gib die E-Mail-Adresse Deines Steuerberaters ein, um unkompliziert Einblick in Deine Buchhaltung zu gewähren - Einnahmen, Ausgaben, Buchungssätzen, Dokumenten und Belegen.',
      inviteButton: 'Steuerberater einladen',
    },
    pending: {
      pausedTaxConsultant: 'Pausierter Steuerberater',
      pausingTaxConsultant: 'Pausiert von Steuerberater',
      requestedTaxConsultant: 'Anfrage gestellt',
      requestingTaxConsultant: 'Anfragender Steuerberater',
      reenable: 'Zusammenarbeit bestätigen',
      disconnect: 'Zusammenarbeit beenden',
      reject: 'Zusammenarbeit ablehnen',
      cancel: 'Anfrage abbrechen',
      resendInvitation: 'Einladungs-E-Mail erneut versenden',
      resentInvitation: 'Einladungs-E-Mail erneut versendet',
    },
    established: {
      taxConsultant: 'Steuerberater',
      title: 'Kontaktinformationen',
      taxConsultantFirstName: 'Vorname',
      taxConsultantLastName: 'Nachname',
      taxConsultantEmail: 'E-Mail',
      taxConsultantPhone: 'Telefon',
      taxConsultantWebsite: 'Website',
      taxConsultantAddress: 'Adresse',
      taxConsultantStreet: 'Straße/Hausnummer',
      taxConsultantCity: 'Stadt',
      taxConsultantZip: 'PLZ',
    },
    deleteTitle: 'Zusammenarbeit beenden',
    deleteDescription: `Möchtest Du die Zusammenarbeit mit Deinem Steuerberater beenden. Die Verbindung wird damit aufgehoben und es ist kein Austausch über ${isOrgaMax ? 'orgaMAX' : 'WISO MeinBüro'} mehr möglich.`,
    rejectTitle: 'Zusammenarbeit ablehnen',
    rejectDescription: 'Möchtest Du die Zusammenarbeit mit Deinem Steuerberater wirklich ablehnen?',
    cancelTitle: 'Anfrage abbrechen',
    cancelDescription: 'Möchtest Du die Anfrage an Deinen Steuerberater wirklich abbrechen?',
    tooManyTitle: 'Es bestehen mehrere Anfragen',
    tooManyText: 'Bitte verwalte diese auf dem',
  },
  taxConsultant: {
    revoke: 'Verknüpfung deaktivieren',
    reenable: 'Verknüpfung aktivieren',
    disconnect: 'Zusammenarbeit beenden',
    resendInvite: 'Die Einladung wurde erneut versendet',
    errors: {
      revoke: 'Zusammenarbeit konnte nicht aufgehoben werden',
      invite: 'Die Einladung konnte nicht versendet werden',
    },
    invitationsModal: {
      heading: 'Ausstehende Einladungen',
      text: 'Hier siehst Du alle ausstehenden Einladungen von Mandanten und kannst diese annehmen oder ablehnen.',
      accept: 'Annehmen',
      reject: 'Ablehnen',
      cancel: 'Abbrechen',
      resend: 'Erneut anfragen',
      acceptLater: 'Später bestätigen',
    },
    selectTaxConsultantModal: {
      title: 'Vorhandene Steuerkanzlei',
      description: (email) => `Der Steuerberater mit der E-Mail-Adresse ${email} gehört bereits einer Kanzlei an. Bitte wähle die Kanzlei aus, die Du verknüpfen möchtest:`,
    },
  },
  receipt: {
    multiUpload: {
      title: 'E-Rechnungen / Belege hochladen',
      description: 'Per Drag & Drop ablegen oder',
      analyseRunning: 'Analyse der E-Rechnungen / Belege läuft.',
      analyseTitle: 'E-Rechnungen / Belege hochladen um Analyse zu starten',
      progress: (todo, done) => `${done} von ${todo} hochgeladen`,
      // „max." with a period, as in the receipt form's static notice (receipt.preview.uploader.hint) —
      // both drop zones are on the same page and previously wrote the same sentence differently.
      allowed: (extensions) => `(${extensions.join(', ')} sind erlaubt, max. 10MB pro Datei, 3000x3000px)`,
      failedTitle: 'Upload fehlgeschlagen',
      failedHint: 'Details findest Du im Upload-Status.',
      retry: 'Erneut versuchen',
      dismiss: 'Schließen',
      errors: {
        onlyEInvoiceAllowed: 'In Deinem aktuellen Plan können ausschließlich E-Rechnungen im XML- oder ZUGFeRD-Format importiert werden. Für den Import weiterer Dateien, führe bitte ein Plan-Upgrade durch.',
        embeddedXmlOrXmlNotSupported: 'Die XML-Datei/ im PDF eingebettete XML-Datei wird nicht unterstützt.',
        embeddedXmlNotSupported: 'Die eingebettete XML-Datei im PDF wird nicht unterstützt.',
        xmlNotSupported: 'Die XML-Datei wird nicht unterstützt.',
        analyze: 'Beim Analysieren ist ein Fehler aufgetreten.',
        documentScanCountReached: 'Maximale Anzahl an Dokumenten-Scans erreicht.',
        invalidFile: 'Der Beleg ist in einem falschen Format oder fehlerhaft.',
        // Tile summary shown when one or more receipt uploads terminally fail (network/auth drop,
        // DMS reject, complete-upload 500). The precise per-file reason is in the upload flyout.
        uploadFailedTile: (failed, total) => total > 1
          ? `${failed} von ${total} Dateien konnten nicht hochgeladen werden.`
          : 'Die Datei konnte nicht hochgeladen werden.',
        // FLAG:useDocuAi — refused file types. Named per file: a mixed drop must say WHICH
        // file was refused, otherwise the upload looks successful and the file is just gone.
        unsupportedFileType: (fileName, formats) =>
          `Das Format der Datei '${fileName}' wird nicht unterstützt. Bitte verwende eines der folgenden Formate: ${formats}.`,
        unsupportedFileTypes: (fileNames, formats) =>
          `Das Format dieser Dateien wird nicht unterstützt und sie wurden nicht hochgeladen: ${fileNames}. Bitte verwende eines der folgenden Formate: ${formats}.`,
      },
    },
    form: {
      analysisPlaceholder: 'Analyse läuft ...',
      // FLAG:useDocuAi — the embedded review's honest exits when no result arrived in time.
      // Delayed: the analysis is queued/running but slower than the screen blocks for.
      // Aborted: the watchdog bounded a stuck analysis to its neutral terminal.
      analysisDelayedInfo: 'Die Analyse dauert länger als erwartet. Sie läuft im Hintergrund weiter – der Beleg kann geprüft werden, sobald die Analyse abgeschlossen ist.',
      analysisAbortedInfo: 'Die Analyse wurde unterbrochen. Der Beleg bleibt in der Liste erhalten und kann dort geprüft oder erneut analysiert werden.',
      title: {
        detail: 'Details',
        positions: 'Positionen',
        tags: 'Tags',
      },
      payee: {
        label: '(L)ieferant / (K)unde',
        placeholder: 'Lieferant/Kunde eingeben oder wählen',
        loadingMessage: 'Lieferanten/Kunden werden geladen ...',
      },
      receiptNumber: 'Beleg-Rechnungsnr.',
      date: 'Belegdatum',
      deliveryDate: 'Lieferdatum',
      deliveryDateEqualsDate: 'Lieferdatum entspricht Belegdatum',
      description: 'Beschreibung',
      tags: 'Tag hinzufügen',
      alias: 'Aliasname-verwendet',
      dueDate: 'Fälligkeit ohne Abzug',
      errors: {
        defaultError: (type) => `Beim Speichern der ${type === RECEIPT_TYPES.EXPENSE ? 'Ausgabe' : 'Einnahme'} ist ein Fehler aufgetreten.`,
        receiptNumberInvalid: 'Die eingegebene Beleg-Rechnungsnr. enthält ungültige Zeichen.',
        receiptNumberMaxLengthExceeded: 'Beleg-Rechnungsnr. darf maximal 36 Zeichen lang sein.',
      },
    },
    payments: {
      title: 'Bezahlung',
      actions: {
        assignBooking: 'Zuordnen',
        bankTransfer: 'Überweisen',
        payment: 'Offline-Zahlung',
        // FLAG:useDocuAi — the document states it is already paid, so starting a transfer
        // would pay it twice. Recording a payment ("Offline-Zahlung") and assigning a bank
        // transaction ("Zuordnen") stay available — those describe a payment that happened.
        bankTransferAlreadyPaid: 'Bereits bezahlt — eine Überweisung würde die Rechnung ein zweites Mal bezahlen.',
      },
    },
    documentContainer: {
      open: 'Dokumentencontainer öffnen',
      close: 'Dokumentencontainer schließen',
      tooltip: {
        title: 'Dokumentencontainer',
        description: `Du hast ${productNameDocument}, dann kannst Du hier aus einer Vielzahl von Dokumenten wählen, die Du in den entsprechenden Ordnern abgelegt hast.`,
        info: `Du hast noch kein ${productNameDocument} - dann informiere Dich hier:`,
        linkLabel: 'Link zur Website',
      },
    },
    preview: {
      download: 'Beleg herunterladen',
      downloadXML: 'XML herunterladen',
      errors: {
        default: 'Entschuldigung, Dein Beleg konnte nicht geladen werden.',
      },
      uploader: {
        title: 'E-Rechnung / Beleg hochladen',
        info: 'Ab sofort kannst Du auch E-Rechnungen im ZUGFeRD- und XRechnungs-Format hochladen.',
        description: {
          dragAndDrop: 'Per Drag & Drop ablegen oder',
          upload: 'E-Rechnung / Beleg hochladen',
        },
        hint: '(jpeg, jpg, png, pdf, xml, ZUGFeRD sind erlaubt, max. 10MB pro Datei, 3000x3000px)',
        // FLAG:useDocuAi — upload no longer uses FineUploader but the DMS path (chunked, validated
        // server-side). Therefore neither number in the sentence above still applies: 3000x3000px
        // was FineUploader's image validation (with the flag ON nobody checks edge lengths), and
        // 10MB already contradicted the receipt form's own sizeLimit (25MB). Instead of setting a
        // second incorrect number, the notice now states only what actually applies: allowed formats.
        hintDms: '(jpeg, jpg, png, pdf, xml, ZUGFeRD sind erlaubt)',
      },
    },
    list: {
      toasts: {
        isAnalysing: 'Der Beleg wird im Moment analysiert. Bitte versuche es später erneut.',
        analyseError: 'Bei der Analyse ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        noInvoiceFound: 'In diesem Dokument wurde kein Rechnungsbeleg erkannt. Die Datei findest Du unter Dokumente.',
      },
    },
    analyseResult: {
      empty: 'Kein Beleg zur Prüfung vorhanden',
      exists: 'Beleg zur Prüfung vorhanden',
      valid: 'E-Rechnung gültig',
      invalid: 'E-Rechnung ungültig',
      // FLAG:useDocuAi — a structured e-invoice whose KoSIT verdict is missing (validator not
      // configured, or analysed before the summary was carried). Says WHAT the document is
      // without claiming it passed.
      eInvoiceDetected: 'E-Rechnung erkannt',
      success: 'KI-Beleganalyse erfolgreich',
      download: 'Prüfbericht herunterladen',
      foundValues: (count) => `${count} Werte gefüllt`,
      tooltip: {
        title: 'E-Rechnung valide?',
        description: 'Wir prüfen, ob die erhaltene E-Rechnung den gesetzlichen Bestimmungen entspricht, nicht auf den korrekten Inhalt, wie z. B. Betragshöhe.',
        items: {
          title: 'Unsere Handlungsempfehlung:',
          valid: {
            title: 'E-Rechnung ungültig',
            description: 'Diese E-Rechnung sollte von Dir nicht verarbeitet werden, lies den Prüfbericht und wende Dich an den Ersteller.<br/> Eine ungültige E-Rechnung kann den Vorsteuerabzug gefährden.',
          },
          invalid: {
            title: 'E-Rechnung gültig',
            description: 'Diese E-Rechnung kann von Dir verarbeitet und gespeichert werden, ist zum Vorsteuerabzug berechtigt.',
          },
        },
      },
      errors: {
        download: 'Der Prüfbericht konnte nicht heruntergeladen werden.',
      },
    },
  },
  units: {
    kg: 'kg',
  },
  upgrade: {
    wizard: {
      stepper: {
        titles: {
          selection: 'Plan',
          extensions: 'Add-ons',
          billingForm: 'Daten & Zahlung',
        },
      },
      titles: {
        selection: 'Plan wählen',
        extensions: 'Add-ons ergänzen',
        billingForm: 'Schließe Deinen Kauf ab',
        success: 'Alles erledigt.',
      },
      stepTitle: (stepNumber, title) => `Schritt ${stepNumber} – ${title}`,
      buttons: {
        back: 'Zurück',
        toExtensions: 'Weiter zu Add-ons',
        toBilling: 'Weiter zu Daten & Zahlung',
        purchase: 'Kauf abschließen',
      },
      cancelDialog: {
        title: 'Kauf abbrechen?',
        description: 'Es wird nichts gekauft und Deine Auswahl geht verloren. Den Shop öffnest Du jederzeit wieder über „Shop“ in der Navigation.',
        confirm: 'Kauf abbrechen',
        abort: 'Weiter einkaufen',
      },
      processing: 'Deine Bestellung wird verarbeitet …',
      sidebar: {
        title: 'Deine Auswahl',
        empty: 'Wähle einen Plan, um Deine Auswahl hier zu sehen.',
        months: (count) => `${count} ${count === 1 ? 'Monat' : 'Monate'}`,
        lineItemTitle: (count, title) => count > 1 ? `${count} × ${title}` : title,
        discountBadge: (percent) => `-${percent} %`,
        couponDiscount: (code) => `Gutschein ${code}`,
        couponRedeem: 'Gutscheincode einlösen',
        couponRemove: 'Entfernen',
        couponLabel: 'Gutscheincode',
        couponPlaceholder: 'Code eingeben',
        couponSubmit: 'Einlösen',
        couponClose: 'Gutscheincode schließen',
        couponInvalid: 'Dieser Code ist ungültig oder abgelaufen.',
        couponRequired: 'Bitte gib einen Gutscheincode ein.',
        legalNotice: {
          prefix: 'Mit dem Klick auf „Kauf abschließen“ akzeptierst Du unsere ',
          agb: 'AGB',
          and: ' und ',
          privacy: 'Datenschutzhinweise',
          suffix: '.',
        },
        open: (count) => `Deine Auswahl öffnen (${count} ${count === 1 ? 'Produkt' : 'Produkte'})`,
        close: 'Deine Auswahl schließen',
        subtotal: (period) => `Zwischensumme netto / ${period === 'month' ? 'Monat' : 'Jahr'}`,
        vat: (percent) => `+ ${percent} % USt.`,
        dueToday: 'Heute fällig',
        discount: (percent, months) => months ? `${percent} % für ${months} Monate` : `${percent} %`,
      },
      addons: {
        title: 'Add-on',
        add: 'Hinzufügen',
        remove: 'Entfernen',
        owned: 'Bereits gekauft',
        perMonth: '/ Monat*',
        perMonthPerUser: '/ Monat je Nutzer*',
        perMonthPerStorage: (sizeInGb) => `/ Monat je ${sizeInGb} GB*`,
        additionalUsers: (title) => `Weitere ${title}`,
        // names of the add-ons in the checkout (design), by module name
        titles: {
          multiUser: 'Weitere Benutzer',
          recurringInvoices: 'Abo-Rechnungen',
          storagePackage: 'Dokumentenspeicher',
          multiLetterPaper: 'Mehrere Briefpapiere',
        },
        // the check circle of a tile, read by screen readers
        toggle: (title) => `${title} auswählen`,
        selected: 'Ausgewählt',
      },
      plans: {
        monthly: 'Monatlich',
        yearly: 'Jährlich',
        yearlySaving: (percent) => `Jährlich (bis zu ${percent} % sparen)`,
        recommended: 'Empfehlung',
        choose: 'Plan wählen',
        selected: 'Ausgewählt',
        active: 'Aktueller Plan',
        // existing customers with a plan from the shop
        current: 'Dein aktueller Plan',
        continueWithCurrent: 'Mit diesem Plan weiter',
        upgrade: 'Upgrade wählen',
        notAvailable: 'Nicht aktivierbar',
        notAvailableReason: 'Wegen Deines aktuellen Plans',
        showFeatures: 'Leistungen anzeigen',
        hideFeatures: 'Leistungen ausblenden',
        perMonth: '€ / Monat*',
        billedYearly: 'bei jährlicher Zahlung',
        billedMonthly: 'bei monatlicher Zahlung',
        discount: (percent, months) => months ? `${percent} % für ${months} Monate` : `${percent} %`,
        claims: {
          buchhaltung: {
            claim: 'Für eine Buchhaltung, die mitdenkt.',
            description: 'Rechnungen, Belege und Steuern an einem Ort erledigt.',
          },
          auftrag: {
            claim: 'Für einen Büroalltag, der läuft.',
            description: 'Aufträge, Buchhaltung und Dokumente in einem durchgängigen Ablauf organisiert.',
          },
          auftragPro: {
            claim: 'Mehr Flexibilität für Deine Aufträge.',
            description: 'Für komplexe Aufträge, Projekte und flexible Abrechnung.',
          },
          warenwirtschaft: {
            claim: 'Wenn Waren ins Spiel kommen.',
            description: 'Für alle, die zusätzlich Lager, Bestände und Einkauf organisieren.',
          },
        },
      },
      billing: {
        address: {
          title: 'Rechnungsadresse',
          fromAccount: 'Aus Deinem Konto übernommen',
          companyName: 'Firmenname',
          street: 'Straße, Hausnummer',
          zipCode: 'PLZ',
          city: 'Ort',
          country: 'Unternehmenssitz',
        },
        contact: {
          title: 'Kontakt',
          salutation: 'Anrede (optional)',
          salutations: {
            none: 'Keine Angabe',
            female: 'Frau',
            male: 'Herr',
            diverse: 'Divers',
          },
          firstName: 'Vorname',
          lastName: 'Nachname',
          email: 'E-Mail',
          invoiceHint: 'An diese E-Mail-Adresse senden wir Deine Rechnung.',
        },
        payment: {
          title: 'Zahlungsart',
          accountHolder: 'Name des Kontoinhabers',
          iban: 'IBAN',
          sepaMandate: 'SEPA-Mandat erteilen',
          // below a valid IBAN, e.g. "Sparkasse Paderborn-Detmold, BIC: WELADE3LXXX"
          bank: (name, bic) => bic ? `${name}, BIC: ${bic}` : name,
        },
        // after a click on "Kauf abschließen" with data missing
        errors: {
          required: 'Bitte fülle dieses Feld aus.',
          sepaMandate: 'Bitte erteile das SEPA-Mandat.',
        },
        sepa: {
          title: 'SEPA-Lastschrift',
          // terminology-ignore:T001 legal text of the SEPA mandate keeps the formal address
          mandateText: 'Mit Klick auf „Kauf abschließen“ ermächtige ich die deltra Business Software GmbH & Co. KG, Zahlungen von meinem Konto mittels SEPA-Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von Ihnen auf mein Konto gezogenen Lastschriften einzulösen. Ich kann innerhalb von 8 Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Dabei gelten die mit meinem Kreditinstitut vereinbarten Bedingungen. Gläubiger-Identifikationsnummer der deltra Business Software GmbH & Co. KG: DE96ZZZ00000024237.',
        },
      },
      success: {
        title: 'Dein Kauf ist abgeschlossen.',
        description: (planTitle, addonCount) => planTitle
          ? `${planTitle} ist ab sofort freigeschaltet. Deine Daten aus dem Test bleiben erhalten und Du kannst direkt weiterarbeiten.`
          // only add-ons booked (no plan): there is a plan already, so no test to speak of
          : `${addonCount === 1 ? 'Dein Add-on ist' : 'Deine Add-ons sind'} ab sofort freigeschaltet und Du kannst direkt weiterarbeiten.`,
        toDashboard: 'Zum Dashboard',
        overview: {
          title: 'Bestellübersicht',
          planTitle: (title) => `${productName} ${title}`,
          // only add-ons booked (no plan): the line starts with them
          summary: (planTitle, addonCount, paidAmount) => [
            planTitle,
            `${addonCount} ${addonCount === 1 ? 'Add-on' : 'Add-ons'}`,
            `${paidAmount} bezahlt`,
          ].filter(Boolean).join(' · '),
          showDetails: 'Details anzeigen',
          hideDetails: 'Details ausblenden',
          tags: {
            yearly: 'Jährliche Zahlung',
            monthly: 'Monatliche Zahlung',
            minimumTerm: (months) => `Mind. ${months} Monate Laufzeit`,
            discount: (percent, months) => months ? `${percent} % für ${months} Monate` : `${percent} %`,
          },
          perPeriod: (period) => `pro ${period === 'month' ? 'Monat' : 'Jahr'} (zzgl. USt.)`,
          count: 'Anzahl',
          paid: 'Bezahlt',
          billingAddress: 'Rechnungsadresse',
          contact: 'Kontakt',
          paymentMethod: 'Zahlungsweise',
          iban: (iban) => `IBAN: ${iban}`,
          sepaMandateGranted: 'SEPA-Mandat erteilt',
        },
        consultation: {
          eyebrow: 'Erste Schritte',
          title: 'Kostenlose Einrichtungsberatung',
          description: 'In 30 Minuten zeigen wir Dir, wie Du orgaMAX passend für Dein Unternehmen einrichtest.',
          button: 'Termin buchen',
        },
      },
      selection: {
        oldPlanHint: `Gerne ändern wir Deinen Plan.<br/>Rufe uns dazu einfach unter ${salesTelephoneNumber} an!`,
        noUpgradeAvailable: 'Zu Deinem aktuellen Plan gibt es keinen Upgrade Pfad. <p> Bei Fragen kontaktiere bitte den Support.',
        legacyPlanHint: {
          title: 'Dein aktueller Tarif unterscheidet sich von unseren neuen Plänen.',
          description: 'Falls Du eine Funktion nicht findest oder Unterstützung bei der Auswahl benötigst, <a href="https://www.orgamax.de/orgamax-buchhaltung/orgamax-erweitern" target="_blank" rel="noopener noreferrer">vereinbare eine Beratung</a> oder kontaktiere uns unter <a href="tel:+49523190000">05231 90 000</a> und <a href="mailto:info@orgamax.de">info@orgamax.de</a>.',
        },
      },
      subscriptionItem: {
        expand: 'Details anzeigen',
        bestseller: 'Unsere Empfehlung',
        activeSubscription: {
          title: 'Plan Infos:',
          paymentInterval: 'Abrechnungszeitraum',
          begin: 'Aktiv seit',
          end: {
            default: 'Nächste Abrechnung',
            cancelled: 'Ablaufdatum',
          },
          user: 'Benutzer',
          userCount: (used, limit) => `${used} von ${limit}`,
        },
        button: {
          default: 'Zum Warenkorb hinzufügen',
          active: 'Aktueller Plan',
          inCart: 'Bereits im Warenkorb',
        },
        toast: {
          addedtoCart: (title) => `"${title}" wurde zum Warenkorb hinzugefügt.`,
        },
        disabledHint: {
          default: 'Dieser Plan ist aufgrund Deiner Konfiguration, bzw. Deines aktuellen Plans nicht aktivierbar.',
          active: 'Du hast diesen Plan bereits gekauft.',
          inCart: 'Dieser Plan ist bereits im Warenkorb.',
          notSelectable: 'Du hast bereits einen Plan im Warenkorb.',
        },
      },
      footer: {
        button: {
          continue: 'Weiter',
        },
        contactInfo: {
          title: 'Hast Du Fragen?',
          // DO-13587: the shop checkout of orgaMAX names the sales team's line (design), MeinBüro keeps its number
          number: isOrgaMax ? '+49 (0)5231 7090 13 00' : salesTelephoneNumber,
          businessHours: isOrgaMax ? 'Mo–Fr 7:00 bis 12:00 Uhr und 13:00 bis 17:00 Uhr' : salesBusinessHours,
        },
      },
    },
  },
  errors: {
    jsonSchemaValidationErrors: {
      additionalProperty: (prop) => `Die folgende Property darf nicht mitgeschickt werden: ${prop}`,
      missingProperty: (prop) => `Die folgende Property fehlt in den gesendeten Daten: ${prop}`,
    },
    financeApiError: {
      default: 'Beim Synchronisieren ist ein Fehler aufgetreten. Bitte wende Dich an den Support.',
    },
  },
  ai: {
    textTransform: {
      contextMenu: {
        options: {
          spellcheck: 'Rechtschreibprüfung',
          shorten: 'Verkürze',
          lengthen: 'Verlängere',
          tone: 'Ton anpassen',
          toneProfessional: 'Professioneller',
          toneSimple: 'Einfacher',
          translate: 'Übersetze',
          translateEn: 'Englisch',
          translateDe: 'Deutsch',
        },
      },
      popover: {
        originalLabel: 'Original:',
        resultLabel: 'Ergebnis:',
        placeholderText: 'Verarbeitetes Ergebnis wird hier angezeigt...',
        variantLabel: (current, total) => `Variante ${current} / ${total}`,
        errorMessage: 'Fehler bei der Verarbeitung. Bitte versuche es erneut.',
        buttons: {
          newSuggestion: 'Neu generieren',
          apply: 'Übernehmen',
        },
        titles: {
          spellcheck: 'Rechtschreibprüfung',
          shorten: 'Text verkürzen',
          lengthen: 'Text verlängern',
          'tone-professional': 'Ton anpassen: Professioneller',
          'tone-simple': 'Ton anpassen: Einfacher',
          'translate-en': 'Übersetzen: Englisch',
          'translate-de': 'Übersetzen: Deutsch',
        },
      },
      button: {
        tooltip: 'Text transformieren',
      },
    },
    chat: {
      title: 'KI-Agent MAX',
      empty: {
        title: 'Hi, ich bin <b>MAX</b>.',
        description: 'Du kannst mir hier <b>offene Fragen zu orgaMAX Buchhaltung</b> stellen. Ich helfe Dir direkt weiter.',
        faq: {
          title: 'FAQ öffnen:',
          link: 'FAQ-Bereich',
        },
        supportTicket: {
          title: 'Supportticket einreichen:',
          link: 'Ticket erstellen',
        },
        hint: 'Und kleiner Ausblick: <b>In Zukunft</b> kann ich noch deutlich mehr <b>Interaktionen gemeinsam mit Dir und Deinem Mandanten</b> übernehmen.',
      },
      input: {
        placeholder: 'Wie kann MAX Dir helfen?',
        placeholderCtrlEnter: 'Wie kann MAX Dir helfen? (Strg+Enter zum Senden)',
      },
      feedback: {
        tooltip: {
          noDetailedFeedback: 'Kein detailliertes Feedback abgegeben.',
        },
        tags: {
          title: 'Warum?',
          positive: [
            'Schnelle Antwort',
            'Präzise Information(en)',
            'Kreativität gezeigt',
            'Gut erklärt',
            'Anweisung perfekt befolgt',
          ],
          negative: [
            'Schlechter Schreibstil',
            'Falsche Informationen',
            'Nicht sachlich korrrekt',
            'Nicht relevant',
            'Zu lang',
            'Zu kurz',
            'Unvollständig',
          ],
        },
        comment: {
          title: 'Zusätzlicher Kommentar',
          placeholder: 'Schreibe hier Dein Feedback.',
        },
        errors: {
          default: 'Beim Speichern des Feedbacks ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        },
      },
      button: 'Frag den KI-Agenten MAX',
      loading: {
        status: 'MAX denkt nach',
      },
      welcome: {
        greeting: 'Hallo! 👋',
        greetingWithName: (name) => `Hallo ${name}! 👋`,
        introPrefix: 'Ich bin',
        introSuffix: 'Dein KI-Agent.',
        support: 'Ich helfe Dir gerne weiter.',
        question: 'Wobei kann ich Dir helfen?',
        context: {
          locationPrefix: 'Du arbeitest gerade im Bereich',
          question: 'Womit kann ich Dir helfen?',
          showGeneral: 'Allgemeine Hilfe',
          showContextPrefix: 'Hilfe für',
        },
      },
      header: {
        status: 'Bereit',
        menu: 'Chat-Verlauf',
        newChat: 'Neuer Chat',
        settings: 'Einstellungen',
        back: 'Zurück zum Chat',
        close: 'Schließen',
        pin: 'Rechts andocken',
        unpin: 'Andockung lösen',
        dragHandle: 'Verschieben',
      },
      conversationList: {
        current: 'Aktueller Chat',
        previous: 'Frühere Chats',
        empty: 'Noch keine Chats vorhanden',
        newChat: 'Neuer Chat',
        startNew: 'Neuen Chat starten',
        loading: 'Lade weitere Chats...',
      },
      settings: {
        title: 'Einstellungen',
        sendMessageOption: {
          title: 'Nachricht senden mit',
          description: 'Wähle, wie Du Deine Nachricht versenden möchtest.',
          options: {
            enter: 'Enter',
            enterCtrl: 'Strg + Enter',
          },
        },
        sendAfterMicInput: {
          title: 'Nach Sprachaufnahme automatisch senden',
          description: 'Wähle, ob die Nachricht automatisch nach der Sprachaufnahme gesendet werden soll.',
          options: {
            yes: 'Ein',
            no: 'Aus',
          },
        },
        panelPlacement: {
          title: 'Position',
          description: 'Rechts angedockt ab einer Fensterbreite von 1025 Pixeln.',
          options: {
            floating: 'Frei schwebend',
            dockedRight: 'Rechts angedockt',
          },
        },
        cornerStyle: {
          title: 'Ecken des Widgets',
          description: 'Wähle die Form passend zum Einsatzbereich.',
          options: {
            rounded: 'Abgerundet',
            square: 'Kantig',
          },
        },
      },
      footer: {
        description: 'Der ChatBot MAX kann Fehler machen. Überprüfe die Antworten.',
      },
      toasts: {
        conversation: {
          deleted: 'Die Konversation wurde erfolgreich gelöscht.',
        },
      },
      errors: {
        default: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
        message: {
          get: 'Die Nachricht konnte nicht abgerufen werden. Bitte versuche es erneut.',
          send: 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
        },
        messages: {
          get: 'Die Nachrichten konnten nicht abgerufen werden. Bitte versuche es erneut.',
        },
        voice: {
          default: 'Beim Spracherkennen ist ein Fehler aufgetreten. Bitte versuche es erneut.',
          notSupported: 'Spracherkennung wird von Deinem Browser nicht unterstützt. Bitte Chrome oder Edge verwenden.',
          notAllowed: 'Spracherkennung wird von Deinem Browser nicht erlaubt. Bitte erlaube den Zugriff auf das Mikrofon in Deinen Browser-Einstellungen.',
        },
        conversation: {
          delete: 'Die Konversation konnte nicht gelöscht werden. Bitte versuche es erneut.',
          get: 'Die Konversation konnte nicht abgerufen werden. Bitte versuche es erneut.',
        },
        conversations: {
          get: 'Die Konversationen konnten nicht abgerufen werden. Bitte versuche es erneut.',
        },
      },
    },
  },
  listHeader: {
    errors: {
      export: 'Exportieren fehlgeschlagen. Bitte versuche es erneut. ',
    },
  },
  directDebitMandateList: {
    title: 'SEPA-Lastschriftmandat',
    selectInputTitle: 'Neues Lastschriftmandat beantragen',
    noMandateFound: 'Kein aktives SEPA-Lastschriftmandat',
    identifierMissing: 'Beantrage eine <a href="settings/mycompany#creditorIdentifier">Gläubiger-Identifikationsnummer</a> und nutze SEPA-Lastschriftmandate.',
    emailMissing: 'Für die Anlage eines SEPA-Lastschriftmandats wird eine Kunden-E-Mail-Adresse benötigt.',
    requestButton: 'Neues SEPA-Lastschriftmandat beantragen',
    mandateTypes: {
      recurring: 'Wiederkehrend',
      oneOff: 'Einmalig',
    },
  },
  folderAccessPermissions: {
    title: 'Ordner freigeben',
    usersWithAccess: 'Benutzer mit Freigabe',
    removeAccessForAll: 'Freigabe für alle beenden',
    addAccessForAll: 'Für alle Benutzer freigeben',
    save: 'Fertig',
    cancel: 'Abbrechen',
    alreadyExists: 'Freigabe existiert bereits',
    noMoreUsers: () => 'Keine weiteren Nutzer gefunden',
    inherited: 'vererbt',
  },
  accessTypes: {
    folder: {
      editor: {
        label: 'Bearbeiter',
      },
      owner: {
        label: 'Besitzer',
      },
      viewer: {
        label: 'Betrachter',
      },
      parent: {
        label: 'Vererbung von',
      },
    },
  },
  folderSharing: {
    sharedWith: (count) => `Geteilt mit ${count} Personen`,
    sharedBy: (name) => `Freigegeben von: ${name}`,
    accessTooltip: {
      owner: 'Du bist Besitzer',
      editor: 'Du bist Bearbeiter',
      viewer: 'Du bist Betrachter',
    },
  },
  directDebitSetting: {
    title: 'SEPA-Lastschrift',
    purposeInputLabel: 'Verwendungszweck',
    purposeInputPlaceholder: 'Verwendungszweck eingeben',
    purposeOfUseTextInvalid: 'Der eingegebene Verwendungszweck enthält ungültige Zeichen.',
    requestButton: 'Neues SEPA-Lastschriftmandat beantragen',
    mandateTitle: 'SEPA-Lastschriftmandat',
    preview: {
      title: 'Vorschau',
      placeholder: 'Vorschau wird hier angezeigt...',
    },
    bankDayInfo: 'Das Wertstellungsdatum gibt an, an welchem Banktag die Lastschrift auf dem Konto des Zahlungspflichtigen wirksam verbucht wird. Der angezeigte Termin zeigt das späteste Einreichungsdatum bei der Bank, damit die Lastschrift rechtzeitig verarbeitet werden kann. Als Banktage gelten Montag bis Freitag; Samstage, Sonntage und gesetzliche Feiertage werden nicht berücksichtigt. Bitte beachte, dass die genauen Cut-off-Zeiten je nach Bank unterschiedlich sein können.',
    deadline: 'Termin',
    deadlineAt: 'Termin am:',
    latestSubmissionDate: 'Spätestes Einreichungsdatum für die Lastschrift',
    characterCount: 'Zeichen',
    characterLimitExceeded: ' (Überschritten!)',
    valueDate: 'Wertstellungsdatum der Lastschrift',
    purposeError: 'Verwendungszweck darf maximal 140 Zeichen haben',
    sequenceType: {
      FRST: 'Erstlastschrift',
      RCUR: 'Folgelastschrift',
      OOFF: 'Einzellastschrift',
      FNAL: 'Endlastschrift',
    },
    state: {
      title: 'Status',
      cancelled: 'Storniert',
      expired: 'Abgelaufen',
      initiated: 'Initiiert',
      notified: 'Benachrichtigt',
      processing: 'Verarbeitet',
      refunded: 'Erstattet',
      ready: 'Bereit',
      rejected: 'Abgelehnt',
      returned: 'Zurückgegeben',
      settled: 'Abgeschlossen',
      submitted: 'Eingereicht',
      check: 'Überprüfen',
      due: 'Fällig',
      overdue: 'Überfällig',
      collected: 'Eingezogen',
    },
    placeholders: {
      customerName: 'Kundenname',
      invoiceDate: 'Rechnungsdatum',
      paymentTerms: 'Zahlungsbedingungen',
      valueDate: 'Fälligkeitstag',
      invoiceNumber: 'Rechnungsnummer',
      mandateReferenceNumber: 'Mandatnummer',
      creditorIdentifier: 'Gläubiger-ID',
      invoiceAmount: 'Rechnungsbetrag',
      customerNumber: 'Kundennummer',
    },
  },
  tenantRestore: {
    status: {
      waiting: 'Warten auf Bestätigung',
      queued: 'In der Warteschlange',
      active: 'Aktiv',
      error: 'Fehler',
      done: 'Fertig',
    },
    detail: {
      status: 'Datenimport',
      headline: (oldPlan, newPlan, isRecommended) => `Mit der Migration wechselst Du von <strong>${oldPlan}</strong> zu <strong>WISO MeinBüro ${newPlan}</strong>${isRecommended ? ' (unsere Empfehlung)' : ''}`,
      featuresHeadline: 'Enthaltene Funktionen:',
      comparePlan: 'Planübersicht',
      submitOrder: 'Vertrag abschließen und Wechsel durchführen',
      successTitle: 'Wechsel erfolgreich!',
      successInvoiceHint: 'Deine Verträge wurden erfolgreich übertragen.\nWeitere Informationen erhältst Du per E-Mail an:',
      successMessage: 'Du kannst Deinen neuen Plan jetzt direkt nutzen.\nViel Erfolg!',
      successButton: 'Jetzt starten',
      emailRequired: 'Bitte E-Mail angeben',
      confirmText: 'Bitte vervollständige die E-Mail-Adresse, unter der das Backup erstellt wurde:',
      errorMismatch: 'Die Daten stimmen leider nicht.',
      notAllowed: 'Backup nicht gefunden oder kein Zugriff',
      title: 'Wiederherstellung',
      previousPlan: 'Dein bisheriger Plan',
      newPlan: 'Dein neuer Plan nach der Migration',
      previousPlanOption: 'Vorheriger Plan',
      nextPlanOption: 'Nächster Plan',
      newPlanUpgradeInfo: 'Ein Upgrade Deines neuen WISO MeinBüro-Vertrages ist nach Vertragsabschluss jederzeit möglich. Ein Downgrade Deines neuen WISO MeinBüro-Vertrages ist zum Ablaufdatum möglich.',
      planDisclaimerTitle: 'Deine bereits gezahlte Vertragslaufzeit für invoiz geht nicht verloren',
      planDisclaimer: 'Der anteilige Wert der verbleibenden Laufzeit deines invoiz-Vertrages innerhalb der aktuellen Zahlungsperiode wird dir nach Abschluss eines neuen WISO MeinBüro-Vertrages innerhalb von 7 Werktagen gutgeschrieben.',
      recommended: 'Empfehlung',
      planUnavailable: 'Dieser Tarif kann gerade nicht bestellt werden. Bitte versuche es später noch einmal.',
      planDisclaimer2: 'Du wirst jetzt in den buhl-Shop weitergeleitet, um den Wechsel zu WISO MeinBüro abzuschließen. Anschließend kannst Du mit Deinen Daten in WISO MeinBüro weiterarbeiten.',
      planLegal: 'Es gelten unsere',
      planLegalLink: 'AGB',
      error: 'Es ist ein Fehler aufgetreten. Support ',
    },
    steps: {
      prepare: 'Vorbereitung',
      article: 'Artikel',
      customer: 'Kunden',
      dunning: 'Mahnungen',
      dunningLevel: 'Mahnstufen',
      invoice: 'Rechnungen',
      job: 'Wiederherstellung',
      miscellaneous: 'Einstellungen',
      offer: 'Angebote',
      payCondition: 'Zahlungsbedingungen',
      tenant: 'Mandant',
      textModule: 'Textmodule',
      user: 'Benutzer',
      letterPaperSetting: 'Briefpapier',
      deliveryNote: 'Lieferscheine',
      recurringInvoice: 'Abos',
      trackedTime: 'Zeiterfassung',
      project: 'Abschläge',
      expense: 'Ausgaben',
    },
    page: {
      noRestoresAvailable: 'Keine Wiederherstellungen verfügbar',
      noRestoresAvailableDescription: 'Es sind derzeit keine Wiederherstellungsanfragen vorhanden. Neue Wiederherstellungen werden hier angezeigt, sobald sie erstellt werden.',
      inRestoreLabel: 'Wiederherstellungen in Bearbeitung',
      restoreLabel: 'Wiederherstellung',
      unknownStatus: 'Unbekannt',
      createdAtLabel: 'Erstellt',
      requestIdLabel: 'Anfrage-ID',
      tenantLabel: 'Mandant',
      showDetailsLabel: 'Details anzeigen',
      clickToViewDetails: 'Auf eine Wiederherstellung klicken, um Details anzuzeigen oder den Status zu überprüfen.',
    },
  },
}

export default {
  ...lang,
  loaderMessages: {
    RENDER_DELIVERY_NOTE_AND_INVOICE: lang.order.reportLoadingMessages.renderDeliveryNoteAndInvoice,
    RENDER_DELIVERY_NOTE: lang.order.reportLoadingMessages.renderDeliveryNote,
    RENDER_INVOICE: lang.order.reportLoadingMessages.renderInvoice,
    RENDER_PARTIAL_INVOICE: lang.order.reportLoadingMessages.renderPartialInvoice,
    RENDER_OFFER: lang.offer.renderOffer,
  },

  invoiceLimitModal: {
    leftTitle: 'Hoppla!<br />Dein Rechnungs-Limit ist erreicht.',
    leftDescription: 'Dein Free-Plan ist ausgeschöpft (5 E-Rechnungen). Upgrade jetzt und arbeite unbegrenzt - ohne Unterbrechung!',
    rightTitle: 'Mit dem Plan S erhältst Du:',
    feature1: 'Unbegrenzt (E-)Rechnungen & Angebote',
    feature2: 'Artikel & Dienstleistungen verwalten',
    feature3: 'Steuerberaterzugang & Buchführungsexport',
    subline: 'Lass Dein Business weiterlaufen - ohne Wartezeit und ohne Umsatzausfälle!',
    upgradeButtonLabel: 'Jetzt Upgraden',
    planSelection: 'Zur Planauswahl',
  },
  deleteMyAccount: {
    title: 'Konto löschen',
    buttonLabel: 'Konto löschen',
    info: 'Wenn Du Dein Konto löschst, werden all Deine Daten dauerhaft gelöscht. Dieser Vorgang lässt sich nicht rückgängig machen. Nur als Besitzer kannst Du Dein Konto löschen.',
    infoHint: 'Damit Du Dein Konto löschen kannst, dürfen in Deinem Konto keine aktiven Verträge bestehen.',
  },
  placeHolder: 'Platzhalter',
  ckEditor: {
    buttons: 'Buttons',
  },
  debitTransactions: {
    title: 'Lastschriften',
    detailTitle: subTitle => `Lastschriften - ${subTitle} - ${productName}`,
    tabLabels: ['Übersicht', 'Geplant', 'Ausgeführt', 'Mandate'],
    mandateListe: {
      loading: 'Mandate werden geladen...',
      noMandatesFound: 'Keine Mandate gefunden',
      searchPlaceholder: 'Mandate suchen',
      mandateTypes: {
        recurring: 'Wiederkehrend',
        oneOff: 'Einmalig',
      },
      mandateUsageStates: {
        first: 'Erstlastschrift',
        followup: 'Folgelastschrift',
      },
      mandateStates: {
        active: 'Aktiv',
        expired: 'Abgelaufen',
        cancelled: 'Storniert',
        pending: 'Ausstehend',
        cancelling: 'Stornierung ausstehend',
      },
      details: {
        mandateReference: 'Mandatsreferenz',
        customer: 'Kunde',
        customerNumber: 'Kundennummer',
        paymentType: 'Zahlungsart',
        email: 'E-Mail-Adresse',
        createdAt: 'Erstellt am',
        bankData: 'Bankdaten',
        iban: 'IBAN',
        bic: 'BIC',
        bankName: 'Kreditinstitut',
        debtorAccountHolderName: 'Kontoinhaber',
        mandateValidityDate: 'Gültig bis',
        creditorIdentifier: 'Gläubiger-ID',
        executionInfo: 'Ausführungsinformationen',
        mandateType: 'Mandat-Typ',
        lastExecution: 'Letzte Ausführung',
        statusDescriptions: {
          active: 'Das Lastschriftmandat ist aktiv und kann für SEPA-Lastschriften verwendet werden.',
          expired: 'Das Lastschriftmandat ist abgelaufen und kann nicht mehr für neue Lastschriften genutzt werden.',
          cancelled: 'Das Lastschriftmandat wurde vom Kunden oder Gläubiger widerrufen und ist nicht mehr gültig.',
          pending: 'Das Lastschriftmandat wurde beantragt, ist aber noch nicht vollständig aktiviert.',
          cancelling: 'Das Lastschriftmandat befindet sich im Prozess der Stornierung.',
        },
        cancelMandate: 'Lastschriftmandat widerrufen',
        resendMandate: 'Lastschriftmandat erneut senden',
        signatureInfo: 'Unterschrieben',
        cancelSuccess: 'Lastschriftmandat erfolgreich storniert.',
        cancelConfirmTitle: 'Lastschriftmandat widerrufen',
        cancelConfirmText: 'Bist Du sicher, dass Du das Lastschriftmandat widerrufen möchtest?',
      },
    },
    debitTransactionList: {
      executeButton: 'Ausführen',
      titles: {
        planned: 'Geplant',
        executed: 'Ausgeführt',
        errors: 'Überfällig',
        success: 'Abgeschlossen',
        failed: 'Fehlgeschlagen',
        overdue: 'Überfällig',
        due: 'Fällig',
        alternativelyPay: 'Alternativzahlung',
      },
      planned: 'Warte auf Einzug',
      success: 'Abgeschlossen',
      overdue: 'Überfällig',
      check: 'Überprüfung erforderlich',
      submitted: 'Warted auf Wertstellung',
      settled: 'Umsatz zugeordnet',
      returned: 'Rücklastschrift',
      due: 'Fällig',
      states: {
        initiated: 'Initiiert',
        submitted: 'Eingereicht',
        processing: 'Verarbeitet',
        settled: 'Abgeschlossen',
        rejected: 'Abgelehnt',
        returned: 'Zurückgegeben',
        refunded: 'Erstattet',
        cancelled: 'Storniert',
        expired: 'Abgelaufen',
        notified: 'Benachrichtigt',
        ready: 'Bereit',
        check: 'Überprüfung erforderlich',
        due: 'Fällig',
        overdue: 'Überfällig',
      },
      sequenceTypes: {
        FRST: 'Erstlastschrift',
        RCUR: 'Folgelastschrift',
        OOFF: 'Einzellastschrift',
        FNAL: 'Endlastschrift',
      },
      // ... existing code ...
      detail: {
        title: 'Lastschrift Details',
        state: 'Status: ',
        customerInfo: 'Kundeninformationen',
        invoiceInfo: 'Rechnungsinformationen',
        states: {
          ready: 'Bereit',
          submitted: 'Eingereicht',
          processing: 'Verarbeitet',
          settled: 'Abgeschlossen',
          rejected: 'Abgelehnt',
          returned: 'Zurückgegeben',
          refunded: 'Erstattet',
          cancelled: 'Storniert',
          expired: 'Abgelaufen',
          notified: 'Benachrichtigt',
          check: 'Überprüfung erforderlich',
          due: 'Fällig',
          overdue: 'Überfällig',
        },
        statesDescriptions: {
          ready: 'Das Lastschriftmandat ist bereit und kann für eine Lastschrift verwendet werden.',
          submitted: 'Die Lastschrift wurde eingereicht und wartet auf die Wertstellung.',
          processing: 'Die Lastschrift wird verarbeitet.',
          settled: 'Die Lastschrift wurde abgeschlossen.',
          rejected: 'Die Lastschrift wurde abgelehnt.',
          returned: 'Die Lastschrift wurde zurückgegeben.',
          refunded: 'Die Lastschrift wurde erstattet.',
          cancelled: 'Die Lastschrift wurde storniert.',
          expired: 'Das Lastschriftmandat ist abgelaufen.',
          notified: 'Die Lastschrift wurde benachrichtigt.',
          check: 'Die Lastschrift benötigt eine Überprüfung.',
          due: 'Die Lastschrift ist fällig.',
          overdue: 'Die Lastschrift ist überfällig.',
        },
        statesHints: {
          mandateNotActive: 'Kein aktives Mandat!',
          invoiceStateChanged: 'Rechnungsstatus manuell geändert',
          invoiceStatePartiallyPaid: 'Rechnung teilweise bezahlt',
          transactionIsDue: 'Lastschrift ist fällig',
          transactionIsOverdue: 'Lastschrift ist überfällig',
          purposeOfUseTextInvalid: 'Der eingegebene Verwendungszweck enthält ungültige Zeichen.',
        },
        buttons: {
          submit: 'Lastschrift einreichen',
        },
        customer: 'Kunde',
        customerNumber: 'Kundennummer',
        amount: 'Lastschriftbetrag',
        invoiceNumber: 'Rechnungsnummer',
        submitSuccess: 'Lastschrift erfolgreich eingereicht',
        submitError: 'Fehler beim Ausführen der Lastschrift',
        notFound: 'Lastschrift nicht gefunden',
        saveSuccess: 'Lastschrift erfolgreich gespeichert',
        saveError: 'Fehler beim Speichern der Lastschrift',
        errors: {
          minDateAndMaxDate: (minDate, maxDate) => `Das Wertstellungsdatum muss zwischen dem ${minDate} und dem ${maxDate} liegen`,
        },
      },
    },
    overviewPanel: {
      planned: 'Geplant',
      executed: 'Ausgeführt',
      total: 'Gesamt',
      mandates: 'Mandate',
      plannedCollections: 'Geplante Einzüge',
      lastschriften: 'Lastschriften',
      noPlannedCollections: 'Keine geplanten Einzüge',
      returnedByReason: 'Rücklastschriften nach Grund',
      noReturnedByReason: 'Keine Rücklastschriften',
      show: 'Anzeigen',
      unconfirmed: 'unbestätigt',
    },
    sidebar: {
      overviewTitle: 'Übersicht',
      toBeEdited: 'Zu bearbeiten',
      plannedTitle: 'Geplant',
      executedTitle: 'Ausgeführt',
      mandatesTitle: 'Mandate',
      total: 'Gesamt',
      toConfirm: 'unbestätigt',
    },
  },
  // FLAG:docuAiEnhancements — „Meine Meldungen": document issues reported by the tenant, with
  // their broad status and related messages.
  customerIssues: {
    title: 'Meine Meldungen',
    intro: 'Hier siehst Du Deine gemeldeten Probleme zu Dokumenten und den aktuellen Stand.',
    empty: 'Du hast noch keine Meldungen.',
    emptyHint: 'Sobald Du ein Problem zu einem Dokument meldest, findest Du es hier wieder.',
    // Bis 2026-09-21 lud die Liste nur EINE Seite und wies mit diesem Satz darauf hin, dass
    // aeltere Meldungen abgeschnitten sind. Jetzt wird geblaettert; der Text bleibt fuer den
    // Fall, dass eine Antwort doch mehr Zeilen meldet, als die Seite traegt.
    truncated: (shown, total) => `Es werden die neuesten ${shown} von ${total} Meldungen angezeigt.`,
    loadError: 'Meldungen konnten nicht geladen werden.',
    // Spaltenueberschriften der Liste.
    columnStatus: 'Status',
    columnSubject: 'Meldung',
    columnLastMessage: 'Letzte Nachricht',
    columnCreatedAt: 'Gemeldet am',
    searchPlaceholder: 'Meldungen durchsuchen',
    loaderText: 'Meldungen werden geladen',
    noSearchResult: 'Keine Meldung passt zu Deiner Suche.',
    noFilterResult: 'Keine Meldung mit diesem Status.',
    noMessageYet: '—',
    countLabel: (total) => (total === 1 ? '1 Meldung' : `${total} Meldungen`),
    retry: 'Erneut versuchen',
    untitled: 'Meldung',
    yourReport: 'Deine Meldung',
    messages: 'Nachrichten',
    noMessages: 'Noch keine Nachrichten.',
    close: 'Schließen',
    // Display text for the broad customer statuses. The keys ARE the DMS contract's wire values
    // (CUSTOMER_ISSUE_STATUS) — hence the map rather than raw values in JSX. Unknown values must
    // continue to be displayed raw (forward compatibility).
    statusLabels: {
      Eingegangen: 'Eingegangen',
      'In Bearbeitung': 'In Bearbeitung',
      Rückfrage: 'Rückfrage',
      Gelöst: 'Gelöst',
      Geschlossen: 'Geschlossen',
    },
  },
  undoTenantDeletion: {
    title: 'Mandant wiederherstellen',
    info: 'Wiederherstellung wird durchgeführt...',
    infoHint: (companyName) => `Dein Mandant ${companyName} ist wieder aktiv.`,
    infoEnd: 'Du kannst Dich jetzt wie gewohnt anmelden.',
    buttonLogin: 'Zur Anmeldung',
    error: 'Ein unbekannter Fehler ist aufgetreten.',
    errorExpired: 'Der Link ist abgelaufen. Die Löschfrist ist bereits überschritten.',
    errorInvalid: 'Der Link ist ungültig.',
    errorNotFound: 'Der Mandant wurde nicht gefunden.',
    errorAlreadyRestored: 'Der Mandant wurde bereits wiederhergestellt.',
    success: 'Mandant erfolgreich wiederhergestellt!',
    // orgaMAX: new brand design, a success state with a tick instead of a green message
    brandSuccessTitle: 'Mandant wiederhergestellt',
    brandErrorTitle: 'Mandant nicht wiederhergestellt',
  },
  productNameWithPrefix,
  gaEvent: {
    doi: {
      category: 'DOI',
      action: 'Rechnung abschließen',
      label: (subscriptionTypeTypesLabel) => `DOI durchgeführt ${subscriptionTypeTypesLabel}`,
    },
  },

  tenantExport: {
    heading: 'Mandantenexport',
    description:
      'Im Mandantenexport sind die zentralen Unternehmensdaten enthalten, darunter Stammdaten, Kontakte, Belege, Buchungen und geschäftliche Vorgänge, die Du zur Weiterverarbeitung oder Sicherung exportieren und herunterladen kannst.',
    loading: 'Datenexport wird geladen...',
    statusTitle: 'Status',
    preparing: 'Export wird vorbereitet...',
    starting: 'Export wird gestartet...',
    running: 'Export wird erstellt',
    startButton: 'Export starten',
    restartButton: 'Erneut starten',
    startButtonLoading: 'Export läuft...',
    downloadButton: 'Herunterladen',
    toasts: {
      startSuccess: 'Der Export wurde gestartet.',
      startError: 'Der Export konnte nicht gestartet werden.',
      finishSuccess: 'Der Export wurde erfolgreich abgeschlossen.',
      finishError: 'Der Export ist fehlgeschlagen.',
      downloadSuccess: 'Der Download wurde gestartet.',
      downloadError: 'Der Download konnte nicht gestartet werden.',
    },
    status: {
      none: 'Noch kein Export vorhanden',
      pending: 'Wird vorbereitet',
      success: 'Erfolgreich',
      failed: 'Fehlgeschlagen',
    },
  },
}
